/**
 * Client ต้นแบบของ Wickhaven
 *
 * พิสูจน์เกตเฟส 1: เปิดสองหน้าต่างแล้วเดินเห็นกัน ตีมอนสเตอร์ตัวเดียวกัน
 * และได้ EXP ทั้งคู่ โดยเซิร์ฟเวอร์ตัดสินดาเมจทั้งหมด
 *
 * หลักการที่ต้องรักษาไว้: client ส่งแค่ "อยากไปตรงนี้" กับ "อยากตีตัวนี้"
 * แล้ววาดตามผลที่ server ตอบกลับ ไม่คำนวณดาเมจและไม่สุ่มอะไรเอง
 *
 * โมเดลยังเป็นรูปทรงพื้นฐาน ส่วนโมเดล chibi เอฟเฟกต์ สกิล และ UI เต็ม
 * จะย้ายมาจาก demo/wickhaven.html ในขั้นถัดไป
 */

import { Client, getStateCallbacks, type Room } from "@colyseus/sdk";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Vector3, Color3, Color4, Matrix } from "@babylonjs/core/Maths/math";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import {
  CLASSES,
  CLASS_LIST,
  MONSTERS,
  slideMove,
  hasLantern,
  lanternPos,
  LANTERN_RANGE,
  parseWaypoints,
  endowOf,
  weaponEndow,
  endowCost,
  ENDOW_ELEMENTS,
  type Element as GameElement,
  findPath, zoneHalf,
  wallDistanceFast,
  getClass,
  getItem,
  findSkill,
  isAdvancedClass,
  getAdvancedClassFor,
  getAllSkillsForClass,
  expToNextLevel,
  jobExpToNextLevel,
  jobLevelCap,
  jobStatBonus,
  skillReqs,
  ZONES,
  STAT_KEYS,
  ITEMS,
  ZONES as ALL_ZONES,
  deriveStats,
  statCost,
  maxCarryWeight,
  bagSlots,
  bagWeight,
  autoSlot,
  mergeModifiers,
  cardSlotsOf,
  sparkElements,
  cardTotals,
  describeCard,
  insertDenial,
  parseCards,
  ELEMENT_NAMES,
  RACE_NAMES,
  SIZE_NAMES,
  DERBY,
  derbyMaxBet,
  derbyOdds,
  derbyProgress,
  BLESSING,
  BLESSING_ID,
  BLESSING_TH,
  blessingCost,
  describeBlessing,
  sizeMul,
  sizeOf,
  type MonsterDef,
  carryState,
  CARRY_TEXT,
  nextBagSlotLevel,
  BAG_SLOTS_MAX,
  BAG_SLOTS_BASE,
  BAG_SLOTS_PER_STEP,
  BAG_SLOTS_STEP,
  EQUIP_SLOTS,
  GENDERS,
  type EquipLook,
  RARITY_TH,
  DEFAULT_GENDER,
  parseGender, parseLook, encodeLook, defaultLook, randomLook, SKIN_COLORS, HAIR_COLORS, EYE_COLORS, HAIR_STYLES,
  type Gender, type CharacterLook,
  equipTotals,
  FREE_SKILL_RESET_BEFORE,
  portalAt,
  townZoneId,
  type PortalDef,
  LEARN_TEXT,
  describeNeeds,
  heldWeapons,
  weaponNeedMet,
  applySkillMods,
  gearSkillMods,
  describeSkillMods,
  passiveTotals,
  describePassive,
  parseGear,
  gearWeight,
  type GearPiece,
  parseOptions,
  setOfItem,
  setProgress,
  describeOption,
  optionQuality,
  rerollCost,
  rerollChances,
  OPTIONS,
  type RolledOption,
  SKILL_MAX_LV,
  learnDenial,
  skillAtLevel,
  skillMaxLv,
  shopStock,
  sellPrice,
  SHOP_RANGE,
  withEquipStats,
  REFINE_STONE_ID,
  PROTECT_STONE_ID,
  ALL_REFINE_STONES,
  MAX_REFINE_LEVEL,
  REFINEABLE_SLOTS,
  canRefineSlot,
  stoneForSlot,
  getRefineRule,
  refineGoldCost,
  refineTownDenial,
  refineBonus,
  type EquipSlotId,
  type ItemDef,
  type SkillDef,
  WEAPON_TYPE_NAMES,
  isTwoHanded,
  type Stats,
  type ClassLook,
  type VendingStall,
  type VendingItem,
  questsForClass,
  getQuest,
  type QuestProgressRecord,
  type QuestDef,
  isQuestReadyToClaim,
  npcsInZone,
  type NPCDef,
  NPC_INTERACT_RANGE,
  type StorageItem,
  STORAGE_MAX_SLOTS,
  STORAGE_TEXT,
} from "@wickhaven/shared";
import {
  filterQuests,
  formatObjectiveProgress,
  questTypeLabel,
  type QuestTab,
} from "./quest-view.js";
import { TradeView } from "./trade-view.js";
import { VendingSetupView, VendingBrowseView } from "./vending-view.js";
import { createEffects, setFxQuality } from "./effects.js";
import { sound } from "./sound.js";
import { createMinimap, type MapDot } from "./minimap.js";
import { createWindow } from "./windows.js";
import { buildWorld, setWorldQuality } from "./world.js";
import { ELEMENT_GLOW } from "./race-model.js";
import { fitCameraRadius } from "./cam-fit.js";
import { buffIcon, debuffIcon, skillIcon } from "./icons.js";
import { layoutSkillTree } from "./skill-tree-layout.js";
import { setupShadows, type Shadows } from "./shadows.js";
import { drawWorldGraph, drawZoneMap, type ZoneBox } from "./world-map.js";
import { createPortrait } from "./portrait.js";
import { approachPoint, inCastRange } from "./aim.js";
import {
  accountsAvailable,
  clearToken,
  createCharacter,
  deleteCharacter,
  httpBase,
  resolveServerUrl,
  listCharacters,
  loadToken,
  login,
  register,
  whoAmI,
  type CharacterInfo,
} from "./account.js";
import { createThumbMaker, DEFAULT_ROT } from "./thumbs.js";
import {
  ACTIONS,
  bind,
  buildLookup,
  canBind,
  defaultBindings,
  duplicates,
  keyLabel,
  loadBindings,
  saveBindings,
  type ActionId,
  type Bindings,
} from "./keys.js";
import { guideLines } from "./guide.js";
import { heldOn, markCombat } from "./combat-hold.js";
import {
  animateChibi,
  setGear,
  setHeldVisible,
  animateMonster,
  createChibi,
  createMonsterModel,
  createPetModel,
  animatePet,
  type ChibiModel,
  type MonsterModel,
  type PetModel,
} from "./models.js";
import { petStep, petHomePoint } from "./pet.js";
import { upgradeMonster, upgradeCharacter } from "./glb.js";
import { monsterModelUrl, characterModelUrl } from "./model-files.js";
import {
  PARTY_HELP,
  hpRatio,
  invitable,
  parsePartyCommand,
  partyMembers,
  remoteMembers,
  type PartyCommand,
  type RosterMember,
  type PartyPlayer,
} from "./party-view.js";
import {
  BAG_TABS,
  DEFAULT_FILTER,
  GEAR_TYPES,
  matchFilter,
  sortItems,
  TAB_ORDER,
  tabOf,
  type BagTab,
  type ItemFilter,
} from "./bag-view.js";
// side-effect import ที่ขาดไม่ได้ ตอน import @babylonjs/core แบบแยกโมดูล
// Scene.prototype.pick นิยามอยู่ในโมดูลนี้ ถ้าไม่ import คลิกจะพังเงียบ ๆ
import "@babylonjs/core/Culling/ray";
/**
 * shader ของ StandardMaterial
 *
 * ปกติ Babylon โหลดให้เองด้วย dynamic import แต่ถ้าโหลดไม่สำเร็จ
 * material จะไม่ "พร้อม" ตลอดกาล Babylon ก็ข้ามไม่วาด mesh นั้น
 * ผลคือเห็นแต่สีพื้นหลังโดยไม่มี error ใด ๆ
 * import ตรงนี้บังคับให้ shader ถูกรวมไปกับ bundle ไม่ต้องโหลดภายหลัง
 */
import "@babylonjs/core/Shaders/default.vertex";
import "@babylonjs/core/Shaders/default.fragment";
import "@babylonjs/core/Meshes/Builders/groundBuilder";
import "@babylonjs/core/Meshes/Builders/capsuleBuilder";
import "@babylonjs/core/Meshes/Builders/sphereBuilder";
import "@babylonjs/core/Meshes/Builders/boxBuilder";
import "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import "@babylonjs/core/Meshes/Builders/torusBuilder";

const SERVER_URL = resolveServerUrl(import.meta.env.VITE_SERVER_URL, location.hostname);

const canvas = document.getElementById("c") as HTMLCanvasElement;
const hud = document.getElementById("hud") as HTMLDivElement;
const log = document.getElementById("log") as HTMLDivElement;
const fx = document.getElementById("fx") as HTMLDivElement;
const errBox = document.getElementById("err") as HTMLDivElement;
const bar = document.getElementById("bar") as HTMLDivElement;
const worldBars = document.getElementById("worldbars") as HTMLDivElement;

/* ---------- ตั้งค่าที่ผู้เล่นเปิดปิดเองได้ ---------- */

interface Settings {
  /** เปิด/ปิดเสียงเอฟเฟกต์ (SFX) */
  sound: boolean;
  /** แสดงแถบ HP/MP เล็ก ๆ ใต้เท้าตัวเอง */
  selfBar: boolean;
  /** แสดงลูกศรชี้ลงเหนือหัวตัวเอง ไว้หาตัวตอนคนเยอะ */
  selfArrow: boolean;
  /**
   * คลิกมอนสเตอร์แล้วต่อสู้อัตโนมัติ
   *
   * เปิด = คลิกแล้วเดินเข้าไปตีเลย ตีต่อเนื่องจนกว่ามอนตาย กด Esc หรือเดินหนี
   * ปิด = คลิกแค่เลือกเป้า (เหมือน Tab) ต้องกด F เพื่อเริ่มตี
   * เหมาะกับคนที่ชอบคลิกดูมอนก่อน หรือสายยิงไกลที่ไม่อยากให้ตัวละครวิ่งเข้าไปเอง
   */
  clickAttack: boolean;
  /** โหมดจอยสติกควบคุมบนหน้าจอ (เปิดอัตโนมัติบนมือถือ/จอสัมผัส) */
  joystick: boolean;
  /** โหมดหน้าจอโล่งสำหรับมือถือ (ซ่อนปุ่มส่วนเกิน รวมเมนูเข้าดรอว์เวอร์) */
  mobileUI: boolean;
  /** ชื่อที่ใช้ล่าสุด เติมให้อัตโนมัติในหน้าเลือกตัวละคร */
  name: string;
  /** อาชีพที่เลือกล่าสุด */
  cls: string;
  /** เพศที่เลือกล่าสุด เติมให้อัตโนมัติในหน้าสร้างตัวละคร */
  gender: string;
  /** ชื่อบัญชีที่ใช้ล่าสุด กรอกให้อัตโนมัติ (ไม่เก็บรหัสผ่าน) */
  account: string;
  /** เห็นการ์ดแนะนำผู้เล่นใหม่แล้ว (client/src/guide.ts) */
  guideSeen: boolean;
  /** หน้าตาที่แต่งไว้ล่าสุดในหน้าสร้างตัวละคร (JSON · shared/looks.ts) ว่าง = ยังไม่เคยแต่ง */
  look: string;
  /** กราฟิกสูง: เงาจริง + หญ้า/ดอกไม้หนาแน่น (ปิด = ลื่นกว่าบนเครื่องอ่อน · ค่าเริ่ม: จอสัมผัสปิด) */
  hiGraphics: boolean;
}

const isTouchDevice = typeof window !== "undefined" && (
  ("ontouchstart" in window) ||
  (navigator.maxTouchPoints > 0) ||
  (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
);

const SETTINGS_KEY = "wickhaven.settings";
const DEFAULT_SETTINGS: Settings = {
  sound: true,
  selfBar: true,
  selfArrow: true,
  // ค่าเริ่มต้นเปิด ตรงกับพฤติกรรมเดิมก่อนมีตัวเลือกนี้ ผู้เล่นเดิมจะไม่รู้สึกว่าอะไรเปลี่ยน
  clickAttack: true,
  joystick: isTouchDevice,
  mobileUI: isTouchDevice,
  name: "ผู้ถือตะเกียง",
  cls: "warden",
  gender: DEFAULT_GENDER,
  account: "",
  guideSeen: false,
  hiGraphics: !isTouchDevice,
  look: "",
};

/**
 * โหลดตั้งค่าจากเครื่อง
 *
 * ต้องครอบ try/catch เพราะ localStorage เข้าถึงไม่ได้ในโหมดส่วนตัวของบางเบราว์เซอร์
 * ถ้าอ่านไม่ได้ก็ใช้ค่าเริ่มต้นไป ไม่ควรทำให้เกมเปิดไม่ขึ้น
 */
function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const s = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
      if (s.joystick === undefined) s.joystick = isTouchDevice;
      if (s.mobileUI === undefined) s.mobileUI = isTouchDevice;
      sound.setEnabled(s.sound !== false);
      return s;
    }
  } catch {
    // ใช้ค่าเริ่มต้น
  }
  return { ...DEFAULT_SETTINGS };
}

const settings = loadSettings();

function saveSettings(): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // บันทึกไม่ได้ก็ยังเล่นได้ แค่ไม่จำค่าไว้รอบหน้า
  }
}

/** การ์ดแนะนำผู้เล่นใหม่ (client/src/guide.ts) — สร้าง DOM เอง ไม่ต้องมีใน index.html */
let guideCard: HTMLDivElement | undefined;
function showGuide(): void {
  guideCard?.remove();
  const k = (a: ActionId): string => keyLabel(bindings[a]);
  const lines = guideLines({
    attack: k("attack"), skill1: k("skill1"), potion: k("bag1"),
    char: k("winChar"), skills: k("winSkill"), quest: k("winQuest"), map: k("winMap"),
  }, isTouchDevice);
  const card = document.createElement("div");
  guideCard = card;
  card.style.cssText = "position:fixed;left:50%;top:14%;transform:translateX(-50%);z-index:60;max-width:min(520px,calc(100vw - 32px));"
    + "background:rgba(20,16,12,.94);border:1px solid #c9a227;border-radius:12px;padding:14px 16px;color:#f5ecd7;font-size:14px;line-height:1.55;box-shadow:0 8px 28px rgba(0,0,0,.5)";
  card.innerHTML = `<div style="font-weight:700;color:#ffd77a;margin-bottom:6px">🏮 ยินดีต้อนรับสู่วิคเฮเวน</div>`
    + `<ol style="margin:0 0 10px;padding-left:20px">${lines.map((l) => `<li>${esc(l)}</li>`).join("")}</ol>`
    + `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px">`
    + `<a href="./handbook.html" target="_blank" rel="noopener" style="color:#ffd77a">📖 คู่มือเต็ม: สูตร มอน ไอเทม แผนที่</a>`
    + `<button type="button" style="background:#c9a227;color:#1c1408;border:0;border-radius:8px;padding:6px 14px;font-weight:700;cursor:pointer">เข้าใจแล้ว</button></div>`;
  card.querySelector("button")?.addEventListener("click", () => {
    card.remove();
    settings.guideSeen = true;
    saveSettings();
  });
  document.body.appendChild(card);
}

/** หยิบ element ตาม id แบบสั้น ๆ ใช้เฉพาะ element ที่มีแน่ใน index.html */
const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const uiHud = el("hud");
const uiName = el("pname");
const uiLv = el("plv");
const uiHpFill = el("hpFill");
const uiHpTxt = el("hpTxt");
const uiMpFill = el("mpFill");
const uiMpTxt = el("mpTxt");
const uiXpFill = el("xpFill");
const uiXpTxt = el("xpTxt");
const uiJobFill = el("jobFill");
const uiJobTxt = el("jobTxt");
const uiStats = el("stats");
const uiZoneName = el("zonename");
const uiMinimap = el<HTMLCanvasElement>("minimap");
const uiHelp = el("help");
const uiMenu = el<HTMLDivElement>("menu");
const uiWins = el<HTMLDivElement>("wins");
const uiQuestTracker = el<HTMLDivElement>("questtracker");
const questRecords = new Map<string, QuestProgressRecord>();
let activeQuestTab: QuestTab = "active";
const uiPortrait = el<HTMLCanvasElement>("portrait");
const uiSetPanel = el<HTMLDivElement>("setpanel");
const uiTarget = el("target");
const uiTName = el("tname");
const uiTLv = el("tlv");
const uiTHpFill = el("thpFill");
const uiTHpTxt = el("thpTxt");
const uiTMode = el("tmode");
const uiBuffs = el("buffs");
const uiChatLog = el<HTMLDivElement>("chatlog");
const uiChatInput = el<HTMLInputElement>("chatinput");
const uiDeath = el<HTMLDivElement>("death");
const uiDeathInfo = el("deathinfo");
const uiRevTown = el<HTMLButtonElement>("revTown");
const uiRevAd = el<HTMLButtonElement>("revAd");
const uiSelfBar = el("selfbar");
const uiSelfHp = uiSelfBar.querySelector(".track.hp i") as HTMLElement;
const uiSelfMp = uiSelfBar.querySelector(".track.mp i") as HTMLElement;

function selfPlayer(): any {
  // ช่วงเพิ่งเข้าห้อง/ย้ายโซน state ชุดแรกยังไม่มา players ยังไม่มี
  return room?.state?.players?.get(room.sessionId);
}

/**
 * แสดง error บนหน้าจอ ไม่ใช่แค่ใน console
 * ตอน dev บนเครื่องที่เปิด DevTools ไม่สะดวก (เช่นมือถือ) จะได้เห็นว่าพังตรงไหน
 */
function showError(where: string, err: unknown): void {
  const head = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  // เอาบรรทัดแรกของ stack ที่เป็นโค้ดเรา จะได้รู้ว่าพังตรงไหนโดยไม่ต้องเปิด DevTools
  const frame = err instanceof Error && err.stack
    ? (err.stack.split("\n").find((l) => l.includes("/src/")) ?? "").trim()
    : "";
  errBox.style.display = "block";
  errBox.textContent = frame ? `[${where}] ${head}\n${frame}` : `[${where}] ${head}`;
  console.error(where, err);
}

window.addEventListener("error", (e) => showError("หน้าเว็บ", e.error ?? e.message));
window.addEventListener("unhandledrejection", (e) => showError("promise", e.reason));

function initEngine(c: HTMLCanvasElement): Engine {
  try {
    return new Engine(c, true, { stencil: true });
  } catch {
    try {
      return new Engine(c, false, { stencil: false });
    } catch {
      throw new Error(
        "เบราว์เซอร์ไม่สามารถเปิด WebGL ได้ (GPU Context อาจเต็มหรือค้างจากการรีโหลดหลายครั้ง)\n"
        + "วิธีแก้ไข:\n"
        + "1. ปิดแท็บนี้แล้วเปิดแท็บใหม่ หรือปิดเบราว์เซอร์ทั้งหมดแล้วเปิดใหม่\n"
        + "2. หากยังไม่หาย ให้ตรวจสอบ edge://settings/system ว่าเปิด 'ใช้การเร่งประสิทธิภาพด้วยฮาร์ดแวร์' ไว้หรือไม่",
      );
    }
  }
}

/* ---------- ฉาก ---------- */
const engine = initEngine(canvas);
const scene = new Scene(engine);

window.addEventListener("beforeunload", () => {
  try {
    scene.dispose();
    engine.dispose();
  } catch {}
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    try {
      scene.dispose();
      engine.dispose();
    } catch {}
  });
}
scene.clearColor = new Color4(0.56, 0.8, 0.93, 1);

/** ค่ากล้องเริ่มต้น ใช้ตอนกด R หรือดับเบิลคลิกเพื่อรีเซ็ต */
const CAM_HOME = { alpha: -Math.PI / 2 - 0.55, beta: 0.92, radius: 22 };
const cam = new ArcRotateCamera("cam", CAM_HOME.alpha, CAM_HOME.beta, CAM_HOME.radius, Vector3.Zero(), scene);
cam.attachControl(canvas, true);
// ลากด้วยปุ่มขวาเพื่อหมุนกล้อง และกันเมนูคลิกขวาของเบราว์เซอร์ไม่ให้เด้ง
canvas.addEventListener("contextmenu", (e) => e.preventDefault());
// ดับเบิลคลิกบนพื้นจอเพื่อรีเซ็ตมุมกล้องกลับค่าเริ่มต้น
canvas.addEventListener("dblclick", () => {
  cam.alpha = CAM_HOME.alpha;
  cam.beta = CAM_HOME.beta;
  cam.radius = CAM_HOME.radius;
});
cam.lowerRadiusLimit = 6;
cam.upperRadiusLimit = 32;
/** ระยะกล้องที่ผู้เล่นซูมไว้ กับค่าที่ตั้งจริงรอบล่าสุด (หดลงเมื่อยืนริมขอบแผนที่ — cam-fit.ts) */
let camWantRadius = CAM_HOME.radius;
let camSetRadius = CAM_HOME.radius;
cam.wheelPrecision = 20;
/**
 * หมุนกล้องอิสระรอบตัว:
 * - beta: เงยได้อิสระเกือบขนานพื้น (1.45 rad ≈ 83°) และก้มมองจากมุมสูงได้ (0.15 rad ≈ 8.5°)
 * - alpha: หมุนรอบตัว 360 องศาได้อิสระไม่มีข้อจำกัด
 */
cam.lowerBetaLimit = 0.15;
cam.upperBetaLimit = 1.45;
cam.lowerAlphaLimit = null;
cam.upperAlphaLimit = null;
// ปล่อยให้โค้ดเลื่อนกล้องตามตัวเองได้ ไม่ให้ Babylon แย่งคุม target
cam.panningSensibility = 0;

/* ---------- โหมดเต็มหน้าจอ (Fullscreen) ---------- */

function isFullscreen(): boolean {
  return !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (!isFullscreen()) {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        (document.documentElement as any).webkitRequestFullscreen();
      }
      if ((screen as any).orientation?.lock) {
        try {
          await (screen as any).orientation.lock("landscape");
        } catch {}
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  } catch (err) {
    console.warn("Fullscreen toggle error:", err);
  }
  updateFullscreenUI();
  engine.resize();
}

function updateFullscreenUI(): void {
  const fs = isFullscreen();
  const icon = fs ? "🗗" : "⛶";
  const label = fs ? "ออกจากเต็มหน้าจอ" : "เต็มหน้าจอ";
  const fsBtn = document.getElementById("fullscreenBtn");
  if (fsBtn) {
    fsBtn.textContent = icon;
    fsBtn.title = label;
    fsBtn.classList.toggle("active", fs);
  }
  const startFs = document.getElementById("startFsBtn");
  if (startFs) {
    startFs.textContent = `${icon} ${label}`;
  }
  syncMenuState();
}

function setupFullscreen(): void {
  document.getElementById("fullscreenBtn")?.addEventListener("click", () => void toggleFullscreen());
  document.getElementById("startFsBtn")?.addEventListener("click", () => void toggleFullscreen());
  document.addEventListener("fullscreenchange", () => {
    updateFullscreenUI();
    setTimeout(() => engine.resize(), 100);
  });
  document.addEventListener("webkitfullscreenchange", () => {
    updateFullscreenUI();
    setTimeout(() => engine.resize(), 100);
  });
}

/* ---------- จอยสติกเสมือน (Virtual Joystick) สำหรับมือถือ ---------- */

let joyActive = false;
let joyPointerId = -1;
let joyKx = 0;
let joyKz = 0;
let wasMovingManually = false;
const JOY_MAX_RADIUS = 44;

function setupJoystick(): void {
  const zone = document.getElementById("joystick-zone");
  const knob = document.getElementById("joystick-knob");
  if (!zone || !knob) return;

  function updateJoyPosition(clientX: number, clientY: number): void {
    const rect = zone!.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    const dist = Math.hypot(dx, dy);
    const maxRadius = Math.max(16, (rect.width / 2) - 10);
    const clampedDist = Math.min(dist, maxRadius);
    const angle = Math.atan2(dy, dx);
    const knobX = Math.cos(angle) * clampedDist;
    const knobY = Math.sin(angle) * clampedDist;

    knob!.style.transform = `translate(calc(-50% + ${knobX.toFixed(1)}px), calc(-50% + ${knobY.toFixed(1)}px))`;

    // Deadzone 4px
    if (dist < 4) {
      joyKx = 0;
      joyKz = 0;
    } else {
      const norm = clampedDist / maxRadius;
      joyKx = Math.cos(angle) * norm;
      joyKz = -Math.sin(angle) * norm; // ดันขึ้นบนจอ (-dy) คือเดินไปข้างหน้า (+kz)
    }
  }

  zone.addEventListener("pointerdown", (e) => {
    if (joyActive) return;
    joyActive = true;
    joyPointerId = e.pointerId;
    try {
      zone.setPointerCapture(e.pointerId);
    } catch {}
    knob.style.transition = "";
    updateJoyPosition(e.clientX, e.clientY);
    e.preventDefault();
    e.stopPropagation();
  });

  zone.addEventListener("pointermove", (e) => {
    if (!joyActive || e.pointerId !== joyPointerId) return;
    updateJoyPosition(e.clientX, e.clientY);
    e.preventDefault();
    e.stopPropagation();
  });

  const stopJoy = (e: PointerEvent) => {
    if (!joyActive || e.pointerId !== joyPointerId) return;
    joyActive = false;
    joyPointerId = -1;
    joyKx = 0;
    joyKz = 0;
    knob.style.transition = "transform 0.12s ease-out";
    knob.style.transform = "translate(-50%, -50%)";
    setTimeout(() => {
      if (!joyActive) knob.style.transition = "";
    }, 130);
    e.preventDefault();
    e.stopPropagation();
  };

  zone.addEventListener("pointerup", stopJoy);
  zone.addEventListener("pointercancel", stopJoy);
}

function applyJoystickVisibility(): void {
  const zone = document.getElementById("joystick-zone");
  const visible = !!settings.joystick && !!room;
  if (zone) {
    zone.style.display = visible ? "block" : "none";
  }
  document.body.classList.toggle("has-joystick", visible);
}

function toggleJoystick(forcedState?: boolean): void {
  settings.joystick = forcedState !== undefined ? forcedState : !settings.joystick;
  saveSettings();
  applyJoystickVisibility();
  buildSetPanel();
  syncMenuState();
  addLog(`จอยสติกควบคุม: ${settings.joystick ? "เปิด" : "ปิด"}`);
}

/* ---------- โหมดหน้าจอโล่งสำหรับมือถือ (Clean Mobile UI) ---------- */

function applyMobileUI(): void {
  const isMobile = !!settings.mobileUI;
  document.body.classList.toggle("is-mobile-ui", isMobile);
  const btn = document.getElementById("uiModeBtn");
  if (btn) {
    btn.textContent = isMobile ? "🖥️" : "📱";
    btn.title = isMobile ? "สลับเป็นโหมดคอมพิวเตอร์ (เต็มรูปแบบ)" : "สลับเป็นโหมดมือถือ (หน้าจอโล่ง)";
    btn.classList.toggle("active", isMobile);
  }
  syncMenuState();
}

function toggleMobileUI(forced?: boolean): void {
  settings.mobileUI = forced !== undefined ? forced : !settings.mobileUI;
  saveSettings();
  applyMobileUI();
  buildSetPanel();
  addLog(settings.mobileUI ? "📱 สลับเป็นโหมดหน้าจอมือถือ (หน้าจอโล่ง)" : "🖥️ สลับเป็นโหมดคอมพิวเตอร์ (เต็มรูปแบบ)");
}

function toggleMobileDrawer(force?: boolean): void {
  const drawer = document.getElementById("mobiledrawer");
  if (!drawer) return;
  const isOpening = force !== undefined ? force : drawer.style.display === "none";
  drawer.style.display = isOpening ? "flex" : "none";
  if (isOpening) {
    syncDrawerAlerts();
  }
}

function syncDrawerAlerts(): void {
  const p = selfPlayer();
  const hasStatPts = p && (p.statPoints ?? 0) > 0;
  const hasSkillPts = p && (p.skillPoints ?? 0) > 0;
  const hasAnyPts = !!(hasStatPts || hasSkillPts);

  const grid = document.getElementById("mobileDrawerGrid");
  if (grid) {
    grid.querySelector(".drawer-card[data-action='winChar']")?.classList.toggle("alert", !!hasStatPts);
    grid.querySelector(".drawer-card[data-action='winSkill']")?.classList.toggle("alert", !!hasSkillPts);
  }

  // แจ้งเตือน ! บนปุ่ม Drawer (ขีด 3 ขีด) และบนรูปโปรไฟล์ตัวละคร
  document.querySelector(".mbtn-drawer")?.classList.toggle("alert", hasAnyPts);
  document.getElementById("portraitbox")?.classList.toggle("alert", hasAnyPts);
}

function buildMobileDrawer(): void {
  const grid = document.getElementById("mobileDrawerGrid");
  if (!grid) return;
  grid.textContent = "";

  const drawerItems: Array<{ glyph: string; label: string; action?: ActionId; danger?: boolean; open: () => void }> = [
    { glyph: "🧍", label: "ตัวละคร", action: "winChar", open: () => winChar.toggle() },
    { glyph: "🎒", label: "กระเป๋า", action: "winBag", open: () => winBag.toggle() },
    { glyph: "✨", label: "สกิล", action: "winSkill", open: () => winSkill.toggle() },
    { glyph: "🏪", label: "ร้านค้า", action: "winShop", open: () => winShop.toggle() },
    { glyph: "⛺", label: "ตั้งร้าน", open: () => openVendingSetup() },
    { glyph: "🔨", label: "ตีบวก", action: "winRefine", open: () => winRefine.toggle() },
    { glyph: "👥", label: "ปาร์ตี้", action: "winParty", open: () => winParty.toggle() },
    { glyph: "📜", label: "เควส", action: "winQuest", open: () => winQuest.toggle() },
    { glyph: "📖", label: "คู่มือ", open: () => openHandbook() },
    { glyph: "🕹️", label: "จอยสติก", open: () => toggleJoystick() },
    { glyph: "⛶", label: "เต็มจอ", open: () => { void toggleFullscreen(); } },
    { glyph: "⚙️", label: "ตั้งค่า", open: () => togglePanel(uiSetPanel) },
    { glyph: "⌨️", label: "ปุ่มลัด", open: () => winKeys.toggle() },
    { glyph: "❔", label: "คำแนะนำ", open: () => togglePanel(uiHelp) },
    { glyph: "🔄", label: "เลือกตัวละคร", open: () => { void leaveGame(false); } },
    { glyph: "🚪", label: "ออกจากเกม", danger: true, open: () => { void leaveGame(true); } },
  ];

  for (const item of drawerItems) {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "drawer-card" + (item.danger ? " danger" : "");
    if (item.action) card.dataset["action"] = item.action;
    card.innerHTML = `<span class="d-icon">${item.glyph}</span>`
      + `<span class="d-label">${item.label}</span>`
      + `<span class="dot">!</span>`;

    card.addEventListener("click", () => {
      toggleMobileDrawer(false);
      item.open();
      syncMenuState();
    });
    grid.appendChild(card);
  }
}

function setupMobileUI(): void {
  buildMobileDrawer();
  document.getElementById("closeDrawerBtn")?.addEventListener("click", () => toggleMobileDrawer(false));
  const drawer = document.getElementById("mobiledrawer");
  drawer?.addEventListener("click", (e) => {
    if (e.target === drawer) toggleMobileDrawer(false);
  });

  document.getElementById("uiModeBtn")?.addEventListener("click", () => toggleMobileUI());

  const uiChatCol = el<HTMLDivElement>("chatcol");
  const uiChatToggle = document.getElementById("chatToggleBtn");
  if (uiChatToggle && uiChatCol) {
    if (settings.mobileUI) {
      uiChatCol.classList.add("collapsed");
    }
    uiChatToggle.addEventListener("click", () => {
      uiChatCol.classList.toggle("collapsed");
    });
  }

  applyMobileUI();
}

const hemi = new HemisphericLight("hemi", new Vector3(0.2, 1, 0.1), scene);
hemi.intensity = 0.8;
hemi.groundColor = new Color3(0.35, 0.4, 0.45);
const sun = new DirectionalLight("sun", new Vector3(-0.4, -1, 0.5), scene);
sun.intensity = 0.5;

/**
 * ฉาก พื้นกับตะเกียงสร้างทันที ส่วนของตกแต่งรอรู้โซนจาก server ก่อน
 * จะได้วางหลบจุดเกิดมอนสเตอร์ตาม zones.json ได้
 */
setWorldQuality(settings.hiGraphics ? "high" : "low");
const world = buildWorld(scene);

/** เงาจริง (shadows.ts) เปิดตามตั้งค่ากราฟิก */
let shadows: Shadows | undefined;
function applyGraphics(): void {
  setWorldQuality(settings.hiGraphics ? "high" : "low");
  setFxQuality(settings.hiGraphics ? 1 : 0.5);
  if (settings.hiGraphics && !shadows) shadows = setupShadows(scene, sun, () => cam.target);
  else if (!settings.hiGraphics && shadows) { shadows.dispose(); shadows = undefined; }
}
applyGraphics();
const ground = world.ground;

/** ระบบเอฟเฟกต์ภาพ ไม่มีผลต่อเกม วาดตาม event ที่ server ส่งมาเท่านั้น */
const fx3d = createEffects(scene);

/** มินิแมพมุมขวาบน สร้างตอนรู้โซนจาก server */
let minimap: ReturnType<typeof createMinimap> | undefined;

/** รูปหน้าตัวละครมุมซ้ายบน สร้างตอนรู้ว่าเราเล่นอาชีพอะไร */
let portrait: ReturnType<typeof createPortrait> | undefined;

/** วาดมินิแมพ เรียกทุกเฟรม */
function updateMinimap(): void {
  if (!room || !minimap) return;
  const dots: MapDot[] = [];

  for (const [id, m] of room.state?.monsters ?? []) {
    const mob: any = m;
    if (mob.dead) continue;
    dots.push({
      x: mob.x,
      z: mob.z,
      kind: MONSTERS[mob.type]?.boss ? "boss" : "monster",
      selected: id === selectedId,
    });
  }
  for (const [id, pl] of room.state?.players ?? []) {
    const other: any = pl;
    if (other.dead || id === room.sessionId) continue;
    const myParty = room.state?.players?.get(room.sessionId)?.partyId;
    dots.push({ x: other.x, z: other.z, kind: myParty && other.partyId === myParty ? "party" : "player" });
  }

  // ส่งมุมกล้องไปด้วย มินิแมพจะได้หมุนให้ตรงกับทิศที่เห็นบนจอ
  minimap.draw(dots, myX, myZ, wantRy, cam.alpha);
}

/* ---------- ตัวแทนในฉาก ---------- */
/**
 * ตัวแทนหนึ่งตัวในฉาก
 *
 * `mesh` เป็นแคปซูล/ทรงกลมใสที่มองไม่เห็น ทำหน้าที่สองอย่าง
 *   1. เป็นตัวรับคลิก (โมเดลจริงตั้ง isPickable = false ทั้งหมด)
 *   2. เป็นจุดอ้างอิงตำแหน่งกลางตัว ที่โค้ดเดิมใช้วางแถบเลือดและเอฟเฟกต์
 * โมเดลจริงเป็นลูกของ mesh จึงขยับและหมุนตามเองโดยไม่ต้อง sync สองที่
 */
interface Avatar {
  mesh: Mesh;
  /** ตำแหน่งเป้าหมายจาก server ค่อย ๆ ขยับเข้าหาเพื่อให้ลื่น */
  tx: number;
  tz: number;
  try_: number;
  /** โมเดลที่เห็นจริง */
  model?: ChibiModel | MonsterModel;
  /** ชนิดมอนสเตอร์ ว่าง = เป็นผู้เล่น */
  monType?: string;
  /** ตำแหน่งเฟรมก่อน ใช้ดูว่ากำลังเดินอยู่ไหม (server ไม่ได้ส่งธงนี้มา) */
  px: number;
  pz: number;
  moving: boolean;
  /** performance.now ที่ท่าตีจะจบ 0 = ไม่ได้ตี */
  swingUntil: number;
  /**
   * ลายเซ็นของชุดที่สวมอยู่ ใช้ดูว่าต้องวาดของใหม่ไหม
   * (ของที่ใส่อยู่เปลี่ยนไม่บ่อย ไม่คุ้มที่จะสร้างใหม่ทุกเฟรม)
   */
  gearSig?: string;
  /**
   * สัตว์เลี้ยงที่ปล่อยอยู่ **ไม่ใช่ลูกของ `mesh`** มีตำแหน่งของตัวเองในโลก
   * จึงต้องสั่ง dispose เองตอนผู้เล่นออกจากห้อง ไม่ได้หายไปพร้อม mesh
   */
  pet?: PetModel;
  /** id ของของในช่อง pet ที่วาดไว้ ใช้ดูว่าต้องสร้างตัวใหม่ไหม */
  petSig?: string;
  /**
   * performance.now ที่จะเก็บอาวุธ ถ้ายังไม่ถึงแปลว่ายังอยู่ในการต่อสู้
   * ขยับออกไปทุกครั้งที่ตี ถูกตี หรือร่ายสกิลเอง (ได้รับบัฟจากเพื่อนไม่นับ)
   */
  combatUntil?: number;
  /** กำลังล็อกเป้าสั่งตีอยู่ (`targetId` ที่ server sync มา) */
  fighting?: boolean;
  /** รหัสอาชีพ */
  classId?: string;
  /** ประเภทอาวุธสำหรับท่าทางอนิเมชัน */
  weaponType?: string;
  /** ประเภทท่าทางปัจจุบัน: โจมตี / บัฟ / เวท */
  swingType?: "attack" | "buff" | "spell";
  /** ระยะเวลาของท่าทาง (ms) */
  swingDuration?: number;
}

function defaultWeaponType(classId: string): string {
  switch (classId) {
    case "warden":
    case "bastion":
      return "sword_1h";
    case "brawler":
    case "berserker":
      return "knuckle_2h";
    case "arcanist":
    case "elementalist":
      return "staff_1h";
    case "ranger":
    case "hunter":
      return "bow_2h";
    case "shade":
    case "assassin":
      return "dagger_1h";
    case "mender":
    case "saint":
      return "lantern_1h";
    default:
      return "sword_1h";
  }
}

const players = new Map<string, Avatar>();
const monsters = new Map<string, Avatar>();

export interface NpcAvatar {
  def: NPCDef;
  mesh: Mesh;
  model: ChibiModel;
}

const npcs = new Map<string, NpcAvatar>();
let pendingNpcId = "";

function spawnZoneNpcs(zoneId: string): void {
  for (const n of npcs.values()) {
    n.mesh.dispose();
    n.model.root.dispose();
  }
  npcs.clear();

  const list = npcsInZone(zoneId);
  for (const def of list) {
    const mesh = MeshBuilder.CreateCapsule(`npc-${def.id}`, { height: 1.8, radius: 0.4 }, scene);
    mesh.visibility = 0;
    mesh.position.set(def.x, 0.9, def.z);
    mesh.rotation.y = def.ry ?? 0;
    mesh.metadata = { kind: "npc", id: def.id, castShadow: true };

    const model = createChibi(scene, { ...def.look, weapon: "lantern" }, undefined, "male");
    model.root.parent = mesh;
    model.root.position.y = -0.9;
    makeUnpickable(model.root);

    npcs.set(def.id, { def, mesh, model });
  }
}

/** ปิดการรับคลิกทั้งโมเดล ให้เหลือแค่กล่องรับคลิกตัวเดียว */
function makeUnpickable(root: { getChildMeshes: () => Mesh[] }): void {
  for (const m of root.getChildMeshes()) m.isPickable = false;
}

/**
 * หน้าตาของของที่ผู้เล่นคนนี้สวมอยู่ เรียงตามผังช่องเพื่อให้ลำดับคงที่
 *
 * ของที่ไม่มี `look` (แหวน ต่างหู) ถูกข้ามไป เล็กเกินกว่าจะเห็นบนตัวละคร
 * จึงไม่คุ้มที่จะเปลืองก้อนในฉาก
 */
function gearLooks(equipped: unknown): EquipLook[] {
  const get = (slot: string): string =>
    String((equipped as { get?: (k: string) => unknown })?.get?.(slot) ?? "");
  const out: EquipLook[] = [];
  for (const slot of EQUIP_SLOTS) {
    const look = ITEMS[get(slot.id)]?.look;
    if (look) out.push(look);
  }
  return out;
}

/**
 * วาดของที่สวมอยู่ใหม่ ถ้าชุดเปลี่ยนจริง
 *
 * **เทียบลายเซ็นก่อนเสมอ** `onChange` ของผู้เล่นยิงทุกครั้งที่ตำแหน่งขยับ
 * ซึ่งคือทุก patch ถ้าสร้างของใหม่ทุกครั้งจะสร้าง-ทิ้ง mesh หลายสิบก้อนต่อวินาที
 * ต่อผู้เล่นหนึ่งคน
 */
function refreshGear(av: Avatar, p: unknown): void {
  const model = av.model as ChibiModel | undefined;
  if (!model?.anchors) return;

  const equipped = (p as { equipped?: unknown }).equipped;
  const refines = (p as { refines?: unknown }).refines;
  const cards = (p as { cards?: unknown }).cards;
  const wCards = String((cards as { get?: (k: string) => unknown })?.get?.("weapon") ?? "");
  const sig = `${gearSig(equipped, refines)}#${wCards}`;
  if (sig === av.gearSig) return;
  av.gearSig = sig;

  const looks = gearLooks(equipped);
  const wRefine = Number((refines as { get?: (k: string) => unknown })?.get?.("weapon") ?? 0);
  const wingRefine = Number((refines as { get?: (k: string) => unknown })?.get?.("wings") ?? 0);
  const flames = sparkElements(wCards, ITEMS).map((e) => ELEMENT_GLOW[e]);
  setGear(scene, model, looks, wRefine, wingRefine, flames);

  // อัปเดตชนิดอาวุธสำหรับท่าทางอนิเมชัน
  const wId = String((equipped as { get?: (k: string) => unknown })?.get?.("weapon") ?? "");
  if (wId && ITEMS[wId]?.weaponType) {
    av.weaponType = ITEMS[wId].weaponType;
  } else if (av.classId) {
    av.weaponType = defaultWeaponType(av.classId);
  }

  // รูปหน้าใน HUD เห็นช่วงหัว หมวกกับของบนหน้าจึงต้องเปลี่ยนตามด้วย
  const id = (av.mesh.metadata as { id?: string } | undefined)?.id;
  if (id && id === room?.sessionId) {
    const cls = CLASSES[String((p as { cls?: string }).cls ?? "")] ?? getClass("warden");
    portrait?.setLook(cls.look, looks);
  }
}

/**
 * ปล่อยหรือเก็บสัตว์เลี้ยงตามของในช่อง `pet`
 *
 * **แยกจาก `refreshGear` เพราะเป็นของคนละชนิด** สัตว์เลี้ยงไม่ได้แขวนบนตัวละคร
 * แต่เป็นก้อนอิสระที่อยู่ระดับฉากและมีตำแหน่งของตัวเอง (ดู `client/src/pet.ts`)
 *
 * เทียบลายเซ็นก่อนเหมือนกัน เพราะ `onChange` ยิงทุก patch คือทุกครั้งที่ขยับ
 */
function refreshPet(av: Avatar, p: unknown): void {
  const equipped = (p as { equipped?: unknown }).equipped;
  const refines = (p as { refines?: unknown }).refines;
  const id = String((equipped as { get?: (k: string) => unknown })?.get?.("pet") ?? "");
  const petLvl = Number((refines as { get?: (k: string) => unknown })?.get?.("pet") ?? 0);
  const sig = `${id}:${petLvl}`;
  if (sig === av.petSig) return;
  av.petSig = sig;

  // **dispose(false, false)** วัสดุถูกแคชใช้ร่วมกันทั้งฉากตามสี
  // สั่งลบวัสดุทีเดียวสีของผู้เล่นคนอื่นที่ใช้สีเดียวกันจะหายไปด้วย
  av.pet?.root.dispose(false, false);
  av.pet = undefined;

  const look = ITEMS[id]?.pet;
  if (!look) return;

  const pet = createPetModel(scene, look, petLvl);
  // เกิดมาตรงจุดที่ควรยืนเลย ไม่ใช่ที่ (0,0) แล้ววิ่งเข้ามาจากกลางแผนที่
  const home = petHomePoint(
    { x: av.mesh.position.x, z: av.mesh.position.z },
    av.mesh.rotation.y,
  );
  pet.root.position.set(home.x, 0, home.z);
  pet.root.rotation.y = av.mesh.rotation.y;
  av.pet = pet;
}

/** ลายเซ็นของชุดที่ใส่อยู่ ใช้ดูว่าต้องวาดของใหม่ไหม */
function gearSig(equipped: unknown, refines?: unknown): string {
  const getEq = (slot: string): string =>
    String((equipped as { get?: (k: string) => unknown })?.get?.(slot) ?? "");
  const getRf = (slot: string): number =>
    Number((refines as { get?: (k: string) => unknown })?.get?.(slot) ?? 0);
  return EQUIP_SLOTS.map((s) => `${getEq(s.id)}:${getRf(s.id)}`).join("|");
}

function makePlayerAvatar(
  sessionId: string,
  classId: string,
  isSelf: boolean,
  gender: Gender = DEFAULT_GENDER,
  custom?: CharacterLook,
): Avatar {
  const mesh = MeshBuilder.CreateCapsule(`p-${sessionId}`, { height: 1.8, radius: 0.4 }, scene);
  const look = (CLASSES[classId] ?? getClass("warden")).look;
  // แคปซูลเป็นตัวรับคลิก ไม่ให้เห็น แต่ยังต้อง "visible" อยู่ ไม่งั้น scene.pick ข้าม
  mesh.visibility = 0;
  mesh.position.y = 0.9;
  mesh.metadata = { kind: "player", id: sessionId, castShadow: true };

  const model = createChibi(scene, look, undefined, gender, custom);
  model.root.parent = mesh;
  // เท้าอยู่ที่ y = 0 ของโมเดล แต่ mesh วางที่กลางตัว จึงต้องดันโมเดลลงครึ่งตัว
  model.root.position.y = -0.9;
  makeUnpickable(model.root);

  // มีไฟล์ .glb ของอาชีพนี้ก็สลับเป็นโมเดลจริงเมื่อโหลดเสร็จ ระหว่างนั้นเห็นรูปทรงเดิม
  const charGlbUrl = characterModelUrl(classId, gender);
  if (charGlbUrl) void upgradeCharacter(scene, model, classId, gender, charGlbUrl, custom);

  return {
    mesh, model, tx: 0, tz: 0, try_: 0,
    px: 0, pz: 0, moving: false, swingUntil: 0, gearSig: "", petSig: "",
    combatUntil: 0, fighting: false,
    classId,
    weaponType: defaultWeaponType(classId),
  };
}

function makeMonsterAvatar(id: string, type: string): Avatar {
  const def = MONSTERS[type];
  const size = def?.look?.size ?? (def?.boss ? 1.9 : type === "boar" ? 1.1 : 0.9);
  const mesh = MeshBuilder.CreateSphere(`m-${id}`, { diameter: size, segments: 8 }, scene);
  mesh.visibility = 0;
  mesh.position.y = size / 2;
  mesh.metadata = { kind: "monster", id, castShadow: true };

  const model = createMonsterModel(scene, type);
  model.root.parent = mesh;
  model.root.position.y = -size / 2;
  makeUnpickable(model.root);
  // มีไฟล์ .glb ของชนิดนี้ก็สลับเป็นโมเดลจริงเมื่อโหลดเสร็จ ระหว่างนั้นเห็นรูปทรงเดิม
  const glbUrl = monsterModelUrl(type);
  if (glbUrl) void upgradeMonster(scene, model, type, size, glbUrl);

  return {
    mesh, model, monType: type, tx: 0, tz: 0, try_: 0,
    px: 0, pz: 0, moving: false, swingUntil: 0,
  };
}

/** เล่นท่าตี/ร่ายบัฟ/ร่ายเวท ให้ตัวที่เพิ่งกระทำ เรียกจาก event hit หรือ skillCast */
function playSwing(
  av: Avatar | undefined,
  actionType: "attack" | "buff" | "spell" = "attack",
  durationMs = 340,
): void {
  if (!av) return;
  av.swingType = actionType;
  av.swingDuration = durationMs;
  av.swingUntil = performance.now() + durationMs;
}

/** ขับท่าทางของทุกตัวในฉาก เรียกทุกเฟรม */
/**
 * ชักอาวุธออกมาและนับเวลาถอยหลังก่อนเก็บ
 *
 * **ยืนเฉย ๆ ไม่ต้องถืออาวุธ** เรียกทุกครั้งที่ตีหรือถูกตี เวลาจึงต่ออายุไปเรื่อย ๆ
 * ระหว่างที่ยังสู้กันอยู่ แล้วเก็บเองเมื่อเงียบไปครบ `COMBAT_HOLD_MS`
 */
function enterCombat(av: Avatar | undefined): void {
  if (!av) return;
  markCombat(av, performance.now());
}

function animateAll(now: number, t: number): void {
  for (const av of [...players.values(), ...monsters.values()]) {
    const dx = av.mesh.position.x - av.px;
    const dz = av.mesh.position.z - av.pz;
    // 0.01 หน่วยต่อเฟรมราว 0.6 หน่วยต่อวินาที ต่ำกว่านี้ถือว่ายืนอยู่
    av.moving = Math.hypot(dx, dz) > 0.01;
    av.px = av.mesh.position.x;
    av.pz = av.mesh.position.z;

    if (!av.model) continue;
    const dur = av.swingDuration && av.swingDuration > 0 ? av.swingDuration : 340;
    const left = av.swingUntil - now;
    const swingProg = left > 0 ? 1 - left / dur : 0;
    if (av.monType) {
      animateMonster(av.model as MonsterModel, av.monType, av.moving, t, swingProg);
    } else {
      animateChibi(
        av.model as ChibiModel,
        av.moving,
        t,
        swingProg,
        av.weaponType ?? "sword_1h",
        av.swingType ?? "attack",
      );
      // ถืออาวุธเฉพาะตอนต่อสู้ ยืนเฉย ๆ หรือเดินทางให้มือเปล่า
      setHeldVisible(av.model as ChibiModel, heldOn(av, now));
    }
  }

  // อนิเมชัน NPC ยืนหายใจเบา ๆ และหันหน้าสบตากับผู้เล่นเมื่อเดินเข้าใกล้
  const me = room ? players.get(room.sessionId) : undefined;
  const myPos = me?.mesh?.position;
  let npcIdx = 0;
  for (const n of npcs.values()) {
    if (!n.model) continue;
    // ปรับ phase offset ตามลำดับ เพื่อไม่ให้ NPC ทุกตัวโยกตัวพร้อมกัน
    const phase = (npcIdx * 1.7) % 7;
    animateChibi(n.model, false, t + phase, 0, "sword_1h");

    // Dynamic Look-At: ถ้าผู้เล่นอยู่ใกล้ในระยะ 4.5 เมตร ให้ NPC ค่อย ๆ หันมอง
    const defaultRy = n.def.ry ?? 0;
    let targetRy = defaultRy;
    if (myPos) {
      const dx = myPos.x - n.mesh.position.x;
      const dz = myPos.z - n.mesh.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 4.5) {
        targetRy = Math.atan2(dx, dz);
      }
    }
    let diff = targetRy - n.mesh.rotation.y;
    while (diff < -Math.PI) diff += Math.PI * 2;
    while (diff > Math.PI) diff -= Math.PI * 2;
    n.mesh.rotation.y += diff * 0.08;

    npcIdx++;
  }
}

/**
 * เดินตามเจ้าของ **client คิดตำแหน่งเอง server ไม่เกี่ยว**
 * (เหตุผลอยู่หัวไฟล์ `client/src/pet.ts` — สัตว์เลี้ยงไม่มีผลต่อการต่อสู้)
 */
function animatePets(dt: number, t: number): void {
  for (const av of players.values()) {
    const pet = av.pet;
    if (!pet) continue;
    // เจ้าของตายหรือหลุดจากฉาก สัตว์เลี้ยงก็หายไปด้วย ไม่ยืนเฝ้าศพอยู่
    const alive = av.mesh.isEnabled();
    pet.root.setEnabled(alive);
    if (!alive) continue;

    const next = petStep(
      { x: pet.root.position.x, z: pet.root.position.z },
      { x: av.mesh.position.x, z: av.mesh.position.z },
      av.mesh.rotation.y,
      dt,
      pet.root.rotation.y,
    );
    pet.root.position.x = next.x;
    pet.root.position.z = next.z;
    pet.root.rotation.y = next.ry;
    animatePet(pet, next.moving, t);
  }
}

/* ---------- ตัวเลขดาเมจลอย ---------- */
/** ตัวเลขที่ลอยต่อเป้าเดียวกันติด ๆ กัน: เรียงซ้อนขึ้นไปแทนสุ่มกระจาย (รอบ 20) key = id เป้า */
const floatStack = new Map<string, { n: number; at: number }>();

function floatText(worldX: number, worldZ: number, worldY: number, text: string, cls: string, stackKey?: string): void {
  // แปลงพิกัดโลกเป็นพิกัดบนหน้าจอ เพื่อวาง div ทับ canvas
  const p = Vector3.Project(
    new Vector3(worldX, worldY, worldZ),
    Matrix.Identity(),
    scene.getTransformMatrix(),
    cam.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
  );
  // อยู่หลังกล้องก็ไม่ต้องวาด
  if (p.z < 0 || p.z > 1) return;
  const el = document.createElement("div");
  el.className = `dmg ${cls}`;
  el.textContent = text;
  el.style.left = `${p.x}px`;
  el.style.top = `${p.y}px`;
  // ลอยเฉียงสุ่ม ตัวเลขที่ตีรัว ๆ จะได้ไม่ทับกันเป็นก้อนเดียว · ดาเมจสูงตัวใหญ่ขึ้น
  let stacked = 0;
  if (stackKey) {
    const now = performance.now();
    const st = floatStack.get(stackKey);
    stacked = st && now - st.at < 450 ? Math.min(st.n + 1, 5) : 0;
    floatStack.set(stackKey, { n: stacked, at: now });
    if (floatStack.size > 200) floatStack.clear();
  }
  el.style.top = `${p.y - stacked * 24}px`;
  el.style.setProperty("--dx", `${Math.round((Math.random() - 0.5) * (stackKey ? 22 : 70))}px`);
  if (!cls.includes("crit") && /^\d{4,}$/.test(text)) el.classList.add("big");
  fx.appendChild(el);
  setTimeout(() => el.remove(), cls.includes("crit") ? 1200 : 1000);
}

function addLog(text: string): void {
  const el = document.createElement("div");
  el.textContent = text;
  log.prepend(el);
  while (log.childElementCount > 6) log.lastElementChild?.remove();
}

/* ---------- วัด ping ---------- */

/** เวลาไปกลับล่าสุด (ms) -1 = ยังไม่ได้วัด */
let pingMs = -1;
/** จำนวนผู้เล่นทั้งหมดที่ออนไลน์อยู่ทั้งเซิร์ฟเวอร์ (รวมทุกแมพและบอท) */
let serverTotalOnline = 0;

// วัดทุก 3 วินาที ถี่กว่านี้ไม่ได้ช่วยอะไรและกินแบนด์วิดท์เปล่า
window.setInterval(() => {
  room?.send("ping", { t: performance.now() });
}, 3000);

/* ---------- หน้าตาย ---------- */

function showDeath(expLoss: number, adLeft: number): void {
  uiDeath.classList.add("on");
  uiDeathInfo.textContent =
    "เลือกวิธีกลับเข้าสนาม การกลับไปจุดตะเกียงจะเสีย EXP เล็กน้อย";
  uiRevTown.textContent = `กลับไปจุดตะเกียง (เสีย EXP ${expLoss})`;
  uiRevAd.textContent = `ดูโฆษณาแล้วเกิดตรงนี้ (เหลือ ${adLeft} ครั้ง)`;
  uiRevAd.disabled = adLeft <= 0;
}

function hideDeath(): void {
  uiDeath.classList.remove("on");
}

uiRevTown.addEventListener("click", () => room?.send("revive", { mode: "town" }));
uiRevAd.addEventListener("click", () => {
  // ของจริงต้องเล่นโฆษณา rewarded ให้จบก่อนแล้วค่อยส่ง ตอนนี้จำลองไว้ก่อน
  addChat("<span class=\"sys\">(จำลอง) ดูโฆษณาจบแล้ว</span>");
  room?.send("revive", { mode: "ad" });
});

/* ---------- เชื่อมต่อ ---------- */
let room: Room | undefined;
/** จุดที่อยากเดินไป (ปลายทางที่คลิก หรือจุดประชิดมอนสเตอร์ที่ล็อกไว้) */
/**
 * ทางอ้อมป่าไปยังปลายทาง (wantX, wantZ) — คิดใหม่เมื่อปลายทางขยับ (ไล่มอนที่เดินอยู่) ไม่เกินทุก 300ms
 * เป็นแค่ความตั้งใจฝั่ง client · server ตัดสินทุกก้าวด้วย slideMove
 */
let navPath: Array<{ x: number; z: number }> = [];
let navGoal = { x: NaN, z: NaN };
let navAt = 0;
function navStep(gx: number, gz: number): { x: number; z: number } {
  const now = performance.now();
  const moved = Math.hypot(gx - navGoal.x, gz - navGoal.z);
  if (!(moved < 0.5) && (moved > 4 || now - navAt > 300 || Number.isNaN(navGoal.x))) {
    navGoal = { x: gx, z: gz };
    navAt = now;
    navPath = findPath(ALL_ZONES[currentZoneId()], myX, myZ, gx, gz);
  }
  while (navPath.length > 1 && Math.hypot(navPath[0]!.x - myX, navPath[0]!.z - myZ) < 0.6) navPath.shift();
  return navPath[0] ?? { x: gx, z: gz };
}

let wantX = 0;
let wantZ = 0;
let wantRy = Math.PI;

/**
 * ตำแหน่งที่ client ทำนายเอง
 *
 * ต้องแยกจากตำแหน่งของ mesh เพราะ mesh วิ่งไล่ค่าจาก server แบบ lerp
 * ถ้าเอา mesh.position ไปคิดก้าวถัดไปแล้วส่งกลับ จะกลายเป็น feedback loop
 * (ส่งค่าที่ตามหลังอยู่ตลอด server ก็ดึงกลับ ตัวละครเลยไม่ยอมหยุด)
 */
let myX = 0;
let myZ = 0;
let hasSelf = false;

/** ห่างจากตำแหน่งที่ server ยืนยันเกินเท่านี้ ให้เด้งไปตรงกับ server ทันที */
const RESYNC_DIST = 1.5;

/**
 * มอนสเตอร์ที่เลือกไว้
 *
 * แยกจากการสั่งตี เลือกไว้เฉย ๆ ก็ได้ (กด Tab) ใช้เล็งสกิลและดู HP
 * จะเดินเข้าไปตีก็ต่อเมื่อ autoAttack เป็น true (คลิกซ้ายที่ตัวมัน)
 */
let selectedId = "";
/** กำลังสั่งโจมตีปกติใส่เป้าที่เลือกอยู่หรือเปล่า */
let autoAttack = false;

/** เวลาสะสมของฉาก (วินาที) ใช้เป็นเฟสของท่าเดินและปีกผึ้ง */
let animTime = 0;

/**
 * ขั้นตอนบัญชี: เข้าสู่ระบบ สมัคร หรือเลือกเล่นแบบไม่บันทึก
 *
 * คืนโทเคนที่ใช้ได้ หรือสตริงว่างถ้าผู้เล่นเลือกเล่นแบบไม่บันทึก
 * โทเคนเดิมที่เก็บไว้ในเครื่องจะถูกตรวจกับเซิร์ฟเวอร์ก่อน ถ้ายังใช้ได้ก็ข้ามหน้านี้ไปเลย
 *
 * **ไม่ตรวจสิทธิ์อะไรที่นี่** server ตรวจโทเคนตอนเข้าห้องอยู่แล้ว
 * หน้านี้มีไว้ให้คนกรอกและเห็นข้อความตอบกลับเท่านั้น
 */
async function chooseAccount(notice = "", forcePrompt = false): Promise<string> {
  const base = httpBase(SERVER_URL);
  // **ต้องเปิดหน้าจอตรงนี้** ไม่ใช่ใน chooseCharacter ซึ่งทำงานทีหลัง
  // ไม่งั้นหน้านี้รอให้กดปุ่มที่ยังถูกซ่อนอยู่ แล้วเกมค้างที่ "กำลังเชื่อมต่อ…" ตลอดไป
  el<HTMLDivElement>("start").classList.add("on");
  const uiAuthBox = el<HTMLDivElement>("authbox");
  const uiCharBox = el<HTMLDivElement>("charbox");
  const uiUser = el<HTMLInputElement>("authuser");
  const uiPass = el<HTMLInputElement>("authpass");
  const uiLogin = el<HTMLButtonElement>("authlogin");
  const uiReg = el<HTMLButtonElement>("authreg");
  const uiGuest = el<HTMLButtonElement>("authguest");
  const uiMsg = el<HTMLDivElement>("authmsg");
  const uiWho = el<HTMLDivElement>("authwho");

  const say = (text: string, type: "ok" | "warn" | "error" | boolean = false): void => {
    uiMsg.textContent = text;
    uiMsg.classList.remove("ok", "warn");
    if (type === true || type === "ok") {
      uiMsg.classList.add("ok");
    } else if (type === "warn") {
      uiMsg.classList.add("warn");
    }
  };

  // กลับมาหน้านี้อีกรอบ ต้องซ่อนขั้นตัวละครและโชว์ขั้นบัญชีใหม่
  uiAuthBox.classList.remove("off");
  uiCharBox.classList.remove("on");
  uiWho.textContent = "";
  if (notice) say(notice);

  /** ผ่านขั้นบัญชีแล้ว เปิดหน้าเลือกตัวละครต่อ */
  const pass = (token: string, who: string): string => {
    uiAuthBox.classList.add("off");
    uiCharBox.classList.add("on");
    uiWho.innerHTML = who
      ? `บัญชี <b>${esc(who)}</b> — ความคืบหน้าจะถูกบันทึก`
      : "<b>โหมดไม่บันทึก</b> — ออกจากเกมแล้วความคืบหน้าจะหาย";
    if (who) {
      const out = document.createElement("button");
      out.type = "button";
      out.textContent = "ออกจากระบบ";
      out.addEventListener("click", () => {
        clearToken();
        location.reload();
      });
      uiWho.appendChild(out);
    }
    return token;
  };

  // ตรวจสอบความพร้อมของฐานข้อมูล ถ้ายังไม่พร้อมให้แจ้งเตือนผู้ใช้ แต่ยังให้ผู้ใช้เลือกเองได้
  const isDbReady = await accountsAvailable(base);
  if (!isDbReady) {
    say("⚠️ เซิร์ฟเวอร์กำลังเชื่อมต่อฐานข้อมูล... หากมีบัญชีอยู่แล้วกรุณารอสักครู่แล้วกดเข้าสู่ระบบ หรือเลือก 'เล่นโดยไม่บันทึก'", "warn");
  }

  // forcePrompt = เพิ่งถูกปฏิเสธมา ต้องให้เลือกเอง ไม่ใช่เข้าด้วยโทเคนเดิมซ้ำแล้วโดนปฏิเสธวนไป
  const saved = forcePrompt ? "" : loadToken();
  if (saved) {
    const who = await whoAmI(base, saved);
    if (who) return pass(saved, who);
    clearToken();
  }

  uiUser.value = settings.account;
  (settings.account ? uiPass : uiUser).focus();

  return new Promise<string>((resolve) => {
    // เรียกหน้านี้ได้หลายรอบ จึงต้องถอด listener เก่าทิ้งเมื่อจบ
    // ไม่งั้นปุ่มเดิมจะผูก handler ซ้อนกันเพิ่มขึ้นทุกรอบ
    const ac = new AbortController();
    const on = (el: HTMLElement, fn: (e: Event) => void): void => {
      el.addEventListener("click", fn, { signal: ac.signal });
    };
    const done = (token: string, who: string): void => {
      ac.abort();
      resolve(pass(token, who));
    };
    let busy = false;

    const run = async (
      action: (u: string, p: string) => Promise<{ ok: boolean; text?: string; username?: string }>,
      doing: string,
    ): Promise<void> => {
      if (busy) return;
      busy = true;
      say(doing, "ok");

      const username = uiUser.value.trim();
      const result = await action(username, uiPass.value);
      busy = false;

      if (!result.ok) {
        sound.playHit("hurt");
        say(result.text ?? "ไม่สำเร็จ", "error");
        return;
      }
      sound.playClick();
      // จำชื่อบัญชีไว้กรอกให้รอบหน้า แต่ไม่เก็บรหัสผ่าน
      settings.account = result.username ?? username;
      saveSettings();
      uiPass.value = "";
      done(loadToken(), result.username ?? username);
    };

    on(uiLogin, () => void run((u, pw) => login(base, u, pw), "กำลังเข้าสู่ระบบ…"));
    on(uiReg, () => void run((u, pw) => register(base, u, pw), "กำลังสมัคร…"));
    on(uiGuest, () => done("", ""));

    // Enter ในช่องรหัสผ่าน = เข้าสู่ระบบ ไม่ต้องย้ายมือไปคลิก
    uiPass.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        e.preventDefault();
        uiLogin.click();
      }
    }, { signal: ac.signal });
    uiUser.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        e.preventDefault();
        uiPass.focus();
      }
    }, { signal: ac.signal });
  });
}

/** ผลลัพธ์จากหน้าเลือกตัวละคร */
interface Chosen {
  name: string;
  cls: string;
  gender: Gender;
  /** หน้าตาที่เลือกเอง (JSON · shared/looks.ts) ใช้ตอนเล่นแบบไม่บันทึก ตัวที่บันทึกแล้ว server ใช้ค่าในฐานข้อมูล */
  look?: string;
  /** id ในฐานข้อมูล ว่าง = เล่นแบบไม่บันทึก */
  characterId: string;
  /** โซนที่จะเข้า ไม่ใส่ = เมือง */
  zone?: string;
  /** ตั๋วย้ายโซน (มีเฉพาะตอนรีโหลดหลังเดินเข้าประตู) */
  handoff?: string;
  /** ปาร์ตี้ (สำหรับจับคู่เข้าห้องดันเจี้ยนปาร์ตี้) */
  partyId?: string;
}

/** ตัวสร้างรูปตัวละครบนการ์ด ใช้ context เดียวร่วมกันทุกใบ */
let thumbs: ReturnType<typeof createThumbMaker> | undefined;

/**
 * หน้าเลือกตัวละคร
 *
 * บัญชีหนึ่งมีได้หลายตัว (ดู MAX_CHARACTERS ฝั่ง server) หน้านี้จึงเป็นผังช่อง
 * ช่องที่มีตัวละคร = กดเพื่อเล่น · ช่องว่าง = กดเพื่อสร้าง
 *
 * **การสร้างเกิดที่นี่ผ่าน HTTP ไม่ใช่ตอนเข้าห้อง** ห้องมีหน้าที่โหลดตัวที่เลือกมาแล้ว
 * จะได้ไม่ต้องเดาว่าผู้เล่นอยากสร้างใหม่หรืออยากเล่นตัวเดิม
 *
 * ผู้เล่นที่ไม่ได้ล็อกอิน (ไม่มีโทเคน) ข้ามผังช่องไปที่ฟอร์มสร้างเลย
 * เพราะไม่มีอะไรให้บันทึกอยู่แล้ว
 */
function chooseCharacter(notice = "", token = ""): Promise<Chosen> {
  const uiStart = el<HTMLDivElement>("start");
  const uiStartName = el<HTMLInputElement>("startname");
  const uiStartClasses = el<HTMLDivElement>("startclasses");
  const uiStartGender = el<HTMLDivElement>("startgender");
  const uiStartGo = el<HTMLButtonElement>("startgo");
  const uiBack = el<HTMLButtonElement>("charback");
  const uiCharMsg = el<HTMLDivElement>("charmsg");
  const uiSlots = el<HTMLDivElement>("charslots");
  const uiPick = el<HTMLDivElement>("charpick");

  const base = httpBase(SERVER_URL);
  const ids = Object.keys(CLASSES).filter((id) => !isAdvancedClass(id));
  let picked = CLASSES[settings.cls] && !isAdvancedClass(settings.cls) ? settings.cls : (ids[0] ?? "warden");
  let gender: Gender = parseGender(settings.gender);
  /** ช่องที่กำลังจะสร้างลงไป -1 = ยังไม่ได้เลือกช่อง (โหมดไม่บันทึก) */
  let targetSlot = -1;

  const say = (text: string, type: "ok" | "warn" | "error" | boolean = false): void => {
    uiCharMsg.textContent = text;
    uiCharMsg.classList.remove("ok", "warn");
    if (type === true || type === "ok") {
      uiCharMsg.classList.add("ok");
    } else if (type === "warn") {
      uiCharMsg.classList.add("warn");
    }
  };
  if (notice) say(notice);

  // ---- ปุ่มเลือกเพศ ----
  // สร้างจาก GENDERS ใน shared เพิ่มแบบที่สามในนั้นแล้วปุ่มขึ้นเอง
  uiStartGender.textContent = "";
  for (const g of GENDERS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.dataset["gender"] = g.id;
    btn.textContent = g.label;
    btn.addEventListener("click", () => {
      gender = g.id;
      // ทรงผมเริ่มต้นตามเพศ ถ้ายังไม่ได้เลือกเอง
      if (!lookTouched) look = { ...look, hairStyle: defaultLook(gender).hairStyle };
      paintGender();
      paintClasses();
      paintLook();
    });
    uiStartGender.appendChild(btn);
  }

  function paintGender(): void {
    for (const b of uiStartGender.querySelectorAll("button")) {
      b.classList.toggle("on", (b as HTMLElement).dataset["gender"] === gender);
    }
  }
  paintGender();

  // ---- แต่งหน้าตา (รอบ 20 · shared/looks.ts) ----
  // จำค่าล่าสุดไว้ในเครื่อง (settings.look) · ส่งไป server ตอนสร้าง (server ตรวจด้วย parseLook อีกชั้น)
  let look: CharacterLook = parseLook(settings.look, gender, CLASSES[picked]?.look.hair);
  let lookTouched = !!settings.look;
  const uiLookOpts = el<HTMLDivElement>("startlookopts");
  const uiDoll = el<HTMLCanvasElement>("startdoll");
  const swatches = (key: "skin" | "hair" | "eyes", label: string, colors: readonly string[]): string =>
    `<div class="lrow"><span>${label}</span>${colors.map((c) =>
      `<button type="button" class="sw" data-k="${key}" data-v="${c}" style="background:${c}" title="${c}"></button>`).join("")}</div>`;
  uiLookOpts.innerHTML = swatches("skin", "สีผิว", SKIN_COLORS)
    + swatches("hair", "สีผม", HAIR_COLORS)
    + `<div class="lrow"><span>ทรงผม</span>${HAIR_STYLES.map((h) =>
      `<button type="button" class="hs" data-k="hairStyle" data-v="${h.id}">${esc(h.th)}</button>`).join("")}</div>`
    + swatches("eyes", "สีตา", EYE_COLORS)
    + `<button type="button" class="rnd" data-rnd>🎲 สุ่มหน้าตา</button>`;
  uiLookOpts.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!b) return;
    if (b.dataset["rnd"] !== undefined) look = randomLook(Math.random);
    else if (b.dataset["k"]) look = { ...look, [b.dataset["k"]]: b.dataset["v"]! } as CharacterLook;
    lookTouched = true;
    sound.playClick();
    paintLook();
  });
  function paintLook(): void {
    for (const b of uiLookOpts.querySelectorAll<HTMLButtonElement>("button[data-k]")) {
      b.classList.toggle("on", look[b.dataset["k"] as keyof CharacterLook] === b.dataset["v"]);
    }
    const cls = CLASSES[picked];
    try {
      thumbs ??= createThumbMaker();
      if (cls) thumbs.draw(uiDoll, cls.look, undefined, gender, [], look);
    } catch { /* รูปเป็นของประดับ */ }
  }

  // ---- การ์ดอาชีพในฟอร์มสร้าง ----
  uiStartClasses.textContent = "";
  for (const id of ids) {
    const c = getClass(id);
    const card = document.createElement("button");
    card.type = "button";
    card.className = "ccard";
    card.dataset["cls"] = id;
    card.innerHTML =
      `<div class="nm">${esc(c.name)}</div>`
      + `<div class="ro">${esc(c.role)}</div>`
      + `<div class="de">${esc(c.desc)}</div>`
      + `<div class="st">`
      + STAT_KEYS.map((k) => `<span>${k} <b>${c.stats[k]}</b></span>`).join("")
      + `</div>`
      + `<div class="sk">`
      + c.skills.map((sk) =>
        `<i title="${esc(sk.th)}" style="background:${skillIcon(sk)} center / cover no-repeat"></i>`
      ).join("")
      + `</div>`
      + (c.advanced ? `<div class="cnext">จะไปต่อเป็น <b>${esc(c.advanced.name)}</b> — ${esc(c.advanced.desc)}</div>` : "");
    card.addEventListener("click", () => {
      picked = id;
      paintClasses();
    });
    uiStartClasses.appendChild(card);
  }

  function paintClasses(): void {
    for (const card of uiStartClasses.querySelectorAll(".ccard")) {
      card.classList.toggle("on", (card as HTMLElement).dataset["cls"] === picked);
    }
    paintLook();
  }
  paintClasses();

  const showPick = (on: boolean): void => {
    uiPick.classList.toggle("on", on);
    uiSlots.style.display = on ? "none" : "grid";
    uiBack.style.display = token ? "" : "none";
    if (on) {
      uiStartName.focus();
      uiStartName.select();
    }
  };

  uiStart.classList.add("on");

  return new Promise<Chosen>((resolve) => {
    const ac = new AbortController();
    const on = (node: Element, fn: () => void): void => {
      node.addEventListener("click", fn, { signal: ac.signal });
    };

    const finish = (chosen: Chosen): void => {
      settings.name = chosen.name;
      settings.cls = chosen.cls;
      settings.gender = chosen.gender;
      saveSettings();
      uiStart.classList.remove("on");
      ac.abort();
      resolve(chosen);
    };

    /** วาดผังช่องใหม่จากข้อมูลล่าสุดของเซิร์ฟเวอร์ */
    const refresh = async (): Promise<void> => {
      const { max, characters } = await listCharacters(base, token);
      uiSlots.textContent = "";
      thumbs ??= createThumbMaker();

      for (let slot = 0; slot < Math.max(1, max); slot++) {
        const c = characters.find((x) => x.slot === slot);
        const card = document.createElement("button");
        card.type = "button";

        if (!c) {
          card.className = "slotcard blank";
          card.textContent = "+ สร้างตัวละคร";
          on(card, () => {
            targetSlot = slot;
            say("");
            showPick(true);
          });
          uiSlots.appendChild(card);
          continue;
        }

        const cls = CLASSES[c.cls];
        card.className = "slotcard";
        card.innerHTML =
          `<canvas class="thumb" width="192" height="192"></canvas>`
          + `<div class="cname">${esc(c.name)}</div>`
          + `<div class="ccls">${esc(cls?.name ?? c.cls)}</div>`
          + `<div class="clv">Base ${c.baseLevel} · Job ${c.jobLevel} · โกลด์ ${c.gold}</div>`
          + `<div class="cstats">`
          + STAT_KEYS.map((k) => `<span>${k} <b>${c.stats[k] ?? "-"}</b></span>`).join("")
          + `</div>`
          + (cls?.advanced ? `<div class="cnext">จะไปต่อเป็น <b>${esc(cls.advanced.name)}</b></div>` : "")
          + `<button type="button" class="del">ลบ</button>`;

        const canvas = card.querySelector<HTMLCanvasElement>(".thumb");
        if (canvas && cls) {
          const g = parseGender(c.gender);
          thumbs.draw(canvas, cls.look, undefined, g, [], parseLook(c.look, g, cls.look.hair));
        }

        on(card, () => finish({
          name: c.name,
          cls: c.cls,
          gender: parseGender(c.gender),
          characterId: c.id,
          ...(c.zone ? { zone: c.zone } : {}),
        }));

        // ลบ: กดสองครั้งกันพลาด และต้องไม่ไปสั่งเล่นตัวนั้นด้วย
        const del = card.querySelector<HTMLButtonElement>(".del");
        if (del) {
          let armed = false;
          del.addEventListener("click", (e) => {
            e.stopPropagation();
            if (!armed) {
              armed = true;
              del.textContent = "ยืนยันลบ?";
              del.classList.add("danger");
              return;
            }
            void (async () => {
              const r = await deleteCharacter(base, token, c.id);
              say(r.ok ? `ลบ ${c.name} แล้ว` : (r.text ?? "ลบไม่สำเร็จ"), r.ok ? "ok" : "error");
              if (r.ok) sound.playClick();
              else sound.playHit("hurt");
              await refresh();
            })();
          }, { signal: ac.signal });
        }

        uiSlots.appendChild(card);
      }
    };

    // ---- ปุ่มสร้าง / เข้าเกม ----
    on(uiStartGo, () => {
      const name = uiStartName.value.trim().slice(0, 16) || DEFAULT_SETTINGS.name;

      // ไม่ได้ล็อกอิน: เล่นเลยแบบไม่บันทึก ไม่ต้องสร้างอะไรในฐานข้อมูล
      if (!token) {
        sound.playClick();
        settings.look = encodeLook(look);
        finish({ name, cls: picked, gender, look: encodeLook(look), characterId: "" });
        return;
      }

      void (async () => {
        say("กำลังสร้างตัวละคร…", "ok");
        const r = await createCharacter(base, token,
          { name, cls: picked, gender, slot: targetSlot, look: encodeLook(look) });
        settings.look = encodeLook(look);
        if (!r.ok || !r.character) {
          sound.playHit("hurt");
          say(r.text ?? "สร้างไม่สำเร็จ", "error");
          return;
        }
        sound.playLevelUp();
        finish({
          name: r.character.name,
          cls: r.character.cls,
          gender: parseGender(r.character.gender),
          characterId: r.character.id,
        });
      })();
    });

    on(uiBack, () => {
      say("");
      showPick(false);
    });

    uiStartName.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        e.preventDefault();
        uiStartGo.click();
      }
    }, { signal: ac.signal });

    if (token) {
      showPick(false);
      void refresh();
    } else {
      // โหมดไม่บันทึก ไม่มีช่องให้เลือก ไปที่ฟอร์มเลย
      uiSlots.style.display = "none";
      showPick(true);
    }
  });
}

async function connect(chosen: Chosen, token: string): Promise<void> {
  const client = new Client(SERVER_URL);

  // ส่งโทเคนกับตัวละครที่เลือกไปด้วย server จะโหลดตัวนั้นและบันทึกให้
  // ไม่มีโทเคนก็เข้าได้ แต่เป็นโหมดไม่บันทึก
  // ห้องแยกตามโซน (server ใช้ filterBy zone) ต้องบอกโซนทุกครั้ง
  // โซนที่ไม่รู้จัก (ข้อมูลเก่า หรือโซนที่ถูกถอดออก) กลับไปเข้าเมือง
  const zone = chosen.zone && ALL_ZONES[chosen.zone] ? chosen.zone : townZoneId();
  room = await client.joinOrCreate("field", {
    zone,
    cls: chosen.cls,
    name: chosen.name,
    gender: chosen.gender,
    ...(chosen.look ? { look: chosen.look } : {}),
    ...(token ? { token, characterId: chosen.characterId } : {}),
    ...(chosen.handoff ? { handoff: chosen.handoff } : {}),
    ...(chosen.partyId ? { partyId: chosen.partyId } : {}),
  });
  myChosen = { ...chosen, zone };

  // @colyseus/schema 5 ไม่มี onAdd/onChange บนตัว state แล้ว
  // ต้องผ่าน proxy ตัวนี้ (เรียก room.state.players.onAdd ตรง ๆ จะพังทันที)
  //
  // cast เพราะยังไม่ได้ผูกชนิด state เข้ากับ Room ฝั่ง client
  // (schema class อยู่ฝั่ง server เพื่อไม่ให้ shared ลาก Colyseus เข้ามา)
  // ถ้าจะทำให้ type ครบ ต้องย้าย schema ไปแพ็กเกจกลางที่ import ได้ทั้งสองฝั่ง
  const $ = getStateCallbacks(room) as unknown as (target: unknown) => any;

  $(room.state).players.onAdd((p: any, sessionId: string) => {
    const isSelf = sessionId === room!.sessionId;
    const g = parseGender(p.gender);
    const av = makePlayerAvatar(sessionId, p.cls, isSelf, g, parseLook(p.look, g, CLASSES[p.cls]?.look.hair));
    // ใส่ของที่คนนี้สวมอยู่ตั้งแต่เห็นครั้งแรก ไม่ใช่รอให้เปลี่ยนของก่อน
    refreshGear(av, p);
    players.set(sessionId, av);
    av.tx = p.x;
    av.tz = p.z;
    av.mesh.position.x = p.x;
    av.mesh.position.z = p.z;
    // **หลังตั้งตำแหน่งแล้วเท่านั้น** สัตว์เลี้ยงเกิดตรงจุดที่ควรยืนข้างหลังเจ้าของ
    // ถ้าเรียกก่อนจะเกิดที่ (0,0) แล้ววิ่งเข้ามาจากกลางแผนที่ให้เห็น
    refreshPet(av, p);
    if (isSelf) {
      wantX = p.x;
      wantZ = p.z;
      myX = p.x;
      myZ = p.z;
      hasSelf = true;
      buildSkillBar(p.cls);
      applyJoystickVisibility();
      applyMobileUI();

      const zoneId = String((room!.state as any)?.zone ?? "meadow");
      const zone = ZONES[zoneId];
      uiZoneName.textContent = zone?.name ?? zoneId;
      minimap = createMinimap(uiMinimap, zone);
      world.decorate(zone);
      // ตารางหาทาง (shared/terrain.ts) สร้างตอนโหลดโซน ไม่ใช่ตอนคลิกเดินครั้งแรก (~0.2 วิ)
      wallDistanceFast(zone, 0, 0);
      spawnZoneNpcs(zoneId);
      try {
        portrait = createPortrait(
          uiPortrait,
          (CLASSES[p.cls] ?? getClass("warden")).look,
          parseGender(p.gender),
          parseLook(p.look, parseGender(p.gender), CLASSES[p.cls]?.look.hair),
        );
      } catch (err) {
        // รูปหน้าเป็นของประดับ ใช้ WebGL context ตัวที่สอง ซึ่งบางเครื่องอาจไม่ให้
        // พลาดตรงนี้ต้องไม่ทำให้เข้าเกมไม่ได้
        console.warn("สร้างรูปหน้าตัวละครไม่สำเร็จ", err);
      }
    }
    $(p).onChange(() => {
      av.tx = p.x;
      av.tz = p.z;
      av.try_ = p.ry;
      av.mesh.setEnabled(!p.dead);
      // ของที่สวมอยู่เปลี่ยนเมื่อไร ตัวละครต้องเปลี่ยนตาม ทั้งของเราและของคนอื่น
      refreshGear(av, p);
      refreshPet(av, p);
      // ล็อกเป้าอยู่ = ตั้งใจจะสู้ ถืออาวุธไว้โดยไม่ต้องรอ event
      av.fighting = String(p.targetId ?? "") !== "";
      if (!isSelf) return;

      const learnedNow = getAllSkillsForClass(p.cls).filter((sk) => sk.kind !== "passive" && Number(p.skills?.get?.(sk.id) ?? 0) > 0).map((sk) => sk.id).join(",");
      if (p.cls === myClass && learnedNow !== learnedSig) buildSkillBar(p.cls);
      if (p.cls !== myClass) {
        buildSkillBar(p.cls);
        if (winSkill.isOpen()) drawSkillWindow();
        if (winChar.isOpen()) drawCharWindow();
        updateHud();
      }

      if (p.dead) clearTarget();
      // server แก้ตำแหน่งให้ (โดนหนีบความเร็ว ตาย หรือเกิดใหม่) ก็ยอมตาม
      if (p.dead || Math.hypot(p.x - myX, p.z - myZ) > RESYNC_DIST) {
        myX = p.x;
        myZ = p.z;
        wantX = p.x;
        wantZ = p.z;
      }
    });
    updateHud();
  });

  $(room.state).players.onRemove((_p: any, sessionId: string) => {
    // สัตว์เลี้ยงอยู่ระดับฉาก ไม่ได้เป็นลูกของ mesh จึงไม่หายไปเอง
    // (dispose(false, false) เพราะวัสดุใช้ร่วมกับผู้เล่นคนอื่น)
    players.get(sessionId)?.pet?.root.dispose(false, false);
    players.get(sessionId)?.mesh.dispose();
    players.delete(sessionId);
    updateHud();
  });

  $(room.state).monsters.onAdd((m: any, id: string) => {
    const av = makeMonsterAvatar(id, m.type);
    monsters.set(id, av);
    av.tx = m.x;
    av.tz = m.z;
    av.mesh.position.x = m.x;
    av.mesh.position.z = m.z;
    $(m).onChange(() => {
      av.tx = m.x;
      av.tz = m.z;
      av.try_ = m.ry;
      av.mesh.setEnabled(!m.dead);
      if (m.dead && selectedId === id) clearTarget();
    });
  });

  $(room.state).monsters.onRemove((_m: any, id: string) => {
    monsters.get(id)?.mesh.dispose();
    monsters.delete(id);
  });

  $(room.state).drops.onAdd((d: any, id: string) => {
    createGroundDrop(id, d);
  });

  $(room.state).drops.onRemove((_d: any, id: string) => {
    removeGroundDrop(id);
  });

  // ---- event จาก server ----
  room.onMessage("pickedDrop", (msg: any) => {
    sound.playLoot();
    const name = msg.itemId === "gold" ? `${msg.count} Gold` : (ITEMS[msg.itemId]?.name ?? msg.itemId);
    addLog(`🎁 เก็บ ${name}${msg.count > 1 && msg.itemId !== "gold" ? ` x${msg.count}` : ""}`);
  });

  room.onMessage("itemDropped", (msg: any) => {
    const name = ITEMS[msg.item]?.name ?? msg.item;
    addLog(`🗑️ ทิ้ง ${name}${msg.count > 1 ? ` x${msg.count}` : ""} ลงพื้น (เหลือ ${msg.left})`);
  });

  room.onMessage("pickFailed", (msg: any) => {
    if (msg?.text) addLog(`⚠️ ${msg.text}`);
  });
  // มอนติดสถานะจากสกิล: จำเวลาหมดไว้นับถอยหลังบนกรอบเป้าหมาย แล้วโชว์ชื่อสกิลลอยเหนือหัว
  room.onMessage("statusOn", (msg: any) => {
    const id = String(msg?.target ?? "");
    const kind = String(msg?.kind ?? "");
    if (!id || !kind) return;
    const per = mobStatusEnds.get(id) ?? new Map<string, { until: number; skill: string }>();
    per.set(kind, { until: performance.now() + Number(msg.sec ?? 0) * 1000, skill: String(msg.skill ?? "") });
    mobStatusEnds.set(id, per);
    const av = monsters.get(id);
    if (av) {
      const sk = findSkillAnywhere(String(msg.skill ?? ""));
      const p = av.mesh.position;
      floatText(p.x, p.z, p.y + 2.1, `${sk?.th ?? ""} · ${STATUS_TH[kind] ?? kind}`, "loot");
    }
  });

  room.onMessage("hit", (msg: any) => {
    // ท่าตีของคนที่ตี ใช้ event จาก server ไม่ใช่เดาจากฝั่ง client (รองรับทั้งผู้เล่นและมอนสเตอร์)
    playSwing(msg.kind === "player" ? players.get(msg.by) : monsters.get(msg.by), "attack", 340);
    // ชักอาวุธออกมาทั้งฝ่ายตีและฝ่ายโดนตี
    enterCombat(players.get(msg.by));
    enterCombat(players.get(msg.target));

    const victim = msg.kind === "player" ? monsters.get(msg.target) : players.get(msg.target);
    if (!victim) return;
    // ตัววัด DPS ของหุ่นฟาง (นับเฉพาะของเรา)
    if (msg.kind === "player" && msg.by === room?.sessionId && !msg.miss) {
      const mt = room?.state.monsters.get(msg.target)?.type;
      if (mt && MONSTERS[mt]?.dummy) dummyHits.push([performance.now(), Number(msg.damage) || 0]);
    }
    const pos = victim.mesh.position;
    if (msg.kind === "player" && msg.by === room?.sessionId) {
      wantRy = Math.atan2(pos.x - myX, pos.z - myZ);
    }

    const attacker = msg.kind === "player" ? players.get(msg.by) : monsters.get(msg.by);
    const isMine = (msg.kind === "player" && msg.by === room?.sessionId) || (msg.kind === "monster" && msg.target === room?.sessionId);

    if (msg.miss) {
      floatText(pos.x, pos.z, pos.y + 1.4, "พลาด", "miss");
      if (isMine) sound.playMiss();
    } else if (msg.blocked) {
      floatText(pos.x, pos.z, pos.y + 1.4, "บล็อก", "miss");
      if (isMine) sound.playBlock();
    } else {
      const self = msg.kind === "monster" && msg.target === room?.sessionId;
      // ของเรา = ทองตัวใหญ่ · คนอื่นตี = ขาวเล็กกว่า · เราโดน = แดง (รอบ 20)
      const mine = msg.kind === "player" && msg.by === room?.sessionId;
      floatText(pos.x, pos.z, pos.y + 1.4, String(msg.damage),
        `${msg.crit ? "crit" : self ? "hurt" : mine ? "mine" : "other"}${self && msg.crit ? " hurt" : ""}`, String(msg.target));
      if (isMine) {
        if (msg.crit) sound.playHit("crit");
        else if (self) sound.playHit("hurt");
        else {
          const wType = attacker?.weaponType ?? "sword_1h";
          if (wType.startsWith("knuckle")) sound.playHit("punch");
          else if (wType.startsWith("dagger")) sound.playHit("stab");
          else sound.playHit("melee");
        }
      }
    }

    // เอฟเฟกต์ของการโจมตีปกติ สกิลวาดตอน skillCast แทน เพราะต้องรู้เป้าทั้งชุด
    if (msg.skill) return;
    if (msg.kind === "player") {
      const cls: any = room?.state?.players?.get(msg.by);
      const kind = CLASSES[cls?.cls]?.autoKind ?? "melee";
      const hex = CLASSES[cls?.cls]?.look.accent ?? "#ffe7a3";
      if (kind === "melee" || !attacker) {
        fx3d.burst(pos.clone(), msg.crit ? "#ffd24a" : hex, msg.crit ? 2.2 : 1.5);
        fx3d.slashArc(pos.clone(), msg.crit ? "#fde047" : hex, attacker?.mesh.rotation.y ?? 0);
        if (!msg.miss) fx3d.sparks(pos.clone(), msg.crit ? "#ffd24a" : hex, msg.crit ? 26 : 10, msg.crit ? 1.4 : 0.9);
        if (msg.crit) fx3d.smoke(pos.clone());
      } else {
        const to = pos.clone();
        fx3d.projectile(attacker.mesh.position.clone(), to, hex, 26, () =>
          {
            fx3d.burst(to, msg.crit ? "#ffd24a" : hex, msg.crit ? 2 : 1.3);
            if (!msg.miss) fx3d.sparks(to, msg.crit ? "#ffd24a" : hex, msg.crit ? 22 : 8, msg.crit ? 1.3 : 0.8);
          });
        if (isMine) sound.playBowShoot();
      }
    } else if (!msg.miss && !msg.blocked) {
      fx3d.burst(pos.clone(), "#ff7a7a", 1.4);
      fx3d.slashArc(pos.clone(), "#f87171", attacker?.mesh.rotation.y ?? 0);
      fx3d.sparks(pos.clone(), "#ff8a7a", 8, 0.8);
    }
  });

  room.onMessage("kill", (msg: any) => {
    // ส่วนแบ่งจากเพื่อนในปาร์ตี้ (เราไม่ได้ตีเอง) ได้ EXP อย่างเดียว เขียนให้ต่างกัน
    if (msg.party) {
      addLog(`ส่วนแบ่งปาร์ตี้จาก ${msg.name} — EXP +${msg.exp}`);
      return;
    }
    if (msg.item || msg.card || (msg.gold && msg.gold > 0)) {
      sound.playLoot();
    }
    const bits = [`EXP +${msg.exp}`, `โกลด์ +${msg.gold}`];
    if (msg.shared > 1) bits.push(`แบ่ง ${msg.shared} คน`);
    if (msg.bonus > 1) bits.push(`โบนัสปาร์ตี้ +${Math.round((msg.bonus - 1) * 100)}%`);
    if (msg.item) bits.push(`ได้ ${itemName(msg.item)}`);
    if (msg.card) bits.push(`ได้ ${itemName(msg.card)}!`);
    addLog(`ล้ม ${msg.name} — ${bits.join(" · ")}`);
  });

  room.onMessage("levelUp", (msg: any) => {
    sound.playLevelUp();
    const me = room ? players.get(room.sessionId) : undefined;
    if (me) {
      fx3d.beam(me.mesh.position.clone(), "#ffd24a", 10, 2.2);
      fx3d.magicCircle(me.mesh.position.x, me.mesh.position.z, 2.4, "#ffd24a", 1.4);
      fx3d.buffRing(me.mesh.position.clone(), "#fef08a");
    }
    addLog(`เลเวลอัป! Base ${msg.baseLevel} · Job ${msg.jobLevel} · แต้ม ${msg.statPoints}`
      + (msg.skillPoints > 0 ? ` · แต้มสกิล ${msg.skillPoints}` : ""));
  });

  // แสงเลื่อนขั้นที่ตัวผู้เล่น ทุกคนในแผนที่เห็น (วงทองสามชั้น + ข้อความลอย)
  room.onMessage("derbyState", (msg: any) => {
    const prev = derby?.phase;
    derby = { ...msg, at: performance.now() };
    if (prev === "race" && msg.phase === "rest" && !msg.mine?.[msg.result?.winner]) derbyNote = msg.mine?.some((x: number) => x > 0) ? "รอบนี้ทายไม่ถูก ลองใหม่รอบหน้า" : "";
    if (winDerby.isOpen()) drawDerbyWindow();
  });
  room.onMessage("derbyFailed", (msg: any) => {
    derbyNote = String(msg?.text ?? "");
    if (winDerby.isOpen()) drawDerbyWindow();
  });
  room.onMessage("derbyPaid", (msg: any) => {
    derbyNote = `🎉 ทายถูก! ได้รับ ${Number(msg?.amount ?? 0).toLocaleString()} โกลด์`;
    addLog(derbyNote);
    sound.playSkill("buff");
    if (winDerby.isOpen()) drawDerbyWindow();
  });

  room.onMessage("advanceFx", (msg: any) => {
    const who = players.get(String(msg?.id ?? ""));
    if (!who) return;
    const pos = who.mesh.position;
    for (let k = 0; k < 3; k++) setTimeout(() => fx3d.buffRing(new Vector3(pos.x, 0.5 + k * 0.6, pos.z), "#f5c542"), k * 250);
    floatText(pos.x, pos.z, pos.y + 2.2, `👑 ${CLASSES[String(msg?.cls)]?.name ?? ""}!`, "crit");
    sound.playSkill("buff");
  });

  room.onMessage("classAdvanced", (msg: any) => {
    addLog(`🎉 เลื่อนขั้นเป็น ${msg?.name ?? msg?.cls} เรียบร้อยแล้ว!`);
    skillNote = `เลื่อนขั้นเป็น ${msg?.name ?? msg?.cls} สำเร็จ!`;
    if (msg?.cls) buildSkillBar(msg.cls);
    if (winSkill.isOpen()) drawSkillWindow();
    if (winChar.isOpen()) drawCharWindow();
    updateHud();
  });

  // ---- แต้มสกิล ----
  room.onMessage("skillLearned", (msg: any) => {
    const cls = CLASSES[myClass];
    const sk = cls ? findSkill(cls, msg.skill) : undefined;
    skillNote = `${sk?.th ?? msg.skill} ขึ้นเป็น Lv ${msg.lv} · เหลือ ${msg.skillPoints} แต้ม`;
    addLog(skillNote);
    if (winSkill.isOpen()) drawSkillWindow();
  });
  room.onMessage("learnFailed", (msg: any) => {
    skillNote = String(msg?.text ?? "ลงแต้มสกิลไม่ได้");
    addLog(skillNote);
    if (winSkill.isOpen()) drawSkillWindow();
  });
  room.onMessage("skillsReset", (msg: any) => {
    skillNote = `รีเซ็ตสกิลแล้ว ได้แต้มคืน ${msg.skillPoints} แต้ม`;
    addLog(skillNote);
    for (const k of Object.keys(pendingSkills)) delete pendingSkills[k];
    if (winSkill.isOpen()) drawSkillWindow();
  });

  room.onMessage("playerDied", (msg: any) => {
    if (msg.id === room?.sessionId) {
      showDeath(msg.expLoss ?? 0, msg.adRevivesLeft ?? 0);
    } else {
      addChat(`<span class="sys">${esc(msg.name ?? "ใครบางคน")} ล้มลง</span>`);
    }
  });

  room.onMessage("revived", (msg: any) => {
    hideDeath();
    addLog(msg.expLost > 0 ? `กลับเข้าสนาม เสีย EXP ${msg.expLost}` : "กลับเข้าสนามโดยไม่เสีย EXP");
  });

  room.onMessage("reviveFailed", (msg: any) => addLog(msg.text ?? "เกิดใหม่ไม่ได้"));

  room.onMessage("chat", (msg: any) => {
    const mine = msg.from === room?.sessionId;
    addChat(`<span class="who">${esc(msg.name)}</span>: ${esc(msg.text)}`, mine ? "me" : "");
  });

  // ---- ย้ายโซน ----
  room.onMessage("warp", (msg: any) => beginWarp(String(msg?.zone ?? ""), String(msg?.ticket ?? ""),
    String(msg?.name ?? ""), msg?.partyId ? String(msg.partyId) : undefined));
  room.onMessage("portalFailed", (msg: any) => {
    portalSent = "";
    addLog(String(msg?.text ?? "ผ่านประตูไม่ได้"));
  });

  // ---- ปาร์ตี้ ----
  room.onMessage("partyInvite", (msg: any) => showPartyInvite(String(msg?.name ?? "ใครบางคน")));
  room.onMessage("party", (msg: any) => {
    const text = String(msg?.text ?? "");
    if (!text) return;
    partyNote = text;
    addChat(`<span class="sys">${esc(text)}</span>`, "party");
  });
  room.onMessage("partyFailed", (msg: any) => {
    const text = String(msg?.text ?? "ทำรายการปาร์ตี้ไม่สำเร็จ");
    partyNote = text;
    addChat(`<span class="sys">${esc(text)}</span>`);
    if (winParty.isOpen()) drawPartyWindow();
  });
  room.onMessage("carryWarn", (msg: any) => addLog(String(msg?.text ?? "")));
  room.onMessage("partyRoster", (msg: any) => {
    partyRoster = Array.isArray(msg?.members) ? msg.members : [];
    partyFrameSig = "";
    if (winParty.isOpen()) drawPartyWindow();
  });
  room.onMessage("partyChat", (msg: any) => {
    addChat(`[ปาร์ตี้] <span class="who">${esc(msg.name)}</span>: ${esc(msg.text)}`, "party");
  });
  room.onMessage("partyLootRule", (msg: any) => {
    if (msg?.rule) currentPartyLootRule = msg.rule;
    if (winParty.isOpen()) drawPartyWindow();
  });

  room.onMessage("heal", (msg: any) => {
    const av = players.get(msg.target);
    if (!av) return;
    const pos = av.mesh.position;
    if (msg.amount > 0) floatText(pos.x, pos.z, pos.y + 1.4, `+${msg.amount}`, "heal");
    if (msg.mp > 0) floatText(pos.x, pos.z, pos.y + 1.8, `+${msg.mp} MP`, "healmp");
    fx3d.healSparks(pos.clone());
    fx3d.sparks(new Vector3(pos.x, 0.3, pos.z), "#6ee7a8", 10, 0.6);
    if (msg.target === room?.sessionId) sound.playSkill("heal");
  });

  room.onMessage("welcome", (msg: any) => {
    if (typeof msg.totalOnline === "number") serverTotalOnline = msg.totalOnline;
    addLog(msg.persist
      ? `ยินดีต้อนรับกลับ ${msg.name} — ความคืบหน้าถูกบันทึกอัตโนมัติ`
      : "โหมดไม่บันทึก — ออกจากเกมแล้วความคืบหน้าจะหาย");
    // ผู้เล่นใหม่: การ์ดแนะนำครั้งเดียว (ในเมืองไม่มีมอน ต้องรู้ว่าไปทางไหน)
    if (!settings.guideSeen && Number(msg.level ?? 1) <= 10) showGuide();
  });

  // ---- เควสและภารกิจ ----
  room.onMessage("questInit", (msg: any) => {
    questRecords.clear();
    for (const r of (msg?.records ?? [])) {
      questRecords.set(r.questId, r);
    }
    updateQuestTracker();
    if (winQuest.isOpen()) drawQuestWindow();
  });

  room.onMessage("questAccepted", (msg: any) => {
    if (msg?.record) {
      questRecords.set(msg.record.questId, msg.record);
      const q = getQuest(msg.record.questId);
      addChat(`<span class="sys">[เควส] รับภารกิจ: ${esc(q?.title ?? msg.record.questId)}</span>`);
      addLog(`รับภารกิจใหม่: ${q?.title ?? msg.record.questId}`);
      updateQuestTracker();
      if (winQuest.isOpen()) drawQuestWindow();
    }
  });

  room.onMessage("questProgress", (msg: any) => {
    const p = selfPlayer();
    const pos = p ? { x: p.x, z: p.z, y: 0.5 } : { x: 0, z: 0, y: 0.5 };
    for (const u of (msg?.updates ?? [])) {
      const rec = questRecords.get(u.questId);
      if (rec) rec.progress[u.objectiveId] = u.currentCount;
      const q = getQuest(u.questId);
      if (q) {
        const obj = q.objectives.find((o) => o.id === u.objectiveId);
        floatText(pos.x, pos.z, pos.y + 2.2, `${q.title}: ${u.currentCount}/${u.targetCount}`, "quest");
        addChat(`<span class="sys">[เควส] ${esc(q.title)}: ${esc(obj?.description ?? "")} (${u.currentCount}/${u.targetCount})</span>`);
      }
    }
    updateQuestTracker();
    if (winQuest.isOpen()) drawQuestWindow();
  });

  room.onMessage("questClaimed", (msg: any) => {
    if (msg?.record) {
      questRecords.set(msg.questId, msg.record);
      const q = getQuest(msg.questId);
      addChat(`<span class="sys" style="color:var(--gold);">[เควส] ✨ สำเร็จภารกิจ ${esc(q?.title ?? msg.questId)}! ได้รับ ${msg.reward?.gold ?? 0} Gold, ${msg.reward?.exp ?? 0} EXP</span>`);
      addLog(`✨ สำเร็จภารกิจ ${q?.title ?? msg.questId}`);
      updateQuestTracker();
      if (winQuest.isOpen()) drawQuestWindow();
    }
  });

  room.onMessage("questFailed", (msg: any) => {
    addChat(`<span class="sys" style="color:#ef4444;">[เควส] ${esc(msg?.reason ?? "ทำรายการไม่สำเร็จ")}</span>`);
  });

  room.onMessage("shopDone", (msg: any) => {
    const name = itemName(msg.item);
    shopNote = msg.mode === "buy"
      ? `ซื้อ ${name} แล้ว · เหลือ ${msg.purse} โกลด์`
      : `ขาย ${name} แล้ว · มี ${msg.purse} โกลด์`;
    addLog(msg.mode === "buy"
      ? `ซื้อ ${name} × ${msg.count} จ่าย ${msg.gold} โกลด์ (เหลือ ${msg.purse})`
      : `ขาย ${name} × ${msg.count} ได้ ${msg.gold} โกลด์ (รวม ${msg.purse})`);
    if (winShop.isOpen()) drawShopWindow();
  });

  room.onMessage("shopFailed", (msg: any) => {
    if (msg?.text) addLog(msg.text);
    // ให้เห็นในหน้าต่างร้านด้วย ไม่ใช่โผล่อยู่มุมจออีกฝั่งแล้วมองไม่เห็น
    shopNote = String(msg?.text ?? "ซื้อขายไม่สำเร็จ");
    if (winShop.isOpen()) drawShopWindow();
  });

  room.onMessage("syncCooldowns", (msg: any) => {
    if (msg?.leftMs) {
      const now = performance.now();
      for (const [sk, left] of Object.entries(msg.leftMs)) {
        cooldownUntil.set(sk, now + Number(left));
      }
    }
  });

  // ---- การแลกเปลี่ยน (Direct Trade) ----
  room.onMessage("tradeInvite", (msg: any) => {
    showTradeInvite(String(msg?.fromId ?? ""), String(msg?.fromName ?? "ใครบางคน"));
  });

  room.onMessage("tradeInvited", (msg: any) => {
    addLog(`ส่งคำชวนแลกเปลี่ยนไปยัง ${msg?.toName ?? "ผู้เล่น"} แล้ว`);
  });

  room.onMessage("tradeDeclined", (msg: any) => {
    addLog(`${msg?.partnerName ?? "ผู้เล่น"} ปฏิเสธคำชวนแลกเปลี่ยน`);
  });

  room.onMessage("tradeStarted", (msg: any) => {
    const self: any = room?.state?.players?.get(room?.sessionId ?? "");
    const inv: Record<string, number> = {};
    for (const [id, count] of (self?.items as any)?.entries?.() ?? []) {
      if (count > 0) inv[id] = count;
    }
    tradeView.startSession(room?.sessionId ?? "", String(msg.partnerId), String(msg.partnerName), msg.session);
    tradeView.render(inv);
  });

  room.onMessage("tradeUpdated", (msg: any) => {
    const self: any = room?.state?.players?.get(room?.sessionId ?? "");
    const inv: Record<string, number> = {};
    for (const [id, count] of (self?.items as any)?.entries?.() ?? []) {
      if (count > 0) inv[id] = count;
    }
    tradeView.updateSession(msg.session);
    tradeView.render(inv);
  });

  room.onMessage("tradeComplete", (msg: any) => {
    addLog(`✅ ${msg?.text ?? "การแลกเปลี่ยนเสร็จสมบูรณ์"}`);
    tradeView.close();
  });

  room.onMessage("tradeClosed", (msg: any) => {
    addLog(`การแลกเปลี่ยนสิ้นสุด: ${msg?.reason ?? "ยกเลิก"}`);
    tradeView.close();
  });

  room.onMessage("tradeDenial", (msg: any) => {
    addLog(`❌ แลกเปลี่ยนไม่ได้: ${msg?.text ?? msg?.reason ?? ""}`);
    tradeView.setNote(`❌ ${msg?.text ?? msg?.reason ?? ""}`);
  });

  // ---- ร้านค้าตั้งแผง (Player Vending) ----
  room.onMessage("vendingOpened", (msg: any) => {
    if (msg?.stall?.ownerId === room?.sessionId) {
      myStall = msg.stall;
      addLog(`🏪 เปิดร้านค้า "${msg.stall.title}" เรียบร้อยแล้ว`);
      vendingSetupView.setStall(myStall);
    }
  });

  room.onMessage("vendingClosed", (msg: any) => {
    if (msg?.ownerId === room?.sessionId) {
      myStall = null;
      addLog(`ร้านค้าของคุณปิดตัวลงแล้ว`);
      vendingSetupView.setStall(null);
    }
    if (vendingBrowseView.isOpen()) {
      vendingBrowseView.setNote("ร้านค้านี้ปิดแล้ว");
    }
  });

  room.onMessage("vendingStallData", (msg: any) => {
    if (msg?.stall) {
      const self: any = room?.state?.players?.get(room?.sessionId ?? "");
      vendingBrowseView.showStall(msg.stall);
      vendingBrowseView.render(self?.gold ?? 0);
    }
  });

  room.onMessage("vendingStallUpdated", (msg: any) => {
    if (vendingBrowseView.isOpen()) {
      // อัปเดตร้านค้าที่กำลังเปิดดูอยู่
      room?.send("vendingBrowse", { ownerId: msg.ownerId });
    }
  });

  room.onMessage("vendingBought", (msg: any) => {
    addLog(`🛒 ซื้อ ${msg.itemName} ×${msg.count} ราคา ${msg.totalPrice.toLocaleString()} G สำเร็จ`);
    if (winVendingBrowse.isOpen()) {
      room?.send("vendingBrowse", { ownerId: msg.ownerId });
    }
  });

  room.onMessage("vendingSold", (msg: any) => {
    addLog(`💰 ${msg.buyerName} ซื้อ ${msg.itemName} ×${msg.count} คุณได้รับ ${msg.payout.toLocaleString()} G (หักภาษี 3%: ${msg.tax.toLocaleString()} G)`);
  });

  room.onMessage("vendingDenial", (msg: any) => {
    addLog(`❌ ร้านค้า: ${msg.text ?? msg.reason ?? ""}`);
    if (winVending.isOpen()) vendingSetupView.setNote(msg.text ?? msg.reason);
    if (winVendingBrowse.isOpen()) vendingBrowseView.setNote(msg.text ?? msg.reason);
  });

  // ---- บทสนทนา NPC (NPC Dialog) ----
  room.onMessage("npcDialog", (msg: any) => {
    openNpcDialog(msg);
  });

  // ---- คลังเก็บของส่วนกลางบัญชี (Account Storage) ----
  room.onMessage("storageInit", (msg: any) => {
    storageItems = msg.items ?? [];
    storageGold = msg.gold ?? 0;
    storageMaxSlots = msg.maxSlots ?? STORAGE_MAX_SLOTS;
    if (winStorage.isOpen()) drawStorageWindow();
  });

  room.onMessage("storageUpdated", (msg: any) => {
    storageItems = msg.items ?? [];
    storageGold = msg.gold ?? 0;
    if (winStorage.isOpen()) drawStorageWindow();
  });

  room.onMessage("storageDenial", (msg: any) => {
    addLog(`❌ คลังสมบัติ: ${msg.text ?? msg.reason ?? ""}`);
    addChat(`<span class="sys" style="color:#ef4444;">[คลังสมบัติ] ${esc(msg.text ?? msg.reason ?? "")}</span>`);
  });

  room.onMessage("statAdded", (msg: any) => {
    addLog(`ลงแต้ม ${msg.stat} เป็น ${msg.value} · เหลือ ${msg.statPoints} แต้ม`);
    if (winChar.isOpen()) drawCharWindow();
  });

  room.onMessage("statFailed", (msg: any) => {
    if (msg?.text) addLog(msg.text);
    if (winChar.isOpen()) drawCharWindow();
  });

  room.onMessage("itemUsed", (msg: any) => {
    itemReadyAt = performance.now() + msg.cd * 1000;
  });

  room.onMessage("itemFailed", (msg: any) => addLog(msg.text ?? "ใช้ไอเทมไม่ได้"));

  room.onMessage("savePointSaved", (msg: any) => {
    addLog(`📍 [จุดเกิด] ${msg?.text ?? "บันทึกจุดเกิดแล้ว"}`);
    addChat(`<span class="sys" style="color:#38bdf8;">[จุดเกิด] ${esc(msg?.text ?? "บันทึกจุดเกิดแล้ว")}</span>`);
    floatText(myX, myZ, 2.0, "บันทึกจุดเกิดแล้ว!", "loot");
    sound.playSkill("bless");
    fx3d.pillar(new Vector3(myX, 0, myZ), "#38bdf8", 6, 1.2);
  });

  room.onMessage("teleported", (msg: any) => {
    if (msg?.text) {
      addLog(`🌀 ${msg.text}`);
      floatText(msg.x ?? myX, msg.z ?? myZ, 2.0, "วาร์ป!", "heal");
    }
    sound.playSkill("teleport");
    const targetX = typeof msg?.x === "number" ? msg.x : myX;
    const targetZ = typeof msg?.z === "number" ? msg.z : myZ;
    fx3d.warpFx(new Vector3(targetX, 0, targetZ));
    if (typeof msg?.x === "number" && typeof msg?.z === "number") {
      if (!msg.id || msg.id === room?.sessionId) {
        myX = msg.x;
        myZ = msg.z;
        const me = room ? players.get(room.sessionId) : undefined;
        if (me) {
          me.mesh.position.x = msg.x;
          me.mesh.position.z = msg.z;
          me.tx = msg.x;
          me.tz = msg.z;
          me.px = msg.x;
          me.pz = msg.z;
        }
        cam.target.copyFromFloats(msg.x, 1, msg.z);
      } else {
        const other = players.get(msg.id);
        if (other) {
          other.mesh.position.x = msg.x;
          other.mesh.position.z = msg.z;
          other.tx = msg.x;
          other.tz = msg.z;
          other.px = msg.x;
          other.pz = msg.z;
        }
      }
    }
  });

  // สวมใส่/ถอด server เป็นคนตัดสิน ที่นี่แค่บอกผลและวาดหน้าต่างใหม่
  room.onMessage("equipDone", (msg: any) => {
    addLog(String(msg.text ?? ""));
    // ของที่ใส่อยู่ไม่ได้อยู่ใน charSig (เป็น MapSchema คนละตัวกับ items)
    // จึงต้องสั่งวาดเองตรงนี้ ไม่งั้นช่องยังว่างอยู่จนกว่าจะมีอย่างอื่นเปลี่ยน
    if (winChar.isOpen()) drawCharWindow();
    if (winBag.isOpen()) drawBagWindow();
    if (winRefine?.isOpen()) drawRefineWindow();
  });
  room.onMessage("equipFailed", (msg: any) => addLog(msg.text ?? "สวมใส่ไม่ได้"));

  room.onMessage("refineDone", (msg: any) => {
    refineNote = String(msg.text ?? "ตีบวกเรียบร้อย");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    if (msg.success) {
      floatText(myX, myZ, 2.0, `ตีบวกสำเร็จ +${msg.newLevel}!`, "loot");
      sound.playLevelUp();
      fx3d.pillar(new Vector3(myX, 0, myZ), "#fbbf24", 5, 1.0);
    } else {
      floatText(myX, myZ, 2.0, `ตีบวกไม่สำเร็จ`, "crit");
      sound.playHit("melee");
    }
    if (winRefine?.isOpen()) drawRefineWindow();
    if (winChar?.isOpen()) drawCharWindow();
  });
  room.onMessage("cardDone", (msg: any) => {
    refineNote = String(msg?.text ?? "ใส่ไฟตะเกียงแล้ว");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    floatText(myX, myZ, 2.0, "ไฟตะเกียงติดแล้ว!", "loot");
    sound.playLoot();
    fx3d.buffRing(new Vector3(myX, 0.5, myZ), "#a855f7");
    if (winRefine?.isOpen()) drawRefineWindow();
  });
  room.onMessage("cardFailed", (msg: any) => {
    refineNote = String(msg?.text ?? "ใส่ไฟตะเกียงไม่ได้");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    if (winRefine?.isOpen()) drawRefineWindow();
  });
  room.onMessage("debuffOn", (msg: any) => addLog(`☠ ${msg?.text ?? "ติดสถานะผิดปกติ"}`));
  room.onMessage("rerollDone", (msg: any) => {
    refineNote = String(msg?.text ?? "ทอยออปชันใหม่แล้ว");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    sound.playLoot();
    // event มาก่อน state patch (รอบ 50ms) วาดซ้ำอีกทีหลัง patch มาถึง ไม่งั้นยังเห็นออปชันเก่า
    if (winRefine?.isOpen()) drawRefineWindow();
    setTimeout(() => { if (winRefine?.isOpen()) drawRefineWindow(); }, 150);
  });
  room.onMessage("endowDone", (msg: any) => {
    refineNote = String(msg?.text ?? "ร่ายธาตุสำเร็จ");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    sound.playLoot();
    fx3d.buffRing(new Vector3(myX, 0.5, myZ), ELEMENT_GLOW[msg?.element as GameElement] ?? "#ffffff");
    if (winRefine?.isOpen()) drawRefineWindow();
    setTimeout(() => { if (winRefine?.isOpen()) drawRefineWindow(); }, 150);
  });
  room.onMessage("endowFailed", (msg: any) => {
    refineNote = String(msg?.text ?? "ร่ายธาตุไม่สำเร็จ");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    if (winRefine?.isOpen()) drawRefineWindow();
  });
  room.onMessage("lanternLit", (msg: any) => {
    addLog(`🏮 จุดตะเกียงแห่ง${msg?.name ?? ""}แล้ว — วาร์ปกลับมาได้ด้วยผงตะเกียงนำทาง`);
    world.setLanternLit(true);
    sound.playLoot();
    fx3d.buffRing(new Vector3(myX, 0.5, myZ), "#ffb547");
  });
  room.onMessage("endowOn", (msg: any) => {
    const el = msg?.element as GameElement;
    addLog(`✨ อาวุธเป็นธาตุ${ELEMENT_NAMES[el] ?? ""} ${Math.round((msg?.duration ?? 0) / 60)} นาที`);
    fx3d.buffRing(new Vector3(myX, 0.5, myZ), ELEMENT_GLOW[el] ?? "#ffffff");
  });
  room.onMessage("rerollFailed", (msg: any) => {
    refineNote = String(msg?.text ?? "ทอยใหม่ไม่ได้");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    if (winRefine?.isOpen()) drawRefineWindow();
  });
  room.onMessage("refineFailed", (msg: any) => {
    refineNote = String(msg?.text ?? "ตีบวกไม่สำเร็จ");
    addLog(`[โรงตีเหล็ก] ${refineNote}`);
    if (winRefine?.isOpen()) drawRefineWindow();
  });

  // ของชุดเริ่มต้นที่เพิ่มเข้ามาทีหลัง ตัวละครเก่าได้ตอนเข้าเกมครั้งถัดไป
  room.onMessage("kit", (msg: any) => addLog(String(msg.text ?? "")));

  room.onMessage("pong", (msg: any) => {
    pingMs = Math.round(performance.now() - msg.t);
    if (typeof msg.totalOnline === "number") serverTotalOnline = msg.totalOnline;
  });

  room.onMessage("serverStats", (msg: any) => {
    if (typeof msg.totalOnline === "number") serverTotalOnline = msg.totalOnline;
  });

  room.onMessage("monsterDied", () => {});

  room.onMessage("buffOn", (msg: any) => {
    buffEndsAt.set(msg.skill, performance.now() + msg.duration * 1000);
    sound.playSkill("buff");
    fx3d.buffRing(new Vector3(myX, 0.5, myZ), "#38bdf8");
    // **การได้รับบัฟไม่นับเป็นการต่อสู้** คนที่ยืนอยู่เฉย ๆ แล้วเพื่อนร่ายบัฟให้
    // ไม่ควรชักอาวุธออกมา เพราะตัวเองไม่ได้เริ่มสู้กับใคร
    // คนร่ายเองยังนับผ่าน skillCast อยู่แล้ว
  });

  room.onMessage("skillCast", (msg: any) => {
    // ร่ายสกิลทุกชนิดนับเป็นการต่อสู้ ทั้งสกิลโจมตี บัฟ และรักษา
    enterCombat(players.get(msg.by));

    // หา def ของสกิลจากอาชีพของคนร่าย ไม่ใช่ของเราเอง เพราะเห็นคนอื่นร่ายด้วย
    const casterState: any = room?.state?.players?.get(msg.by);
    const casterCls = CLASSES[casterState?.cls];
    const sk = casterCls ? findSkill(casterCls, msg.skill) : undefined;

    // ตรวจสอบชนิดท่าทาง: บัฟ / เวท / โจมตี
    const clsId = casterCls?.id ?? "";
    const isBuff = !!(sk && (sk.kind === "support" || sk.glyph === "buff" || sk.glyph === "stance" || sk.glyph === "barrier"));
    const isSpell = !isBuff && !!(sk && (Boolean(casterCls?.role?.includes("เวท")) || ["arcanist", "elementalist", "mender", "saint"].includes(clsId) || ["fire", "ice", "bolt", "star", "heal", "ray"].includes(sk.glyph)));
    const actionType: "attack" | "buff" | "spell" = isBuff ? "buff" : isSpell ? "spell" : "attack";
    const duration = isBuff ? 550 : isSpell ? 480 : 340;

    playSwing(players.get(msg.by), actionType, duration);

    if (msg.by === room?.sessionId && sk) {
      addLog(`ร่าย ${sk.th}${msg.targets?.length > 1 ? ` โดน ${msg.targets.length} ตัว` : ""}`);
    }
    if (!sk) return;

    const caster = players.get(msg.by);
    // ชื่อสกิลลอยเหนือหัวคนร่าย เห็นได้ทุกคน ช่วยให้อ่านจังหวะของเพื่อนร่วมปาร์ตี้ออก
    if (caster) {
      const cp = caster.mesh.position;
      floatText(cp.x, cp.z, cp.y + 1.6, sk.th, "skillname");
    }
    const origin = caster ? caster.mesh.position.clone() : new Vector3(msg.x, 1, msg.z);
    const [bright, dark] = sk.colors;

    sound.playSkill(sk.glyph);

    // รอบ 20: วงเวทอักขระบนพื้น + ลำแสง + ประกาย (fx-textures.ts) แทนวงทอรัสเรียบ ๆ
    if (sk.kind === "support") {
      fx3d.magicCircle(origin.x, origin.z, Math.max(1.6, sk.radius || 1.6), bright, 1.1);
      fx3d.beam(origin, bright, 7, 1.3);
      fx3d.buffRing(origin, bright);
      fx3d.healSparks(origin, bright);
      fx3d.sparks(new Vector3(origin.x, 0.4, origin.z), bright, 20, 0.8);
      return;
    }

    if (sk.targeting === "around") {
      fx3d.magicCircle(origin.x, origin.z, sk.radius, bright);
      fx3d.groundRing(origin.x, origin.z, sk.radius, bright);
      fx3d.sparks(new Vector3(origin.x, 0.5, origin.z), bright, 30, 1.3);
      if (sk.glyph === "roar" || sk.glyph === "slam") {
        fx3d.burst(origin, bright, sk.radius * 0.8);
      }
    }

    const ids: string[] = msg.targets ?? [];
    // สกิลวงกว้างวาดวงบนพื้นรอบเป้าหลักด้วย ให้เห็นขอบเขตที่โดน
    if (sk.radius > 0 && sk.targeting === "enemy" && ids[0]) {
      const first = monsters.get(ids[0]);
      if (first) {
        fx3d.magicCircle(first.mesh.position.x, first.mesh.position.z, sk.radius, bright);
        fx3d.groundRing(first.mesh.position.x, first.mesh.position.z, sk.radius, dark);
      }
    }

    for (const id of ids) {
      const av = monsters.get(id);
      if (!av) continue;
      const to = av.mesh.position.clone();
      const glyph = (sk.glyph || "").toLowerCase();
      const skId = (sk.id || "").toLowerCase();

      // สกิลพิเศษตามประเภท glyph / skill
      if (glyph.includes("meteor") || skId.includes("meteor") || glyph.includes("flame")) {
        fx3d.meteorStrike(to, bright);
      } else if (glyph.includes("frost") || glyph.includes("ice") || skId.includes("frost") || skId.includes("blizzard")) {
        fx3d.iceSpikes(to, bright);
      } else if (glyph.includes("spark") || glyph.includes("lightning") || glyph.includes("thunder") || skId.includes("spark")) {
        fx3d.lightningBolt(origin, to, bright);
      } else if (glyph.includes("slash") || glyph.includes("bash") || glyph.includes("pierce") || glyph.includes("cross")) {
        const rotY = caster ? caster.mesh.rotation.y : 0;
        fx3d.slashArc(to, bright, rotY);
      }

      // ระยะไกลยิงกระสุนให้เห็นทางวิ่ง ระยะประชิดแสดงแรงกระแทกตรงตัวเลย
      if (sk.range > 4) {
        for (let i = 0; i < Math.max(1, sk.hits); i++) {
          // ตีหลายครั้งทยอยยิง ไม่ให้ซ้อนกันเป็นลูกเดียว
          window.setTimeout(() => fx3d.projectile(origin, to, bright, 30, () => {
            fx3d.burst(to, bright, 1.8);
            fx3d.sparks(to, bright, 14, 1.1);
          }), i * 110);
        }
      } else {
        fx3d.burst(to, bright, 2.1);
        fx3d.sparks(to, bright, 18, 1.2);
        fx3d.smoke(to, "#ffffff", 5);
      }
    }
  });

  room.onMessage("castFailed", (msg: any) => {
    // เซิร์ฟเวอร์ไม่ให้ร่าย คืนคูลดาวน์ที่ client ตั้งไว้ล่วงหน้า
    cooldownUntil.delete(msg.skill);
    addLog(`ร่ายไม่ได้ — ${msg.text ?? msg.reason}`);
  });

  updateHud();
}

/* ---------- ลูกศรชี้ตัวเอง ---------- */

/**
 * กรวยคว่ำลอยเหนือหัวตัวเอง ไว้หาตัวตอนผู้เล่นหรือมอนสเตอร์เยอะ
 * ปิดได้ในตั้งค่า (ปุ่ม B)
 */
const selfArrow = MeshBuilder.CreateCylinder(
  "selfArrow",
  { height: 0.55, diameterTop: 0.5, diameterBottom: 0, tessellation: 4 },
  scene,
);
const selfArrowMat = new StandardMaterial("selfArrowMat", scene);
selfArrowMat.diffuseColor = Color3.FromHexString("#ffcf5a");
selfArrowMat.emissiveColor = Color3.FromHexString("#ffcf5a").scale(0.8);
selfArrowMat.specularColor = Color3.Black();
selfArrow.material = selfArrowMat;
selfArrow.isPickable = false;
selfArrow.setEnabled(false);

function updateSelfArrow(): void {
  const me = room ? players.get(room.sessionId) : undefined;
  const meState: any = room?.state?.players?.get(room.sessionId);
  if (!settings.selfArrow || !me || !meState || meState.dead) {
    selfArrow.setEnabled(false);
    return;
  }
  selfArrow.setEnabled(true);
  const bob = Math.sin(performance.now() / 260) * 0.14;
  selfArrow.position.set(me.mesh.position.x, me.mesh.position.y + 1.5 + bob, me.mesh.position.z);
  selfArrow.rotation.y += 0.02;
}

/* ---------- การเลือกเป้าหมาย ---------- */

/** วงแหวนใต้เท้าเป้าหมายที่เลือกไว้ ย้ายไปตามตัวที่เลือก */
const marker = MeshBuilder.CreateTorus(
  "marker",
  { diameter: 1.7, thickness: 0.12, tessellation: 24 },
  scene,
);
const markerMat = new StandardMaterial("markerMat", scene);
markerMat.diffuseColor = Color3.FromHexString("#ffcf5a");
markerMat.emissiveColor = Color3.FromHexString("#ffcf5a").scale(0.7);
markerMat.specularColor = Color3.Black();
marker.material = markerMat;
marker.isPickable = false;
marker.setEnabled(false);

/**
 * เลือกเป้าหมาย
 * @param attack true = สั่งโจมตีปกติด้วย, false = เลือกไว้เฉย ๆ (Tab)
 */
function selectTarget(id: string, attack: boolean): void {
  if (!room) return;
  selectedId = id;
  if (attack) {
    autoAttack = true;
    room.send("target", { id });
  } else if (autoAttack) {
    // เปลี่ยนเป้าแบบไม่ตี ต้องบอก server เลิกตีตัวเดิมด้วย
    stopAttacking();
  }
}

function stopAttacking(): void {
  autoAttack = false;
  room?.send("stop");
}

/** ไกลเกินนี้ Tab จะไม่เลือก กันเลือกตัวที่อยู่คนละมุมแผนที่ */
const TARGET_RANGE = 25;

/** หามอนสเตอร์ที่ยังมีชีวิตและอยู่ใกล้เราที่สุดในระยะ TARGET_RANGE */
function findClosestMonster(): string | undefined {
  if (!room) return undefined;
  let bestId: string | undefined;
  let bestDist = TARGET_RANGE;
  for (const [id, m] of room.state?.monsters ?? []) {
    const mob: any = m;
    if (mob.dead) continue;
    const d = Math.hypot(mob.x - myX, mob.z - myZ);
    if (d < bestDist) {
      bestDist = d;
      bestId = id;
    }
  }
  return bestId;
}

/** สั่งโจมตีเป้าหมายที่เลือกไว้ หรือมอนสเตอร์ที่ใกล้ที่สุด (กดแล้วไม่ยกเลิก เป็นการสั่งโจมตีทุกครั้งตามคำขอผู้เล่น) */
function attackTarget(): void {
  if (!room) return;
  let targetId = selectedId;
  let mob: any = targetId ? room.state?.monsters?.get(targetId) : undefined;
  if (!mob || mob.dead) {
    const closest = findClosestMonster();
    if (closest) {
      targetId = closest;
      mob = room.state?.monsters?.get(closest);
    }
  }
  if (!mob || mob.dead) {
    clearTarget();
    return;
  }
  selectTarget(targetId, true);
}

/** สั่งโจมตีเป้าหมาย (ตอนนี้ปรับเป็นกดแล้วไม่ยกเลิก ให้เป็นการสั่งโจมตีทุกครั้ง) */
function toggleAttack(): void {
  attackTarget();
}

function clearTarget(): void {
  selectedId = "";
  cancelPending();
  if (autoAttack) stopAttacking();
}

/**
 * วนเลือกมอนสเตอร์ตัวถัดไปโดยไม่โจมตี (ปุ่ม Tab)
 * เรียงตามระยะห่างจากตัวเรา กด Tab ซ้ำจะไล่จากใกล้ไปไกล แล้ววนกลับ
 */
function cycleTarget(): void {
  if (!room) return;
  const list: { id: string; d: number }[] = [];
  for (const [id, m] of room.state?.monsters ?? []) {
    const mob: any = m;
    if (mob.dead) continue;
    const d = Math.hypot(mob.x - myX, mob.z - myZ);
    if (d <= TARGET_RANGE) list.push({ id, d });
  }
  if (list.length === 0) {
    clearTarget();
    return;
  }
  list.sort((a, b) => a.d - b.d);

  const at = list.findIndex((e) => e.id === selectedId);
  const next = list[(at + 1) % list.length]!;
  selectTarget(next.id, false);
}

/* ---------- เครื่องหมายสถานะบนหัวมอนสเตอร์ ---------- */

/**
 * ลูกบอลเล็กลอยเหนือหัวมอนสเตอร์ที่ติดสถานะ
 *
 * ใช้ pool เพราะปกติมีไม่กี่ตัวที่ติดสถานะพร้อมกัน
 * ไม่ได้สร้างติดตัวมอนสเตอร์ทุกตัวเพื่อไม่ให้มี mesh เปล่าค้าง 22 ชิ้น
 */
const STATUS_COLOR: Record<string, string> = {
  stun: "#ffd24a",
  root: "#c9a27a",
  slow: "#8fd8ff",
  poison: "#7ddc4a",
  weaken: "#a0a0b8",
  armorbreak: "#ff8a3c",
};
const statusPins: Mesh[] = [];

/** ชื่อไทยของสถานะ */
const STATUS_TH: Record<string, string> = {
  stun: "มึน", root: "ตรึง", slow: "ช้า", poison: "พิษ", weaken: "อ่อนแรง", armorbreak: "เกราะแตก",
};

/** ดีบัฟที่ไม่กระทบการเดิน server ส่งเป็นสตริงคั่น "," */
const mobDebuffs = (mob: any): string[] => String(mob?.debuffs ?? "").split(",").filter(Boolean);

/** เวลาหมดของสถานะบนมอนแต่ละตัว (performance.now) จาก event statusOn */
const mobStatusEnds = new Map<string, Map<string, { until: number; skill: string }>>();

/** ข้อความสถานะของมอนบนกรอบเป้าหมาย พร้อมสกิลที่ทำและวินาทีที่เหลือ */
function statusLine(mob: any, id: string): string {
  const per = mobStatusEnds.get(id);
  const out: string[] = [];
  for (const kind of ["stun", "root", "slow", "poison", "weaken", "armorbreak"]) {
    const on = kind === "stun" ? mob.stunned : kind === "root" ? mob.rooted : kind === "slow" ? mob.slowed
      : mobDebuffs(mob).includes(kind);
    if (!on) continue;
    const info = per?.get(kind);
    const left = info ? Math.max(0, (info.until - performance.now()) / 1000) : 0;
    const sk = info ? findSkillAnywhere(info.skill)?.th : undefined;
    out.push(`${STATUS_TH[kind]}${sk ? ` (${sk})` : ""}${left > 0 ? ` ${left.toFixed(1)} วิ` : ""}`);
  }
  return out.join(" · ");
}

function statusOf(mob: any): string | null {
  if (mob.stunned) return "stun";
  if (mob.rooted) return "root";
  if (mob.slowed) return "slow";
  return mobDebuffs(mob)[0] ?? null;
}

function updateStatusPins(): void {
  let used = 0;
  for (const [id, av] of monsters) {
    const mob: any = room?.state?.monsters?.get(id);
    if (!mob || mob.dead) continue;
    const st = statusOf(mob);
    if (!st) continue;

    let pin = statusPins[used];
    if (!pin) {
      pin = MeshBuilder.CreateSphere(`pin${used}`, { diameter: 0.34, segments: 6 }, scene);
      pin.isPickable = false;
      pin.material = new StandardMaterial(`pinMat${used}`, scene);
      statusPins[used] = pin;
    }
    const mat = pin.material as StandardMaterial;
    const c = Color3.FromHexString(STATUS_COLOR[st] ?? "#ffffff");
    mat.diffuseColor = c;
    mat.emissiveColor = c.scale(0.9);
    mat.specularColor = Color3.Black();

    pin.setEnabled(true);
    // ลอยขึ้นลงเบา ๆ ให้สังเกตเห็น
    const bob = Math.sin(performance.now() / 180 + used) * 0.12;
    pin.position.set(av.mesh.position.x, av.mesh.position.y + 1.1 + bob, av.mesh.position.z);
    used++;
  }
  for (let i = used; i < statusPins.length; i++) statusPins[i]?.setEnabled(false);
}

/** ย้ายวงแหวนไปใต้เท้าเป้าหมาย เรียกทุกเฟรม */
function updateMarker(): void {
  const av = selectedId ? monsters.get(selectedId) : undefined;
  const mob: any = selectedId ? room?.state?.monsters?.get(selectedId) : undefined;
  if (!av || !mob || mob.dead) {
    marker.setEnabled(false);
    return;
  }
  marker.setEnabled(true);
  marker.position.set(av.mesh.position.x, 0.08, av.mesh.position.z);
  // กำลังตีอยู่เป็นสีแดง เลือกไว้เฉย ๆ เป็นสีเหลือง
  const hex = autoAttack ? "#ff7a5a" : "#ffcf5a";
  markerMat.diffuseColor = Color3.FromHexString(hex);
  markerMat.emissiveColor = Color3.FromHexString(hex).scale(0.7);
}

/* ---------- ไอเทมบนพื้น (Ground Drops) ---------- */

interface GroundDropAvatar {
  id: string;
  itemId: string;
  count: number;
  x: number;
  z: number;
  mesh: Mesh;
  despawnAt: number;
  ownerId: string;
  priorityUntil: number;
}
const groundDrops = new Map<string, GroundDropAvatar>();

function createGroundDrop(id: string, d: any): void {
  removeGroundDrop(id);
  const isGold = d.itemId === "gold";
  const def = isGold ? undefined : ITEMS[d.itemId];

  let mesh: Mesh;
  if (isGold) {
    mesh = MeshBuilder.CreateCylinder(`gdrop_${id}`, { height: 0.1, diameter: 0.45, tessellation: 12 }, scene);
    const mat = new StandardMaterial(`gdrop_mat_${id}`, scene);
    mat.diffuseColor = Color3.FromHexString("#fbbf24");
    mat.emissiveColor = Color3.FromHexString("#fbbf24").scale(0.8);
    mat.specularColor = Color3.White();
    mesh.material = mat;
  } else if (def?.kind === "equip") {
    // อุปกรณ์: ทรงผลึกอัญมณีลอยส่องแสงตามสีความหายาก
    mesh = MeshBuilder.CreatePolyhedron(`gdrop_${id}`, { type: 1, size: 0.28 }, scene);
    const mat = new StandardMaterial(`gdrop_mat_${id}`, scene);
    const colorHex = RARITY_COLOR[def.rarity ?? "common"] ?? "#94a3b8";
    const c = Color3.FromHexString(colorHex);
    mat.diffuseColor = c;
    mat.emissiveColor = c.scale(0.7);
    mat.specularColor = Color3.White();
    mesh.material = mat;
  } else if (def?.kind === "card") {
    mesh = MeshBuilder.CreateBox(`gdrop_${id}`, { width: 0.38, height: 0.05, depth: 0.5 }, scene);
    const mat = new StandardMaterial(`gdrop_mat_${id}`, scene);
    mat.diffuseColor = Color3.FromHexString("#c084fc");
    mat.emissiveColor = Color3.FromHexString("#a855f7").scale(0.7);
    mat.specularColor = Color3.White();
    mesh.material = mat;
  } else {
    // ยา / วัตถุดิบ: ทรงกลมขวด/ห่อสมุนไพร
    mesh = MeshBuilder.CreateSphere(`gdrop_${id}`, { diameter: 0.36, segments: 8 }, scene);
    const mat = new StandardMaterial(`gdrop_mat_${id}`, scene);
    const cHex = def?.kind === "heal" ? "#ef4444" : "#10b981";
    const c = Color3.FromHexString(cHex);
    mat.diffuseColor = c;
    mat.emissiveColor = c.scale(0.65);
    mat.specularColor = Color3.White();
    mesh.material = mat;
  }

  mesh.position.set(d.x, 0.22, d.z);
  mesh.metadata = { kind: "drop", id };
  mesh.isPickable = true;

  groundDrops.set(id, {
    id,
    itemId: String(d.itemId ?? ""),
    count: Number(d.count ?? 1),
    x: Number(d.x ?? 0),
    z: Number(d.z ?? 0),
    mesh,
    despawnAt: Number(d.despawnAt ?? 0),
    ownerId: String(d.ownerId ?? ""),
    priorityUntil: Number(d.priorityUntil ?? 0),
  });
}

function removeGroundDrop(id: string): void {
  const av = groundDrops.get(id);
  if (av) {
    av.mesh.material?.dispose();
    av.mesh.dispose();
    groundDrops.delete(id);
  }
}

function nearestDropNear(x: number, z: number, maxDist = 1.3): string | undefined {
  let best: string | undefined;
  let bestD = maxDist;
  for (const [id, d] of groundDrops) {
    const dist = Math.hypot(d.x - x, d.z - z);
    if (dist < bestD) {
      bestD = dist;
      best = id;
    }
  }
  return best;
}

/* ---------- แถบสกิล ---------- */

/**
 * คูลดาวน์ที่ client จำไว้เพื่อวาดม่านทึบบนปุ่ม
 *
 * เป็นแค่การคาดการณ์ไว้แสดงผล ตัวตัดสินจริงคือเซิร์ฟเวอร์
 * ถ้าเซิร์ฟเวอร์ปฏิเสธ (castFailed) จะคืนค่าให้ทันที
 */
const cooldownUntil = new Map<string, number>();
/** สกิลที่ลงแต้มแล้วตอนวาดแถบลัดล่าสุด เปลี่ยนเมื่อไรต้องวาดแถบใหม่ */
let learnedSig = "";

/** เวลา (performance.now) ที่ใช้ไอเทมได้อีกครั้ง คูลดาวน์ร่วมทุกไอเทม */
let itemReadyAt = 0;

/**
 * ผังช่องแถบลัด แถวละ 10 ช่องตามที่ GDD กำหนด
 *
 * ปุ่มที่ใช้ไปแล้วและห้ามซ้ำ: W A S D (เดิน) · F (ตี) · R (กล้อง) · V · B (ตั้งค่า)
 * Tab Enter Esc (เป้า แชท ยกเลิก)
 * แถวบนใช้เลข 1-9 แล้ว 0 แถวล่างไล่ตามแป้นที่เหลือซึ่งอยู่ใกล้มือซ้าย
 */
interface BarSlot {
  /** คำสั่งที่ช่องนี้ผูกอยู่ ปุ่มจริงมาจาก bindings ไม่ได้ตายตัวที่นี่ */
  action: ActionId;
  /** id ไอเทมใน items.json (ช่องหนึ่งมีได้อย่างเดียว: ไอเทม สกิล หรือว่าง) */
  item: string;
  /** id สกิลที่ผูกอยู่ */
  skill: string;
}

/** แถวบน = ช่อง 0-9 (ปุ่ม 1-0) · แถวล่าง = ช่อง 10-19 — ใส่สกิลหรือไอเทมช่องไหนก็ได้ */
const TOP_SLOTS: BarSlot[] = Array.from({ length: 10 }, (_, i) => ({
  action: `skill${i + 1}` as ActionId,
  item: "",
  skill: "",
}));

/** ที่เก็บแถบลัดรุ่นเก่า (เฉพาะไอเทมแถวล่าง) อ่านไว้เป็นค่าเริ่มต้นของคนที่ยังไม่เคยจัดเอง */
const LEGACY_QUICK_KEY = "wickhaven.quickslots";
function loadLegacyQuick(): string[] {
  try {
    const raw = localStorage.getItem(LEGACY_QUICK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length === 10) return parsed.map((x) => String(x ?? ""));
    }
  } catch {}
  return ["potion_small", "ether_small", "", "", "", "", "", "", "", ""];
}

const BOTTOM_SLOTS: BarSlot[] = Array.from({ length: 10 }, (_, i) => ({
  action: `bag${i + 1}` as ActionId,
  item: "",
  skill: "",
}));

/** ทั้ง 20 ช่องเรียงตามตำแหน่ง (แถวบนก่อน) */
const BAR_SLOTS: BarSlot[] = [...TOP_SLOTS, ...BOTTOM_SLOTS];

/**
 * ผังแถบลัดที่ผู้เล่นจัดเอง เก็บต่ออาชีพเริ่มต้น (อาชีพขั้นสูงใช้ผังของสายเดียวกัน)
 * ค่าของช่อง: "s:<id สกิล>" · "i:<id ไอเทม>" · "" (ว่าง)
 * `seen` = สกิลที่เคยเห็นแล้ว สกิลที่เพิ่งเรียนและไม่อยู่ในนี้จะถูกลงช่องว่างช่องแรกให้เอง
 * ไม่เคยจัดเอง (ไม่มีผัง) = แถวบนเรียงสกิลอัตโนมัติ แถวล่างเป็นไอเทมเริ่มต้นเหมือนเดิม
 * การอ่านเขียน localStorage ครอบ try/catch เสมอ (โหมดส่วนตัวของบางเบราว์เซอร์เข้าไม่ได้)
 */
const HOTBAR_KEY = "wickhaven.hotbar";
interface SavedBar {
  slots: string[];
  seen: string[];
}
function loadHotbarStore(): Record<string, SavedBar> {
  try {
    const raw = localStorage.getItem(HOTBAR_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, SavedBar>;
      const out: Record<string, SavedBar> = {};
      for (const [k, v] of Object.entries(parsed ?? {})) {
        if (Array.isArray(v?.slots) && v.slots.length === 20) {
          out[k] = { slots: v.slots.map((x) => String(x ?? "")), seen: Array.isArray(v.seen) ? v.seen.map(String) : [] };
        }
      }
      return out;
    }
  } catch {}
  return {};
}
const hotbarStore: Record<string, SavedBar> = loadHotbarStore();

function saveHotbarStore(): void {
  try {
    localStorage.setItem(HOTBAR_KEY, JSON.stringify(hotbarStore));
  } catch {}
}

const slotCode = (s: BarSlot): string => (s.skill ? `s:${s.skill}` : s.item ? `i:${s.item}` : "");
function setSlotCode(s: BarSlot, code: string): void {
  s.skill = code.startsWith("s:") ? code.slice(2) : "";
  s.item = code.startsWith("i:") ? code.slice(2) : "";
}

/** คีย์ผังของอาชีพ: ขั้นสูงใช้ผังของอาชีพเริ่มต้นสายเดียวกัน (เลื่อนขั้นแล้วผังเดิมไม่หาย) */
function barKeyOf(classId: string): string {
  return CLASSES[classId]?.baseClass ?? classId;
}

/** เขียนผังปัจจุบันลงที่เก็บ (เรียกหลังผู้เล่นจัดเอง) */
function commitBarLayout(): void {
  const key = barKeyOf(myClass);
  const prev = hotbarStore[key];
  const learned = learnedSig.split(",").filter(Boolean);
  hotbarStore[key] = {
    slots: BAR_SLOTS.map(slotCode),
    seen: [...new Set([...(prev?.seen ?? []), ...learned])],
  };
  saveHotbarStore();
}

/** ใส่ค่าจากผัง (หรือค่าอัตโนมัติถ้ายังไม่เคยจัดเอง) ลงช่องทั้ง 20 */
function applyBarLayout(classId: string, all: SkillDef[], learned: Set<string>): void {
  const saved = hotbarStore[barKeyOf(classId)];
  if (!saved) {
    const auto = [...all.filter((sk) => learned.has(sk.id)), ...all.filter((sk) => !learned.has(sk.id))].slice(0, 10);
    const quick = loadLegacyQuick();
    BAR_SLOTS.forEach((s, i) => {
      s.skill = i < 10 ? (auto[i]?.id ?? "") : "";
      s.item = i >= 10 ? (quick[i - 10] ?? "") : "";
    });
    return;
  }
  BAR_SLOTS.forEach((s, i) => setSlotCode(s, saved.slots[i] ?? ""));
  // สกิลที่เพิ่งเรียนและยังไม่เคยเห็น: ลงช่องว่างช่องแรกให้เอง (ผู้เล่นย้าย/เอาออกได้ตามใจ)
  let changed = false;
  for (const id of learned) {
    if (saved.seen.includes(id)) continue;
    saved.seen.push(id);
    changed = true;
    if (!BAR_SLOTS.some((s) => s.skill === id)) {
      const free = BAR_SLOTS.find((s) => !s.skill && !s.item);
      if (free) setSlotCode(free, `s:${id}`);
    }
  }
  if (changed) {
    saved.slots = BAR_SLOTS.map(slotCode);
    saveHotbarStore();
  }
}

/** สถานะเปิด/ปิดการเปรียบเทียบสเตตัสกับของที่สวมใส่อยู่ */
let compareGearOpen = false;

/** ตรวจว่าตัวละครกำลังสวมใส่ไอเทมชิ้นนี้อยู่ในช่องใดช่องหนึ่งหรือไม่ */
function isEquipped(self: any, itemId: string): boolean {
  if (!self?.equipped || !itemId) return false;
  for (const s of EQUIP_SLOTS) {
    if (String(self.equipped.get?.(s.id) ?? "") === itemId) return true;
  }
  return false;
}

/** กดใช้งานหรือสวมใส่ไอเทมจากแถบคีย์ลัด */
function triggerHotbarItem(itemId: string): void {
  if (!itemId) return;
  const def = ITEMS[itemId];
  if (!def) return;
  if (def.kind === "equip") {
    equipFromBag(itemId);
  } else {
    useItem(itemId);
  }
}

/* ---------- ลากวางสกิล/ไอเทมลงแถบลัด (ช่องไหนก็ได้ทั้งสองแถว) ---------- */

interface DragState {
  pointerId: number;
  kind: "item" | "skill";
  id: string;
  /** ลากมาจากช่องในแถบลัด (0-19) ไม่ใช่จากกระเป๋า/หน้าต่างสกิล */
  fromIdx?: number;
  startX: number;
  startY: number;
  ghost?: HTMLDivElement;
  started: boolean;
}

let activeDrag: DragState | null = null;
/** เวลาที่การลากจบล่าสุด — คลิกที่เบราว์เซอร์ยิงตามหลังการลากต้องไม่ไปกดใช้สกิล */
let lastDragEndAt = 0;
const justDragged = (): boolean => performance.now() - lastDragEndAt < 250;
/** เวลาที่แตะช่องแถบลัดบนจอสัมผัสล่าสุด — กัน click ยิงซ้ำ */
let lastSlotTouchAt = 0;

function beginDrag(e: PointerEvent, d: Omit<DragState, "pointerId" | "startX" | "startY" | "started">): void {
  if (e.button !== 0 || !d.id) return;
  activeDrag = { ...d, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, started: false };
}

/** ลากไอเทมจากกระเป๋า */
function startItemDrag(e: PointerEvent, itemId: string, _fromIdx?: number): void {
  beginDrag(e, { kind: "item", id: itemId });
}

/** ลากสกิลจากหน้าต่างสกิล (เฉพาะที่ลงแต้มแล้ว) */
function startSkillDrag(e: PointerEvent, skillId: string): void {
  beginDrag(e, { kind: "skill", id: skillId });
}

/** ลากของที่อยู่ในช่องแถบลัดอยู่แล้ว (ย้ายช่อง สลับกัน หรือลากออกไปทิ้ง) */
function startSlotDrag(e: PointerEvent, idx: number): void {
  const s = BAR_SLOTS[idx];
  if (!s) return;
  if (s.skill) beginDrag(e, { kind: "skill", id: s.skill, fromIdx: idx });
  else if (s.item) beginDrag(e, { kind: "item", id: s.item, fromIdx: idx });
}

const barSlotAt = (x: number, y: number): HTMLElement | null =>
  document.elementFromPoint(x, y)?.closest?.<HTMLElement>("#bar .slot[data-slot-idx]") ?? null;

window.addEventListener("pointermove", (e) => {
  if (!activeDrag || e.pointerId !== activeDrag.pointerId) return;
  const dx = e.clientX - activeDrag.startX;
  const dy = e.clientY - activeDrag.startY;
  const dist = Math.hypot(dx, dy);
  const isTouch = e.pointerType === "touch";
  if (!activeDrag.started) {
    // บนจอสัมผัส ถ้าเป็นการปัดแนวตั้งจากกระเป๋า/หน้าต่าง (เลื่อนดูของ) ให้ยกเลิกการลาก ไม่ขัดการเลื่อน
    if (isTouch && activeDrag.fromIdx === undefined && Math.abs(dy) > Math.abs(dx) * 1.3 && dist < 35) {
      activeDrag = null;
      return;
    }
    if (dist > (isTouch ? 18 : 7)) {
      activeDrag.started = true;
      document.body.classList.add("dragging-active");
      const ghost = document.createElement("div");
      ghost.className = "drag-ghost";
      if (activeDrag.kind === "skill") {
        const sk = findSkillAnywhere(activeDrag.id);
        ghost.innerHTML = `<span class="g" style="display:block;width:22px;height:22px;border-radius:5px;`
          + `background:${sk ? skillIcon(sk) : "none"} center / cover no-repeat"></span><span class="nm">${esc(sk?.th ?? activeDrag.id)}</span>`;
      } else {
        const def = ITEMS[activeDrag.id];
        ghost.innerHTML = `<span class="g">${itemGlyph(def)}</span><span class="nm">${esc(def?.name ?? activeDrag.id)}</span>`;
      }
      ghost.style.left = `${e.clientX}px`;
      ghost.style.top = `${e.clientY}px`;
      document.body.appendChild(ghost);
      activeDrag.ghost = ghost;
    }
  } else if (activeDrag.ghost) {
    // ลากอยู่ ไม่ให้หน้าจอสัมผัสเลื่อน/ซูมตาม
    if (e.cancelable) e.preventDefault();
    activeDrag.ghost.style.left = `${e.clientX}px`;
    activeDrag.ghost.style.top = `${e.clientY}px`;
    const over = barSlotAt(e.clientX, e.clientY);
    for (const el of bar.querySelectorAll(".slot")) el.classList.toggle("drop-hover", el === over);
  }
}, { passive: false });

/**
 * วางสกิล/ไอเทม (`code` แบบ "s:..."/"i:...") ลงช่องแถบลัด
 * - ลากมาจากช่องอื่นในแถบ = สลับกัน (ช่องเดิมได้ของที่อยู่ปลายทาง)
 * - ลากมาจากกระเป๋า/หน้าต่างสกิล = ของชิ้นเดียวกันที่อยู่ช่องอื่นอยู่แล้วย้ายมา ไม่ให้ซ้ำสองช่อง
 * ของที่ถูกวางทับ (กรณีมาจากข้างนอก) หายไปจากแถบ
 */
function dropOnBar(code: string, to: number, from?: number): void {
  const target = BAR_SLOTS[to];
  if (!target || from === to) return;
  if (from !== undefined) {
    const src = BAR_SLOTS[from];
    if (src) setSlotCode(src, slotCode(target));
  } else {
    for (const s of BAR_SLOTS) if (slotCode(s) === code) setSlotCode(s, "");
  }
  setSlotCode(target, code);
  commitBarLayout();
  buildSkillBar(myClass);
  updateSkillBar();
  if (settings.sound) sound.playClick();
}

/** ใส่สกิลลงช่องว่างช่องแรก (ช่องที่เห็นบนจอมือถือก่อน) — ปุ่ม "ใส่แถบลัด" ในหน้าต่างสกิล */
function placeSkillInFreeSlot(skillId: string): void {
  if (BAR_SLOTS.some((s) => s.skill === skillId)) {
    addLog(`${findSkillAnywhere(skillId)?.th ?? skillId} อยู่ในแถบลัดแล้ว`);
    return;
  }
  // บนมือถือโชว์เฉพาะช่อง 1-8 กับ 11-14 จึงเลือกจากช่องเหล่านี้ก่อน
  const order = [0, 1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 13, 8, 9, 14, 15, 16, 17, 18, 19];
  const to = order.find((i) => !BAR_SLOTS[i]!.skill && !BAR_SLOTS[i]!.item);
  if (to === undefined) {
    addLog("แถบลัดเต็ม — ลากสกิลไปวางทับช่องที่ต้องการแทน");
    return;
  }
  dropOnBar(`s:${skillId}`, to);
  addLog(`🎯 ใส่ ${findSkillAnywhere(skillId)?.th ?? skillId} ลงช่องลัดที่ ${to + 1}`);
}

/** เอาของออกจากช่อง (ลากออกไปปล่อยพื้นที่ว่าง) */
function clearBarSlot(idx: number): void {
  const s = BAR_SLOTS[idx];
  if (!s || (!s.skill && !s.item)) return;
  setSlotCode(s, "");
  commitBarLayout();
  buildSkillBar(myClass);
  updateSkillBar();
  if (settings.sound) sound.playClick();
}

const endItemDrag = (e: PointerEvent): void => {
  const d = activeDrag;
  document.body.classList.remove("dragging-active");
  if (!d || (e.pointerId !== undefined && e.pointerId !== d.pointerId)) return;
  activeDrag = null;
  if (!d.started) {
    if (e.pointerType === "touch" && d.fromIdx !== undefined) {
      lastSlotTouchAt = performance.now();
      const slot = BAR_SLOTS[d.fromIdx];
      if (slot) {
        if (slot.skill) {
          const sk = findSkillAnywhere(slot.skill);
          if (sk) castSkill(sk);
        } else if (slot.item) {
          triggerHotbarItem(slot.item);
        }
      }
    }
    return;
  }
  d.ghost?.remove();
  lastDragEndAt = performance.now();
  for (const el of bar.querySelectorAll(".slot")) el.classList.remove("drop-hover");
  const code = `${d.kind === "skill" ? "s" : "i"}:${d.id}`;
  const name = d.kind === "skill" ? (findSkillAnywhere(d.id)?.th ?? d.id) : (ITEMS[d.id]?.name ?? d.id);
  const cancelled = e.type === "pointercancel";
  const slotEl = cancelled ? null : barSlotAt(e.clientX, e.clientY);
  if (slotEl) {
    const to = Number(slotEl.dataset["slotIdx"]);
    dropOnBar(code, to, d.fromIdx);
    if (d.fromIdx !== to) addLog(`🎯 ใส่ ${name} ลงช่องลัดที่ ${to + 1}`);
  } else if (d.fromIdx !== undefined && !cancelled) {
    // ลากออกจากช่องแล้วปล่อยที่อื่น = เอาออก
    clearBarSlot(d.fromIdx);
    addLog(`🗑️ นำ ${name} ออกจากช่องลัดแล้ว`);
  }
};

window.addEventListener("pointerup", endItemDrag);
window.addEventListener("pointercancel", endItemDrag);

/* ---------- หน้าต่างจัดการช่องทางลัด (Quickslot Manager) ---------- */

let qsAssignCode = "";
let qsDeleteMode = false;

function openQuickslotManager(code = ""): void {
  qsAssignCode = code;
  qsDeleteMode = false;
  const modal = document.getElementById("quickslotmodal");
  if (!modal) return;
  modal.classList.add("on");
  renderQuickslotManager();
}

function closeQuickslotManager(): void {
  const modal = document.getElementById("quickslotmodal");
  if (modal) modal.classList.remove("on");
  qsAssignCode = "";
  qsDeleteMode = false;
}

function renderQuickslotManager(): void {
  const iconEl = document.getElementById("qsInfoIcon");
  const nameEl = document.getElementById("qsInfoName");
  const subEl = document.getElementById("qsInfoSub");
  const topGrid = document.getElementById("qsTopGrid");
  const botGrid = document.getElementById("qsBottomGrid");
  const clearBtn = document.getElementById("qsClearSlotBtn") as HTMLButtonElement | null;
  if (!topGrid || !botGrid) return;

  if (qsDeleteMode) {
    if (iconEl) iconEl.innerHTML = "🗑️";
    if (nameEl) nameEl.textContent = "โหมดลบของในช่องลัด";
    if (subEl) subEl.textContent = "แตะที่ช่องใดก็ได้เพื่อลบของออกจากช่องนั้น";
    if (clearBtn) clearBtn.textContent = "↩️ ยกเลิกโหมดลบ";
  } else if (qsAssignCode) {
    const isSkill = qsAssignCode.startsWith("s:");
    const id = qsAssignCode.slice(2);
    if (isSkill) {
      const sk = findSkillAnywhere(id);
      if (iconEl) iconEl.innerHTML = `<span style="display:block;width:24px;height:24px;border-radius:4px;background:${sk ? skillIcon(sk) : "none"} center/cover no-repeat"></span>`;
      if (nameEl) nameEl.textContent = `ใส่: ${sk?.th ?? id}`;
      if (subEl) subEl.textContent = "แตะช่องด้านล่าง (1-20) เพื่อใส่สกิลนี้";
    } else {
      const it = ITEMS[id];
      if (iconEl) iconEl.innerHTML = itemGlyph(it);
      if (nameEl) nameEl.textContent = `ใส่: ${it?.name ?? id}`;
      if (subEl) subEl.textContent = "แตะช่องด้านล่าง (1-20) เพื่อใส่ไอเทมนี้";
    }
    if (clearBtn) clearBtn.textContent = "❌ โหมดลบของในช่อง";
  } else {
    if (iconEl) iconEl.innerHTML = "⚡";
    if (nameEl) nameEl.textContent = "ผังช่องทางลัดทั้งหมด (Hotbar 1 - 20)";
    if (subEl) subEl.textContent = "แตะช่องเพื่อจัดการ หรือเลือกสกิล/ไอเทมจากหน้าต่าง";
    if (clearBtn) clearBtn.textContent = "❌ โหมดลบของในช่อง";
  }

  const renderSlot = (i: number): string => {
    const s = BAR_SLOTS[i];
    let iconHtml = `<span class="empty">-</span>`;
    let label = "ว่าง";
    let has = false;
    if (s?.skill) {
      const sk = findSkillAnywhere(s.skill);
      iconHtml = `<span style="display:block;width:20px;height:20px;border-radius:4px;background:${sk ? skillIcon(sk) : "none"} center/cover no-repeat"></span>`;
      label = sk?.th ?? s.skill;
      has = true;
    } else if (s?.item) {
      const it = ITEMS[s.item];
      iconHtml = `<span style="font-size:16px">${itemGlyph(it)}</span>`;
      label = it?.name ?? s.item;
      has = true;
    }
    const isCurrent = qsAssignCode && slotCode(s!) === qsAssignCode;
    return `<div class="qs-slot${has ? " has-item" : ""}${isCurrent ? " current-selected" : ""}" data-qs-slot="${i}">
      <span class="num">${i + 1}</span>
      <div class="icon">${iconHtml}</div>
      <div class="label" title="${esc(label)}">${esc(label)}</div>
    </div>`;
  };

  topGrid.innerHTML = Array.from({ length: 10 }, (_, i) => renderSlot(i)).join("");
  botGrid.innerHTML = Array.from({ length: 10 }, (_, i) => renderSlot(10 + i)).join("");
}

document.getElementById("qsCloseBtn")?.addEventListener("click", closeQuickslotManager);
document.getElementById("qsDoneBtn")?.addEventListener("click", closeQuickslotManager);
document.getElementById("qsClearSlotBtn")?.addEventListener("click", () => {
  qsDeleteMode = !qsDeleteMode;
  renderQuickslotManager();
});

document.getElementById("quickslotmodal")?.addEventListener("click", (e) => {
  const target = e.target as HTMLElement | null;
  const slotEl = target?.closest?.<HTMLElement>("[data-qs-slot]");
  if (slotEl) {
    const idx = Number(slotEl.dataset["qsSlot"]);
    if (isNaN(idx) || idx < 0 || idx >= BAR_SLOTS.length) return;
    if (qsDeleteMode) {
      clearBarSlot(idx);
      addLog(`🗑️ นำของออกจากช่องลัดที่ ${idx + 1} แล้ว`);
      renderQuickslotManager();
    } else if (qsAssignCode) {
      const isSkill = qsAssignCode.startsWith("s:");
      const id = qsAssignCode.slice(2);
      const name = isSkill ? (findSkillAnywhere(id)?.th ?? id) : (ITEMS[id]?.name ?? id);
      dropOnBar(qsAssignCode, idx);
      addLog(`🎯 ใส่ ${name} ลงช่องลัดที่ ${idx + 1}`);
      renderQuickslotManager();
    } else {
      // ไม่มีของเลือก: แตะเพื่อสลับหรือลบ
      const s = BAR_SLOTS[idx];
      if (s?.skill || s?.item) {
        clearBarSlot(idx);
        addLog(`🗑️ นำของออกจากช่องลัดที่ ${idx + 1} แล้ว`);
        renderQuickslotManager();
      }
    }
  }
});

/** ตัวอักษรที่โชว์บนช่องแถบลัด อ่านจากปุ่มที่ผู้เล่นตั้งไว้ */
function slotLabel(slot: BarSlot): string {
  return keyLabel(bindings[slot.action]);
}

/* ---------- หน้าต่างตัวละครและสกิล ---------- */

/** เพดานสเตตัสตาม GDD ใช้ซ่อนปุ่มบวกเมื่อเต็ม ต้องตรงกับ STAT_CAP ฝั่ง server */
const STAT_CAP = 99;

/**
 * ลากหนึ่งพิกเซลแล้วรูปตัวละครหมุนไปกี่เรเดียน
 *
 * **ทิศเป็นลบ** ลากไปทางขวาแล้วตัวละครต้องหมุนตามมือเหมือนจับตัวมันหมุน
 * ไม่ใช่หมุนสวนทาง (กล้องมองจากด้านหน้า แกน y ของ Babylon จึงกลับทางกับที่คิด)
 */
const DOLL_ROT_PER_PX = 0.015;

/** ไอคอนสำรองตามประเภท ใช้เมื่อไอเทมไม่ได้ระบุไอคอนของตัวเองใน items.json */
const ITEM_GLYPH: Record<string, string> = {
  heal: "\u{1F9EA}",
  material: "\u{1FAB5}",
  card: "\u{1F0CF}",
  equip: "\u{1F9F3}",
};

/**
 * ไอคอนของไอเทมชิ้นหนึ่ง
 *
 * **อ่านจาก `icon` ใน items.json ก่อนเสมอ** ตกมาใช้ไอคอนประจำประเภทเมื่อไม่ได้ระบุ
 * เคยใช้ไอคอนประจำประเภทอย่างเดียว แล้วหมวก เสื้อ รองเท้า แหวน หน้าตาเหมือนกันหมด
 */
function itemGlyph(def: ItemDef | undefined): string {
  return def?.icon ?? ITEM_GLYPH[def?.kind ?? "material"] ?? "\u{1F4E6}";
}

/* ---------- ป้ายชื่อไอเทมและการดูไอเทม ---------- */

const uiTip = el<HTMLDivElement>("tip");

/**
 * ป้ายชื่อตอนเอาเมาส์ชี้
 *
 * ใช้กล่องของเราเองแทน `title` ของเบราว์เซอร์ เพราะ `title` ขึ้นช้าราววินาทีหนึ่ง
 * และจัดหน้าตาไม่ได้ ซึ่งช้าเกินไปสำหรับการกวาดดูของทั้งกระเป๋า
 *
 * **ไม่รับคลิก** (`pointer-events: none` ใน CSS) ไม่งั้นมันไปบังช่องที่กำลังจะกด
 */
function showTip(html: string, ev: MouseEvent): void {
  uiTip.innerHTML = html;
  uiTip.classList.add("on");
  moveTip(ev);
}

function moveTip(ev: MouseEvent): void {
  if (!uiTip.classList.contains("on")) return;
  // หนีบให้อยู่ในจอ ชี้ของที่อยู่ริมขวาแล้วป้ายต้องไม่ล้นออกไป
  const r = uiTip.getBoundingClientRect();
  const x = Math.min(ev.clientX + 14, window.innerWidth - r.width - 6);
  const y = Math.min(ev.clientY + 16, window.innerHeight - r.height - 6);
  uiTip.style.left = `${Math.max(6, x)}px`;
  uiTip.style.top = `${Math.max(6, y)}px`;
}

function hideTip(): void {
  uiTip.classList.remove("on");
}

/**
 * ไอเทมที่กำลัง "ดู" อยู่ (คลิกเดียว)
 *
 * `slot` ไม่ว่าง = กำลังดูของที่สวมอยู่ในช่องนั้น ปุ่มในการ์ดจะเป็น "ถอด"
 * ว่าง = ดูของในกระเป๋า ปุ่มจะเป็น "สวม" หรือ "ใช้" ตามชนิด
 */
/** ของที่กำลังดู `uid` = ชิ้นมีตัวตนในกระเป๋า (ตีบวกแล้ว/มีการ์ด — shared/gear.ts) */
let inspect: { item: string; slot: string; uid?: string; source?: "char" | "bag" | "shop" } | undefined;

/** ชื่อไทยของประเภทไอเทม ใช้ในการ์ดรายละเอียด */
const KIND_TH: Record<string, string> = {
  heal: "ของใช้",
  material: "วัตถุดิบ",
  card: "ไฟตะเกียง (ประกายวิญญาณ)",
  equip: "ของสวมใส่",
};

/** รายการค่าที่ของสวมใส่ให้ คิดจาก `bonus` ใน items.json ตรง ๆ และบวกโบนัสตีบวก */
function bonusLines(def: ItemDef, refineLvl = 0): string[] {
  const b = def.bonus ?? {};
  const out: string[] = [];
  if (b.weaponAtk) {
    const rf = refineBonus(b.weaponAtk, refineLvl);
    out.push(`ATK ${b.weaponAtk + rf}${rf > 0 ? ` (+${rf})` : ""}`);
  }
  if (b.weaponMatk) {
    const rf = refineBonus(b.weaponMatk, refineLvl);
    out.push(`MATK ${b.weaponMatk + rf}${rf > 0 ? ` (+${rf})` : ""}`);
  }
  if (b.flatDef) {
    const rf = refineBonus(b.flatDef, refineLvl);
    out.push(`DEF +${b.flatDef + rf}${rf > 0 ? ` (+${rf})` : ""}`);
  }
  if (b.flatCrit) out.push(`คริ +${b.flatCrit}%`);
  if (b.flatBlock) out.push(`บล็อก +${b.flatBlock}%`);
  if (b.aspdMul) out.push(`ความเร็วตี ×${b.aspdMul}`);
  if (b.hpMul) out.push(`HP สูงสุด ×${b.hpMul}`);
  for (const k of STAT_KEYS) {
    const add = b.stats?.[k];
    if (add) {
      const isStatRefined = def.slot === "wings" || (!b.weaponAtk && !b.weaponMatk && !b.flatDef);
      const rf = isStatRefined ? refineBonus(add, refineLvl) : 0;
      out.push(`${k} +${add + rf}${rf > 0 ? ` (+${rf})` : ""}`);
    }
  }
  if (def.slot === "pet" && refineLvl >= 10) {
    out.push("✨ ร่างวิวัฒนาการ Max Lv 10: ทุกสเตตัส +5 · Max HP +5% · DEF +15 · คริ +5%");
  }
  out.push(...describeSkillMods(b.skillMods));
  return out;
}

/** เงื่อนไขการใส่ (เลเวล อาชีพ จำนวนมือ) ว่าง = ใครก็ใส่ได้ */
function reqLine(def: ItemDef): string {
  const req: string[] = [];
  if ((def.reqLevel ?? 1) > 1) req.push(`Base Lv ${def.reqLevel}`);
  if (def.slot === "weapon" && def.hands) {
    req.push(def.hands === 2 ? "อาวุธสองมือ (ไม่สามารถใส่ของมือรองได้)" : "อาวุธมือเดียว");
  }
  if (def.reqClass?.length) {
    req.push(`อาชีพ: ${def.reqClass.map((c) => CLASSES[c]?.name ?? c).join(" / ")}`);
  }
  return req.length > 0 ? `เงื่อนไข: ${req.join(" · ")}` : "";
}



/**
 * ไอคอนประจำช่องสวมใส่ ใช้แทนรูปจริงไปก่อน
 *
 * **ผังช่อง (id ชื่อ ตำแหน่ง) อยู่ใน `EQUIP_SLOTS` ของ shared** ไม่ใช่ที่นี่
 * เพราะ server เป็นคนตัดสินว่าของชิ้นไหนลงช่องไหนได้ ที่นี่เหลือแค่เรื่องภาพ
 */
const SLOT_GLYPH: Record<string, string> = {
  head: "\u{1F9E2}",
  face: "\u{1F637}",
  weapon: "\u{1F5E1}\uFE0F",
  offhand: "\u{1F6E1}\uFE0F",
  body: "\u{1F455}",
  legs: "\u{1F456}",
  feet: "\u{1F97E}",
  ear1: "\u{1F48D}",
  ear2: "\u{1F48D}",
  special: "\u{1F9FF}",
  pet: "\u{1F43E}",
  wings: "\u{1FAB6}",
};

/** สีกรอบตามความหายาก ตาม GDD หัวข้อ 5 */
const RARITY_COLOR: Record<string, string> = {
  common: "rgba(255, 255, 255, .45)",
  fine: "#7fc96a",
  rare: "#5aa8e8",
  epic: "#b47be0",
  legend: "#e8c45a",
};

/**
 * ป้ายสั้นตอนเอาเมาส์ชี้ — ชื่อไอเทมเป็นหลัก
 *
 * รายละเอียดเต็มอยู่ในการ์ดที่ขึ้นตอนคลิก ป้ายนี้มีไว้ให้กวาดสายตาหาของได้เร็ว
 * ไม่ใช่ให้อ่านทุกบรรทัดระหว่างเลื่อนเมาส์ผ่าน
 */
function itemTip(def: ItemDef | undefined, fallback: string): string {
  if (!def) return `<span class="tn">${esc(fallback)}</span>`;
  const sub = def.kind === "equip"
    ? `${RARITY_TH[def.rarity ?? "common"]} · ${bonusLines(def).join(" · ") || "ไม่มีค่าเพิ่ม"}`
    : KIND_TH[def.kind] ?? "";
  return `<span class="tn">${esc(def.name)}</span>`
    + (sub ? `<br /><span class="td">${esc(sub)}</span>` : "")
    + `<br /><span class="td">คลิกเพื่อดู · ดับเบิลคลิกเพื่อ${
      def.kind === "equip" ? "สวม" : "ใช้"}</span>`;
}

/** ชื่อไทยของสเตตัสแต่ละค่า เอาไว้อธิบายให้คนที่ไม่คุ้นศัพท์ */
const STAT_TH: Record<string, string> = {
  STR: "พลัง",
  AGI: "ว่องไว",
  VIT: "อึด",
  INT: "ปัญญา",
  DEX: "แม่น",
  LUK: "โชค",
};

const uiCarry = el<HTMLDivElement>("carry");
let carrySig = "";

/**
 * ป้ายเตือนน้ำหนักใต้กรอบตัวละคร — โชว์เฉพาะตอนหนักเกิน 70%
 * client คิดเพื่อ "แสดง" เท่านั้น server ตัดสินจริงด้วยสูตรเดียวกันใน shared/carry.ts
 * ไม่งั้นผู้เล่นงงว่าทำไมเลือดไม่ฟื้นหรือกดตีแล้วเงียบ
 */
function updateCarryBadge(self: any): void {
  const gear = equipTotals(self.equipped ?? {}, ITEMS, self.refines ?? {});
  const jobStr = jobStatBonus(CLASSES[self.cls] ?? getClass("warden"), self.jobLevel);
  const str = withEquipStats(withEquipStats(selfStats(self), jobStr), gear.stats).STR;
  const state = carryState(bagWeight(self.items ?? [], ITEMS) + gearWeight(self.gear ?? {}, ITEMS), maxCarryWeight(str));
  if (state === carrySig) return;
  carrySig = state;
  uiCarry.textContent = state === "ok" ? "" : `🎒 ${CARRY_TEXT[state]}`;
  uiCarry.className = state;
}

/** อ่านสเตตัสของตัวเองจาก state ที่ server ส่งมา */
function selfStats(self: any): Stats {
  return {
    STR: self.str ?? 1,
    AGI: self.agi ?? 1,
    VIT: self.vit ?? 1,
    INT: self.int ?? 1,
    DEX: self.dex ?? 1,
    LUK: self.luk ?? 1,
  };
}

/** การ์ดรายละเอียดไอเทมแบบลอย อยู่นอกหน้าต่างเพราะ .win ตัดขอบ (overflow: hidden) */
const uiItemPop = document.getElementById("itempop") as HTMLDivElement;
/** ปุ่มเก็บไอเทมรอบตัวทั้งหมด */
const uiLootBtn = document.getElementById("lootbtn") as HTMLButtonElement | null;
uiLootBtn?.addEventListener("click", () => {
  if (room) {
    room.send("pickAll");
    sound.playLoot();
  }
});

/**
 * วางการ์ดรายละเอียดไว้ข้างช่องที่กำลังดู
 *
 * ขวาของช่องก่อน ถ้าล้นจอให้ไปซ้าย และหนีบแนวตั้งให้อยู่ในจอเสมอ
 * **เรียกซ้ำทุกเฟรมตอนเปิดอยู่** (ดู game loop) เพราะลากหน้าต่าง หรือเลื่อน
 * กระเป๋าแล้วช่องขยับ การ์ดต้องตามไปด้วย ไม่ค้างลอยอยู่ที่เดิม
 * ช่องที่เลื่อนจนพ้นกรอบกระเป๋าไปแล้ว ซ่อนการ์ดไว้ ไม่ลอยชี้ไปที่ว่าง
 */
/** วาดการ์ดรายละเอียดใหม่ (เรียกจากทั้งหน้าต่างตัวละครและกระเป๋า) */
function refreshItemPop(self: any): void {
  uiItemPop.innerHTML = detailCard(self);
  placeItemPop();
}

function placeItemPop(): void {
  // ของที่ดูอยู่อาจอยู่ในหน้าต่างตัวละคร (ช่องสวมใส่), หน้าต่างกระเป๋า, หรือร้านค้า
  const cell = !uiItemPop.innerHTML
    ? null
    : (winChar.isOpen() ? winChar.body.querySelector<HTMLElement>(".eqslot.sel") : null)
      ?? (winBag.isOpen() ? winBag.body.querySelector<HTMLElement>(".bagslot.sel") : null)
      ?? (winShop.isOpen() ? winShop.body.querySelector<HTMLElement>(".shrow.sel") : null);
  if (!cell) {
    uiItemPop.classList.remove("on");
    return;
  }

  const r = cell.getBoundingClientRect();
  const scroller = cell.closest<HTMLElement>(".bagscroll, .shlist");
  if (scroller) {
    const box = scroller.getBoundingClientRect();
    if (r.bottom < box.top || r.top > box.bottom) {
      uiItemPop.classList.remove("on");
      return;
    }
  }

  uiItemPop.classList.add("on");
  const gap = 6;
  const w = uiItemPop.offsetWidth;
  const h = uiItemPop.offsetHeight;
  let x = r.right + gap;
  if (x + w > window.innerWidth - 4) x = r.left - gap - w;
  const y = Math.min(Math.max(4, r.top), window.innerHeight - h - 4);
  uiItemPop.style.left = `${Math.max(4, x)}px`;
  uiItemPop.style.top = `${y}px`;
}

// ปุ่มในการ์ดทำอย่างเดียวกับดับเบิลคลิก มีไว้ให้คนที่ดูแล้วค่อยตัดสินใจ
// และให้จอสัมผัสที่ดับเบิลแท็บยากกดได้
// **ตัวจับเดียวที่ตัวการ์ด** ไม่ผูกทีละปุ่ม เพราะการ์ดถูกวาดใหม่ทุกครั้งที่หน้าต่างวาด
uiItemPop.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement | null)?.closest?.<HTMLElement>("button[data-act]");
  if (!btn || !inspect) return;
  const act = btn.dataset["act"];
  if (act === "unequip") {
    if (inspect.slot) room?.send("unequip", { slot: inspect.slot });
    return;
  }
  if (act === "shop-buy") {
    if (nearShop()) room?.send("buy", { item: inspect.item, count: 1 });
    return;
  }
  if (act === "shop-sell") {
    const count = parseInt(btn.dataset["count"] ?? "1", 10) || 1;
    if (!nearShop()) return;
    if (inspect.uid) {
      // ชิ้นที่ตีบวก/มีการ์ด/ออปชัน ขายแล้วหายถาวร ถามก่อน
      if (!window.confirm("ของชิ้นนี้ตีบวก มีไฟตะเกียง หรือมีออปชันอยู่ ขายแล้วหายถาวร ต้องการขายไหม?")) return;
      room?.send("sell", { item: inspect.item, count: 1, uid: inspect.uid });
    } else {
      room?.send("sell", { item: inspect.item, count });
    }
    return;
  }
  if (act === "shop-sell-custom") {
    const max = parseInt(btn.dataset["max"] ?? "1", 10) || 1;
    const def = ITEMS[inspect.item];
    const val = window.prompt(`ระบุจำนวนที่ต้องการขาย ${def?.name ?? inspect.item} (1 - ${max}):`, String(max));
    if (val) {
      const count = parseInt(val, 10);
      if (!isNaN(count) && count > 0) {
        if (nearShop()) room?.send("sell", { item: inspect.item, count: Math.min(count, max) });
      }
    }
    return;
  }
  if (act === "hotbar-manage") {
    openQuickslotManager(`i:${inspect.item}`);
    return;
  }
  if (act === "hotbar") {
    const idx = Number(btn.dataset["idx"]);
    if (!BOTTOM_SLOTS[idx]) return;
    dropOnBar(`i:${inspect.item}`, 10 + idx);
    addLog(`🎯 ใส่ ${ITEMS[inspect.item]?.name ?? inspect.item} ลงช่องลัดช่องที่ ${idx + 1}`);
    uiItemPop.innerHTML = detailCard(room?.state?.players?.get(room.sessionId));
    return;
  }
  if (act === "drop-item") {
    const self: any = room?.state?.players?.get(room.sessionId);
    const def = ITEMS[inspect.item];
    const have = inspect.uid ? 1 : self?.items?.get?.(inspect.item) ?? 1;
    let count = 1;
    if (have > 1) {
      const val = window.prompt(`ระบุจำนวนที่ต้องการทิ้ง ${def?.name ?? inspect.item} (1 - ${have}):`, "1");
      if (!val) return;
      count = parseInt(val, 10);
      if (isNaN(count) || count <= 0) return;
      count = Math.min(count, have);
    } else {
      if (!window.confirm(`ต้องการทิ้ง ${def?.name ?? inspect.item} ลงบนพื้นหรือไม่?`)) return;
    }
    room?.send("dropItem", inspect.uid ? { item: inspect.item, uid: inspect.uid } : { item: inspect.item, count });
    if (settings.sound) sound.playClick();
    inspect = undefined;
    compareGearOpen = false;
    uiItemPop.classList.remove("on");
    uiItemPop.innerHTML = "";
    return;
  }
  if (act === "compare-gear") {
    compareGearOpen = !compareGearOpen;
    const self: any = room?.state?.players?.get(room.sessionId);
    if (self) refreshItemPop(self);
    return;
  }
  const def = ITEMS[inspect.item];
  // ไม่บอกช่อง ให้ server เลือกเอง (ต่างหูจะได้ลงช่องว่างก่อนแทนที่จะทับวงแรก)
  if (def?.kind === "equip") equipFromBag(inspect.item, inspect.uid);
  else useItem(inspect.item);
});

/** ตัวกรองของหน้าต่างกระเป๋า (จำไว้ระหว่างเปิดปิด ไม่บันทึกข้ามการรีโหลด) */
let bagFilter: ItemFilter = { ...DEFAULT_FILTER };
/** ตัวกรองของร้านค้า แท็บ "sell" = หน้าขาย */
let shopFilter: Omit<ItemFilter, "tab"> & { tab: BagTab | "sell" } = { tab: "use", gear: "all", cls: "mine" };

/**
 * แถบแท็บและตัวกรองที่ใช้ร่วมกันระหว่างกระเป๋ากับร้าน
 * ตัวกรองชนิดและอาชีพโชว์เฉพาะแท็บอุปกรณ์ (ของอื่นไม่มีชนิดหรืออาชีพ)
 */
function filterBar(
  f: { tab: string; gear: string; cls: string },
  tabs: ReadonlyArray<{ id: string; label: string }>,
): string {
  let html = `<div class="ftabs">`
    + tabs.map((t) => `<button type="button" class="${f.tab === t.id ? "on" : ""}" data-f-tab="${t.id}">`
      + `${esc(t.label)}</button>`).join("")
    + `</div>`;
  if (f.tab === "equip") {
    html += `<div class="fgear">`
      + GEAR_TYPES.map((g) => `<button type="button" class="${f.gear === g.id ? "on" : ""}" data-f-gear="${g.id}">`
        + `${esc(g.label)}</button>`).join("")
      + `</div>`
      + `<div class="fcls"><span class="dim">อาชีพ</span><select data-f-cls="1">`
      + [["all", "ทุกอาชีพ"], ["mine", "ใส่ได้กับฉัน"], ...CLASS_LIST.map((c) => [c.id, c.name])]
        .map(([id, label]) => `<option value="${id}"${f.cls === id ? " selected" : ""}>${esc(label!)}</option>`)
        .join("")
      + `</select></div>`;
  }
  return html;
}

/** อ่านการคลิก/เปลี่ยนตัวกรอง คืน true ถ้าเป็นการกดตัวกรอง (ผู้เรียกต้องวาดใหม่) */
function applyFilterEvent(e: Event, f: { tab: string; gear: string; cls: string }): boolean {
  const t = e.target as HTMLElement | null;
  const tab = t?.closest?.<HTMLElement>("[data-f-tab]")?.dataset["fTab"];
  if (tab) {
    f.tab = tab;
    return true;
  }
  const gear = t?.closest?.<HTMLElement>("[data-f-gear]")?.dataset["fGear"];
  if (gear) {
    f.gear = gear;
    return true;
  }
  if (e.type === "change" && t instanceof HTMLSelectElement && t.dataset["fCls"]) {
    f.cls = t.value;
    return true;
  }
  return false;
}

// กระเป๋ากับตัวละครเป็นหน้าต่างบานเดียว (winChar) ซ้าย = ตัวละคร อุปกรณ์ สเตตัส · ขวา = กระเป๋า
// ปุ่ม I กับ B เปิด/ปิดบานเดียวกัน — `winBag` ด้านล่างเป็นแค่ทางลัดไปยังบานนั้น (อยู่ต่อท้าย winChar)

let bagSig = "";
let bagSorted = false;

function sortBagList<T extends { id: string; n: number; def: ItemDef | undefined; isEquip?: boolean }>(list: T[]): T[] {
  const RARITY_RANK: Record<string, number> = {
    legendary: 5, epic: 4, rare: 3, uncommon: 2, common: 1,
  };
  return list.sort((a, b) => {
    const tabDiff = TAB_ORDER[tabOf(a.def)] - TAB_ORDER[tabOf(b.def)];
    if (tabDiff !== 0) return tabDiff;
    const rA = RARITY_RANK[a.def?.rarity ?? "common"] ?? 0;
    const rB = RARITY_RANK[b.def?.rarity ?? "common"] ?? 0;
    if (rB !== rA) return rB - rA;
    const lvA = a.def?.reqLevel ?? 0;
    const lvB = b.def?.reqLevel ?? 0;
    if (lvB !== lvA) return lvB - lvA;
    return a.id.localeCompare(b.id);
  });
}

function drawBagWindow(): void {
  hideTip();
  const self: any = room?.state?.players?.get(room.sessionId);
  if (!self) {
    winBag.body.textContent = "กำลังเชื่อมต่อ…";
    return;
  }
  const oldScroll = winBag.body.querySelector<HTMLElement>(".bagscroll")?.scrollTop ?? 0;

  // ของสวมใส่แยกช่องละ 1 ชิ้น ส่วนของใช้วัตถุดิบซ้อนกันในช่องเดียว
  const all: Array<{ id: string; n: number; def: ItemDef | undefined; isEquip?: boolean; equipIndex?: number; uid?: string; piece?: GearPiece }> = [];
  // ชิ้นมีตัวตน (ตีบวกแล้ว/มีการ์ด) แยกช่องของตัวเอง
  for (const [uid, raw] of (self.gear ?? []) as Iterable<[string, string]>) {
    const piece = parseGear(raw);
    const def = ITEMS[piece.itemId];
    if (def) all.push({ id: piece.itemId, n: 1, def, isEquip: true, uid, piece });
  }
  for (const [id, n] of (self.items ?? []) as Iterable<[string, number]>) {
    if (n > 0) {
      const def = ITEMS[id];
      if (def?.kind === "equip") {
        for (let i = 0; i < n; i++) {
          all.push({ id, n: 1, def, isEquip: true, equipIndex: i });
        }
      } else {
        all.push({ id, n, def });
      }
    }
  }
  if (bagSorted) {
    sortBagList(all);
  } else {
    sortItems(all);
  }
  const shown = all.filter((x) => matchFilter(x.def, bagFilter, self.cls));

  const st = selfStats(self);
  const gear = equipTotals(self.equipped ?? {}, ITEMS, self.refines ?? {});
  const weight = all.reduce((sum, x) => sum + (x.def?.weight ?? 0) * x.n, 0); // รวมชิ้นมีตัวตนแล้ว
  // STR รวมของสวมใส่ ให้ตรงกับที่ server ใช้ตัดสิน (carryStr ใน FieldRoom)
  const weightMax = maxCarryWeight(
    withEquipStats(withEquipStats(st, jobStatBonus(CLASSES[self.cls] ?? getClass("warden"), self.jobLevel)), gear.stats).STR);
  const openSlots = bagSlots(self.baseLevel);
  const nextLv = nextBagSlotLevel(self.baseLevel);

  const cells: string[] = shown.map(({ id, n, def, isEquip, uid, piece }) => {
    // คลิกเดียว = ดู · ดับเบิลคลิก = ใช้หรือสวม
    const heal = (def?.healHpPct ?? 0) > 0 || (def?.healMpPct ?? 0) > 0;
    const color = isEquip && def ? RARITY_COLOR[def.rarity ?? "common"] : "";
    const picked = inspect && !inspect.slot && inspect.item === id && (inspect.uid ?? "") === (uid ?? "");
    const countBadge = isEquip
      ? (piece?.refine ? `<span class="n">+${piece.refine}</span>` : "")
      : `<span class="n">${n}</span>`;
    return `<div class="bagslot ${def?.kind ?? "material"}${heal || isEquip ? " usable" : ""}`
      + `${picked ? " sel" : ""}" data-item="${id}"${uid ? ` data-uid="${uid}"` : ""}`
      + (color ? ` style="border-color:${color}"` : "")
      + `><span class="g">${itemGlyph(def)}</span>${countBadge}</div>`;
  });
  // แท็บทั้งหมดโชว์ช่องว่างกับช่องล็อกตามเลเวลด้วย จะได้เห็นว่าเหลือที่เท่าไร
  if (bagFilter.tab === "all") {
    for (let i = shown.length; i < BAG_SLOTS_MAX; i++) {
      if (i < openSlots) cells.push(`<div class="bagslot empty"></div>`);
      else {
        const unlockLv = (Math.floor((i - BAG_SLOTS_BASE) / BAG_SLOTS_PER_STEP) + 1) * BAG_SLOTS_STEP;
        cells.push(`<div class="bagslot locked" title="ปลดล็อกที่ Base ${unlockLv}"><span class="g">\u{1F512}</span></div>`);
      }
    }
  } else if (shown.length === 0) {
    cells.push(`<div class="bagnone">ไม่มีของในหมวดนี้</div>`);
  }

  winBag.body.innerHTML =
    filterBar(bagFilter, BAG_TABS)
    + `<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 6px 2px 6px;">`
    + `<span class="dim" style="font-size:11px;">ช่องเก็บของ (${all.length}/${openSlots})</span>`
    + `<button type="button" class="btn-bag-sort" data-bag-act="sort">🔀 จัดเรียง</button>`
    + `</div>`
    + `<div class="bagscroll bagwin"><div class="bag">${cells.join("")}</div></div>`
    + `<div class="bagfoot">`
    + `<span>💰 ${self.gold}</span>`
    + `<span class="${weight > weightMax * 0.7 ? "c10" : "dim"}">⚖ ${weight}/${weightMax}</span>`
    + `<span class="dim">${all.length}/${openSlots} ช่อง</span>`
    + `</div>`
    + (bagFilter.tab === "all"
      ? `<div class="hint">${nextLv > 0 ? `ปลดล็อกอีก ${BAG_SLOTS_PER_STEP} ช่องที่ Base ${nextLv}` : "ปลดล็อกครบทุกช่องแล้ว"}</div>`
      : "");

  const newScroll = winBag.body.querySelector<HTMLElement>(".bagscroll");
  if (newScroll && oldScroll > 0) newScroll.scrollTop = oldScroll;

  for (const cell of winBag.body.querySelectorAll<HTMLElement>(".bagslot[data-item]")) {
    const id = String(cell.dataset["item"] ?? "");
    const uid = cell.dataset["uid"] || undefined;
    const def = ITEMS[id];
    cell.addEventListener("mouseenter", (e) => showTip(itemTip(def, id), e as MouseEvent));
    cell.addEventListener("mousemove", (e) => moveTip(e as MouseEvent));
    cell.addEventListener("mouseleave", hideTip);
    cell.addEventListener("pointerdown", (e) => startItemDrag(e, id, undefined));
    cell.addEventListener("click", () => {
      // กดชิ้นเดิมซ้ำ = เลิกดู
      compareGearOpen = false;
      const same = inspect && !inspect.slot && inspect.item === id && (inspect.uid ?? "") === (uid ?? "");
      inspect = same ? undefined : { item: id, slot: "", uid };
      drawBagWindow();
      if (winChar.isOpen()) drawCharWindow();
    });
    cell.addEventListener("dblclick", (e) => {
      e.preventDefault();
      if (def?.kind === "equip") equipFromBag(id, uid);
      else useItem(id);
    });
  }
  refreshItemPop(self);
}

/**
 * สวมของจากกระเป๋า `uid` = ชิ้นมีตัวตน (ตีบวก/การ์ดตามไปด้วย)
 * ของเดิมในช่องกลับเข้ากระเป๋าพร้อมตีบวกและการ์ดของมัน ไม่ต้องถามยืนยันแล้ว (PLAN ขั้นที่ 3.2)
 */
function equipFromBag(itemId: string, uid?: string): void {
  room?.send("equip", uid ? { item: itemId, uid } : { item: itemId });
}

const winChar = createWindow(uiWins, {
  title: "ตัวละคร",
  x: 14,
  y: 150,
  onOpen: () => {
    drawCharWindow();
    drawBagWindow();
  },
  // ปิดหน้าต่างแล้วต้องเลิกดูของที่ค้างไว้ ไม่งั้นเปิดใหม่มาเจอการ์ดเก่าค้างอยู่
  onClose: () => {
    inspect = undefined;
    compareGearOpen = false;
    hideTip();
    uiItemPop.classList.remove("on");
    uiItemPop.innerHTML = "";
    for (const k of STAT_KEYS) pendingStats[k] = 0;
  },
});

// โครงสองคอลัมน์สร้างครั้งเดียว: drawCharWindow เขียนฝั่งซ้าย drawBagWindow เขียนฝั่งขวา
// แยกกันวาดได้ (กระเป๋าเปลี่ยนบ่อยกว่า) โดยไม่ล้างอีกฝั่ง
const charLeft = document.createElement("div");
charLeft.className = "cleft";
const bagPane = document.createElement("div");
bagPane.className = "cright";
const charCols = document.createElement("div");
charCols.className = "charcols";
charCols.append(charLeft, bagPane);
winChar.body.append(charCols);

/** ทางลัดของกระเป๋า: บานเดียวกับตัวละคร `body` ชี้ไปฝั่งขวา */
const winBag = {
  body: bagPane,
  isOpen: (): boolean => winChar.isOpen(),
  open: (): void => winChar.open(),
  close: (): void => winChar.close(),
  toggle: (): boolean => winChar.toggle(),
};

// ตัวกรองใช้ตัวจับเดียวที่ฝั่งกระเป๋า (วาดใหม่ทั้งก้อนทุกครั้ง)
for (const ev of ["click", "change"]) {
  bagPane.addEventListener(ev, (e) => {
    if (applyFilterEvent(e, bagFilter)) drawBagWindow();
  });
}

bagPane.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement | null)?.closest?.<HTMLElement>("button[data-bag-act='sort']");
  if (btn) {
    bagSorted = true;
    if (settings.sound) sound.playClick();
    addLog("🔀 จัดเรียงไอเทมในกระเป๋าเรียบร้อย");
    drawBagWindow();
  }
});

/** แต้มสเตตัสที่เลือกไว้แต่ยังไม่ได้กดยืนยัน (ป้องกันการอัพผิด) */
const pendingStats: Record<string, number> = { str: 0, agi: 0, vit: 0, int: 0, dex: 0, luk: 0 };

/** คำนวณแต้มสเตตัสที่ต้องใช้สำหรับ pending stats ทั้งหมด */
function calcPendingStatsCost(baseStats: Stats, pending: Record<string, number>): number {
  let totalCost = 0;
  for (const k of STAT_KEYS) {
    const cur = baseStats[k] ?? 1;
    const count = pending[k] ?? 0;
    for (let i = 0; i < count; i++) {
      totalCost += statCost(cur + i);
    }
  }
  return totalCost;
}

/** แต้มสกิลที่เลือกไว้แต่ยังไม่ได้กดยืนยัน (ป้องกันการอัพผิด) */
const pendingSkills: Record<string, number> = {};

const winSkill = createWindow(uiWins, {
  title: "สกิล",
  x: 240,
  y: 150,
  onOpen: () => {
    skillWinSig = "";
    drawSkillWindow();
  },
  // ปิดหน้าต่างแล้วยกเลิกการยืนยันรีเซ็ตที่ค้างไว้ และล้างแต้มสกิลที่ยังไม่กดยืนยัน
  onClose: () => {
    skillResetArmed = false;
    for (const k of Object.keys(pendingSkills)) delete pendingSkills[k];
  },
});

/** รูปตัวละครในหน้าต่าง เก็บไว้ใช้ซ้ำ ไม่ต้องเรนเดอร์ใหม่ทุกครั้งที่วาด DOM */
let dollCanvas: HTMLCanvasElement | undefined;
let dollCls = "";
let dollGender: Gender = DEFAULT_GENDER;
/** ลายเซ็นของชุดที่รูปในหน้าต่างวาดไว้ล่าสุด */
let dollGear = "";
/** ของที่รูปในหน้าต่างใส่อยู่ เก็บไว้ใช้ตอนลากหมุน จะได้ไม่ต้องอ่าน state ซ้ำ */
let dollLooks: EquipLook[] = [];
/** มุมหันของรูป เก็บไว้นอกฟังก์ชันวาด ปิดเปิดหน้าต่างแล้วยังหันมุมเดิม */
let dollRot = DEFAULT_ROT;

/** สั่งวาดรูปตัวละครในหน้าต่างใหม่ตามมุมปัจจุบัน */
function redrawDoll(look: ClassLook, gear: EquipLook[] = dollLooks): void {
  if (!dollCanvas) return;
  dollLooks = gear;
  try {
    thumbs ??= createThumbMaker();
    const me = selfPlayer();
    thumbs.draw(dollCanvas, look, dollRot, dollGender, gear,
      me ? parseLook(me.look, dollGender, CLASSES[me.cls]?.look.hair) : undefined);
  } catch (err) {
    // รูปเป็นของประดับ ขาด WebGL context ไปก็ต้องยังเปิดหน้าต่างได้
    console.warn("วาดรูปตัวละครในหน้าต่างไม่สำเร็จ", err);
  }
}

/**
 * ลากที่รูปเพื่อหมุนดูอุปกรณ์รอบตัว
 *
 * ผูกครั้งเดียวตอนสร้าง canvas เพราะตัว canvas ถูกใช้ซ้ำทุกครั้งที่วาดหน้าต่างใหม่
 * (ย้ายไปแขวนใต้ DOM ชุดใหม่เฉย ๆ listener จึงไม่หลุด และไม่ซ้อนกันเพิ่มขึ้นทุกรอบ)
 *
 * **ต้อง stopPropagation** ไม่งั้นการลากทะลุไปถึงฉากหลังแล้วกล้องในเกมหมุนตามไปด้วย
 */
function attachDollDrag(canvas: HTMLCanvasElement): void {
  let dragId = -1;
  let lastX = 0;

  canvas.addEventListener("pointerdown", (e) => {
    dragId = e.pointerId;
    lastX = e.clientX;
    canvas.setPointerCapture(e.pointerId);
    e.stopPropagation();
    e.preventDefault();
  });

  canvas.addEventListener("pointermove", (e) => {
    if (e.pointerId !== dragId) return;
    e.stopPropagation();
    dollRot -= (e.clientX - lastX) * DOLL_ROT_PER_PX;
    lastX = e.clientX;
    const cls = CLASSES[dollCls] ?? getClass("warden");
    redrawDoll(cls.look, dollLooks);
  });

  const end = (e: PointerEvent): void => {
    if (e.pointerId === dragId) dragId = -1;
  };
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointercancel", end);

  // ดับเบิลคลิกคืนมุมเริ่มต้น เหมือนปุ่ม R ที่รีเซ็ตกล้องในเกม
  canvas.addEventListener("dblclick", (e) => {
    e.stopPropagation();
    dollRot = DEFAULT_ROT;
    redrawDoll((CLASSES[dollCls] ?? getClass("warden")).look, dollLooks);
  });
}

/** ลายเซ็นของข้อมูลที่หน้าต่างตัวละครแสดง ใช้เลี่ยงการวาด DOM ใหม่ทุกเฟรม */
let charSig = "";

/**
 * ลายเซ็นของสิ่งที่หน้าต่างร้านแสดง (อยู่ในระยะไหม · โกลด์ · ของในกระเป๋า)
 * วาดใหม่เฉพาะตอนค่านี้เปลี่ยน ไม่ใช่ทุกเฟรม
 */
let shopSig = "";

/**
 * การ์ดรายละเอียดของไอเทมที่กำลังดูอยู่ คืนสตริงว่างถ้าไม่ได้ดูอะไรอยู่
 *
 * คลิกเดียว = ดู (ขึ้นการ์ดนี้) · ดับเบิลคลิก = ใช้หรือสวมเลย
 * แยกสองจังหวะเพราะของสวมใส่กดผิดแล้วต้องมานั่งถอดใหม่
 * ส่วนของที่กดแล้วหายไปเลย (ยา) ยิ่งต้องให้ดูก่อนได้
 */
function detailCard(self: any): string {
  if (!inspect) return "";
  const def = ITEMS[inspect.item];
  if (!def) return "";

  // ของที่ดูค้างไว้อาจถูกใช้หมดหรือถอดไปแล้ว ต้องไม่โชว์การ์ดของที่ไม่มีอยู่จริง
  const piece = inspect.uid ? self.gear?.get?.(inspect.uid) : undefined;
  const gp = piece ? parseGear(piece) : undefined;
  // ชิ้นมีตัวตนทำได้แค่สวม (ขาย ทิ้ง เทรด ฝากคลัง ยังรับเฉพาะของนับจำนวน)
  const inBag = inspect.uid ? !!gp : (self.items?.get?.(inspect.item) ?? 0) > 0;
  const inSlot = inspect.slot
    && String(self.equipped?.get?.(inspect.slot) ?? "") === inspect.item;
  const inShop = inspect.source === "shop" && winShop.isOpen();
  if (!inSlot && !inBag && !inShop) return "";

  const refineLvl = inSlot && inspect.slot ? Number((self.refines as any)?.get?.(inspect.slot) ?? 0) : gp?.refine ?? 0;
  const color = RARITY_COLOR[def.rarity ?? "common"] ?? "";
  const bonus = def.kind === "equip" ? bonusLines(def, refineLvl) : [];
  const req = def.kind === "equip" ? reqLine(def) : "";
  const kind = def.kind === "equip"
    ? (def.slot === "weapon" && def.weaponType
        ? `${WEAPON_TYPE_NAMES[def.weaponType] ?? "อาวุธ"} · ${RARITY_TH[def.rarity ?? "common"]}`
        : `${KIND_TH.equip} · ${RARITY_TH[def.rarity ?? "common"]}`)
    : KIND_TH[def.kind] ?? "";

  let nameDisplay = refineLvl > 0 ? `${def.name} +${refineLvl}` : def.name;
  if (def.slot === "pet") {
    if (refineLvl >= 10) {
      nameDisplay = `🌟 ${def.name} [ร่างวิวัฒนาการ Max Lv 10] ✨`;
    } else if (refineLvl > 0) {
      nameDisplay = `🐾 ${def.name} Lv ${refineLvl}`;
    }
  }

  // ปุ่มทำอะไรได้บ้าง ขึ้นกับว่ากำลังดูของที่สวมอยู่ ของในกระเป๋า หรือในร้านค้า
  let action = "";
  if (inShop) {
    const far = !nearShop();
    if (shopFilter.tab === "sell") {
      const have = gp ? 1 : self.items?.get?.(inspect.item) ?? 1;
      const canSell = inBag && sellPrice(def) > 0;
      if (have > 1) {
        action = `<div style="display:flex;gap:4px;flex-wrap:wrap">`
          + `<button type="button" data-act="shop-sell" data-count="1"${far || !canSell ? " disabled" : ""}>ขาย 1 (${sellPrice(def)} G)</button>`
          + `<button type="button" data-act="shop-sell" data-count="${have}"${far || !canSell ? " disabled" : ""}>ขายหมด ${have} (${sellPrice(def) * have} G)</button>`
          + `<button type="button" data-act="shop-sell-custom" data-max="${have}"${far || !canSell ? " disabled" : ""}>ระบุจำนวน</button>`
          + `</div>`;
      } else {
        action = `<button type="button" data-act="shop-sell" data-count="1"${far || !canSell ? " disabled" : ""}>ขาย 1 ชิ้น (${sellPrice(def)} โกลด์)</button>`;
      }
    } else {
      const canAfford = self.gold >= def.price;
      action = `<button type="button" data-act="shop-buy"${far || !canAfford ? " disabled" : ""}>ซื้อ 1 ชิ้น (${def.price} โกลด์)</button>`;
    }
  } else if (inSlot) {
    action = `<button type="button" data-act="unequip">ถอดออก</button>`;
  } else if (def.kind === "equip") {
    action = `<button type="button" data-act="equip">สวมใส่</button>`;
  } else if ((def.healHpPct ?? 0) > 0 || (def.healMpPct ?? 0) > 0) {
    action = `<button type="button" data-act="use">ใช้</button>`;
  }

  // ใส่ช่องลัดด้วยการแตะ — บนจอสัมผัสหรือในโหมดมือถือ
  const hotbarable = def.kind === "equip" || (def.healHpPct ?? 0) > 0 || (def.healMpPct ?? 0) > 0 || def.kind === "material";
  if (inBag && !inShop && hotbarable) {
    action += `<button type="button" data-act="hotbar-manage" style="width:100%;margin-top:6px;padding:7px;background:linear-gradient(145deg,#d4af37,#997a15);color:#1a1a1a;font-weight:700;border:none;border-radius:6px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;">⚡ จัดใส่ช่องทางลัด (เลือกช่อง 1 - 20)</button>`;
  }

  if (gp && !inShop) {
    action += `<div class="d dim" style="margin-top:6px">ชิ้นนี้มีตีบวก/ไฟตะเกียง/ออปชันติดอยู่ — ถอดสลับแล้วค่าติดไปกับชิ้น (ยังเทรด/ฝากคลังไม่ได้)</div>`;
  }
  if (inBag && !inSlot && !inShop) {
    action += `<button type="button" data-act="drop-item" style="background:#5a2323;color:#fca5a5;border:1px solid #ef4444;border-radius:4px;padding:4px 8px;margin-top:6px;width:100%;cursor:pointer;font-weight:600;">🗑️ ทิ้งไอเทม</button>`;
  }

  let compareBtn = "";
  let compareBox = "";
  if (def.kind === "equip" && !inSlot) {
    const targetSlot = inspect.slot || autoSlot(def, self?.equipped ?? {});
    const slotDef = EQUIP_SLOTS.find((s) => s.id === targetSlot);
    const slotLabelName = slotDef?.label ?? (targetSlot || "อุปกรณ์");
    compareBtn = `<button type="button" class="btn-compare" data-act="compare-gear">⚖️ ${compareGearOpen ? "ปิดการเปรียบเทียบ" : `เทียบกับที่สวมใส่อยู่ (${slotLabelName})`}</button>`;

    if (compareGearOpen && targetSlot) {
      const curItemId = String(self?.equipped?.get?.(targetSlot) ?? "");
      const curDef = curItemId ? ITEMS[curItemId] : undefined;
      const curRefine = curItemId ? Number((self?.refines as any)?.get?.(targetSlot) ?? 0) : 0;

      if (!curDef) {
        compareBox = `<div class="gear-compare-box">`
          + `<div class="gc-head"><span>🛡️</span><span>ช่อง ${esc(slotLabelName)}</span></div>`
          + `<div class="gc-empty">ยังไม่ได้สวมใส่อุปกรณ์ในช่องนี้ (สวมใส่เพื่อเพิ่มค่าพลังทันที)</div>`
          + `</div>`;
      } else {
        const curName = curRefine > 0 ? `${curDef.name} +${curRefine}` : curDef.name;
        const curColor = RARITY_COLOR[curDef.rarity ?? "common"] ?? "";

        const statKeys: Array<{ key: string; label: string; getVal: (d: ItemDef) => number }> = [
          { key: "weaponAtk", label: "ATK", getVal: (d) => d.bonus?.weaponAtk ?? 0 },
          { key: "weaponMatk", label: "MATK", getVal: (d) => d.bonus?.weaponMatk ?? 0 },
          { key: "flatDef", label: "DEF", getVal: (d) => d.bonus?.flatDef ?? 0 },
          { key: "flatCrit", label: "CRIT", getVal: (d) => d.bonus?.flatCrit ?? 0 },
          { key: "flatBlock", label: "BLOCK", getVal: (d) => d.bonus?.flatBlock ?? 0 },
          { key: "STR", label: "STR", getVal: (d) => d.bonus?.stats?.STR ?? 0 },
          { key: "AGI", label: "AGI", getVal: (d) => d.bonus?.stats?.AGI ?? 0 },
          { key: "VIT", label: "VIT", getVal: (d) => d.bonus?.stats?.VIT ?? 0 },
          { key: "INT", label: "INT", getVal: (d) => d.bonus?.stats?.INT ?? 0 },
          { key: "DEX", label: "DEX", getVal: (d) => d.bonus?.stats?.DEX ?? 0 },
          { key: "LUK", label: "LUK", getVal: (d) => d.bonus?.stats?.LUK ?? 0 },
          { key: "weight", label: "น้ำหนัก", getVal: (d) => d.weight ?? 0 },
        ];

        const rows: string[] = [];
        for (const sk of statKeys) {
          const vCur = sk.getVal(curDef);
          const vNew = sk.getVal(def);
          if (vCur === 0 && vNew === 0) continue;
          const diff = vNew - vCur;
          let diffHtml = "";
          if (sk.key === "weight") {
            if (diff < 0) diffHtml = `<span class="gc-diff-pos">(${diff})</span>`;
            else if (diff > 0) diffHtml = `<span class="gc-diff-neg">(+${diff})</span>`;
            else diffHtml = `<span class="gc-diff-eq">(0)</span>`;
          } else {
            if (diff > 0) diffHtml = `<span class="gc-diff-pos">(+${diff})</span>`;
            else if (diff < 0) diffHtml = `<span class="gc-diff-neg">(${diff})</span>`;
            else diffHtml = `<span class="gc-diff-eq">(0)</span>`;
          }
          rows.push(`<div class="gc-row"><span>${sk.label}</span><span>${vCur} ➔ ${vNew} ${diffHtml}</span></div>`);
        }

        compareBox = `<div class="gear-compare-box">`
          + `<div class="gc-head"><span>🛡️ กำลังสวม:</span>`
          + `<span style="color:${curColor || "inherit"}">${esc(curName)}</span></div>`
          + (rows.length > 0 ? rows.join("") : `<div class="gc-empty">ค่าพลังพื้นฐานเท่ากัน</div>`)
          + `</div>`;
      }
    }
  }

  const price = sellPrice(def);
  const priceLine = inShop && shopFilter.tab !== "sell"
    ? `ราคาซื้อ ${def.price} โกลด์`
    : (price > 0 ? `ราคาขาย ${price} โกลด์` : "ขายไม่ได้");

  return `<div class="idet">`
    + `<div class="ih"><span class="ig">${itemGlyph(def)}</span>`
    + `<span class="it"${color ? ` style="color:${color}"` : ""}>${esc(nameDisplay)}</span>`
    + `<span class="ik">${esc(kind)}</span></div>`
    + `<div class="ides">${esc(def.desc)}</div>`
    + (bonus.length > 0 ? `<div class="ib">${esc(bonus.join(" · "))}</div>` : "")
    + optionLines(def, inSlot ? parseOptions((self.options as any)?.get?.(inspect.slot)) : gp?.options ?? [])
    + setLines(def, self)
    + (req ? `<div class="ireq">${esc(req)}</div>` : "")
    + cardDetail(def, inSlot ? parseCards((self.cards as any)?.get?.(inspect.slot)) : gp?.cards ?? [])
    + compareBtn
    + compareBox
    + `<div class="ides">น้ำหนัก ${def.weight} · ${priceLine}</div>`
    + action
    + `</div>`;
}

/** ชุดเซ็ตของไอเทมนี้: ชิ้นที่สวม/ขาด และโบนัสที่ติดแล้ว (สว่าง) หรือยังไม่ติด (จาง) — ขั้น 3.5 */
function setLines(def: ItemDef, self: any): string {
  const set = setOfItem(def.id);
  if (!set) return "";
  const prog = setProgress(self.equipped ?? {}, self.refines ?? {}).find((x) => x.set.id === set.id);
  const worn = new Set(prog?.worn ?? []);
  const pieces = set.pieces.map((id) =>
    `<span class="${worn.has(id) ? "" : "dim"}">${esc(ITEMS[id]?.name ?? id)}</span>`).join(" · ");
  const rows = set.bonuses.map((b) => {
    const on = worn.size >= b.count;
    return `<div class="d${on ? "" : " dim"}">${on ? "✔" : "○"} ${b.count} ชิ้น: ${esc(describeCard(b.bonus))}</div>`;
  });
  for (const b of set.refineBonuses ?? []) {
    const on = (prog?.minRefine ?? 0) >= b.minRefine;
    rows.push(`<div class="d${on ? "" : " dim"}">${on ? "✔" : "○"} ครบชุด +${b.minRefine} ทุกชิ้น: ${esc(describeCard(b.bonus))}</div>`);
  }
  return `<div class="wsep"></div><div class="d" style="color:#86efac">🧩 ${esc(set.name)} (${worn.size}/${set.pieces.length})</div>`
    + `<div class="d" style="font-size:11px">${pieces}</div>` + rows.join("");
}

/** บรรทัดออปชันสุ่ม (ขั้น 3.6) สีตามคุณภาพการทอยเทียบช่วงของชั้น */
function optionLines(def: ItemDef, opts: RolledOption[]): string {
  if (opts.length === 0) return "";
  return `<div class="iopts">` + opts.map((o) => {
    const q = optionQuality(o, def);
    const c = q >= 0.95 ? "#f59e0b" : q >= 0.66 ? "#a78bfa" : q >= 0.33 ? "#60a5fa" : "#9ca3af";
    return `<div class="d" style="color:${c}">◆ ${esc(describeOption(o))}</div>`;
  }).join("") + `</div>`;
}

/** บรรทัดเรื่องการ์ดในการ์ดรายละเอียดไอเทม (ผลของการ์ด หรือช่องการ์ดของอุปกรณ์) */
function cardDetail(def: ItemDef, inserted: string[]): string {
  if (def.kind === "card" && def.card) {
    const fits = def.card.fits.map((k) => EQUIP_SLOTS.find((s) => s.kind === k)?.label ?? k).join(", ");
    return `<div class="ib">${esc(describeCard(def.card.bonus, def.card.reqClass))}</div>`
      + `<div class="ireq">ใส่กับ: ${esc(fits)} · ที่โรงตีเหล็ก (ถอดไม่ได้)</div>`;
  }
  const slots = cardSlotsOf(def);
  if (def.kind !== "equip" || slots === 0) return "";
  const names = inserted.map((id) => ITEMS[id]?.name ?? id);
  return `<div class="ireq">ช่องตะเกียง ${inserted.length}/${slots}`
    + (names.length > 0 ? ` — ${esc(names.join(", "))}` : "")
    + `</div>`;
}

/**
 * เวลาหมดอายุของบัฟแต่ละตัว (performance.now)
 *
 * server บอกความยาวมาตอนร่ายผ่าน event buffOn แล้ว client นับถอยหลังเอง
 * ไม่ sync เวลาที่เหลือทุก tick เพราะจะเปลือง bandwidth โดยไม่จำเป็น
 * ตัวตัดสินว่าบัฟยังอยู่ไหมคือ p.buffs ที่ server ส่งมา ไม่ใช่นาฬิกาฝั่งนี้
 */
const buffEndsAt = new Map<string, number>();

function findSkillAnywhere(id: string): SkillDef | undefined {
  for (const c of Object.values(CLASSES)) {
    const found = findSkill(c, id);
    if (found) return found;
  }
  return undefined;
}

function buffGlyph(id: string): string {
  switch (id) {
    case "lantern_blessing": return "🏮";
    case "stance": return "🛡";
    case "rage": return "🔥";
    case "eye": return "🎯";
    case "edge": return "🗡";
    case "ward": return "❇";
    case "mend": return "💚";
    case "adexp": return "⭐";
    default: return "✨";
  }
}

function buffBonusText(sk: SkillDef): string {
  if (sk.desc) return sk.desc;
  const b = sk.buff;
  if (!b) return "";
  const parts: string[] = [];
  if (b.flatDef) parts.push(`DEF +${b.flatDef}`);
  if (b.flatBlock) parts.push(`บล็อก +${Math.round(b.flatBlock * 100)}%`);
  if (b.flatCrit) parts.push(`คริ +${b.flatCrit}%`);
  if (b.aspdMul) parts.push(`ความเร็วตี +${Math.round((b.aspdMul - 1) * 100)}%`);
  if (b.healPct) parts.push(`ฟื้น HP ${Math.round(b.healPct * 100)}%`);
  return parts.join(" · ");
}

function drawCharWindow(): void {
  // DOM ชุดเก่าถูกแทนที่ทั้งก้อน ป้ายของช่องเดิมจะไม่ได้รับ mouseleave อีก
  // ไม่ปิดเองตรงนี้แล้วป้ายจะค้างกลางจอ
  hideTip();

  const self: any = room?.state?.players?.get(room.sessionId);
  if (!self) {
    charLeft.textContent = "กำลังเชื่อมต่อ…";
    return;
  }

  const st = selfStats(self);
  const cls = CLASSES[self.cls] ?? getClass("warden");
  // คิดค่ารองฝั่ง client ด้วยสูตรและฟังก์ชันชุดเดียวกับ server เพื่อแสดงผล
  // **ของสวมใส่ต้องรวมด้วย** ไม่งั้นใส่อาวุธแล้วตัวเลขในหน้าต่างไม่ขยับ
  // ทั้งที่ดาเมจจริงเปลี่ยนไปแล้ว ซึ่งหลอกตามากกว่าไม่โชว์เสียอีก
  // ยังไม่รวมบัฟ (บัฟมีไอคอนนับถอยหลังของมันเอง) ตัวเลขจริง server เป็นผู้ตัดสิน
  const gear = equipTotals(self.equipped ?? {}, ITEMS, self.refines ?? {});
  // การ์ดรวมด้วย ไม่งั้นตัวเลขในหน้าต่างไม่ตรงกับที่ server คิด (FieldRoom.refreshDerived)
  const cardsTot = cardTotals(self.equipped ?? {}, self.cards ?? {}, ITEMS, self.options ?? {}, self.refines ?? {});
  // โบนัสสเตตัสจาก Job Level (ฟังก์ชัน shared ตัวเดียวกับที่ server ใช้ใน refreshDerived)
  const jobBonus = jobStatBonus(cls, self.jobLevel);
  // สกิลติดตัว (shared/passives.ts) ฟังก์ชันเดียวกับ server
  const passive = passiveTotals(getAllSkillsForClass(self.cls), self.skills ?? {}, heldWeapons(self.cls, self.equipped ?? {}, ITEMS));

  // คำนวณแต้มที่ใช้ไปและแต้มคงเหลือสำหรับ pendingStats
  const totalPendingCost = calcPendingStatsCost(st, pendingStats);
  const remainingStatPoints = Math.max(0, Number(self.statPoints ?? 0) - totalPendingCost);
  const totalPendingAllocated = Object.values(pendingStats).reduce((a, b) => a + b, 0);

  // คำนวณ Stats จำลองรวม pendingStats เพื่อคำนวณและแสดงผล Preview ค่ารอง (ATK, DEF, HP, etc.) แบบเรียลไทม์
  const stPreview: Stats = { ...st };
  for (const k of STAT_KEYS) {
    stPreview[k] = (stPreview[k] ?? 1) + (pendingStats[k] ?? 0);
  }

  const d = deriveStats(cls,
    withEquipStats(withEquipStats(withEquipStats(withEquipStats(stPreview, jobBonus), gear.stats), cardsTot.stats), passive.stats),
    self.baseLevel, mergeModifiers(mergeModifiers(gear.mods, cardsTot.mods), passive.mods));

  const rows = STAT_KEYS.map((k) => {
    const curVal = st[k] + (pendingStats[k] ?? 0);
    const cost = curVal >= STAT_CAP ? 0 : statCost(curVal);
    const canPlus = cost > 0 && remainingStatPoints >= cost;
    const canMinus = (pendingStats[k] ?? 0) > 0;
    const fromGear = (gear.stats[k] ?? 0) + (jobBonus[k] ?? 0) + (cardsTot.stats[k] ?? 0) + (passive.stats[k] ?? 0);
    const pendingBadge = (pendingStats[k] ?? 0) > 0 ? `<b style="color:#38bdf8;margin-left:3px;font-size:9.5px;">(+${pendingStats[k]})</b>` : "";
    return `<div class="srow">`
      + `<span class="k">${k}</span>`
      + `<span class="v">${st[k]}${fromGear ? `<i>+${fromGear}</i>` : ""}${pendingBadge}</span>`
      + `<div style="display:flex;gap:3px;align-items:center;">`
      + (canMinus ? `<button type="button" class="minus" data-stat-minus="${k}" title="ลดแต้มที่เลือก">-</button>` : "")
      + `<button type="button" data-stat-plus="${k}" ${canPlus ? "" : "disabled"}`
      + ` title="เพิ่มแต้มที่ ${STAT_TH[k]} (ใช้ ${cost || "เต็ม"} แต้ม)">+</button>`
      + `</div>`
      + `<span class="c">${STAT_TH[k]}`
      + (cost > 0 ? ` · ${cost} แต้ม` : " · เต็ม")
      + `</span>`
      + `</div>`;
  }).join("");

  let confirmBar = "";
  if (totalPendingAllocated > 0) {
    confirmBar = `<div class="stat-confirm-bar">`
      + `<div class="info">`
      + `<span style="color:#7dd3fc;font-weight:700;">⚡ รอการยืนยัน: +${totalPendingAllocated} แต้ม</span>`
      + `<span style="color:#e0f2fe;font-size:10px;">ใช้ ${totalPendingCost} แต้ม (คงเหลือ ${remainingStatPoints})</span>`
      + `</div>`
      + `<div class="actions">`
      + `<button type="button" class="btn-confirm-save" id="btnConfirmStats">💾 ยืนยันการอัพสเตตัส</button>`
      + `<button type="button" class="btn-confirm-cancel" id="btnCancelStats">❌ ยกเลิก</button>`
      + `</div>`
      + `</div>`;
  }

  const derived: Array<[string, string]> = [
    ["HP", `${Math.ceil(self.hp)} / ${Math.round(self.maxHp)}`],
    ["MP", `${Math.ceil(self.mp)} / ${Math.round(self.maxMp)}`],
    ["ATK", String(d.atk)],
    ["MATK", String(d.matk)],
    ["DEF", `${d.def} (+${d.softDef.toFixed(1)})`],
    ["HIT", String(d.hit)],
    ["FLEE", String(d.flee)],
    ["คริ", `${d.crit.toFixed(1)}%`],
    ["ความเร็วตี", `${d.aspd.toFixed(2)} ครั้ง/วิ`],
    ["บล็อก", `${d.block}%`],
  ];

  // ---- ช่องสวมใส่ รอบรูปตัวละคร ----
  // ผังมาจาก shared ที่นี่เติมแค่ไอคอนกับของที่ใส่อยู่
  const eqCell = (slot: { id: string; label: string }): string => {
    const rawItemId = String((self.equipped as any)?.get?.(slot.id) ?? "");
    const wepId = String((self.equipped as any)?.get?.("weapon") ?? "");
    const wepDef = wepId ? ITEMS[wepId] : undefined;
    const isOccupiedBy2H = slot.id === "offhand" && !rawItemId && isTwoHanded(wepDef);

    const itemId = isOccupiedBy2H ? wepId : rawItemId;
    const def = itemId ? ITEMS[itemId] : undefined;
    const color = def ? RARITY_COLOR[def.rarity ?? "common"] : "";
    const picked = inspect?.slot === slot.id || (isOccupiedBy2H && inspect?.slot === "weapon");
    const refineLvl = !isOccupiedBy2H ? Number((self.refines as any)?.get?.(slot.id) ?? 0) : 0;
    const badge = refineLvl > 0 ? `<span class="refine-badge">+${refineLvl}</span>` : "";
    const twoHandTag = isOccupiedBy2H ? `<span class="two-hand-tag">2 มือ</span>` : "";

    return `<div class="eqslot${def ? " on" : ""}${isOccupiedBy2H ? " two-handed" : ""}${picked ? " sel" : ""}"`
      + ` data-equip="${slot.id}"`
      + (isOccupiedBy2H ? ` data-2h="true"` : "")
      + (color && !isOccupiedBy2H ? ` style="border-color:${color};border-style:solid"` : "")
      + `>`
      + `${def ? itemGlyph(def) : SLOT_GLYPH[slot.id] ?? "\u2B1A"}`
      + badge
      + twoHandTag
      + `</div>`;
  };

  const eqSide = (side: "l" | "r"): string =>
    EQUIP_SLOTS.filter((e) => e.side === side).map(eqCell).join("");

  const eqCells =
    `<div class="eqcol">${eqSide("l")}</div>`
    + `<div class="eqdoll" id="chardoll"></div>`
    + `<div class="eqcol">${eqSide("r")}</div>`
    + `<div class="eqbottom">`
    + EQUIP_SLOTS.filter((e) => e.side === "wide").map(eqCell).join("")
    + `</div>`;

  const eqGrid = `<div class="eq">${eqCells}</div>`;

  // ---- รายการบัฟที่ทำงานอยู่ ----
  const activeBuffIds: string[] = self?.buffs ? String(self.buffs).split(",").filter(Boolean) : [];
  const buffCards = activeBuffIds.map((id) => {
    const sk = findSkillAnywhere(id);
    const glyph = buffGlyph(id);
    const name = sk ? sk.th : id === BLESSING_ID ? BLESSING_TH : id;
    const desc = sk ? buffBonusText(sk) : id === BLESSING_ID ? describeBlessing() : "";
    const left = Math.max(0, (buffEndsAt.get(id) ?? 0) - performance.now());
    const sec = Math.ceil(left / 1000);
    const bg = sk ? skillIcon(sk) : buffIcon(id);
    void glyph;
    return `<div class="char-buff-row" data-buff-id="${id}">`
      + `<span class="char-buff-icon" style="background:${bg} center / cover no-repeat"></span>`
      + `<div class="char-buff-info">`
      + `<div class="char-buff-name">${esc(name)}</div>`
      + (desc ? `<div class="char-buff-desc">${esc(desc)}</div>` : "")
      + `</div>`
      + `<span class="char-buff-time">${sec > 0 ? `${sec} วิ` : "ติดอยู่"}</span>`
      + `</div>`;
  }).join("");

  // ไม่มีบัฟก็ไม่ต้องกินที่ หน้าต่างจะได้เตี้ยลง (HUD มีแถวบัฟของมันเองอยู่แล้ว)
  const buffSection = activeBuffIds.length === 0 ? "" :
    `<div class="wsep"></div>`
    + `<div class="drow"><span class="pts">รายการบัฟที่ทำงานอยู่</span>`
    + `<span class="dim">${activeBuffIds.length} รายการ</span></div>`
    + `<div class="char-buffs">`
    + (buffCards || `<div class="dim" style="font-size:10px;padding:2px 0;">ไม่มีบัฟที่ทำงานอยู่</div>`)
    + `</div>`;

  // ซ้าย: ตัวละครกับของสวมใส่อยู่บนสุด แล้วค่อยเป็นสเตตัส (ขวาคือกระเป๋า อยู่ใน drawBagWindow)
  charLeft.innerHTML =
    `<div class="drow"><span>${esc(self.name)}</span><span>${esc(cls.name)}</span></div>`
    + `<div class="drow"><span class="dim">Base ${self.baseLevel} · Job ${self.jobLevel}</span>`
    + `<span class="pts">แต้มเหลือ ${remainingStatPoints}${totalPendingCost > 0 ? ` <i style="color:#38bdf8;font-style:normal;">(-${totalPendingCost})</i>` : ""}</span></div>`
    + eqGrid
    + `<div class="hint">ลากที่ตัวละครเพื่อหมุนดูรอบตัว</div>`
    + `<div class="wsep"></div>`
    + rows
    + confirmBar
    + `<div class="wsep"></div>`
    + `<div class="dgrid">`
    + derived.map(([k, v]) => `<div class="drow"><span class="dim">${k}</span><span>${v}</span></div>`).join("")
    + `</div>`
    + buffSection;

  // รูปตัวละครกลางผังอุปกรณ์ — ใช้ตัววาดเดียวกับการ์ดในหน้าเลือกตัวละคร
  // เป็น canvas 2D ที่คัดลอกภาพมา ไม่ใช่ฉากสด จึงไม่กิน WebGL context เพิ่ม
  // และวาดเฉพาะตอนอาชีพเปลี่ยนหรือตอนลากหมุน ระหว่างนิ่งไม่เรนเดอร์เลย
  const doll = winChar.body.querySelector<HTMLElement>("#chardoll");
  if (doll) {
    if (!dollCanvas) {
      dollCanvas = document.createElement("canvas");
      dollCanvas.width = 192;
      dollCanvas.height = 192;
      attachDollDrag(dollCanvas);
    }
    const g = parseGender(self.gender);
    const sig = gearSig(self.equipped);
    if (dollCls !== self.cls || dollGender !== g || dollGear !== sig) {
      dollCls = String(self.cls);
      dollGender = g;
      dollGear = sig;
      redrawDoll(cls.look, gearLooks(self.equipped));
    }
    doll.appendChild(dollCanvas);
  }

  // ช่องสวมใส่: ชี้ = ป้ายชื่อ · คลิก = ดู · ดับเบิลคลิก = ถอด
  for (const cell of winChar.body.querySelectorAll<HTMLElement>("[data-equip]")) {
    const slot = String(cell.dataset["equip"] ?? "");
    const is2H = cell.dataset["2h"] === "true";
    const label = EQUIP_SLOTS.find((e) => e.id === slot)?.label ?? slot;
    const rawWorn = String((self.equipped as any)?.get?.(slot) ?? "");
    const wepWorn = String((self.equipped as any)?.get?.("weapon") ?? "");
    const worn = is2H ? wepWorn : rawWorn;
    const def = worn ? ITEMS[worn] : undefined;

    cell.addEventListener("mouseenter", (e) => {
      if (is2H && def) {
        showTip(
          `<span class="tn">${esc(def.name)}</span>`
            + `<br /><span class="td">อาวุธ 2 มือ (ครองช่องอาวุธและโล่) · ดับเบิลคลิกเพื่อถอด</span>`,
          e as MouseEvent,
        );
      } else {
        showTip(
          def
            ? `<span class="tn">${esc(def.name)}</span>`
              + `<br /><span class="td">${esc(label)} · ดับเบิลคลิกเพื่อถอด</span>`
            : `<span class="tn">${esc(label)}</span><br /><span class="td">ว่าง</span>`,
          e as MouseEvent,
        );
      }
    });
    cell.addEventListener("mousemove", (e) => moveTip(e as MouseEvent));
    cell.addEventListener("mouseleave", hideTip);

    cell.addEventListener("click", () => {
      if (!worn) {
        addLog(`ช่อง${label}ว่างอยู่ — ดับเบิลคลิกของในกระเป๋าเพื่อสวม`);
        return;
      }
      const targetSlot = is2H ? "weapon" : slot;
      inspect = inspect?.slot === targetSlot ? undefined : { item: worn, slot: targetSlot };
      drawCharWindow();
    });

    cell.addEventListener("dblclick", (e) => {
      e.preventDefault();
      const targetSlot = is2H ? "weapon" : slot;
      if (worn) room?.send("unequip", { slot: targetSlot });
    });
  }

  // การ์ดรายละเอียดลอยอยู่ข้างช่องที่คลิก วาดหลังตัวหน้าต่างเพื่อให้หาช่องที่เลือกเจอ
  refreshItemPop(self);

  // ปุ่มเพิ่มแต้มสเตตัส (Pending)
  for (const btn of winChar.body.querySelectorAll<HTMLElement>("button[data-stat-plus]")) {
    btn.addEventListener("click", () => {
      const stat = btn.dataset["statPlus"];
      if (!stat) return;
      pendingStats[stat] = (pendingStats[stat] ?? 0) + 1;
      if (settings.sound) sound.playClick();
      drawCharWindow();
    });
  }

  // ปุ่มลดแต้มสเตตัส (Pending)
  for (const btn of winChar.body.querySelectorAll<HTMLElement>("button[data-stat-minus]")) {
    btn.addEventListener("click", () => {
      const stat = btn.dataset["statMinus"];
      if (!stat) return;
      pendingStats[stat] = Math.max(0, (pendingStats[stat] ?? 0) - 1);
      if (settings.sound) sound.playClick();
      drawCharWindow();
    });
  }

  // ปุ่มยืนยันการอัพสเตตัสเพื่อบันทึกผล
  const btnConfirmStats = winChar.body.querySelector<HTMLElement>("#btnConfirmStats");
  if (btnConfirmStats) {
    btnConfirmStats.addEventListener("click", () => {
      if (!room) return;
      let totalCount = 0;
      for (const k of STAT_KEYS) {
        const count = pendingStats[k] ?? 0;
        for (let i = 0; i < count; i++) {
          room.send("addStat", { stat: k });
          totalCount++;
        }
        pendingStats[k] = 0;
      }
      if (totalCount > 0) {
        if (settings.sound) sound.playLevelUp?.();
        addLog(`💾 บันทึกการอัพสเตตัสเรียบร้อย (${totalCount} แต้ม)`);
      }
      drawCharWindow();
    });
  }

  // ปุ่มยกเลิกแต้มสเตตัสที่เลือกไว้
  const btnCancelStats = winChar.body.querySelector<HTMLElement>("#btnCancelStats");
  if (btnCancelStats) {
    btnCancelStats.addEventListener("click", () => {
      for (const k of STAT_KEYS) pendingStats[k] = 0;
      if (settings.sound) sound.playClick();
      drawCharWindow();
    });
  }
}

/** ล้างแต้มสกิล pending ที่กลายเป็นโมฆะ (เช่น เมื่อแต้มสกิลที่เป็นเงื่อนไขถูกลดลง) */
function cleanPendingSkills(cls: any, baseLevels: Record<string, number>): void {
  let changed = true;
  while (changed) {
    changed = false;
    for (const [skId, pts] of Object.entries(pendingSkills)) {
      if (pts <= 0) {
        delete pendingSkills[skId];
        continue;
      }
      const sk = cls.skills?.find((s: any) => s.id === skId);
      if (!sk) continue;
      const reqsMet = skillReqs(sk).every((r) => {
        const totalLv = (baseLevels[r.skill] ?? 0) + (pendingSkills[r.skill] ?? 0);
        return totalLv >= r.lv;
      });
      if (!reqsMet) {
        delete pendingSkills[skId];
        changed = true;
      }
    }
  }
}

function drawSkillWindow(): void {
  const self: any = room?.state?.players?.get(room.sessionId);
  if (!self) {
    winSkill.body.textContent = "กำลังเชื่อมต่อ…";
    return;
  }

  const cls = CLASSES[self.cls] ?? getClass("warden");
  const levels = skillLevels(self);
  const points = Number(self.skillPoints ?? 0);

  // คำนวณ pendingSkills
  const totalPendingSkills = Object.values(pendingSkills).reduce((a, b) => a + b, 0);
  const remainingSkillPoints = Math.max(0, points - totalPendingSkills);

  // สร้าง effectiveLevels รวม pending เพื่อนำไปตรวจสอบเงื่อนไขและพรีวิวตัวเลข
  const effectiveLevels: Record<string, number> = { ...levels };
  for (const [skId, pts] of Object.entries(pendingSkills)) {
    if (pts > 0) {
      effectiveLevels[skId] = (effectiveLevels[skId] ?? 0) + pts;
    }
  }

  // รีเซ็ตฟรีได้ไหม (server ตรวจซ้ำอยู่แล้ว ตรงนี้แค่เลือกว่าจะโชว์ปุ่มไหม)
  const canReset = !self.skillResetUsed && self.baseLevel < FREE_SKILL_RESET_BEFORE;
  const spentAny = cls.skills.some((s, i) => (levels[s.id] ?? 0) > (i === 0 ? 1 : 0));

  // แบนเนอร์เลื่อนขั้นอาชีพ
  let advBanner = "";
  const advClass = getAdvancedClassFor(self.cls);
  if (!isAdvancedClass(self.cls) && advClass) {
    const jobMax = jobLevelCap(cls);
    if (self.jobLevel >= jobMax) {
      advBanner = `<div class="adv-banner" style="background:linear-gradient(135deg, rgba(234, 179, 8, 0.2), rgba(202, 138, 4, 0.4)); border:1px solid #eab308; border-radius:6px; padding:10px; margin-bottom:12px; text-align:center;">`
        + `<div style="font-weight:bold; color:#fef08a; font-size:14px;">🌟 สามารถเลื่อนขั้นอาชีพได้แล้ว!</div>`
        + `<div style="font-size:12px; color:#cbd5e1; margin:4px 0 6px;">เลื่อนขั้นเป็น [${esc(advClass.name)}] (${esc(advClass.role)})</div>`
        + `<div style="font-size:11px; color:#94a3b8; margin-bottom:8px;">${esc(advClass.desc)}</div>`
        + `<button type="button" class="btn-advance" data-advance="${advClass.id}" style="background:#eab308; color:#000; border:none; padding:6px 14px; font-weight:bold; border-radius:4px; cursor:pointer;">`
        + `✨ เลื่อนขั้นเป็น ${esc(advClass.name)}`
        + `</button>`
        + `</div>`;
    } else {
      advBanner = `<div class="adv-banner" style="background:rgba(30, 41, 59, 0.6); border:1px solid #334155; border-radius:6px; padding:8px 10px; margin-bottom:12px; font-size:12px; color:#94a3b8; text-align:center;">`
        + `อาชีพขั้นสูงถัดไป: <b style="color:#e2e8f0;">${esc(advClass.name)}</b> (${esc(advClass.role)}) · ต้อง Job Lv เต็ม ${jobMax} (ปัจจุบัน ${self.jobLevel}/${jobMax})`
        + `</div>`;
    }
  }

  const renderSkillList = (skills: SkillDef[], title?: string) => {
    let html = "";
    if (title) {
      html += `<div style="font-weight:bold; font-size:12px; color:#94a3b8; margin:8px 0 4px;">${esc(title)}</div>`;
    }
    const held = heldWeapons(self.cls, self.equipped, ITEMS);
    const gearMods = gearSkillMods(self.equipped, self.cards, ITEMS, self.refines);
    html += skills.map((sk, i) => {
      const baseLv = levels[sk.id] ?? 0;
      const pendingLv = pendingSkills[sk.id] ?? 0;
      const lv = effectiveLevels[sk.id] ?? 0;
      const max = skillMaxLv(sk);
      const now = applySkillMods(skillAtLevel(sk, Math.max(1, lv)), gearMods[sk.id]);
      const next = lv < max ? applySkillMods(skillAtLevel(sk, lv + 1), gearMods[sk.id]) : undefined;
      const denial = learnDenial(cls, effectiveLevels, sk.id, self.jobLevel);

      const fx = (s: SkillDef): string => {
        if (s.kind === "passive" && s.passive) return describePassive(s.passive, s === next ? lv + 1 : Math.max(1, lv));
        const bits: string[] = [];
        if (s.power > 0) bits.push(`พลัง ${Math.round(s.power * 100)}%`);
        if (s.buff) bits.push(`${s.buff.duration} วิ`);
        if (s.status && s.power > 0) bits.push(`ติดสถานะ ${s.status.duration} วิ`);
        bits.push(`MP ${s.mp}`);
        return bits.join(" · ");
      };
      const stat = lv > 0
        ? `<span class="d">Lv ${lv}: ${esc(fx(now))}</span>`
          + (next ? `<br /><span class="d nx">Lv ${lv + 1}: ${esc(fx(next))}</span>` : "")
        : `<span class="d nx">Lv 1: ${esc(fx(now))}</span>`;

      // ท่าถือที่สกิลต้องการ — ลงแต้มได้เสมอ แต่ร่ายไม่ได้ถ้าถือไม่ตรง (server ตัดสิน)
      const hold = describeNeeds(sk);
      let need = "";
      if (denial === "job") need = `ต้อง Job ${sk.job}`;
      else if (denial === "req") {
        need = skillReqs(sk)
          .filter((r) => (effectiveLevels[r.skill] ?? 0) < r.lv)
          .map((r) => `ต้องมี ${(cls.skills.find((s) => s.id === r.skill)?.th) ?? r.skill} Lv ${r.lv}`)
          .join(" · ");
      }

      // สกิลอาชีพขั้นต้นของคนที่เลื่อนขั้นแล้ว: ใช้ได้ตามเลเวลเดิม แต่ลงแต้มเพิ่มไม่ได้
      let btn = "";
      if (denial === "unknown") {
        btn = `<span class="sklv max" title="สกิลอาชีพขั้นต้น ใช้ได้ตามเลเวลที่ลงไว้">ติดตัว</span>`;
      } else {
        const canMinus = pendingLv > 0;
        const canPlus = denial === undefined;
        const minusBtn = canMinus
          ? `<button type="button" class="sklearn minus" data-learn-minus="${sk.id}" title="ลดแต้มที่เลือก">-</button>`
          : "";
        const plusBtn = denial === "max"
          ? `<span class="sklv max">MAX</span>`
          : `<button type="button" class="sklearn" data-learn-plus="${sk.id}"`
            + `${!canPlus ? ` disabled title="${esc(LEARN_TEXT[denial])}"` : ""}>+</button>`;
        btn = `<div style="display:flex;gap:3px;align-items:center;">${minusBtn}${plusBtn}</div>`;
      }

      const pendingBadge = pendingLv > 0 ? `<b style="color:#38bdf8;margin-left:3px;font-size:9.5px;">(+${pendingLv})</b>` : "";

      return `<div class="skrow${lv < 1 ? " off" : ""}">`
        + `<span class="gl" style="background:${skillIcon(sk)} center / cover no-repeat"></span>`
        + `<span class="txt"><span class="t">${esc(sk.th)}</span>`
        + ` <span class="dim">Lv ${baseLv}${pendingBadge}/${max}${sk.kind === "passive" ? " · ติดตัว" : ` · ${sk.cd}s`}</span>`
        + `<br /><span class="d">${esc(sk.desc)}</span>`
        + `<br />${stat}`
        + (need ? `<br /><span class="d need">${esc(need)}</span>` : "")
        + (hold ? `<br /><span class="d${weaponNeedMet(sk, held) ? "" : " need"}">${esc(hold)}</span>` : "")
        + `</span>${btn}</div>`;
    }).join("");
    return html;
  };

  // ---- ต้นสกิล (รอบ 20 · skill-tree-layout.ts) แท็บอาชีพขั้นต้น/ขั้นสูง + แผงรายละเอียดสกิลที่เลือก ----
  const baseCls = cls.tier === 2 && cls.baseClass ? CLASSES[cls.baseClass] : undefined;
  const tabCls = baseCls && skillTab === "base" ? baseCls : cls;
  const tabs = baseCls
    ? `<div class="sktabs"><button type="button" data-sktab="adv" class="${tabCls === cls ? "on" : ""}">${esc(cls.name)}</button>`
      + `<button type="button" data-sktab="base" class="${tabCls === baseCls ? "on" : ""}">${esc(baseCls.name)}</button></div>`
    : "";
  const tree = layoutSkillTree(tabCls.skills, tabCls.branches ?? {});
  if (!tabCls.skills.some((k) => k.id === skillSel)) skillSel = tabCls.skills[0]?.id ?? "";
  const CW = 62, RH = 70, PAD = 8;
  const px = (n: { col: number; row: number }) => ({ x: PAD + n.col * CW + CW / 2, y: 22 + n.row * RH + 26 });
  const pos = new Map(tree.nodes.map((n) => [n.id, px(n)]));
  const lines = tree.edges.map((e) => {
    const a = pos.get(e.from), b = pos.get(e.to);
    if (!a || !b) return "";
    const on = (effectiveLevels[e.to] ?? 0) > 0;
    return `<path d="M${a.x} ${a.y + 22} C${a.x} ${a.y + 44} ${b.x} ${b.y - 44} ${b.x} ${b.y - 22}" class="${on ? "on" : ""}"/>`;
  }).join("");
  const nodesHtml = tree.nodes.map((n) => {
    const sk = tabCls.skills.find((k) => k.id === n.id)!;
    const baseLv = levels[sk.id] ?? 0;
    const pendingLv = pendingSkills[sk.id] ?? 0;
    const lv = effectiveLevels[sk.id] ?? 0;
    const p = pos.get(n.id)!;
    const can = !learnDenial(tabCls, effectiveLevels, sk.id, self.jobLevel) && tabCls === cls;
    const badge = pendingLv > 0 ? ` (+${pendingLv})` : "";
    return `<button type="button" class="sknode${lv > 0 ? " learned" : ""}${can ? " can" : ""}${sk.id === skillSel ? " sel" : ""}${sk.kind === "passive" ? " passive" : ""}"`
      + ` data-sksel="${sk.id}" style="left:${p.x - 22}px;top:${p.y - 22}px;background:${skillIcon(sk)} center / cover no-repeat" title="${esc(sk.th)}">`
      + `<span class="lvb">${baseLv}${badge}/${skillMaxLv(sk)}</span><span class="nmb">${esc(sk.th)}</span></button>`;
  }).join("");
  const heads = tree.headers.map((h) => `<div class="skhead" style="left:${PAD + h.col * CW}px;width:${h.span * CW}px">${esc(h.label)}</div>`).join("");
  const W = PAD * 2 + tree.cols * CW, H = 22 + tree.rows * RH + 18;
  const selSk = tabCls.skills.find((k) => k.id === skillSel);
  let skillsHtml = tabs
    + `<div class="sktree" style="width:${W}px;height:${H}px">${heads}<svg width="${W}" height="${H}">${lines}</svg>${nodesHtml}</div>`
    + (selSk ? `<div class="skdetail">${renderSkillList([selSk])}`
      + (tabCls === cls && selSk.kind !== "passive" && (levels[selSk.id] ?? 0) > 0
        ? `<button type="button" class="btn-bag-sort" data-hbskill="${selSk.id}" style="margin-top:4px;display:flex;align-items:center;justify-content:center;gap:4px">⚡ จัดใส่ช่องทางลัด (เลือกช่อง 1 - 20)</button>`
          + `<div class="hint">หรือลากไอคอนสกิลไปวางที่ช่องในแถบลัดได้เลย</div>`
        : "") + `</div>` : "");

  let confirmBar = "";
  if (totalPendingSkills > 0) {
    confirmBar = `<div class="skill-confirm-bar">`
      + `<div class="info">`
      + `<span style="color:#7dd3fc;font-weight:700;">⚡ รอการยืนยัน: +${totalPendingSkills} แต้ม</span>`
      + `<span style="color:#e0f2fe;font-size:10px;">คงเหลือ ${remainingSkillPoints} แต้ม</span>`
      + `</div>`
      + `<div class="actions">`
      + `<button type="button" class="btn-confirm-save" id="btnConfirmSkills">💾 ยืนยันการอัพสกิล</button>`
      + `<button type="button" class="btn-confirm-cancel" id="btnCancelSkills">❌ ยกเลิก</button>`
      + `</div>`
      + `</div>`;
  }

  winSkill.body.innerHTML =
    `<div class="drow"><span>${esc(cls.name)} · Job ${self.jobLevel}</span>`
    + `<span class="pts">แต้มสกิล ${remainingSkillPoints}${totalPendingSkills > 0 ? ` <i style="color:#38bdf8;font-style:normal;">(-${totalPendingSkills})</i>` : ""}</span></div>`
    + advBanner
    + confirmBar
    + `<div class="wsep"></div>`
    + skillsHtml
    + `<div class="wsep"></div>`
    + (skillNote ? `<div class="d">${esc(skillNote)}</div>` : "")
    + `<div class="d">ได้ 1 แต้มทุกครั้งที่ Job Level ขึ้น (เต็มที่ Job ${jobLevelCap(cls)}) · สกิลละสูงสุด ${SKILL_MAX_LV} เลเวล</div>`
    + (canReset && spentAny
      ? `<button type="button" class="skreset" data-reset="1">`
        + (skillResetArmed ? "กดอีกครั้งเพื่อยืนยันรีเซ็ต" : "รีเซ็ตสกิล (ฟรี 1 ครั้ง)")
        + `</button>`
      : "")
    + (self.skillResetUsed
      ? `<div class="d">ใช้สิทธิ์รีเซ็ตฟรีไปแล้ว</div>`
      : "");
}

/** ต้นสกิล: สกิลที่เลือกดูรายละเอียด และแท็บ (อาชีพขั้นสูง/ขั้นต้น) */
let skillSel = "";
let skillTab: "adv" | "base" = "adv";

/** ผลการลงแต้ม/รีเซ็ตล่าสุด แสดงในหน้าต่างสกิลเอง (เหตุผลเดียวกับ shopNote) */
let skillNote = "";
/**
 * รีเซ็ตต้องกดสองครั้ง ใช้สิทธิ์ฟรีมีครั้งเดียว กดพลาดแล้วเอาคืนไม่ได้
 * ไม่ใช้ confirm() ของเบราว์เซอร์ เพราะหยุดทั้งเกมระหว่างรอ
 */
let skillResetArmed = false;
/** ลายเซ็นของหน้าต่างสกิล **ไม่รวม MP** หน้าต่างนี้มีปุ่ม (ดูเหตุผลที่หน้าต่างปาร์ตี้) */
let skillWinSig = "";

/** เลเวลของสกิลหนึ่งที่ผู้เล่นลงแต้มไว้ 0 = ยังไม่ได้เรียน */
function skillLv(self: any, id: string): number {
  return Number(self?.skills?.get?.(id) ?? 0) || 0;
}

/** เลเวลสกิลทั้งหมดเป็น object ธรรมดา (MapSchema → Record) */
function skillLevels(self: any): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, lv] of (self?.skills ?? []) as Iterable<[string, number]>) out[id] = lv;
  return out;
}

// ปุ่มในหน้าต่างสกิล — ตัวจับเดียวที่ตัวหน้าต่าง
winSkill.body.addEventListener("pointerdown", (e) => {
  const node = (e.target as HTMLElement | null)?.closest?.<HTMLElement>(".sknode.learned:not(.passive)");
  if (node?.dataset["sksel"]) startSkillDrag(e, node.dataset["sksel"]);
});

winSkill.body.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement | null)?.closest?.("button");
  if (!btn || btn.disabled || !room) return;
  const advance = btn.dataset["advance"];
  if (advance) {
    room.send("classAdvance", { toClass: advance });
    return;
  }
  // แตะ "ใส่แถบลัด" (จอสัมผัสลากจากหน้าต่างที่เลื่อนได้ยาก): เปิดหน้าต่างเลือกช่อง 1-20 ได้อย่างอิสระ
  const hbSkill = btn.dataset["hbskill"];
  if (hbSkill) {
    openQuickslotManager(`s:${hbSkill}`);
    return;
  }
  const learnPlus = btn.dataset["learnPlus"] || btn.dataset["learn"];
  if (learnPlus) {
    skillResetArmed = false;
    pendingSkills[learnPlus] = (pendingSkills[learnPlus] ?? 0) + 1;
    if (settings.sound) sound.playClick();
    drawSkillWindow();
    return;
  }
  const learnMinus = btn.dataset["learnMinus"];
  if (learnMinus) {
    skillResetArmed = false;
    pendingSkills[learnMinus] = Math.max(0, (pendingSkills[learnMinus] ?? 0) - 1);
    if (pendingSkills[learnMinus] === 0) delete pendingSkills[learnMinus];
    const self: any = room.state?.players?.get(room.sessionId);
    if (self) {
      const cls = CLASSES[self.cls] ?? getClass("warden");
      cleanPendingSkills(cls, skillLevels(self));
    }
    if (settings.sound) sound.playClick();
    drawSkillWindow();
    return;
  }
  if (btn.id === "btnConfirmSkills") {
    const self: any = room.state?.players?.get(room.sessionId);
    if (!self) return;
    const cls = CLASSES[self.cls] ?? getClass("warden");
    const levels = skillLevels(self);
    let totalCount = 0;
    // ส่งคำสั่งตามลำดับ requirement ที่ถูกต้อง
    const curLv = { ...levels };
    const toSend = { ...pendingSkills };
    let madeProgress = true;
    while (madeProgress) {
      madeProgress = false;
      for (const sk of cls.skills) {
        if ((toSend[sk.id] ?? 0) > 0) {
          const reqsMet = skillReqs(sk).every((r) => (curLv[r.skill] ?? 0) >= r.lv);
          if (reqsMet) {
            room.send("learnSkill", { skill: sk.id });
            curLv[sk.id] = (curLv[sk.id] ?? 0) + 1;
            toSend[sk.id] = (toSend[sk.id] ?? 0) - 1;
            totalCount++;
            madeProgress = true;
          }
        }
      }
    }
    // สกิลที่เหลือ (ถ้ามี)
    for (const [id, count] of Object.entries(toSend)) {
      for (let i = 0; i < count; i++) {
        room.send("learnSkill", { skill: id });
        totalCount++;
      }
    }
    for (const k of Object.keys(pendingSkills)) delete pendingSkills[k];
    if (totalCount > 0) {
      if (settings.sound) sound.playLevelUp?.();
      addLog(`💾 บันทึกการอัพสกิลเรียบร้อย (${totalCount} แต้ม)`);
    }
    drawSkillWindow();
    return;
  }
  if (btn.id === "btnCancelSkills") {
    for (const k of Object.keys(pendingSkills)) delete pendingSkills[k];
    if (settings.sound) sound.playClick();
    drawSkillWindow();
    return;
  }
  if (btn.dataset["sksel"]) {
    skillSel = btn.dataset["sksel"];
    drawSkillWindow();
    return;
  }
  if (btn.dataset["sktab"]) {
    skillTab = btn.dataset["sktab"] as "adv" | "base";
    skillSel = "";
    drawSkillWindow();
    return;
  }
  if (btn.dataset["reset"]) {
    if (!skillResetArmed) {
      skillResetArmed = true;
      drawSkillWindow();
      return;
    }
    skillResetArmed = false;
    for (const k of Object.keys(pendingSkills)) delete pendingSkills[k];
    room.send("resetSkills", {});
  }
});

/* ---------- ปุ่มลัดที่ตั้งเองได้ ---------- */

/**
 * ปุ่มที่ผู้เล่นตั้งไว้ กับตารางค้นกลับจากปุ่มเป็นคำสั่ง
 *
 * `lookup` ต้องสร้างใหม่ทุกครั้งที่ `bindings` เปลี่ยน ไม่งั้นตั้งปุ่มแล้วกดไม่ติด
 * ใช้ `rebind()` ที่เดียวจะได้ไม่ลืม
 */
let bindings = loadBindings();
let lookup = buildLookup(bindings);

/**
 * กำลังรอรับปุ่มใหม่อยู่หรือไม่
 *
 * ไม่ใช่ null = หน้าตั้งค่าปุ่มกำลังรอ ตัวจับ keydown ต้องส่งปุ่มให้ที่นี่
 * แทนที่จะเอาไปสั่งเดินหรือร่ายสกิล ไม่งั้นกดปุ่มที่จะตั้งแล้วตัวละครวิ่งไปด้วย
 */
let grabKey: ((code: string) => void) | null = null;

function rebind(next: Bindings): void {
  bindings = next;
  lookup = buildLookup(bindings);
  saveBindings(bindings);
  // ตัวอักษรบนช่องแถบลัดกับแผงช่วยเหลือ อ่านจาก bindings จึงต้องวาดใหม่
  if (myClass) buildSkillBar(myClass);
  buildHelp();
  buildMenu();
  drawKeysWindow();
}

const winKeys = createWindow(uiWins, {
  title: "ตั้งค่าปุ่ม",
  x: 320,
  y: 120,
  onOpen: () => drawKeysWindow(),
});

function drawKeysWindow(): void {
  if (!winKeys.isOpen()) return;

  const dups = new Set(duplicates(bindings));
  const groups: string[] = [];
  for (const a of ACTIONS) if (!groups.includes(a.group)) groups.push(a.group);

  winKeys.body.innerHTML =
    `<div class="hint">คลิกที่ปุ่มแล้วกดปุ่มใหม่ที่ต้องการ · Esc เพื่อยกเลิก</div>`
    + groups.map((g) =>
      `<div class="kgroup">${esc(g)}</div>`
      + ACTIONS.filter((a) => a.group === g).map((a) => {
        const code = bindings[a.id] ?? "";
        const bad = code !== "" && dups.has(code);
        const also = (a.also ?? []).map(keyLabel).join(" ");
        return `<div class="krow">`
          + `<span class="kl">${esc(a.label)}</span>`
          + (also ? `<span class="ka">${esc(also)}</span>` : "")
          + `<button type="button" class="kb${bad ? " bad" : ""}" data-act="${a.id}"`
          + ` title="${bad ? "ปุ่มนี้ซ้ำกับคำสั่งอื่น" : "คลิกเพื่อเปลี่ยน"}">`
          + `${esc(keyLabel(code))}</button>`
          + `</div>`;
      }).join("")
    ).join("")
    + `<div class="wsep"></div>`
    + `<button type="button" class="kreset">คืนค่าเริ่มต้นทั้งหมด</button>`;

  for (const btn of winKeys.body.querySelectorAll<HTMLButtonElement>("button[data-act]")) {
    btn.addEventListener("click", () => {
      const id = btn.dataset["act"] as ActionId;
      // ปุ่มอื่นที่กำลังรออยู่ต้องเลิกรอ ไม่งั้นมีสองช่องกะพริบพร้อมกัน
      for (const other of winKeys.body.querySelectorAll(".kb.wait")) {
        other.classList.remove("wait");
      }
      btn.classList.add("wait");
      btn.textContent = "กดปุ่ม…";

      grabKey = (code: string): void => {
        grabKey = null;
        if (code === "Escape") {
          drawKeysWindow();
          return;
        }
        if (!canBind(code)) {
          addLog(`ปุ่ม ${keyLabel(code)} ใช้ตั้งไม่ได้ (เบราว์เซอร์ใช้เอง)`);
          drawKeysWindow();
          return;
        }
        rebind(bind(bindings, id, code));
        addLog(`ตั้ง "${ACTIONS.find((a) => a.id === id)?.label}" เป็นปุ่ม ${keyLabel(code)}`);
      };
    });
  }

  winKeys.body.querySelector(".kreset")?.addEventListener("click", () => {
    rebind(defaultBindings());
    addLog("คืนค่าปุ่มทั้งหมดเป็นค่าเริ่มต้นแล้ว");
  });
}

const winShop = createWindow(uiWins, {
  title: "ร้านค้ากลางเมือง",
  x: 470,
  y: 150,
  onOpen: () => drawShopWindow(),
  onClose: () => {
    if (inspect?.source === "shop") {
      inspect = undefined;
      compareGearOpen = false;
      uiItemPop.classList.remove("on");
      uiItemPop.innerHTML = "";
    }
    hideTip();
  },
});

/**
 * รายการและราคาที่ร้านรับซื้อ **อ่านจาก `shared/src/shop.ts`**
 *
 * client คิดเองเพื่อ "แสดง" ให้เห็นก่อนกดเท่านั้น เงินที่ได้จริงมาจาก server
 * เสมอ (event shopDone) ตามกฎข้อ 1 แต่กฎต้องมาจากที่เดียวกับที่ server ใช้
 * เคยลอกกฎมาไว้ที่นี่แล้วกรองเฉพาะของกิน ของสวมใส่กับสัตว์เลี้ยงที่ร้านขายจริง
 * จึงไม่มีปุ่มให้กดเลย — ซื้อได้แต่ไม่มีทางกด
 */

/**
 * ผลของคำสั่งซื้อขายครั้งล่าสุด แสดงในหน้าต่างร้านเอง
 *
 * บันทึกเหตุการณ์อยู่มุมซ้ายล่างซึ่งไกลจากหน้าต่างร้านมาก กดปุ่มในร้านแล้ว
 * ผลไปโผล่อีกมุมจอ ผู้เล่นจึงอ่านว่า "กดแล้วไม่มีอะไรเกิดขึ้น" ได้ง่าย
 * ทั้งที่ server ตอบมาแล้ว (เช่น ของในกระเป๋าไม่พอ)
 */
let shopNote = "";

/** ยืนใกล้ใจกลางเมืองพอจะซื้อขายได้ไหม ใช้แค่เพื่อบอกผู้เล่นก่อนกด */
function nearShop(): boolean {
  const zoneId = String((room?.state as any)?.zone ?? "meadow");
  // ร้านมีเฉพาะในเมือง ต้องตรงกับ handleShop ฝั่ง server
  if (!ALL_ZONES[zoneId]?.town) return false;
  const town = ALL_ZONES[zoneId]?.spawnPoint;
  if (!town) return false;
  return Math.hypot(myX - town.x, myZ - town.z) <= SHOP_RANGE;
}

const SHOP_TABS: ReadonlyArray<{ id: BagTab | "sell"; label: string }> = [
  { id: "use", label: "ยา" },
  { id: "equip", label: "อุปกรณ์" },
  { id: "material", label: "อื่น ๆ" },
  { id: "sell", label: "ขาย" },
];

function drawShopWindow(): void {
  const self: any = room?.state?.players?.get(room.sessionId);
  if (!self) {
    winShop.body.textContent = "กำลังเชื่อมต่อ…";
    return;
  }
  // บันทึกตำแหน่ง scroll เดิมไว้ เพื่อให้กดขายของต่อเนื่องได้โดยไม่ต้องเลื่อนใหม่ทุกครั้ง
  const oldScroll = winShop.body.querySelector<HTMLElement>(".shlist")?.scrollTop ?? 0;
  const far = !nearShop();
  const row = (def: ItemDef, price: number, btn: string, extra = ""): string => {
    const color = def.kind === "equip" ? RARITY_COLOR[def.rarity ?? "common"] : "";
    const req = def.kind === "equip" ? reqLine(def) : "";
    const picked = inspect?.source === "shop" && inspect.item === def.id;
    return `<div class="shrow${picked ? " sel" : ""}" data-item="${def.id}" title="${esc(def.desc)}">`
      + `<span class="g">${itemGlyph(def)}</span>`
      + `<span class="nm"><b${color ? ` style="color:${color}"` : ""}>${esc(def.name)}</b>${extra}`
      + (req ? `<i>${esc(req)}</i>` : "") + `</span>`
      + `<span class="pr">${price}</span>${btn}</div>`;
  };

  let list = "";
  if (shopFilter.tab === "sell") {
    const bag: Array<{ id: string; n: number; def: ItemDef | undefined }> = [];
    for (const [id, n] of (self.items ?? []) as Iterable<[string, number]>) {
      if (n > 0 && sellPrice(ITEMS[id]) > 0) bag.push({ id, n, def: ITEMS[id] });
    }
    sortItems(bag);
    list = bag.length === 0
      ? `<div class="d">ไม่มีของที่ร้านรับซื้อ</div>`
      : bag.map(({ id, n, def }) => {
          let btnHtml = `<button type="button" data-sell="${id}" data-count="1" ${far ? "disabled" : ""}>ขาย 1</button>`;
          if (n > 1) {
            btnHtml += `<button type="button" data-sell="${id}" data-count="${n}" ${far ? "disabled" : ""} style="margin-left:3px">ขายหมด (${n})</button>`;
            btnHtml += `<button type="button" data-sell-custom="${id}" data-max="${n}" ${far ? "disabled" : ""} style="margin-left:3px">ระบุจำนวน</button>`;
          }
          return row(def!, sellPrice(def), btnHtml, ` ×${n}`);
        }).join("");
    // ชิ้นมีตัวตน (ตีบวก/การ์ด/ออปชัน) ขายทีละชิ้น ราคาเท่าของธรรมดา — ตีบวกไม่เพิ่มราคา
    for (const [uid, raw] of (self.gear ?? []) as Iterable<[string, string]>) {
      const g = parseGear(raw);
      const def = ITEMS[g.itemId];
      if (!def || sellPrice(def) <= 0) continue;
      const tag = [g.refine > 0 ? `+${g.refine}` : "", g.cards.length ? `ไฟ ${g.cards.length}` : "",
        g.options?.length ? `ออปชัน ${g.options.length}` : ""].filter(Boolean).join(" · ");
      list += row(def, sellPrice(def),
        `<button type="button" data-sell-uid="${uid}" ${far ? "disabled" : ""}>ขาย</button>`,
        ` <span class="dim">(${esc(tag)})</span>`);
    }
  } else {
    const stock = shopStock(ITEMS, ZONES[currentZoneId()]).filter((it) => matchFilter(it, shopFilter as ItemFilter, self.cls));
    list = stock.length === 0
      ? `<div class="d">ไม่มีสินค้าในหมวดนี้</div>`
      : stock.map((it) => row(it, it.price,
        `<button type="button" data-buy="${it.id}" ${far || self.gold < it.price ? "disabled" : ""}>ซื้อ</button>`))
        .join("");
  }

  winShop.body.innerHTML =
    filterBar(shopFilter, SHOP_TABS)
    + `<div class="shlist">${list}</div>`
    + `<div class="bagfoot"><span>💰 ${self.gold}</span>`
    + `<span class="dim">${far ? "อยู่ไกลร้าน" : shopFilter.tab === "sell" ? "กดเพื่อขาย 1 ชิ้น" : "กดเพื่อซื้อ 1 ชิ้น"}</span></div>`
    + (shopNote ? `<div class="d">${esc(shopNote)}</div>` : "")
    + (shopFilter.tab === "sell" ? `<div class="d dim">ประกายวิญญาณขายร้านไม่ได้</div>` : "");

  // คืนค่าตำแหน่ง scroll เดิม
  const newList = winShop.body.querySelector<HTMLElement>(".shlist");
  if (newList && oldScroll > 0) {
    newList.scrollTop = oldScroll;
  }

  for (const rowEl of winShop.body.querySelectorAll<HTMLElement>(".shrow[data-item]")) {
    const id = String(rowEl.dataset["item"] ?? "");
    const def = ITEMS[id];
    rowEl.addEventListener("mouseenter", (e) => showTip(itemTip(def, id), e as MouseEvent));
    rowEl.addEventListener("mousemove", (e) => moveTip(e as MouseEvent));
    rowEl.addEventListener("mouseleave", hideTip);
  }
  refreshItemPop(self);
}

for (const ev of ["click", "change"]) {
  winShop.body.addEventListener(ev, (e) => {
    if (applyFilterEvent(e, shopFilter)) {
      if (inspect?.source === "shop") {
        inspect = undefined;
        uiItemPop.classList.remove("on");
        uiItemPop.innerHTML = "";
      }
      drawShopWindow();
    }
  });
}

/**
 * ปุ่มซื้อขายใช้ตัวจับเดียวที่ตัวหน้าต่าง **ไม่ใช่ผูกทีละปุ่มหลังวาด**
 *
 * `drawShopWindow` เขียน `innerHTML` ใหม่ทั้งก้อนทุกครั้งที่วาด (ซื้อขายเสร็จ
 * หรือเดินเข้าออกระยะร้าน) ปุ่มชุดเก่าจึงถูกทิ้งไปพร้อมตัวจับที่ผูกไว้
 * ถ้าการวาดใหม่มาคาบเกี่ยวระหว่างกดปุ่มค้างอยู่ เบราว์เซอร์จะไม่ยิง `click` เลย
 * เพราะปุ่มที่กดลงไปไม่ใช่ปุ่มเดิมที่ปล่อยนิ้ว — กดแล้วเงียบโดยไม่มี error
 *
 * ผูกไว้ที่ตัวหน้าต่างซึ่งอยู่ตลอดอายุเกม แล้วอ่านว่าปุ่มไหนถูกกดจาก `closest`
 * จึงไม่ขึ้นกับจังหวะการวาดอีกต่อไป
 */
const self0 = (): any => room?.state?.players?.get(room.sessionId);
winShop.body.addEventListener("click", (e) => {
  const target = e.target as HTMLElement | null;
  const btn = target?.closest?.("button");
  if (btn) {
    if (btn.disabled) return;
    const buy = btn.dataset["buy"];
    const sell = btn.dataset["sell"];
    const sellCustom = btn.dataset["sellCustom"];
    const sellUid = btn.dataset["sellUid"];
    if (sellUid) {
      const g = parseGear(self0()?.gear?.get?.(sellUid));
      if (window.confirm(`ขาย${ITEMS[g.itemId]?.name ?? g.itemId} ที่มีตีบวก/ไฟตะเกียง/ออปชันติดอยู่? ขายแล้วหายถาวร`)) {
        room?.send("sell", { item: g.itemId, count: 1, uid: sellUid });
      }
      return;
    }
    if (buy) {
      room?.send("buy", { item: buy, count: 1 });
    } else if (sellCustom) {
      const max = parseInt(btn.dataset["max"] ?? "1", 10) || 1;
      const def = ITEMS[sellCustom];
      const val = window.prompt(`ระบุจำนวนที่ต้องการขาย ${def?.name ?? sellCustom} (1 - ${max}):`, String(max));
      if (val) {
        const count = parseInt(val, 10);
        if (!isNaN(count) && count > 0) {
          room?.send("sell", { item: sellCustom, count: Math.min(count, max) });
        }
      }
    } else if (sell) {
      const count = parseInt(btn.dataset["count"] ?? "1", 10) || 1;
      room?.send("sell", { item: sell, count });
    }
    return;
  }

  // คลิกที่แถวไอเทม (หรือไอคอน/ชื่อ) เพื่อดูรายละเอียด
  const row = target?.closest?.<HTMLElement>(".shrow[data-item]");
  if (row) {
    const id = row.dataset["item"];
    if (!id) return;
    compareGearOpen = false;
    if (inspect?.source === "shop" && inspect.item === id) {
      inspect = undefined;
      uiItemPop.classList.remove("on");
      uiItemPop.innerHTML = "";
    } else {
      inspect = { item: id, slot: "", source: "shop" };
    }
    if (settings.sound) sound.playClick();
    drawShopWindow();
    if (winChar.isOpen()) drawCharWindow();
    if (winBag.isOpen()) drawBagWindow();
  }
});

/* ---------- โรงตีเหล็ก (Refine) ---------- */

/* ---------- ย้ายโซน ---------- */

/** ตัวละครที่ใช้เข้าห้องนี้ เก็บไว้ใช้ตอนรีโหลดไปโซนใหม่ */
let myChosen: Chosen | undefined;
/** id ประตูที่ส่งคำขอไปแล้ว กันส่งซ้ำทุกเฟรมระหว่างยืนในวง */
let portalSent = "";
/** กำลังย้ายโซนอยู่ ไม่รับคำสั่งเกมอีก */
let warping = false;

const uiPortalHint = el<HTMLDivElement>("portalhint");
const uiWarpVeil = el<HTMLDivElement>("warpveil");
const uiWarpTxt = el<HTMLDivElement>("warptxt");

/** ระยะที่เริ่มโชว์ป้ายบอกปลายทาง ให้รู้ก่อนเดินเข้าว่าประตูนี้ไปไหน */
const PORTAL_HINT_DIST = 10;

/**
 * เช็คทุกเฟรมว่ายืนในประตูหรือใกล้ประตูไหม
 *
 * client แค่ "ขอ" ผ่านประตู server ตรวจตำแหน่งซ้ำแล้วเป็นคนตัดสิน (กฎข้อ 1)
 * ส่งครั้งเดียวต่อการเข้าวง ออกจากวงแล้วค่อยส่งใหม่ได้
 */
function updatePortals(): void {
  const self: any = room?.state?.players?.get(room.sessionId);
  const zone = ALL_ZONES[String((room?.state as any)?.zone ?? "")];
  if (!self || !zone || warping) {
    uiPortalHint.classList.remove("on");
    return;
  }

  let near: PortalDef | undefined;
  let nearD = PORTAL_HINT_DIST;
  for (const p of zone.portals ?? []) {
    const d = Math.hypot(p.x - myX, p.z - myZ);
    if (d < nearD) {
      nearD = d;
      near = p;
    }
  }
  if (near) {
    const dest = ALL_ZONES[near.to];
    const lv = dest ? ` (Lv ${dest.levelRange[0]}–${dest.levelRange[1]})` : "";
    const prefix = dest?.isDungeon ? "🏰 <b>ดันเจี้ยนปาร์ตี้:</b> " : "ประตูสู่ ";
    const text = `${prefix}<b>${esc(dest?.name ?? near.to)}</b>${lv} · เดินเข้าวงแสงเพื่อเดินทาง`;
    if (uiPortalHint.innerHTML !== text) uiPortalHint.innerHTML = text;
    uiPortalHint.classList.add("on");
  } else {
    uiPortalHint.classList.remove("on");
  }

  const inside = !self.dead ? portalAt(zone, myX, myZ) : undefined;
  if (!inside) {
    portalSent = "";
    return;
  }
  if (portalSent === inside.id) return;
  portalSent = inside.id;
  room?.send("portal", { id: inside.id });
}

const WARP_KEY = "wickhaven.warp";

interface PendingWarp {
  chosen: Chosen;
  token: string;
  /** เวลาที่ออกตั๋ว ใช้ทิ้งของค้างที่เก่าเกินไป */
  at: number;
}

/**
 * server ส่งตั๋วย้ายโซนมา — เก็บไว้แล้วรีโหลดหน้า
 *
 * **รีโหลดทั้งหน้า ไม่ใช่ต่อห้องใหม่ในหน้าเดิม** ด้วยเหตุผลเดียวกับปุ่มออกจากเกม
 * ฉาก Babylon ผูกกับห้องเก่าอยู่ทั้งก้อน (ผู้เล่น มอน ของตกแต่งของโซนเก่า)
 * ล้างทีละส่วนให้ครบเสี่ยงหลุดมากกว่ารีโหลด ซึ่งใช้เวลาแค่ไม่กี่วินาที
 * เก็บใน sessionStorage (ของแท็บนี้) ไม่ใช่ localStorage ไม่งั้นแท็บอื่นหยิบไปใช้
 */
function beginWarp(zone: string, ticket: string, name: string, partyId?: string): void {
  if (!zone || !ticket || !myChosen) return;
  warping = true;
  uiPortalHint.classList.remove("on");
  uiWarpTxt.textContent = `กำลังเดินทางไป ${name || zone}…`;
  uiWarpVeil.classList.add("on");
  try {
    const pending: PendingWarp = {
      chosen: { ...myChosen, zone, handoff: ticket, ...(partyId ? { partyId } : {}) },
      token: loadToken(),
      at: Date.now(),
    };
    sessionStorage.setItem(WARP_KEY, JSON.stringify(pending));
  } catch {
    // เก็บไม่ได้ (โหมดส่วนตัว) ก็ยังรีโหลดได้ ตัวละครที่บันทึกไว้อยู่ปลายทางแล้ว
    // จะเข้าหน้าเลือกตัวละครแทนที่จะเข้าห้องปลายทางทันที
  }
  // ย้ายโซนแบบไม่รีโหลดหน้า เพื่อไม่ให้โหมดเต็มจอหลุด (ต้องกดใหม่ทุกครั้งที่เข้าประตู)
  // ถ้าเข้าห้องปลายทางไม่สำเร็จ ค่อยถอยไปรีโหลดหน้า ซึ่งตั๋วที่เก็บไว้ใน sessionStorage ใช้ต่อได้
  window.setTimeout(() => {
    const pending = takePendingWarp();
    if (!pending) {
      location.reload();
      return;
    }
    void softWarp(pending);
  }, 150);
}

/** ล้างของที่ผูกกับห้องเก่า แล้วต่อห้องปลายทางในหน้าเดิม */
async function softWarp(pending: PendingWarp): Promise<void> {
  try {
    try { room?.leave(); } catch {}
    room = undefined;
    hasSelf = false;

    for (const av of players.values()) {
      av.pet?.root.dispose(false, false);
      av.mesh.dispose();
    }
    players.clear();
    for (const av of monsters.values()) av.mesh.dispose();
    monsters.clear();
    for (const id of [...groundDrops.keys()]) removeGroundDrop(id);

    selectedId = "";
    autoAttack = false;
    pendingNpcId = "";
    portalSent = "";
    try { cancelPending(); } catch {}
    try { clearTarget(); } catch {}
    try { portrait?.dispose(); } catch {}
    portrait = undefined;
    // ล้างเป้าเดินและการเดินค้าง ไม่งั้นตัวละครวิ่งไปจุดของโซนเก่า
    joyKx = 0;
    joyKz = 0;
    heldKeys.clear();

    await connect(pending.chosen, pending.token);
    warping = false;
    uiWarpVeil.classList.remove("on");
  } catch (err) {
    console.warn("ย้ายโซนแบบไม่รีโหลดไม่สำเร็จ ถอยไปรีโหลดหน้า", err);
    try {
      sessionStorage.setItem(WARP_KEY, JSON.stringify({ ...pending, at: Date.now() }));
    } catch {}
    location.reload();
  }
}

/** อ่านแล้วลบทิ้งทันที ตั๋วใช้ได้ครั้งเดียว รีโหลดซ้ำต้องไม่ใช้ของเดิม */
function takePendingWarp(): PendingWarp | undefined {
  try {
    const raw = sessionStorage.getItem(WARP_KEY);
    sessionStorage.removeItem(WARP_KEY);
    if (!raw) return undefined;
    const p = JSON.parse(raw) as PendingWarp;
    // ตั๋วฝั่ง server หมดอายุใน 60 วิ เก่ากว่านั้นไม่ต้องลอง
    if (!p?.chosen?.handoff || Date.now() - Number(p.at ?? 0) > 55_000) return undefined;
    return p;
  } catch {
    return undefined;
  }
}

/* ---------- ปาร์ตี้ ---------- */

/**
 * ผู้เล่นทุกคนในห้องในรูปที่ party-view ใช้
 * อ่านจาก state ที่ server sync มาเท่านั้น ไม่ได้คิดเองว่าใครอยู่ปาร์ตี้ไหน
 */
function partyPlayers(): PartyPlayer[] {
  const out: PartyPlayer[] = [];
  const list = room?.state?.players;
  if (!list) return out;
  for (const [id, p] of list as Iterable<[string, any]>) {
    out.push({
      id,
      name: String(p.name ?? ""),
      cls: String(p.cls ?? ""),
      baseLevel: Number(p.baseLevel ?? 1),
      hp: Number(p.hp ?? 0),
      maxHp: Number(p.maxHp ?? 0),
      dead: !!p.dead,
      partyId: String(p.partyId ?? ""),
      partyLeader: !!p.partyLeader,
    });
  }
  return out;
}

const uiPartyFrame = el<HTMLDivElement>("partyframe");
const uiPartyInv = el<HTMLDivElement>("partyinv");
const uiPartyInvTxt = el<HTMLDivElement>("partyinvtxt");

/** ลายเซ็นของแถบเพื่อน วาด DOM ใหม่เฉพาะตอนเปลี่ยน ไม่ใช่ทุกเฟรม */
let partyFrameSig = "";

/**
 * แถบเลือดเพื่อนร่วมปาร์ตี้ใต้แถวบัฟ
 *
 * **ไม่มีปุ่ม** เป็นแถบอ่านอย่างเดียว HP เปลี่ยนบ่อยมากระหว่างต่อสู้
 * ถ้ามีปุ่มอยู่ในก้อนที่วาดใหม่ตลอด กดแล้วจะไม่ติด (ปุ่มถูกแทนระหว่างกด)
 * การจัดการ (เตะ ออก ชวน) จึงอยู่ในหน้าต่างปาร์ตี้ ซึ่งไม่ได้วาดใหม่ตาม HP
 */
let selectedPartyTargetId = "";
let currentPartyLootRule: "free" | "round_robin" | "random" = "free";

function updatePartyFrame(): void {
  const selfId = room?.sessionId ?? "";
  const mates = partyMembers(partyPlayers(), selfId, false);
  const far = myPartyId() ? remoteMembers(partyRoster, currentZoneId()) : [];
  const mePlayer = (room?.state as any)?.players?.get(selfId);
  const isHealer = mePlayer?.cls === "mender" || mePlayer?.cls === "saint";

  const sig = mates
    .map((m) => `${m.id}:${m.name}:${m.baseLevel}:${Math.ceil(m.hp)}:${m.maxHp}:${m.dead}:${m.partyLeader}:${selectedPartyTargetId === m.id}`)
    .join("|") + "#" + far.map((m) => `${m.key}:${m.zone}:${m.away}:${m.lv}`).join("|") + `#target:${selectedPartyTargetId}`;
  if (sig === partyFrameSig) return;
  partyFrameSig = sig;

  uiPartyFrame.innerHTML = mates.map((m) => {
    const cls = CLASSES[m.cls];
    const isSelected = selectedPartyTargetId === m.id;
    const resBtn = (m.dead && isHealer)
      ? `<div><button type="button" class="btn-resurrect" data-res="${m.id}">✨ ชุบชีวิต</button></div>`
      : "";
    return `<div class="pm${m.dead ? " dead" : ""}${isSelected ? " selected" : ""}" data-id="${m.id}">`
      + `<div class="pn"><b>${m.partyLeader ? `<span class="crown">👑</span>` : ""}${esc(m.name)}</b>`
      + `<span class="dim">${esc(cls?.name ?? m.cls)} Lv ${m.baseLevel}</span></div>`
      + `<div class="pb"><i style="width:${(hpRatio(m) * 100).toFixed(1)}%"></i></div>`
      + resBtn
      + `</div>`;
  }).join("") + far.map((m) => {
    // อยู่ต่างโซน: ไม่มี HP สด บอกแค่ว่าอยู่ไหน
    const cls = CLASSES[m.cls];
    return `<div class="pm far">`
      + `<div class="pn"><b>${m.leader ? `<span class="crown">👑</span>` : ""}${esc(m.name)}</b>`
      + `<span class="dim">${esc(cls?.name ?? m.cls)} Lv ${m.lv}</span></div>`
      + `<div class="pz">${esc(whereText(m))}</div>`
      + `</div>`;
  }).join("");
}

uiPartyFrame.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-res]");
  if (btn) {
    e.stopPropagation();
    const targetId = btn.dataset.res;
    if (targetId) room?.send("partyResurrect", { targetId });
    return;
  }

  const card = (e.target as HTMLElement).closest<HTMLElement>(".pm[data-id]");
  if (card) {
    const id = card.dataset.id ?? "";
    selectedPartyTargetId = selectedPartyTargetId === id ? "" : id;
    partyFrameSig = "";
    updatePartyFrame();
  }
});

/** รายชื่อปาร์ตี้ล่าสุดจาก server รวมเพื่อนที่อยู่ต่างโซน */
let partyRoster: RosterMember[] = [];

function currentZoneId(): string {
  return String((room?.state as any)?.zone ?? "meadow");
}

function myPartyId(): string {
  return String((room?.state as any)?.players?.get(room?.sessionId ?? "")?.partyId ?? "");
}

function whereText(m: RosterMember): string {
  const z = ALL_ZONES[m.zone]?.name ?? m.zone;
  return m.away ? `กำลังเดินทางไป${z}` : `อยู่ที่${z}`;
}

/** ผลการทำรายการปาร์ตี้ล่าสุด แสดงในหน้าต่างเอง (เหตุผลเดียวกับ shopNote) */
let partyNote = "";
/** ลายเซ็นของหน้าต่างปาร์ตี้ **ไม่รวม HP** ดูเหตุผลที่ updatePartyFrame */
let partyWinSig = "";
let partyInvTimer = 0;

function showPartyInvite(fromName: string): void {
  uiPartyInvTxt.innerHTML = `<b>${esc(fromName)}</b> ชวนคุณเข้าปาร์ตี้`;
  uiPartyInv.classList.add("on");
  window.clearTimeout(partyInvTimer);
  // คำชวนฝั่ง server หมดอายุใน 30 วิ (INVITE_TTL_MS) ซ่อนก่อนนิดหน่อย
  // จะได้ไม่กดรับคำชวนที่หมดอายุไปแล้วแล้วได้ข้อความ error
  partyInvTimer = window.setTimeout(() => uiPartyInv.classList.remove("on"), 28_000);
}

function answerPartyInvite(accept: boolean): void {
  window.clearTimeout(partyInvTimer);
  uiPartyInv.classList.remove("on");
  room?.send("partyReply", { accept });
}
el("partyyes").addEventListener("click", () => answerPartyInvite(true));
el("partyno").addEventListener("click", () => answerPartyInvite(false));

/* ---------- ระบบเทรดตรงและร้านค้าตั้งแผง ---------- */
const uiTradeInv = el<HTMLDivElement>("tradeinv");
const uiTradeInvTxt = el<HTMLDivElement>("tradeinvtxt");
let tradeInvTimer = 0;

function showTradeInvite(fromId: string, fromName: string): void {
  uiTradeInvTxt.innerHTML = `<b>${esc(fromName)}</b> ชวนคุณแลกเปลี่ยนไอเทม`;
  uiTradeInv.classList.add("on");
  window.clearTimeout(tradeInvTimer);
  tradeInvTimer = window.setTimeout(() => uiTradeInv.classList.remove("on"), 28_000);
}

function answerTradeInvite(accept: boolean): void {
  window.clearTimeout(tradeInvTimer);
  uiTradeInv.classList.remove("on");
  room?.send("tradeReply", { accept });
}
el("tradeyes").addEventListener("click", () => answerTradeInvite(true));
el("tradeno").addEventListener("click", () => answerTradeInvite(false));

const uiPlayerAct = el<HTMLDivElement>("playeraction");
const uiActPlayerName = el<HTMLDivElement>("actPlayerName");
let targetPlayerActId = "";

function showPlayerActionModal(playerId: string, playerName: string, screenX: number, screenY: number): void {
  targetPlayerActId = playerId;
  uiActPlayerName.textContent = playerName;
  uiPlayerAct.style.left = `${Math.min(window.innerWidth - 150, Math.max(10, screenX))}px`;
  uiPlayerAct.style.top = `${Math.min(window.innerHeight - 100, Math.max(10, screenY))}px`;
  uiPlayerAct.classList.add("on");
}

function hidePlayerActionModal(): void {
  uiPlayerAct.classList.remove("on");
  targetPlayerActId = "";
}

el("actTrade").addEventListener("click", () => {
  if (targetPlayerActId) {
    room?.send("tradeInvite", { targetId: targetPlayerActId });
  }
  hidePlayerActionModal();
});

el("actParty").addEventListener("click", () => {
  if (targetPlayerActId) {
    room?.send("partyInvite", { id: targetPlayerActId });
  }
  hidePlayerActionModal();
});

el("actClose").addEventListener("click", hidePlayerActionModal);

// หน้าต่างการแลกเปลี่ยน
const winTrade = createWindow(uiWins, {
  title: "การแลกเปลี่ยน (Direct Trade)",
  x: 260,
  y: 120,
  onClose: () => {
    if (tradeView.isOpen()) room?.send("tradeCancel");
  },
});

const tradeView = new TradeView(winTrade, {
  onSetGold: (gold) => room?.send("tradeSetGold", { gold }),
  onOfferItem: (item, count) => room?.send("tradeOfferItem", { item, count }),
  onLock: (locked) => room?.send("tradeLock", { locked }),
  onConfirm: () => room?.send("tradeConfirm"),
  onCancel: () => room?.send("tradeCancel"),
});

// หน้าต่างร้านค้าตั้งแผง
const winVending = createWindow(uiWins, {
  title: "ร้านค้าตั้งแผง (Market Stall)",
  x: 280,
  y: 140,
});

const winVendingBrowse = createWindow(uiWins, {
  title: "เลือกซื้อสินค้า",
  x: 320,
  y: 160,
});

let myStall: VendingStall | null = null;

const vendingSetupView = new VendingSetupView(winVending, {
  onOpenStall: (title, items) => room?.send("vendingOpen", { title, items }),
  onCloseStall: () => room?.send("vendingClose"),
});

const vendingBrowseView = new VendingBrowseView(winVendingBrowse, {
  onBuyItem: (ownerId, itemId, count) => room?.send("vendingBuy", { ownerId, itemId, count }),
  onClose: () => winVendingBrowse.close(),
});

function openVendingSetup(): void {
  const self: any = room?.state?.players?.get(room?.sessionId ?? "");
  if (!self) return;
  const inv: Record<string, number> = {};
  for (const [id, count] of (self.items as any)?.entries?.() ?? []) {
    if (count > 0) inv[id] = count;
  }
  vendingSetupView.open(myStall ?? undefined, inv);
}

/** ส่งคำสั่งปาร์ตี้ที่แปลงมาจากแชท */
function runPartyCommand(cmd: PartyCommand): void {
  switch (cmd.type) {
    case "help":
      addChat(`<span class="sys">${esc(PARTY_HELP)}</span>`);
      return;
    case "partyLeave":
      room?.send("partyLeave", {});
      return;
    case "partyInvite":
      room?.send("partyInvite", { name: cmd.name });
      return;
    case "partyKick":
      room?.send("partyKick", { name: cmd.name });
      return;
    case "partyChat":
      room?.send("partyChat", { text: cmd.text });
      return;
  }
}

const winParty = createWindow(uiWins, {
  title: "ปาร์ตี้",
  x: 260,
  y: 140,
  onOpen: () => {
    partyWinSig = "";
    drawPartyWindow();
  },
});

function drawPartyWindow(): void {
  const selfId = room?.sessionId ?? "";
  const all = partyPlayers();
  const me = all.find((p) => p.id === selfId);
  if (!me) {
    winParty.body.textContent = "กำลังเชื่อมต่อ…";
    return;
  }

  const members = partyMembers(all, selfId, true);
  const far = me.partyId ? remoteMembers(partyRoster, currentZoneId()) : [];
  const leader = me.partyLeader;
  const total = members.length + far.length;
  // ชวนได้เมื่อยังไม่มีปาร์ตี้ (จะตั้งใหม่ตอนอีกฝ่ายรับ) หรือเป็นหัวหน้าและยังไม่เต็ม
  const canInvite = !me.partyId || (leader && total < 6);
  const free = canInvite ? invitable(all, selfId) : [];

  let html = "";
  if (me.partyId) {
    html += `<div class="drow"><span class="pts">สมาชิก</span>`
      + `<span class="dim">${total}/6 คน</span></div>`;
    html += members.map((m) => {
      const cls = CLASSES[m.cls];
      const kick = leader && m.id !== selfId
        ? `<button type="button" data-kick="${m.id}">เชิญออก</button>`
        : "";
      return `<div class="prow">`
        + `<span class="pname">${m.partyLeader ? "👑 " : ""}${esc(m.name)}`
        + `${m.id === selfId ? ` <span class="dim">(คุณ)</span>` : ""}</span>`
        + `<span class="pinfo">${esc(cls?.name ?? m.cls)} Lv ${m.baseLevel}</span>`
        + kick
        + `</div>`;
    }).join("");
    html += far.map((m) => {
      const cls = CLASSES[m.cls];
      const kick = leader ? `<button type="button" data-kick="${esc(m.key)}">เชิญออก</button>` : "";
      return `<div class="prow">`
        + `<span class="pname">${m.leader ? "👑 " : ""}${esc(m.name)}`
        + ` <span class="dim">(${esc(whereText(m))})</span></span>`
        + `<span class="pinfo">${esc(cls?.name ?? m.cls)} Lv ${m.lv}</span>`
        + kick
        + `</div>`;
    }).join("");
    html += `<div class="pact"><button type="button" data-leave="1">ออกจากปาร์ตี้</button></div>`;
    html += `<div class="d">เพื่อนที่อยู่ใกล้ได้ EXP ด้วยแม้ไม่ได้ตี · โบนัส +5% ต่อคน`
      + ` · เลเวลห่างเกิน 15 ไม่แบ่ง · ดรอปเป็นของคนที่ตีเท่านั้น</div>`;
  } else {
    html += `<div class="d">ยังไม่ได้อยู่ในปาร์ตี้ ชวนผู้เล่นด้านล่างเพื่อตั้งปาร์ตี้ใหม่</div>`;
  }

  if (canInvite) {
    html += `<div class="wsep"></div>`
      + `<div class="drow"><span class="pts">ชวนผู้เล่นในแผนที่</span></div>`
      + (free.length === 0
        ? `<div class="d">ไม่มีผู้เล่นที่ว่างอยู่ในแผนที่</div>`
        : free.map((p) => {
          const cls = CLASSES[p.cls];
          return `<div class="prow">`
            + `<span class="pname">${esc(p.name)}</span>`
            + `<span class="pinfo">${esc(cls?.name ?? p.cls)} Lv ${p.baseLevel}</span>`
            + `<button type="button" data-invite="${p.id}">ชวน</button>`
            + `</div>`;
        }).join(""));
  } else if (me.partyId && !leader) {
    html += `<div class="wsep"></div><div class="d">เฉพาะหัวหน้าปาร์ตี้ที่ชวนสมาชิกใหม่ได้</div>`;
  }

  if (me.partyId) {
    const ruleLabels: Record<string, string> = {
      free: "ใครตีได้คนนั้นทอย (Free-for-all)",
      round_robin: "เวียนสลับ (Round Robin)",
      random: "สุ่มสมาชิกในระยะ (Random)",
    };

    html += `<div class="wsep"></div><div class="drow"><span class="pts">กฎการแบ่งไอเทมดรอป</span></div>`;
    if (leader) {
      html += `<div style="display:flex; gap:6px; margin: 4px 0 8px;">`
        + `<button type="button" data-loot="free" ${currentPartyLootRule === "free" ? "style='border-color:#ffd700; color:#ffd700;'" : ""}>ใครตีได้</button>`
        + `<button type="button" data-loot="round_robin" ${currentPartyLootRule === "round_robin" ? "style='border-color:#ffd700; color:#ffd700;'" : ""}>เวียนสลับ</button>`
        + `<button type="button" data-loot="random" ${currentPartyLootRule === "random" ? "style='border-color:#ffd700; color:#ffd700;'" : ""}>สุ่ม</button>`
        + `</div>`;
    } else {
      html += `<div class="d">กฎปัจจุบัน: <b>${ruleLabels[currentPartyLootRule] ?? currentPartyLootRule}</b></div>`;
    }
  }

  html += `<div class="wsep"></div>`
    + (partyNote ? `<div class="d">${esc(partyNote)}</div>` : "")
    + `<div class="d">${esc(PARTY_HELP)}</div>`;

  winParty.body.innerHTML = html;
}

// ปุ่มในหน้าต่างปาร์ตี้ — ตัวจับเดียวที่ตัวหน้าต่าง (เหตุผลเดียวกับหน้าต่างร้าน)
winParty.body.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement | null)?.closest?.("button");
  if (!btn || btn.disabled) return;
  const invite = btn.dataset["invite"];
  const kick = btn.dataset["kick"];
  const loot = btn.dataset["loot"];
  if (invite) room?.send("partyInvite", { id: invite });
  else if (kick) room?.send("partyKick", { id: kick });
  else if (btn.dataset["leave"]) room?.send("partyLeave", {});
  else if (loot) room?.send("partySetLootRule", { rule: loot });
});

// ---- แผนที่โลก: ซ้าย = แผนที่โซนนี้ + รายละเอียดโซนที่เลือก · ขวา = เส้นทางทั้งโลกตามเลเวล (คลิกเลือกได้) ----
const winWorldMap = createWindow(uiWins, {
  title: "🗺 แผนที่โลก",
  x: 120,
  y: 70,
  onOpen: () => { mapSelected = currentZoneId(); drawWorldMap(); },
});

/** ไอคอนธาตุในแผนที่โลก */
const ELEMENT_ICON: Record<string, string> = {
  neutral: "⚪", earth: "🪨", water: "💧", fire: "🔥", wind: "🌪️", poison: "☠️", holy: "✨", dark: "🌑", ice: "❄️",
};

/** โซนที่เลือกดูในแผนที่โลก (ค่าเริ่ม = โซนที่อยู่) และโซนที่เมาส์ชี้ */
let mapSelected = "";
let mapHover = "";
let mapBoxes: ZoneBox[] = [];

function drawWorldMap(): void {
  const here = currentZoneId();
  winWorldMap.body.innerHTML =
    `<div class="wmap">`
    + `<div class="wmap-left">`
    + `<div class="pts">📍 ${esc(ALL_ZONES[here]?.name ?? here)}</div>`
    + `<canvas data-map="zone" data-size="300"></canvas>`
    + `<div class="d dim wmap-legend">● เหลือง = คุณ · ฟ้า = ประตู · วงสี = มอน (สีตามธาตุ) · 👑 บอส · 🏮 ตะเกียง · เหนืออยู่บน</div>`
    + `<div class="wmap-detail"></div>`
    + `</div>`
    + `<div class="wmap-right">`
    + `<div class="pts">🌍 โลกเอลาเรีย <span class="d dim" style="font-size:11px">— คลิกโซนเพื่อดูรายละเอียด · กรอบเขียว = เหมาะกับเลเวลคุณ · ⚔ ดันเจี้ยนปาร์ตี้</span></div>`
    + `<div class="wmap-track"><canvas data-map="world"></canvas></div>`
    + `</div></div>`;
  drawZoneDetail();
  redrawWorldCanvases();
}

/** การ์ดรายละเอียดของโซนที่เลือก (วาดใหม่เฉพาะตอนเปลี่ยนโซนที่เลือก — มีลิงก์ให้กด) */
function drawZoneDetail(): void {
  const box = winWorldMap.body.querySelector<HTMLElement>(".wmap-detail");
  const z = ALL_ZONES[mapSelected] ?? ALL_ZONES[currentZoneId()];
  if (!box || !z) return;
  const lv = selfPlayer()?.baseLevel ?? 1;
  const [lo, hi] = z.levelRange;
  const tag = z.town ? "🏘 เมือง" : z.isDungeon ? "⚔ ดันเจี้ยนปาร์ตี้" : "สนาม";
  const fit = !z.town && lv >= lo && lv <= hi;
  const lit = parseWaypoints(selfPlayer()?.waypoints ?? "").includes(z.id);
  const links = [...new Set((z.portals ?? []).map((p) => p.to))]
    .filter((id) => ALL_ZONES[id])
    .map((id) => `<a href="#" data-zone="${esc(id)}">${esc(ALL_ZONES[id]!.name)}</a>`).join(" · ");
  const mons = [...new Set(z.spawns.map((sp) => sp.monster))]
    .map((id) => MONSTERS[id]).filter((md) => !!md)
    .sort((a, b) => Number(!!a!.boss) - Number(!!b!.boss) || a!.lv - b!.lv)
    .map((md) => `<div class="wmap-mon${md!.boss ? " boss" : ""}">${md!.boss ? "👑" : ""}${ELEMENT_ICON[md!.element] ?? ""} ${esc(md!.name)} <span class="d">Lv ${md!.lv} · ${SIZE_NAMES[sizeOf(md!)]}</span></div>`)
    .join("");
  box.innerHTML = `
    <div class="wmap-title"><b>${esc(z.name)}</b> <span class="d">${z.town ? "" : `Lv ${lo}–${hi} · `}${tag}</span></div>
    ${z.id === currentZoneId() ? `<div style="color:#f5c542;font-size:12px">● คุณอยู่ที่นี่</div>` : ""}
    ${fit ? `<div style="color:#7ddc7d;font-size:12px">✓ เหมาะกับเลเวลคุณ</div>` : ""}
    ${hasLantern(z) && !z.town ? `<div class="d" style="font-size:12px">🏮 ตะเกียงวาร์ป: ${lit ? "จุดแล้ว" : "ยังไม่ได้จุด"}</div>` : ""}
    ${links ? `<div class="d" style="font-size:12px;margin-top:4px">ทางไป: ${links}</div>` : ""}
    ${mons ? `<div class="wmap-mons">${mons}</div>` : ""}`;
}

/** ดาเมจที่เราตีหุ่นฟาง [เวลา, ดาเมจ] — ใช้วัด DPS ย้อนหลัง 10 วินาที */
const dummyHits: Array<[number, number]> = [];
function dummyDps(): string {
  const now = performance.now();
  while (dummyHits.length && now - dummyHits[0]![0] > 10000) dummyHits.shift();
  if (!dummyHits.length) return "ตีเพื่อวัด DPS";
  const span = Math.max(1, (now - dummyHits[0]![0]) / 1000);
  const sum = dummyHits.reduce((a, h) => a + h[1], 0);
  return `DPS ${Math.round(sum / Math.min(10, Math.max(span, 1)))} (10 วิ) · ${dummyHits.length} ครั้ง`;
}

/** อาวุธที่ถือตอนนี้ได้เปรียบ/เสียเปรียบกับขนาดนี้ไหม (ตัวคูณจาก shared/sizes.ts) */
function sizeHint(def: MonsterDef): string {
  const me = selfPlayer();
  if (!me) return "";
  const held = heldWeapons(me.cls, me.equipped, ITEMS);
  const cls = CLASSES[me.cls];
  const k = sizeMul(held.weapon, sizeOf(def), cls?.autoKind === "magic");
  return k > 1.001 ? ` (อาวุธคุณ +${Math.round((k - 1) * 100)}%)` : k < 0.999 ? ` (อาวุธคุณ −${Math.round((1 - k) * 100)}%)` : "";
}

/** วาดสองแผนที่ใหม่ (จุดของเราขยับ) — เรียกจากตัวจับเวลา ไม่แตะการ์ดรายละเอียด (มีลิงก์) */
function redrawWorldCanvases(): void {
  const zc = winWorldMap.body.querySelector<HTMLCanvasElement>("canvas[data-map=zone]");
  const wc = winWorldMap.body.querySelector<HTMLCanvasElement>("canvas[data-map=world]");
  const here = currentZoneId();
  const zn = ALL_ZONES[here];
  const lit = parseWaypoints(selfPlayer()?.waypoints ?? "");
  if (zc) {
    const names: Record<string, string> = {};
    for (const p of zn?.portals ?? []) names[p.to] = ALL_ZONES[p.to]?.name ?? p.to;
    const lantern = zn && hasLantern(zn) && !zn.town && lit.includes(zn.id) ? lanternPos(zn) : undefined;
    drawZoneMap(zc, zn, hasSelf ? { x: myX, z: myZ } : undefined, { names, lantern });
  }
  if (wc) mapBoxes = drawWorldGraph(wc, ALL_ZONES, townZoneId(), here, selfPlayer()?.baseLevel ?? 1, lit, mapSelected, mapHover);
}
setInterval(() => { if (winWorldMap.isOpen()) redrawWorldCanvases(); }, 250);

/** หาโซนใต้เมาส์บนผังโลก */
function zoneAtMapEvent(e: MouseEvent): string {
  const c = e.target as HTMLCanvasElement;
  if (c.dataset?.["map"] !== "world") return "";
  const r = c.getBoundingClientRect();
  const x = e.clientX - r.left, y = e.clientY - r.top;
  return mapBoxes.find((b) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h)?.id ?? "";
}
winWorldMap.body.addEventListener("mousemove", (e) => {
  const id = zoneAtMapEvent(e);
  if (id === mapHover) return;
  mapHover = id;
  (e.target as HTMLElement).style.cursor = id ? "pointer" : "";
  redrawWorldCanvases();
});
winWorldMap.body.addEventListener("click", (e) => {
  const link = (e.target as HTMLElement).closest<HTMLElement>("[data-zone]");
  const id = link?.dataset["zone"] ?? zoneAtMapEvent(e);
  if (link) e.preventDefault();
  if (!id || !ALL_ZONES[id]) return;
  mapSelected = id;
  drawZoneDetail();
  redrawWorldCanvases();
});
// ซูมมินิแมพ: ปุ่ม +/− และล้อเมาส์ (ไม่ส่งต่อไปซูมกล้องในเกม)
el("mapZoomIn").addEventListener("click", (e) => { e.stopPropagation(); minimap?.zoom(1); });
el("mapZoomOut").addEventListener("click", (e) => { e.stopPropagation(); minimap?.zoom(-1); });
uiMinimap.style.pointerEvents = "auto";
uiMinimap.addEventListener("wheel", (e) => {
  e.preventDefault();
  e.stopPropagation();
  minimap?.zoom(e.deltaY < 0 ? 1 : -1);
}, { passive: false });
el("mapExpand").addEventListener("click", (e) => {
  e.stopPropagation();
  winWorldMap.toggle();
});

// ---- มอนวิ่งแข่งชิงโชค (shared/derby.ts · server/derby-hub.ts) ----
interface DerbyView {
  id: number;
  phase: "bet" | "race" | "rest";
  endsInMs: number;
  runners: { id: string; name: string }[];
  pools: number[];
  mine: number[];
  result?: { winner: number; finish: number[]; seed: number };
  /** performance.now() ตอนได้รับ — นับถอยหลังเองฝั่ง client */
  at: number;
}
let derby: DerbyView | undefined;
let derbyNote = "";
const DERBY_COLORS = ["#ef4444", "#3b82f6", "#22c55e", "#eab308", "#a855f7", "#f97316"];

const winDerby = createWindow(uiWins, {
  title: "🏁 มอนวิ่งแข่งชิงโชค",
  x: 260,
  y: 90,
  onOpen: () => { room?.send("derbyInfo"); drawDerbyWindow(); },
});

function derbyLeftMs(): number {
  return derby ? Math.max(0, derby.endsInMs - (performance.now() - derby.at)) : 0;
}

/** วาดส่วนที่มีปุ่ม (เฉพาะตอนได้สถานะใหม่ — ไม่วาดทุกเฟรม ปุ่มจะกดไม่ติด) */
function drawDerbyWindow(): void {
  const d = derby;
  if (!d) {
    winDerby.body.innerHTML = `<div class="d">กำลังโหลดสนามแข่ง…</div>`;
    return;
  }
  const lv = selfPlayer()?.baseLevel ?? 1;
  const mineTotal = d.mine.reduce((a, b) => a + b, 0);
  const rows = d.runners.map((r, i) => {
    const odds = derbyOdds(d.pools, i);
    const md = MONSTERS[r.id];
    const win = d.result && d.phase === "rest" && d.result.winner === i;
    return `<tr${win ? ` style="color:#f5c542;font-weight:700"` : ""}>
      <td><span style="color:${DERBY_COLORS[i]}">●</span> ${win ? "🏆 " : ""}${esc(r.name)} <span class="d">${md ? `Lv ${md.lv}` : ""}</span></td>
      <td class="d">${d.pools[i]!.toLocaleString()}</td>
      <td>${odds ? `×${odds.toFixed(2)}` : "—"}</td>
      <td>${d.mine[i] ? d.mine[i]!.toLocaleString() : ""}</td>
      <td>${d.phase === "bet" ? `<button type="button" class="btn" data-derby="${i}">แทง</button>` : ""}</td></tr>`;
  }).join("");
  winDerby.body.innerHTML = `<div style="width:min(440px,90vw);font-size:12px">
    <div style="display:flex;justify-content:space-between"><b>รอบที่ ${d.id}</b><span class="derby-clock"></span></div>
    <canvas class="derby-track" width="440" height="${d.runners.length * 26 + 16}" style="width:100%;border-radius:6px;background:#2f4a2a;margin:6px 0"></canvas>
    <table style="width:100%;border-collapse:collapse"><tr class="d"><th align="left">ตัววิ่ง</th><th>กองเงิน</th><th>จ่ายราว</th><th>ของคุณ</th><th></th></tr>${rows}</table>
    <div style="margin-top:6px">จำนวนที่แทง <input class="derby-amt" type="number" min="${DERBY.minBet}" step="50" value="${Math.max(DERBY.minBet, Number(localStorageGet("wickhaven.derbyAmt")) || DERBY.minBet)}" style="width:90px">
      <span class="d">ขั้นต่ำ ${DERBY.minBet} · เพดานรอบนี้ ${derbyMaxBet(lv).toLocaleString()} (แทงแล้ว ${mineTotal.toLocaleString()})</span></div>
    <div class="d" style="margin-top:4px">ระบบหัก ${Math.round(DERBY.cut * 100)}% จากกองรวม ที่เหลือแบ่งให้คนที่ทายถูกตามสัดส่วนที่แทง · ใช้โกลด์เท่านั้น</div>
    ${derbyNote ? `<div style="margin-top:4px;color:#f5c542">${esc(derbyNote)}</div>` : ""}
  </div>`;
  drawDerbyTrack();
}

/** อ่านค่า localStorage แบบไม่พังในโหมดส่วนตัว */
function localStorageGet(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}

/** วาดลู่วิ่งและนาฬิกา (ทุกเฟรมตอนเปิดหน้าต่าง ไม่แตะปุ่ม) */
function drawDerbyTrack(): void {
  const d = derby;
  const cv = winDerby.body.querySelector<HTMLCanvasElement>(".derby-track");
  const clock = winDerby.body.querySelector<HTMLElement>(".derby-clock");
  if (!d || !cv) return;
  const left = derbyLeftMs();
  if (clock) {
    const s = Math.ceil(left / 1000);
    clock.textContent = d.phase === "bet" ? `ปิดรับใน ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
      : d.phase === "race" ? "กำลังวิ่ง!" : `รอบใหม่ใน ${s} วิ`;
  }
  const ctx = cv.getContext("2d");
  if (!ctx) return;
  const W = cv.width, H = cv.height, x0 = 16, x1 = W - 30;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#fff";
  ctx.fillRect(x1, 4, 3, H - 8);
  const longest = d.result ? Math.max(...d.result.finish) : 1;
  const raceT = d.phase === "race" ? 1 - left / (DERBY.raceSec * 1000) : d.phase === "rest" ? 1 : 0;
  d.runners.forEach((r, i) => {
    const y = 14 + i * 26;
    ctx.strokeStyle = "rgba(255,255,255,.15)";
    ctx.beginPath(); ctx.moveTo(x0, y + 12); ctx.lineTo(x1, y + 12); ctx.stroke();
    const prog = d.result ? derbyProgress(d.result.seed, i, d.result.finish[i]!, longest, Math.min(1, raceT * (DERBY.raceSec / longest))) : 0;
    const x = x0 + (x1 - x0) * prog;
    ctx.fillStyle = DERBY_COLORS[i]!;
    ctx.beginPath(); ctx.arc(x, y + 2, 8, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.font = "11px sans-serif";
    ctx.fillText(r.name, Math.min(x + 12, x1 - 80), y + 6);
  });
}
setInterval(() => { if (winDerby.isOpen()) drawDerbyTrack(); }, 50);

winDerby.body.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>("[data-derby]");
  if (!b) return;
  const amt = Math.trunc(Number(winDerby.body.querySelector<HTMLInputElement>(".derby-amt")?.value ?? 0));
  try { localStorage.setItem("wickhaven.derbyAmt", String(amt)); } catch { /* โหมดส่วนตัว */ }
  derbyNote = "";
  room?.send("derbyBet", { runner: Number(b.dataset["derby"]), amount: amt });
});

// ---- หน้าต่างเควสและภารกิจ ----
const winQuest = createWindow(uiWins, {
  title: "📜 เควสและภารกิจ",
  x: 280,
  y: 120,
  onOpen: () => drawQuestWindow(),
});

/** เควสที่อาชีพเรามองเห็น (เควสเลื่อนขั้นของสายอื่นไม่โชว์) */
function myQuests(): readonly QuestDef[] {
  const cls = selfPlayer()?.cls ?? myClass;
  return questsForClass(cls, CLASSES[cls]?.baseClass);
}

function drawQuestWindow(): void {
  const p = selfPlayer();
  const lv = p?.baseLevel ?? 1;
  const all = myQuests();
  const getInvCount = (id: string) => p?.items.get(id) ?? 0;
  const list = filterQuests(all, questRecords, lv, activeQuestTab, getInvCount);

  let html = `<div class="qtab-bar">
    <button type="button" class="qtab-btn ${activeQuestTab === "active" ? "on" : ""}" data-qtab="active">กำลังทำ (${filterQuests(all, questRecords, lv, "active", getInvCount).length})</button>
    <button type="button" class="qtab-btn ${activeQuestTab === "available" ? "on" : ""}" data-qtab="available">รับได้ (${filterQuests(all, questRecords, lv, "available", getInvCount).length})</button>
    <button type="button" class="qtab-btn ${activeQuestTab === "completed" ? "on" : ""}" data-qtab="completed">สำเร็จแล้ว (${filterQuests(all, questRecords, lv, "completed", getInvCount).length})</button>
  </div>`;

  if (list.length === 0) {
    html += `<div class="d" style="text-align:center; padding: 20px 0;">ไม่มีเควสในหมวดนี้</div>`;
  } else {
    for (const item of list) {
      const q = item.def;
      const typeInfo = questTypeLabel(q.type);
      html += `<div class="qcard" data-qid="${q.id}">
        <div class="qcard-top">
          <span class="qcard-badge" style="background:${typeInfo.color}22; color:${typeInfo.color}; border:1px solid ${typeInfo.color}55;">
            ${typeInfo.icon} ${typeInfo.text} · Lv ${q.minLevel}
          </span>
          ${item.readyToClaim ? `<span class="qt-claim">✨ พร้อมส่งเควส!</span>` : ""}
        </div>
        <div class="qcard-title">${esc(q.title)}</div>
        <div class="qcard-desc">${esc(q.desc)}</div>
        <div class="qcard-objs">`;
      for (const obj of q.objectives) {
        const prog = formatObjectiveProgress(obj, item.record, getInvCount);
        html += `<div class="qt-obj ${prog.done ? "done" : ""}">${prog.done ? "✓ " : "• "}${esc(prog.text)}</div>`;
      }
      html += `</div>
        <div class="qcard-rewards">
          รางวัล: EXP ${q.rewards.exp} · Job ${q.rewards.jobExp} · ${q.rewards.gold} Gold`
          + (q.rewards.items?.map((it) => ` · ${ITEMS[it.id]?.name ?? it.id} ×${it.count}`).join("") ?? "")
        + `</div>
        <div class="qcard-act">`;
      if (activeQuestTab === "available") {
        html += `<button type="button" data-qaccept="${q.id}">รับภารกิจ</button>`;
      } else if (activeQuestTab === "active" && item.readyToClaim) {
        html += `<button type="button" data-qclaim="${q.id}">ส่งภารกิจ / รับรางวัล</button>`;
      }
      html += `</div></div>`;
    }
  }

  winQuest.body.innerHTML = html;
}

winQuest.body.addEventListener("click", (e) => {
  const target = e.target as HTMLElement | null;
  const tabBtn = target?.closest<HTMLButtonElement>("[data-qtab]");
  if (tabBtn) {
    activeQuestTab = (tabBtn.dataset["qtab"] as QuestTab) ?? "active";
    drawQuestWindow();
    return;
  }
  const acceptBtn = target?.closest<HTMLButtonElement>("[data-qaccept]");
  if (acceptBtn) {
    const qid = acceptBtn.dataset["qaccept"];
    if (qid) room?.send("questAccept", { questId: qid });
    return;
  }
  const claimBtn = target?.closest<HTMLButtonElement>("[data-qclaim]");
  if (claimBtn) {
    const qid = claimBtn.dataset["qclaim"];
    if (qid) room?.send("questClaim", { questId: qid });
    return;
  }
});

function updateQuestTracker(): void {
  const p = selfPlayer();
  const lv = p?.baseLevel ?? 1;
  const all = myQuests();
  const getInvCount = (id: string) => p?.items.get(id) ?? 0;
  const activeList = filterQuests(all, questRecords, lv, "active", getInvCount);

  if (activeList.length === 0) {
    const avail = filterQuests(all, questRecords, lv, "available", getInvCount);
    if (avail.length > 0) {
      uiQuestTracker.style.display = "flex";
      uiQuestTracker.innerHTML = `<div class="qt-head"><span>📜 เควส</span><span class="dim">ใหม่ (${avail.length})</span></div>
        <div class="qt-item"><span class="dim" style="font-size:9.5px;">มีเควสใหม่ ${avail.length} เควส<br>คลิกเพื่อเปิดดู</span></div>`;
    } else {
      uiQuestTracker.style.display = "none";
    }
    return;
  }

  uiQuestTracker.style.display = "flex";
  let html = `<div class="qt-head"><span>📜 เควสที่กำลังทำ</span><span class="dim">${activeList.length}</span></div>`;
  for (const item of activeList.slice(0, 3)) {
    const q = item.def;
    const typeInfo = questTypeLabel(q.type);
    html += `<div class="qt-item">
      <div class="qt-title">${typeInfo.icon} ${esc(q.title)}</div>`;
    for (const obj of q.objectives) {
      const prog = formatObjectiveProgress(obj, item.record, getInvCount);
      html += `<div class="qt-obj ${prog.done ? "done" : ""}">${prog.done ? "✓ " : "• "}${esc(prog.text)}</div>`;
    }
    if (item.readyToClaim) {
      html += `<div class="qt-claim">✨ พร้อมส่งเควส! (คลิก)</div>`;
    }
    html += `</div>`;
  }

  uiQuestTracker.innerHTML = html;
}

uiQuestTracker.addEventListener("click", () => {
  winQuest.toggle();
  syncMenuState();
});

/* ---------- ระบบ NPC บทสนทนา และสัญลักษณ์เควส ---------- */

function getNpcQuestStatus(npcId: string, baseLevel: number): "ready" | "available" | "none" {
  for (const [qid, rec] of questRecords) {
    if (rec.status === "active") {
      const q = getQuest(qid);
      if (q) {
        const hasObj = q.objectives.some((o) => o.type === "talk_npc" && o.targetId === npcId && (rec.progress[o.id] ?? 0) < o.targetCount);
        if (hasObj) return "ready";
        const me: any = room?.state?.players?.get(room?.sessionId ?? "");
        if (isQuestReadyToClaim(q, rec.progress, (id) => me?.items?.get(id) ?? 0)) {
          return "ready";
        }
      }
    }
  }

  for (const q of myQuests()) {
    if (!questRecords.has(q.id) && q.minLevel <= baseLevel) {
      if (npcId === "elder_ignis" || npcId === "scout_kael") {
        return "available";
      }
    }
  }
  return "none";
}

const uiNpcDialog = el<HTMLDivElement>("npcdialog");
const uiNpcDialogName = el<HTMLDivElement>("npcDialogName");
const uiNpcDialogTitle = el<HTMLDivElement>("npcDialogTitle");
const uiNpcDialogMsg = el<HTMLDivElement>("npcDialogMsg");
const uiNpcDialogActions = el<HTMLDivElement>("npcDialogActions");
const uiNpcAvatarBadge = el<HTMLDivElement>("npcAvatarBadge");
el("npcDialogCloseX").addEventListener("click", () => {
  uiNpcDialog.classList.remove("on");
});

function openNpcDialog(data: {
  npcId: string;
  name: string;
  title: string;
  greeting: string;
  actions: string[];
}): void {
  uiNpcDialogName.textContent = data.name;
  uiNpcDialogTitle.textContent = data.title;
  uiNpcDialogMsg.textContent = data.greeting;

  const badges: Record<string, string> = {
    elder_ignis: "🔥",
    merchant_silas: "🛒",
    blacksmith_torin: "⚒️",
    vault_keeper: "📦",
    scout_kael: "🏹",
  };
  uiNpcAvatarBadge.textContent = badges[data.npcId] ?? "👤";

  uiNpcDialogActions.textContent = "";
  for (const act of data.actions) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npc-act-btn";

    if (act === "quest") {
      btn.classList.add("primary");
      btn.textContent = "📜 ภารกิจ (Quests)";
      btn.addEventListener("click", () => {
        uiNpcDialog.classList.remove("on");
        winQuest.open();
        drawQuestWindow();
      });
      uiNpcDialogActions.appendChild(btn);
    } else if (act === "shop") {
      btn.classList.add("primary");
      btn.textContent = "🛒 ร้านค้า (Shop)";
      btn.addEventListener("click", () => {
        uiNpcDialog.classList.remove("on");
        winShop.open();
        drawShopWindow();
      });
      uiNpcDialogActions.appendChild(btn);
    } else if (act === "refine") {
      btn.classList.add("primary");
      btn.textContent = "⚒️ ตีบวกอุปกรณ์ (Refine)";
      btn.addEventListener("click", () => {
        uiNpcDialog.classList.remove("on");
        winRefine.open();
        drawRefineWindow();
      });
      uiNpcDialogActions.appendChild(btn);
    } else if (act === "storage") {
      btn.classList.add("primary");
      btn.textContent = "📦 คลังสมบัติบัญชี (Storage)";
      btn.addEventListener("click", () => {
        uiNpcDialog.classList.remove("on");
        openStorageWindow();
      });
      uiNpcDialogActions.appendChild(btn);
    } else if (act === "derby") {
      btn.classList.add("primary");
      btn.textContent = "🏁 ดูสนามแข่ง / แทงตัววิ่ง";
      btn.addEventListener("click", () => {
        uiNpcDialog.classList.remove("on");
        winDerby.open();
      });
      uiNpcDialogActions.appendChild(btn);
    } else if (act === "blessing") {
      btn.classList.add("primary");
      const lv = selfPlayer()?.baseLevel ?? 1;
      const cost = blessingCost(lv);
      btn.textContent = `🏮 รับ${BLESSING_TH} (${cost ? `${cost.toLocaleString()} โกลด์` : `ฟรีถึง Lv ${BLESSING.freeUntil}`})`;
      btn.title = describeBlessing();
      btn.addEventListener("click", () => {
        room?.send("blessing");
        uiNpcDialog.classList.remove("on");
      });
      uiNpcDialogActions.appendChild(btn);
    } else if (act === "save_point") {
      btn.classList.add("primary");
      btn.textContent = "📍 บันทึกจุดเกิด (Save Point)";
      btn.addEventListener("click", () => {
        room?.send("savePoint");
        uiNpcDialog.classList.remove("on");
      });
      uiNpcDialogActions.appendChild(btn);
    }
  }

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "npc-act-btn";
  closeBtn.textContent = "❌ ลาก่อน";
  closeBtn.addEventListener("click", () => {
    uiNpcDialog.classList.remove("on");
  });
  uiNpcDialogActions.appendChild(closeBtn);

  uiNpcDialog.classList.add("on");
}

/* ---------- หน้าต่างคลังเก็บของส่วนกลางบัญชี (Account Storage) ---------- */
const winStorage = createWindow(uiWins, {
  title: "📦 คลังสมบัติบัญชี (Account Storage)",
  x: 260,
  y: 110,
  onOpen: () => drawStorageWindow(),
});

let storageItems: StorageItem[] = [];
let storageGold = 0;
let storageMaxSlots = STORAGE_MAX_SLOTS;

function openStorageWindow(): void {
  winStorage.open();
  room?.send("storageOpen");
  drawStorageWindow();
}

function drawStorageWindow(): void {
  const me: any = room?.state?.players?.get(room?.sessionId ?? "");
  if (!me) {
    winStorage.body.textContent = "กำลังเชื่อมต่อ…";
    return;
  }

  const bagList = Array.from((me.items as any)?.entries?.() ?? []) as [string, number][];
  const curWeight = bagWeight(bagList, ITEMS);
  const maxWeight = maxCarryWeight(me.str);
  const weightPct = ((curWeight / maxWeight) * 100).toFixed(0);

  let html = `<div class="storage-container">`;

  // --- ฝั่งซ้าย: กระเป๋าตัวละคร ---
  html += `<div class="storage-pane">
    <div class="storage-pane-title">
      <span>🎒 กระเป๋าสัมภาระ</span>
      <span>${curWeight}/${maxWeight} (${weightPct}%)</span>
    </div>
    <div style="font-size: 11px; color: var(--gold);">โกลด์ในตัว: <b>${(me.gold ?? 0).toLocaleString()}</b> G</div>
    <div class="storage-gold-row">
      <span>ฝาก:</span>
      <input type="number" id="stDepGoldInput" min="1" max="${me.gold ?? 0}" value="${Math.min(1000, me.gold ?? 0)}" />
      <button type="button" class="npc-act-btn" id="stDepGoldBtn" style="padding: 3px 8px; font-size: 11px;">ฝาก Gold</button>
    </div>
    <div style="font-size: 10px; opacity: .7; margin-top: 2px;">คลิกไอเทมเพื่อฝากเข้าคลัง:</div>
    <div class="storage-grid">`;

  const validBag = bagList.filter(([id, cnt]) => cnt > 0 && !ITEMS[id]?.kind?.includes?.("quest"));
  if (validBag.length === 0) {
    html += `<div style="grid-column: 1/-1; text-align: center; opacity: .5; padding: 20px 0; font-size: 11px;">ไม่มีไอเทมที่สามารถฝากได้</div>`;
  } else {
    for (const [id, count] of validBag) {
      const def = ITEMS[id];
      const icon = def?.icon ?? (def?.kind === "equip" ? "⚔️" : def?.kind === "heal" ? "🧪" : "📦");
      html += `<div class="storage-slot has-item" data-bag-item="${id}" title="${def?.name ?? id} (คลิกเพื่อฝาก)">
        <span>${icon}</span>
        <span class="storage-count">${count}</span>
      </div>`;
    }
  }

  html += `</div></div>`;

  // --- ฝั่งขวา: คลังสมบัติบัญชี ---
  html += `<div class="storage-pane">
    <div class="storage-pane-title">
      <span>📦 คลังสมบัติบัญชี</span>
      <span>${storageItems.length}/${storageMaxSlots} ช่อง</span>
    </div>
    <div style="font-size: 11px; color: var(--gold);">โกลด์ในคลัง: <b>${storageGold.toLocaleString()}</b> G</div>
    <div class="storage-gold-row">
      <span>ถอน:</span>
      <input type="number" id="stWithGoldInput" min="1" max="${storageGold}" value="${Math.min(1000, storageGold)}" />
      <button type="button" class="npc-act-btn" id="stWithGoldBtn" style="padding: 3px 8px; font-size: 11px;">ถอน Gold</button>
    </div>
    <div style="font-size: 10px; opacity: .7; margin-top: 2px;">คลิกไอเทมเพื่อเบิกใส่กระเป๋า:</div>
    <div class="storage-grid">`;

  if (storageItems.length === 0) {
    html += `<div style="grid-column: 1/-1; text-align: center; opacity: .5; padding: 20px 0; font-size: 11px;">คลังเก็บของว่างเปล่า</div>`;
  } else {
    for (const it of storageItems) {
      const def = ITEMS[it.itemId];
      const icon = def?.icon ?? (def?.kind === "equip" ? "⚔️" : def?.kind === "heal" ? "🧪" : "📦");
      html += `<div class="storage-slot has-item" data-storage-slot="${it.slot}" title="${def?.name ?? it.itemId} (คลิกเพื่อเบิก)">
        <span>${icon}</span>
        <span class="storage-count">${it.count}</span>
      </div>`;
    }
  }

  html += `</div></div></div>`;

  winStorage.body.innerHTML = html;

  const depBtn = winStorage.body.querySelector("#stDepGoldBtn");
  const depInp = winStorage.body.querySelector("#stDepGoldInput") as HTMLInputElement | null;
  depBtn?.addEventListener("click", () => {
    const val = Math.floor(Number(depInp?.value ?? 0));
    if (val > 0) room?.send("storageDepositGold", { amount: val });
  });

  const withBtn = winStorage.body.querySelector("#stWithGoldBtn");
  const withInp = winStorage.body.querySelector("#stWithGoldInput") as HTMLInputElement | null;
  withBtn?.addEventListener("click", () => {
    const val = Math.floor(Number(withInp?.value ?? 0));
    if (val > 0) room?.send("storageWithdrawGold", { amount: val });
  });

  for (const slotEl of winStorage.body.querySelectorAll("[data-bag-item]")) {
    slotEl.addEventListener("click", () => {
      const itemId = (slotEl as HTMLElement).dataset["bagItem"]!;
      room?.send("storageDeposit", { itemId, count: 1 });
    });
  }

  for (const slotEl of winStorage.body.querySelectorAll("[data-storage-slot]")) {
    slotEl.addEventListener("click", () => {
      const slot = Number((slotEl as HTMLElement).dataset["storageSlot"]);
      room?.send("storageWithdraw", { slot, count: 1 });
    });
  }
}

const winRefine = createWindow(uiWins, {
  title: "โรงตีเหล็ก",
  x: 500,
  y: 140,
  onOpen: () => drawRefineWindow(),
});

let selectedRefineSlot: EquipSlotId = "weapon";
let useProtectStone = false;
let refineNote = "";
let forgeWasNear = false;
let refineSig = "";

function nearForge(): boolean {
  return nearShop();
}

function drawRefineWindow(): void {
  const self: any = room?.state?.players?.get(room.sessionId);
  if (!self) {
    winRefine.body.textContent = "กำลังเชื่อมต่อ…";
    return;
  }

  const far = !nearForge();
  const reqStoneId = stoneForSlot(selectedRefineSlot) ?? REFINE_STONE_ID;
  const reqStoneDef = ITEMS[reqStoneId];
  const reqStoneCount = Number(self.items?.get?.(reqStoneId) ?? 0);
  const generalStoneCount = Number(self.items?.get?.(REFINE_STONE_ID) ?? 0);
  const hasSpecificStone = reqStoneCount >= 1;
  const hasAnyStone = hasSpecificStone || generalStoneCount >= 1;
  const protectStones = Number(self.items?.get?.(PROTECT_STONE_ID) ?? 0);

  // รายการช่องสวมใส่ที่ตีบวกได้
  const slotButtons = REFINEABLE_SLOTS.map((slotId) => {
    const slotDef = EQUIP_SLOTS.find((s) => s.id === slotId);
    const itemId = String((self.equipped as any)?.get?.(slotId) ?? "");
    const def = itemId ? ITEMS[itemId] : undefined;
    const refineLvl = Number((self.refines as any)?.get?.(slotId) ?? 0);
    const isSel = selectedRefineSlot === slotId;
    const badge = refineLvl > 0 ? `<span class="refine-badge">+${refineLvl}</span>` : "";
    return `<button type="button" class="rf-slot-btn${isSel ? " sel" : ""}${def ? "" : " empty"}" data-rf-slot="${slotId}">`
      + `<span>${def ? itemGlyph(def) : (SLOT_GLYPH[slotId] ?? "📦")}</span>`
      + `<span>${esc(slotDef?.label ?? slotId)}</span>`
      + badge
      + `</button>`;
  }).join("");

  const currentItemId = String((self.equipped as any)?.get?.(selectedRefineSlot) ?? "");
  const currentDef = currentItemId ? ITEMS[currentItemId] : undefined;
  const currentLvl = Number((self.refines as any)?.get?.(selectedRefineSlot) ?? 0);

  let detailsHtml = "";

  if (!currentDef) {
    detailsHtml = `<div class="d" style="text-align:center;padding:20px 0;">ยังไม่มีอุปกรณ์สวมใส่ในช่องนี้<br><span class="dim">เปิดหน้าต่างตัวละครเพื่อสวมอุปกรณ์ก่อน</span></div>`;
  } else if (currentLvl >= MAX_REFINE_LEVEL) {
    detailsHtml = `<div class="rf-main-card">`
      + `<div class="drow"><span class="pts">${itemGlyph(currentDef)} ${esc(currentDef.name)}</span>`
      + `<span class="rf-chance c100">+${MAX_REFINE_LEVEL} (สูงสุดแล้ว)</span></div>`
      + `<div class="d dim">อุปกรณ์ชิ้นนี้ได้รับการตีบวกจนถึงระดับสูงสุดแล้ว!</div>`
      + `</div>`;
  } else {
    const targetLvl = currentLvl + 1;
    const rule = getRefineRule(targetLvl);
    const cost = refineGoldCost(targetLvl);
    // บริการเฉพาะเมือง (shared/towns.ts) — server ตัดสินจริง ตรงนี้แค่บอกล่วงหน้า
    const townNo = refineTownDenial(ALL_ZONES[currentZoneId()] ?? { id: currentZoneId() }, selectedRefineSlot, targetLvl);
    const canAfford = !townNo && self.gold >= cost && hasAnyStone && (!useProtectStone || protectStones >= 1);
    const chancePct = Math.round(rule.chance * 100);
    const chanceCls = chancePct === 100 ? "c100" : chancePct >= 80 ? "c80" : chancePct >= 60 ? "c60" : chancePct >= 40 ? "c40" : chancePct >= 25 ? "c25" : "c10";

    const statPreviews: string[] = [];
    const b = currentDef.bonus;
    if (b?.weaponAtk) {
      const cur = b.weaponAtk + refineBonus(b.weaponAtk, currentLvl);
      const nxt = b.weaponAtk + refineBonus(b.weaponAtk, targetLvl);
      statPreviews.push(`ATK: ${cur} → <b style="color:var(--gold,#ffd700)">${nxt}</b> (+${nxt - cur})`);
    }
    if (b?.weaponMatk) {
      const cur = b.weaponMatk + refineBonus(b.weaponMatk, currentLvl);
      const nxt = b.weaponMatk + refineBonus(b.weaponMatk, targetLvl);
      statPreviews.push(`MATK: ${cur} → <b style="color:var(--gold,#ffd700)">${nxt}</b> (+${nxt - cur})`);
    }
    if (b?.flatDef) {
      const cur = b.flatDef + refineBonus(b.flatDef, currentLvl);
      const nxt = b.flatDef + refineBonus(b.flatDef, targetLvl);
      statPreviews.push(`DEF: ${cur} → <b style="color:var(--gold,#ffd700)">${nxt}</b> (+${nxt - cur})`);
    }
    if (b?.flatBlock) {
      const cur = b.flatBlock + refineBonus(b.flatBlock, currentLvl);
      const nxt = b.flatBlock + refineBonus(b.flatBlock, targetLvl);
      statPreviews.push(`BLOCK: +${cur}% → <b style="color:var(--gold,#ffd700)">+${nxt}%</b> (+${nxt - cur}%)`);
    }
    if (b?.flatCrit) {
      const cur = b.flatCrit + refineBonus(b.flatCrit, currentLvl);
      const nxt = b.flatCrit + refineBonus(b.flatCrit, targetLvl);
      statPreviews.push(`CRIT: +${cur}% → <b style="color:var(--gold,#ffd700)">+${nxt}%</b> (+${nxt - cur}%)`);
    }
    if (b?.stats) {
      const isStatOnly = !b.weaponAtk && !b.weaponMatk && !b.flatDef;
      const isStatRefined = selectedRefineSlot === "wings" || selectedRefineSlot === "face" || selectedRefineSlot === "ear1" || selectedRefineSlot === "ear2" || selectedRefineSlot === "special" || isStatOnly;
      if (isStatRefined) {
        for (const [k, val] of Object.entries(b.stats)) {
          if (val) {
            const cur = val + refineBonus(val, currentLvl);
            const nxt = val + refineBonus(val, targetLvl);
            statPreviews.push(`${k}: +${cur} → <b style="color:var(--gold,#ffd700)">+${nxt}</b> (+${nxt - cur})`);
          }
        }
      }
    }

    const penaltyText = rule.failPenalty === 0
      ? "ไม่ลดระดับ (เสียแค่วัตถุดิบ)"
      : `ลดลง ${rule.failPenalty} ระดับ (เหลือ +${Math.max(0, currentLvl - rule.failPenalty)})`;

    const stoneLabel = reqStoneDef ? reqStoneDef.name : "หินตีบวก";
    let stoneStatusText = "";
    if (hasSpecificStone) {
      stoneStatusText = `1 ก้อน (มี ${reqStoneCount})`;
    } else if (generalStoneCount >= 1) {
      stoneStatusText = `1 ก้อน (ใช้หินทั่วไป มี ${generalStoneCount})`;
    } else {
      stoneStatusText = `1 ก้อน (ไม่มีหิน)`;
    }

    detailsHtml = `<div class="rf-main-card">`
      + `<div class="drow"><span class="pts">${itemGlyph(currentDef)} ${esc(currentDef.name)}</span>`
      + `<span class="dim">${esc(currentDef.desc)}</span></div>`
      + `<div class="rf-prog"><span>+${currentLvl}</span> ➔ <span style="color:var(--gold,#ffd700)">+${targetLvl}</span></div>`
      + (statPreviews.length > 0 ? `<div class="d" style="text-align:center;margin-bottom:6px;">${statPreviews.join(" · ")}</div>` : "")
      + `<div class="drow"><span class="dim">โอกาสสำเร็จ:</span><span class="rf-chance ${chanceCls}">${chancePct}%</span></div>`
      + `<div class="drow"><span class="dim">หากล้มเหลว:</span><span style="color:#fab1a0">${penaltyText}</span></div>`
      + `</div>`
      + `<div class="drow"><span class="dim">ค่าธรรมเนียม:</span><span class="${self.gold < cost ? "c10" : "pts"}">${cost} โกลด์ (มี ${self.gold})</span></div>`
      + `<div class="drow"><span class="dim">หินที่ต้องใช้:</span><span class="${hasAnyStone ? "pts" : "c10"}">${itemGlyph(reqStoneDef ?? { glyph: "💎" } as any)} ${esc(stoneLabel)}: ${stoneStatusText}</span></div>`
      + (rule.failPenalty > 0
        ? `<div class="drow" style="margin-top:4px;"><label style="cursor:pointer;display:flex;align-items:center;gap:6px;">`
          + `<input type="checkbox" id="chkProtect" ${useProtectStone ? "checked" : ""} ${protectStones < 1 ? "disabled" : ""}>`
          + `<span>ใช้หินคุ้มครอง (มี ${protectStones})</span></label>`
          + `<span class="dim" style="font-size:11px;">ป้องกันระดับลด</span></div>`
        : "")
      + (townNo ? `<div class="d c10" style="margin-top:6px">🔥 ${esc(townNo)}</div>` : "")
      + `<div style="margin-top:10px;">`
      + `<button type="button" class="rf-action-btn" data-action="refine" ${far || !canAfford ? "disabled" : ""}>`
      + (far ? "อยู่ไกลโรงตีเหล็ก" : "ตีบวกอุปกรณ์")
      + `</button></div>`;
  }

  if (currentDef) detailsHtml += cardSection(self, currentDef, far) + optionSection(self, currentDef, far) + endowSection(self, currentDef, far);

  winRefine.body.innerHTML =
    `<div class="drow"><span class="pts">เลือกอุปกรณ์</span>`
    + `<span class="dim">${far ? "อยู่ไกลโรงตีเหล็ก" : "โรงตีเหล็กกลางเมือง"}</span></div>`
    + `<div class="rf-slots">${slotButtons}</div>`
    + `<div class="wsep"></div>`
    + detailsHtml
    + (refineNote ? `<div class="wsep"></div><div class="d" style="color:var(--gold,#ffd700)">${esc(refineNote)}</div>` : "")
    + `<div class="wsep"></div>`
    + `<div class="d dim" style="font-size:11px;">ตีบวกได้สูงสุด +10 โดยไอเทมจะไม่แตกหาย (GDD ข้อ 5)</div>`;
}

/**
 * ส่วนทอยออปชันใหม่ทีละบรรทัด (ขั้น 3.6) โชว์ราคาและโอกาสของทุกผลที่ออกได้ก่อนกด
 * จ่ายด้วยโกลด์และหินตีบวกเท่านั้น (ไม่ใช้เพชร — กฎข้อ 2)
 */
function optionSection(self: any, gear: ItemDef, far: boolean): string {
  const opts = parseOptions(self.options?.get?.(selectedRefineSlot));
  if (opts.length === 0) return "";
  const cost = rerollCost(gear, selectedRefineSlot);
  const stones = Number(self.items?.get?.(cost.stone) ?? 0);
  const can = !far && self.gold >= cost.gold && stones >= 1;
  let html = `<div class="wsep"></div><div class="drow"><span class="pts">ออปชัน</span>`
    + `<span class="dim">ทอยใหม่ ${cost.gold} โกลด์ + ${esc(ITEMS[cost.stone]?.name ?? "หิน")} 1 (มี ${stones})</span></div>`;
  // บรรทัดธาตุอาวุธทอยไม่ได้ (เปลี่ยนที่ส่วน "ธาตุอาวุธ" ด้านล่าง)
  html += opts.map((o, i) => `<div class="drow"><span>◆ ${esc(describeOption(o))}</span>`
    + (endowOf(o.id) ? "" : `<button type="button" data-reroll="${i}" ${can ? "" : "disabled"}>ทอยใหม่</button>`) + `</div>`).join("");
  const odds = rerollChances(gear, opts, 0)
    .sort((a, b) => b.chance - a.chance)
    .map((c) => `${OPTIONS[c.id]?.name ?? c.id} ${(c.chance * 100).toFixed(1)}%`).join(" · ");
  return html + `<div class="d dim" style="font-size:11px">โอกาสแต่ละแบบ (บรรทัดแรก): ${esc(odds)}</div>`;
}

/**
 * ร่ายธาตุถาวรใส่อาวุธ (endow.ts) — หินธาตุ 1 ก้อน + โกลด์ตามเลเวลอาวุธ **ไม่ใช้เพชร**
 * โชว์ราคาเต็มก่อนกดทุกปุ่ม เปลี่ยนธาตุใหม่จ่ายเต็มอีกรอบ
 */
function endowSection(self: any, gear: ItemDef, far: boolean): string {
  if (selectedRefineSlot !== "weapon") return "";
  const now = weaponEndow(parseOptions(self.options?.get?.("weapon")));
  let html = `<div class="wsep"></div><div class="drow"><span class="pts">ธาตุอาวุธ</span>`
    + `<span class="dim">ตอนนี้: ${now ? ELEMENT_NAMES[now] : "ไร้ธาตุ"}</span></div>`
    + `<div class="d dim" style="font-size:11px">ตีธรรมดาและสกิลกายภาพที่ไม่มีธาตุจะเป็นธาตุนี้ · เปลี่ยนธาตุใหม่ต้องจ่ายเต็มอีกครั้ง</div>`;
  html += ENDOW_ELEMENTS.map((el) => {
    const cost = endowCost(gear, el);
    const stones = Number(self.items?.get?.(cost.stone) ?? 0);
    const ok = !far && el !== now && self.gold >= cost.gold && stones >= 1;
    return `<div class="drow"><span style="color:${ELEMENT_GLOW[el]}">● ${ELEMENT_NAMES[el]}</span>`
      + `<span class="dim">${cost.gold.toLocaleString()} โกลด์ + ${esc(ITEMS[cost.stone]?.name ?? "หินธาตุ")} (มี ${stones})</span>`
      + `<button type="button" data-endow="${el}" ${ok ? "" : "disabled"}>${el === now ? "ใช้อยู่" : "ร่าย"}</button></div>`;
  }).join("");
  return html;
}

/** การ์ดที่กดครั้งแรกไว้ รอกดยืนยันอีกครั้ง (ใส่แล้วถอดไม่ได้ จึงต้องกดสองครั้ง) */
let pendingCard = "";

/** ส่วนใส่การ์ดในหน้าต่างโรงตีเหล็ก */
function cardSection(self: any, gear: ItemDef, far: boolean): string {
  const slots = cardSlotsOf(gear);
  const current = parseCards(self.cards?.get?.(selectedRefineSlot));
  let html = `<div class="wsep"></div><div class="drow"><span class="pts">ช่องตะเกียง</span>`
    + `<span class="dim">${current.length}/${slots}</span></div>`;
  if (slots === 0) return html + `<div class="d dim">อุปกรณ์ชิ้นนี้ไม่มีช่องตะเกียง</div>`;
  html += current.map((id) => `<div class="d">${ITEMS[id]?.icon ?? "🕯️"} ${esc(ITEMS[id]?.name ?? id)}`
    + ` <span class="dim">${esc(describeCard(ITEMS[id]?.card?.bonus ?? {}, ITEMS[id]?.card?.reqClass))}</span></div>`).join("");
  if (current.length >= slots) return html;

  const fit = [...((self.items ?? []) as Iterable<[string, number]>)]
    .filter(([id, n]) => n > 0 && !insertDenial(ITEMS, gear.id, current, id, self.cls));
  if (fit.length === 0) {
    return html + `<div class="d dim">ไม่มีประกายวิญญาณที่ใส่กับ${esc(gear.name)}ได้ในกระเป๋า</div>`;
  }
  return html + `<div class="d dim">ไฟที่ใส่แล้วถอดออกจากของไม่ได้ (ติดไปกับชิ้นนี้เวลาถอด/สลับ) · ไฟในอาวุธเรืองตามธาตุให้คนอื่นเห็น</div>`
    + fit.map(([id, n]) => {
      const def = ITEMS[id]!;
      const sure = pendingCard === id;
      return `<div class="prow"><span class="pname">${def.icon ?? "🕯️"} ${esc(def.name)} ×${n}</span>`
        + `<span class="pinfo">${esc(describeCard(def.card!.bonus, def.card!.reqClass))}</span>`
        + `<button type="button" data-card="${id}" ${far ? "disabled" : ""}>`
        + (sure ? "ยืนยัน (ถอดไม่ได้)" : "จุดไฟใส่") + `</button></div>`;
    }).join("");
}

winRefine.body.addEventListener("click", (e) => {
  const target = e.target as HTMLElement | null;
  const endowBtn = target?.closest?.("[data-endow]") as HTMLButtonElement | null;
  if (endowBtn && !endowBtn.disabled) {
    room?.send("endow", { element: endowBtn.dataset["endow"] });
    return;
  }
  const rerollBtn = target?.closest?.("[data-reroll]") as HTMLButtonElement | null;
  if (rerollBtn && !rerollBtn.disabled) {
    room?.send("rerollOption", { slot: selectedRefineSlot, line: Number(rerollBtn.dataset["reroll"]) });
    return;
  }
  const cardBtn = target?.closest?.("[data-card]") as HTMLButtonElement | null;
  if (cardBtn && !cardBtn.disabled) {
    const id = cardBtn.dataset["card"] ?? "";
    if (pendingCard === id) {
      pendingCard = "";
      room?.send("insertCard", { slot: selectedRefineSlot, card: id });
    } else {
      pendingCard = id;
      refineNote = "กดอีกครั้งเพื่อยืนยันการใส่ไฟตะเกียง";
      drawRefineWindow();
    }
    return;
  }
  const slotBtn = target?.closest?.("[data-rf-slot]") as HTMLElement | null;
  if (slotBtn) {
    const slot = slotBtn.dataset["rfSlot"] as EquipSlotId;
    if (slot && canRefineSlot(slot)) {
      selectedRefineSlot = slot;
      drawRefineWindow();
      return;
    }
  }

  const actBtn = target?.closest?.("[data-action='refine']") as HTMLButtonElement | null;
  if (actBtn && !actBtn.disabled) {
    if (selectedRefineSlot) {
      room?.send("refine", { slot: selectedRefineSlot, protect: useProtectStone });
    }
  }
});

winRefine.body.addEventListener("change", (e) => {
  const chk = (e.target as HTMLElement | null)?.closest?.("#chkProtect") as HTMLInputElement | null;
  if (chk) {
    useProtectStone = chk.checked;
    drawRefineWindow();
  }
});

/**
 * ไอคอนเมนูมุมขวาล่าง
 *
 * ส่วนใหญ่ยังเป็นที่จองไว้ หน้าต่างจริงรอเฟส 2 กดแล้วบอกตรง ๆ ว่ายังไม่เปิด
 * ดีกว่าเปิดหน้าต่างเปล่าให้เข้าใจผิดว่าพัง
 */
interface MenuBtn {
  action?: ActionId | "attack" | "mobileDrawer";
  glyph: string;
  label: string;
  /** แสดงบนหน้าจอมือถือ (โหมดหน้าจอโล่ง) */
  showOnMobile?: boolean;
  /** ทำงานได้จริงแล้วหรือยัง */
  open?: () => void;
}

const MENU: MenuBtn[] = [
  // 3 ปุ่มหลักสำหรับหน้าจอมือถือ (โจมตีอัตโนมัติ, กระเป๋า, และเมนูดรอว์เวอร์)
  { action: "attack", glyph: "⚔️", label: "โจมตีอัตโนมัติ (F)", showOnMobile: true, open: () => toggleAttack() },
  { action: "winBag", glyph: "🎒", label: "กระเป๋า", showOnMobile: true, open: () => winBag.toggle() },
  { action: "mobileDrawer", glyph: "☰", label: "เมนูหลัก", showOnMobile: true, open: () => toggleMobileDrawer() },

  // ปุ่มอื่น ๆ สำหรับโหมดคอมพิวเตอร์ (บนมือถือจะเข้าไปอยู่ในเมนูดรอว์เวอร์ ☰)
  { action: "winChar", glyph: "🧍", label: "ตัวละครและแต้มสเตตัส", open: () => winChar.toggle() },
  { action: "winSkill", glyph: "✨", label: "สกิล", open: () => winSkill.toggle() },
  { action: "winShop", glyph: "🏪", label: "ร้านค้า", open: () => winShop.toggle() },
  { glyph: "⛺", label: "ตั้งร้านค้า (Vending)", open: () => openVendingSetup() },
  { action: "winRefine", glyph: "🔨", label: "โรงตีเหล็ก (ตีบวก)", open: () => winRefine.toggle() },
  { action: "winParty", glyph: "👥", label: "ปาร์ตี้", open: () => winParty.toggle() },
  { action: "winQuest", glyph: "📜", label: "เควสและภารกิจ", open: () => winQuest.toggle() },
  { glyph: "🗺", label: "แผนที่โลก", open: () => winWorldMap.toggle() },
  { glyph: "🕹️", label: "จอยสติกควบคุม", open: () => toggleJoystick() },
  { glyph: "⛶", label: "เต็มหน้าจอ", open: () => { void toggleFullscreen(); } },
  { action: "winKeys", glyph: "⌨", label: "ตั้งค่าปุ่ม", open: () => winKeys.toggle() },
  { action: "winSettings", glyph: "⚙", label: "ตั้งค่า", open: () => togglePanel(uiSetPanel) },
  { action: "winHelp", glyph: "❔", label: "ปุ่มควบคุม", open: () => togglePanel(uiHelp) },
  { glyph: "📖", label: "คู่มือผู้เล่น (สูตร มอน ไอเทม แผนที่ ตัวจำลอง)", open: () => openHandbook() },
];

/** คู่มือผู้ทดสอบเป็นหน้าเว็บแยก (handbook.html) เปิดแท็บใหม่ เกมเดิมยังเชื่อมต่ออยู่ */
function openHandbook(): void {
  window.open("./handbook.html", "_blank", "noopener");
}

/** เปิดแผงหนึ่ง ปิดแผงอื่นในคอลัมน์เดียวกัน ไม่ให้ซ้อนกันจนบังจอ */
function togglePanel(panel: HTMLElement): void {
  const on = !panel.classList.contains("on");
  uiSetPanel.classList.remove("on");
  uiHelp.classList.remove("on");
  panel.classList.toggle("on", on);
  syncMenuState();
}

/** เปิด/ปิดป้าย "!" บนไอคอนเมนูที่ตรงกับ action นี้ (ไม่เจอ action นี้ในเมนูก็เงียบ ๆ ไป) */
function setMenuAlert(action: ActionId, on: boolean): void {
  const i = MENU.findIndex((m) => m.action === action);
  if (i < 0) return;
  menuBtn(i)?.classList.toggle("alert", on);
  syncMenuToggleAlert();
}

/** ปุ่มของรายการที่ i ใน MENU (ไม่ใช้ลำดับ DOM เพราะมีปุ่ม ☰ รวมเมนูแทรกอยู่) */
function menuBtn(i: number): HTMLButtonElement | null {
  return uiMenu.querySelector<HTMLButtonElement>(`button[data-mi="${i}"]`);
}

/** ปุ่ม ☰ ขึ้นป้าย ! เมื่อมีไอคอนในเมนูที่รวมไว้กำลังเตือน (เช่น มีแต้มรอลง) */
function syncMenuToggleAlert(): void {
  const any = !!uiMenu.querySelector(".menupop .mbtn.alert");
  uiMenu.querySelector(".menu-toggle")?.classList.toggle("alert", any);
}

/** ไฮไลต์ไอคอนของแผงหรือหน้าต่างที่เปิดอยู่ */
function syncMenuState(): void {
  const btns = MENU.map((_m, i) => menuBtn(i));
  MENU.forEach((m, i) => {
    let on = false;
    if (m.action === "winChar") on = winChar.isOpen();
    else if (m.action === "winBag") on = winBag.isOpen();
    else if (m.action === "winSkill") on = winSkill.isOpen();
    else if (m.action === "winShop") on = winShop.isOpen();
    else if (m.action === "winRefine") on = winRefine.isOpen();
    else if (m.action === "winParty") on = winParty.isOpen();
    else if (m.action === "winQuest") on = winQuest.isOpen();
    else if (m.action === "winKeys") on = winKeys.isOpen();
    else if (m.action === "winSettings") on = uiSetPanel.classList.contains("on");
    else if (m.action === "winHelp") on = uiHelp.classList.contains("on");
    else if (m.action === "attack") {
      btns[i]?.classList.toggle("attacking", !!autoAttack);
    }
    else if (m.glyph === "🕹️") on = !!settings.joystick;
    else if (m.glyph === "⛶") on = isFullscreen();
    btns[i]?.classList.toggle("on", on);
  });
  syncDrawerAlerts();
}

/** ปุ่มเมนูที่เปิดค้างอยู่ปิดเมื่อคลิกที่อื่น (ผูกครั้งเดียว) */
let menuPopHooked = false;

/**
 * แถวไอคอนมุมขวาล่าง: **ไอคอนทั้งหมดรวมอยู่ในปุ่ม ☰ ปุ่มเดียว** (กดแล้วกางเป็นแผงรายการพร้อมชื่อ)
 * ยกเว้นปุ่มเฟืองตั้งค่า ⚙ ที่โชว์แยกไว้ข้าง ๆ เสมอ
 * บนมือถือ (โหมดหน้าจอโล่ง) แผงนี้ `display: contents` — ปุ่มโจมตี/กระเป๋า/เมนูหลักยังลอยตามเดิม
 */
function buildMenu(): void {
  uiMenu.textContent = "";
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "mbtn menu-toggle";
  toggle.title = "เมนู — รวมไอคอนทั้งหมด";
  toggle.innerHTML = `☰<span class="dot">!</span>`;
  const pop = document.createElement("div");
  pop.className = "menupop";
  toggle.addEventListener("click", () => pop.classList.toggle("on"));
  uiMenu.appendChild(toggle);

  const gearBtns: HTMLButtonElement[] = [];
  MENU.forEach((m, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.dataset["mi"] = String(i);
    btn.className = "mbtn"
      + (m.showOnMobile ? " show-on-mobile" : "")
      + (m.action === "attack" ? " mbtn-attack" : "")
      + (m.action === "mobileDrawer" ? " mbtn-drawer" : "");
    if (m.action) btn.dataset["action"] = m.action;
    const hotkey = m.action && (m.action as any) in bindings ? keyLabel(bindings[m.action as ActionId]) : "";
    btn.title = hotkey && hotkey !== "—" ? `${m.label} (${hotkey})` : m.label;
    btn.innerHTML = `${m.glyph}`
      + `<span class="lbl">${esc(m.label.replace(/\s*\(.*\)\s*$/, ""))}</span>`
      + (hotkey && hotkey !== "—" ? `<span class="key">${esc(hotkey)}</span>` : "")
      + `<span class="dot">!</span>`;
    btn.addEventListener("click", () => {
      pop.classList.remove("on");
      if (m.open) {
        m.open();
        syncMenuState();
      } else {
        addLog(`${m.label}: ยังไม่เปิดใช้งาน (เฟส 2)`);
      }
    });
    // สำหรับปุ่มโจมตีบนมือถือ ให้รองรับ pointerdown เพื่อให้ตอบสนองทันทีแม้กดพร้อมเดินด้วยจอยสติก
    if (m.action === "attack") {
      btn.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "touch" || e.button === 0) {
          attackTarget();
          syncMenuState();
        }
      });
    }
    // ⚙ ตั้งค่าอยู่นอกแผง ที่เหลือ (รวมปุ่มสำหรับมือถือ) อยู่ในแผงรวม
    if (m.action === "winSettings") gearBtns.push(btn);
    else pop.appendChild(btn);
  });
  for (const g of gearBtns) uiMenu.appendChild(g);
  uiMenu.appendChild(pop);

  if (!menuPopHooked) {
    menuPopHooked = true;
    document.addEventListener("pointerdown", (e) => {
      if (!(e.target as HTMLElement | null)?.closest?.("#menu")) uiMenu.querySelector(".menupop")?.classList.remove("on");
    });
  }
  syncMenuToggleAlert();
}

/** รายการค่าที่ตั้งได้จริงตอนนี้ ตรงกับหัวข้อ "ตั้งค่าของผู้เล่น" ใน AGENTS.md */
function buildSetPanel(): void {
  // เฉพาะค่าที่เป็นเปิด/ปิด ชื่อกับอาชีพอยู่ในหน้าเลือกตัวละครไม่ใช่ที่นี่
  type ToggleKey = {
    [K in keyof Settings]: Settings[K] extends boolean ? K : never;
  }[keyof Settings];

  // ช่องที่สามคือปุ่มลัด ว่าง = ตั้งได้จากแผงนี้อย่างเดียว ไม่มีปุ่มลัด
  const rows: Array<[string, ToggleKey, string]> = [
    ["เสียงเอฟเฟกต์ (SFX)", "sound", esc(keyLabel(bindings.soundToggle))],
    ["แถบเลือดใต้ตัวละคร", "selfBar", "V"],
    ["ลูกศรชี้ตัวเอง", "selfArrow", "M"],
    ["คลิกมอนแล้วต่อสู้อัตโนมัติ", "clickAttack", ""],
    ["จอยสติกควบคุม (หน้าจอมือถือ)", "joystick", ""],
    ["โหมดหน้าจอโล่ง (สำหรับมือถือ)", "mobileUI", ""],
    ["กราฟิกสูง (เงา หญ้าหนา)", "hiGraphics", ""],
  ];
  uiSetPanel.textContent = "";
  for (const [label, key, hot] of rows) {
    const btn = document.createElement("button");
    btn.innerHTML = `<span>${label}${hot ? ` <span class="dim">(${hot})</span>` : ""}</span>`
      + `<b>${settings[key] ? "เปิด" : "ปิด"}</b>`;
    btn.addEventListener("click", () => {
      settings[key] = !settings[key];
      if (key === "sound") {
        sound.setEnabled(settings.sound);
        if (settings.sound) sound.playClick();
      }
      if (key === "joystick") {
        applyJoystickVisibility();
        syncMenuState();
      }
      if (key === "mobileUI") {
        applyMobileUI();
      }
      if (key === "hiGraphics") {
        applyGraphics();
        addLog("กราฟิก: เงาเปลี่ยนทันที · หญ้า/ดอกไม้เปลี่ยนเมื่อเข้าแผนที่ใหม่");
      }
      saveSettings();
      buildSetPanel();
      // แผงช่วยเหลือบอกสถานะเปิด/ปิดของสองค่านี้ด้วย ต้องวาดใหม่ให้ตรงกัน
      buildHelp();
    });
      uiSetPanel.appendChild(btn);
  }

  // ---- โหมดเต็มหน้าจอ ----
  const fsBtn = document.createElement("button");
  fsBtn.innerHTML = `<span>เต็มหน้าจอ</span><b>${isFullscreen() ? "เปิดอยู่" : "ปิด"}</b>`;
  fsBtn.addEventListener("click", () => {
    void toggleFullscreen();
  });
  uiSetPanel.appendChild(fsBtn);

  // ---- ตั้งค่าปุ่ม ----
  const keysBtn = document.createElement("button");
  keysBtn.innerHTML = `<span>ตั้งค่าปุ่ม <span class="dim">(${esc(keyLabel(bindings.winKeys))})</span></span>`
    + `<b>เปิด</b>`;
  keysBtn.addEventListener("click", () => {
    winKeys.toggle();
    syncMenuState();
  });
  uiSetPanel.appendChild(keysBtn);

  // ---- ออกจากเกม ----
  // อยู่ท้ายแผงตั้งค่าเพราะเป็นปุ่มที่กดผิดแล้วเสียเวลา ไม่ควรอยู่ใกล้ปุ่มที่กดบ่อย
  uiSetPanel.appendChild(document.createElement("hr"));

  const exits: Array<[string, string, boolean]> = [
    ["เลือกตัวละคร", "กลับไปหน้าเลือกตัวละคร", false],
    ["ออกจากเกม", "ออกจากระบบแล้วกลับไปหน้าเข้าสู่ระบบ", true],
  ];
  for (const [label, hint, logout] of exits) {
    const btn = document.createElement("button");
    btn.className = "danger";
    btn.innerHTML = `<span>${label}</span><b>→</b>`;
    btn.title = hint;
    btn.addEventListener("click", () => {
      void leaveGame(logout);
    });
    uiSetPanel.appendChild(btn);
  }
}

/**
 * ออกจากห้องแล้วเริ่มใหม่ที่หน้าเลือกตัวละคร (หรือหน้าเข้าสู่ระบบถ้า `logout`)
 *
 * **ต้องรอ `room.leave()` ให้จบก่อนรีโหลด** server บันทึกความคืบหน้าใน `onLeave`
 * ถ้าปิดหน้าไปเลยจะเหลือแค่การบันทึกอัตโนมัติรอบ 30 วิ ของที่เพิ่งได้มาอาจหาย
 * และบัญชีจะค้างเป็น "กำลังเล่นอยู่" จนกว่า server จะรู้ว่าหลุด ทำให้เข้าใหม่ไม่ได้ทันที
 *
 * รีโหลดหน้าแทนการเรียก `boot()` ซ้ำ เพราะฉาก Babylon ตัวเดิมยังผูกกับผู้เล่นและมอนสเตอร์
 * ของห้องเก่าอยู่ การรื้อทั้งหมดให้สะอาดมีที่พลาดเยอะกว่าการเริ่มใหม่ทั้งหน้า
 */
/**
 * ออกจากห้องแล้วเริ่มใหม่ที่หน้าเลือกตัวละคร (หรือหน้าเข้าสู่ระบบถ้า `logout`)
 *
 * แสดง Modal ยืนยันในเกม ไม่พึ่งพา `window.confirm` ของเบราว์เซอร์ที่อาจโดนบล็อกบนมือถือ/fullscreen
 */
async function leaveGame(logout: boolean): Promise<void> {
  // สร้าง Modal ยืนยันในเกม
  document.querySelector(".leave-confirm-modal")?.remove();
  const modal = document.createElement("div");
  modal.className = "leave-confirm-modal";
  modal.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.72);display:flex;align-items:center;justify-content:center;z-index:9999;touch-action:auto;";
  
  const box = document.createElement("div");
  box.style.cssText = "background:#0f172a;border:1.5px solid var(--gold,#ffcf5a);border-radius:10px;padding:16px 20px;text-align:center;max-width:280px;width:86%;color:#fff;font-family:inherit;box-shadow:0 8px 32px rgba(0,0,0,0.8);";
  
  const title = logout ? "ออกจากเกม (Logout)" : "สลับตัวละคร";
  const desc = logout ? "คุณต้องการออกจากระบบไปยังหน้าเข้าสู่ระบบใช่หรือไม่?" : "คุณต้องการกลับไปที่หน้าเลือกตัวละครใช่หรือไม่?";
  
  box.innerHTML = `
    <div style="font-weight:700;font-size:13px;color:var(--gold,#ffcf5a);margin-bottom:8px;">${title}</div>
    <div style="font-size:10.5px;opacity:.88;margin-bottom:14px;line-height:1.4;">${desc}</div>
    <div style="display:flex;gap:10px;justify-content:center;">
      <button type="button" id="btnCancelLeave" style="flex:1;padding:6px 10px;border-radius:6px;border:1px solid rgba(255,255,255,0.25);background:rgba(255,255,255,0.1);color:#fff;font-weight:600;font-size:11px;cursor:pointer;">ยกเลิก</button>
      <button type="button" id="btnConfirmLeave" style="flex:1;padding:6px 10px;border-radius:6px;border:1px solid #ef4444;background:#ef4444;color:#fff;font-weight:700;font-size:11px;cursor:pointer;">ยืนยัน</button>
    </div>
  `;
  modal.appendChild(box);
  document.body.appendChild(modal);

  modal.querySelector("#btnCancelLeave")?.addEventListener("click", () => modal.remove());
  modal.querySelector("#btnConfirmLeave")?.addEventListener("click", async () => {
    modal.remove();
    const label = logout ? "ออกจากเกม" : "กลับไปหน้าเลือกตัวละคร";
    addLog(`${label}…`);
    try {
      // ตั้ง timeout 800ms หากเซิร์ฟเวอร์ตอบช้าหรือไม่ตอบ ให้รีโหลดได้ทันที
      await Promise.race([
        room?.leave(),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ]);
    } catch (err) {
      console.warn("ออกจากห้องไม่สำเร็จ", err);
    }
    if (logout) clearToken();
    location.reload();
  });
}

/**
 * แผงช่วยเหลือ (ไอคอน ❔)
 *
 * **อ่านปุ่มจาก bindings ทุกบรรทัด ไม่มีชื่อปุ่มตายตัว**
 * ตั้งปุ่มใหม่แล้วแผงนี้ต้องเปลี่ยนตาม ไม่งั้นกลายเป็นคำแนะนำที่ผิด
 * วาดเมื่อมีอะไรเปลี่ยนเท่านั้น (ตอนบูต ตั้งปุ่ม หรือสลับค่าตั้งค่า) ไม่ใช่ทุกเฟรม
 */
function buildHelp(): void {
  const k = (id: ActionId): string => `<b>${esc(keyLabel(bindings[id]))}</b>`;
  const move = (["moveF", "moveL", "moveB", "moveR"] as ActionId[])
    .map((id) => esc(keyLabel(bindings[id]))).join("");

  uiHelp.innerHTML = [
    `<b>${move}</b> เดิน · ${k("targetNext")} เลือกเป้า · ${k("attack")} สั่งตี/หยุดตี`,
    `${k("skill1")}-${k("skill10")} แถบสกิล · ${k("cancel")} ยกเลิกเป้า`
      + ` · ${k("camReset")} รีเซ็ตกล้อง`,
    `${k("selfBar")} แถบใต้ตัวละคร: ${settings.selfBar ? "เปิด" : "ปิด"}`
      + ` · ${k("selfArrow")} ลูกศรชี้ตัวเอง: ${settings.selfArrow ? "เปิด" : "ปิด"}`,
    `${k("bag1")} ยาฟื้น HP · ${k("bag2")} น้ำมันฟื้น MP · ${k("chat")} พิมพ์แชท`,
    `${k("winChar")} ตัวละคร · ${k("winSkill")} สกิล · ${k("winShop")} ร้านค้า`
      + ` · ${k("winKeys")} ตั้งค่าปุ่ม`,
    `คลิกซ้ายที่พื้น = เดิน · คลิกซ้ายที่มอนสเตอร์ = ${settings.clickAttack
      ? "ตีเลย (ตีจนตาย กด " + k("cancel") + " หรือเดินหนีเพื่อหยุด)"
      : "เลือกเป้า แล้วกด " + k("attack") + " เพื่อตี"}`,
    "ลากคลิกขวา = หมุนกล้อง · ล้อเมาส์ = ซูม",
  ].join("<br />");
}

buildMenu();
buildSetPanel();
buildHelp();
setupJoystick();
setupFullscreen();
applyJoystickVisibility();
setupMobileUI();

uiHud.addEventListener("click", () => {
  winChar.toggle();
  syncMenuState();
});

/** อาชีพของแถบสกิลที่วาดอยู่ ใช้วาดแถบใหม่เมื่อเปลี่ยนปุ่ม */
let myClass = "";

function buildSkillBar(classId: string): void {
  myClass = classId;
  // สกิลติดตัวกดใช้ไม่ได้ ไม่ขึ้นแถบลัด · ผังช่องมาจาก applyBarLayout (ผู้เล่นจัดเองได้อิสระ)
  const all = getAllSkillsForClass(CLASSES[classId] ? classId : "warden").filter((sk) => sk.kind !== "passive");
  const meNow: any = room?.state?.players?.get?.(room.sessionId);
  const lvOf = (id: string): number => Number(meNow?.skills?.get?.(id) ?? 0);
  const learned = new Set(all.filter((sk) => lvOf(sk.id) > 0).map((sk) => sk.id));
  learnedSig = [...learned].join(",");
  applyBarLayout(classId, all, learned);
  bar.textContent = "";

  const top = document.createElement("div");
  top.className = "barrow";
  const bottom = document.createElement("div");
  bottom.className = "barrow";
  BAR_SLOTS.forEach((slot, idx) => (idx < 10 ? top : bottom).appendChild(makeBarButton(slot, idx)));
  bar.appendChild(top);
  bar.appendChild(bottom);
}

/** วาดปุ่มของช่องหนึ่ง: สกิล · ไอเทม · หรือช่องว่าง */
function makeBarButton(slot: BarSlot, idx: number): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.dataset["slotIdx"] = String(idx);
  btn.dataset["row"] = idx < 10 ? "top" : "bottom";
  const sk = slot.skill ? findSkillAnywhere(slot.skill) : undefined;
  const def = !sk && slot.item ? (ITEMS[slot.item] ?? getItem(slot.item)) : undefined;

  if (sk) {
    btn.className = "slot";
    btn.dataset["skill"] = sk.id;
    // ไอคอนเป็นภาพ (icons.ts: ภาพที่วางไว้ หรือสัญลักษณ์บนสีไล่ของสกิล) — รอบ 20
    btn.style.background = `${skillIcon(sk)} center / cover no-repeat`;
    btn.innerHTML =
      `<span class="key">${slotLabel(slot)}</span>`
      + `<span class="nm">${sk.th}</span>`
      + `<span class="mp">MP ${sk.mp}</span>`
      + `<span class="cd" style="transform:scaleY(0)"></span>`
      + `<span class="cdnum"></span>`;
    btn.title = `${sk.th} — ${sk.desc}\n(ลากไปสลับช่อง · ลากออกจากแถบแล้วปล่อยเพื่อเอาออก)`;
    btn.addEventListener("click", () => {
      if (!justDragged() && performance.now() - lastSlotTouchAt > 250) castSkill(sk);
    });
    btn.addEventListener("pointerdown", (e) => startSlotDrag(e, idx));
    return btn;
  }

  if (def) {
    const isEquip = def.kind === "equip";
    const heal = def.healHpPct > 0
      ? `${Math.round(def.healHpPct * 100)}% HP`
      : def.healMpPct > 0
        ? `${Math.round(def.healMpPct * 100)}% MP`
        : "";
    btn.dataset["item"] = slot.item;
    btn.className = `slot item ${isEquip ? "equip" : def.healHpPct > 0 ? "hp" : "mp"}`;
    btn.title = `${def.name} — ${def.desc}\n(คลิกเพื่อใช้งาน/สวมใส่ · ลากไปสลับช่อง · ลากออกจากแถบแล้วปล่อยเพื่อเอาออก)`;
    btn.innerHTML =
      `<span class="key">${slotLabel(slot)}</span>`
      + `<span class="count">0</span>`
      + `<span class="nm">${def.name.replace("ยาฟื้นพลัง", "ยาฟื้น").replace("น้ำมันตะเกียง", "น้ำมัน")}</span>`
      + (heal ? `<span class="mp">${heal}</span>` : `<span class="mp">${itemGlyph(def)}</span>`)
      + `<span class="cd" style="transform:scaleY(0)"></span>`
      + `<span class="cdnum"></span>`;
    btn.addEventListener("click", () => {
      if (!justDragged() && performance.now() - lastSlotTouchAt > 250) triggerHotbarItem(slot.item);
    });
    btn.addEventListener("pointerdown", (e) => startSlotDrag(e, idx));
    return btn;
  }

  btn.className = "slot empty";
  btn.innerHTML = `<span class="key">${slotLabel(slot)}</span>`;
  btn.title = "ช่องว่าง — ลากสกิลจากหน้าต่างสกิล หรือไอเทมจากกระเป๋ามาวางที่นี่";
  return btn;
}

function useItem(id: string): void {
  if (!room || !id) return;
  const self: any = room.state?.players?.get(room.sessionId);
  if (!self || self.dead) return;
  if ((self.items?.get(id) ?? 0) <= 0) return;
  if (itemReadyAt > performance.now()) return;
  // ผงตะเกียงนำทาง: เลือกปลายทางก่อน (ตะเกียงที่จุดไว้แล้ว)
  if (ITEMS[id]?.teleportType === "waypoint") {
    openWarpChooser(id);
    return;
  }
  room.send("useItem", { item: id });
}

/* ---- ตะเกียงจุดวาร์ป (shared/waypoints.ts) ---- */
const winWarp = createWindow(uiWins, { title: "🏮 วาร์ปไปตะเกียง", x: 360, y: 140 });
let warpItemId = "";
function openWarpChooser(itemId: string): void {
  warpItemId = itemId;
  const self: any = room?.state?.players?.get(room.sessionId);
  const lit = parseWaypoints(self?.waypoints ?? "");
  const here = currentZoneId();

  // แยกเมืองและแผนที่ผจญภัย
  const allTowns = Object.values(ALL_ZONES).filter((z) => z.town);
  const litAdventure = lit
    .filter((id) => !ALL_ZONES[id]?.town)
    .map((id) => ALL_ZONES[id]!)
    .sort((a, b) => a.levelRange[0] - b.levelRange[0]);

  let rows = `<div class="d" style="font-weight:700;color:#facc15;margin-top:4px">🏰 เมืองและจุดพัก (Towns):</div>`;
  for (const tz of allTowns) {
    const isLit = lit.includes(tz.id);
    const isHere = tz.id === here;
    rows += `<div class="drow"><span>🏰 ${esc(tz.name)} <span class="dim">Lv ${tz.levelRange[0]}</span></span>`
      + (isLit
        ? `<button type="button" data-warp="${tz.id}" ${isHere ? "disabled" : ""}>${isHere ? "อยู่ที่นี่" : "วาร์ป"}</button>`
        : `<span class="dim" style="font-size:11px;color:#94a3b8">🔒 ต้องไปจุดตะเกียงก่อน</span>`)
      + `</div>`;
  }

  if (litAdventure.length > 0) {
    rows += `<div class="d" style="font-weight:700;color:#facc15;margin-top:8px">🏮 แผนที่ผจญภัยที่จุดตะเกียงแล้ว:</div>`;
    for (const z of litAdventure) {
      const isHere = z.id === here;
      rows += `<div class="drow"><span>🏮 ${esc(z.name)} <span class="dim">Lv ${z.levelRange[0]}–${z.levelRange[1]}</span></span>`
        + `<button type="button" data-warp="${z.id}" ${isHere ? "disabled" : ""}>${isHere ? "อยู่ที่นี่" : "วาร์ป"}</button></div>`;
    }
  }

  winWarp.body.innerHTML = `<div class="d dim" style="font-size:11px">ใช้${esc(ITEMS[itemId]?.name ?? "")} 1 ชิ้น · วาร์ปกลับเข้าเมืองหรือแผนที่ที่เคยจุดตะเกียงแล้ว</div>`
    + `<div style="max-height:55vh;overflow-y:auto;min-width:280px">${rows}</div>`;
  winWarp.open();
}
winWarp.body.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement | null)?.closest?.("[data-warp]") as HTMLButtonElement | null;
  if (!b || b.disabled || !room) return;
  room.send("useItem", { item: warpItemId, zone: b.dataset["warp"] });
  winWarp.close();
});

/** คลิกตะเกียงไว้แล้วกำลังเดินไปหา ถึงระยะแล้วจุดเอง (เดินเองด้วยปุ่ม/คลิกพื้น = ยกเลิก) */
let pendingLantern = false;
function checkLantern(): void {
  if (!room || !hasSelf) return;
  const zn = ALL_ZONES[currentZoneId()];
  const self: any = room.state?.players?.get(room.sessionId);
  // ไฟตะเกียงในฉากตามสถานะจริงจาก server (เมืองหลักแรกจุดไว้เสมอ · เมืองอื่นและโซนอื่นต้องจุดก่อน)
  if (zn && hasLantern(zn)) {
    const isLit = zn.id === "meadow" || parseWaypoints(self?.waypoints ?? "").includes(zn.id);
    world.setLanternLit(isLit);
  }
  if (!pendingLantern || !zn || !hasLantern(zn)) return;
  const l = lanternPos(zn);
  if (Math.hypot(myX - l.x, myZ - l.z) > LANTERN_RANGE) return;
  pendingLantern = false;
  room.send("lightLantern");
}

/**
 * สกิลที่กดค้างไว้รอเดินเข้าระยะ
 *
 * กดสกิลตอนยืนไกลเกินระยะแล้วไม่เกิดอะไรเลยคือสิ่งที่คนเล่นงง
 * (server ปฏิเสธด้วย out-of-range ซึ่งถูกของมัน client ต่างหากที่ควรเดินเข้าไปเอง)
 * ตรงนี้เป็นแค่ "ความตั้งใจ" ฝั่ง client เท่านั้น ตอนถึงระยะก็ยังส่งคำสั่งไปให้ server ตัดสินเหมือนเดิม
 */
let pendingSkill: { skill: SkillDef; until: number } | undefined;

/** เดินเข้าหาเป้าได้นานสุดกี่มิลลิวินาทีก่อนเลิกล้มความตั้งใจ */
const APPROACH_MS = 8000;

function sendCast(sk: SkillDef): void {
  if (!room) return;
  pendingSkill = undefined;
  room.send("cast", { skill: sk.id, target: selectedId || undefined });
  // ตั้งคูลดาวน์ไว้ก่อนให้ปุ่มตอบสนองทันที ถ้าเซิร์ฟเวอร์ปฏิเสธจะคืนให้
  cooldownUntil.set(sk.id, performance.now() + sk.cd * 1000);
}

/** เลิกตั้งใจร่ายสกิลที่รออยู่ */
function cancelPending(): void {
  pendingSkill = undefined;
}

function castSkill(sk: SkillDef): void {
  if (!room) return;
  const self: any = room.state?.players?.get(room.sessionId);
  if (!self || self.dead) return;

  // กันการกดรัวตอนยังไม่พร้อม ไม่ให้ยิง packet ทิ้งเปล่า
  // ยังไม่ได้ลงแต้ม บอกทางให้ด้วย ไม่ใช่กดแล้วเงียบ
  if (skillLv(self, sk.id) < 1) {
    addLog(`${sk.th}: ยังไม่ได้ลงแต้ม — กด ${keyLabel(bindings.winSkill)} เพื่อเปิดหน้าต่างสกิล`);
    return;
  }
  if ((cooldownUntil.get(sk.id) ?? 0) > performance.now()) return;

  // สกิลที่ต้องเล็งศัตรู: ถ้ายังไม่มีเป้า หรือเป้าเดิมตายแล้ว ให้เลือกมอนสเตอร์ที่ใกล้ที่สุดทันที
  if (sk.targeting === "enemy") {
    let mob: any = selectedId ? room.state?.monsters?.get(selectedId) : undefined;
    if (!mob || mob.dead) {
      const closest = findClosestMonster();
      if (closest) {
        selectTarget(closest, false);
        mob = room.state?.monsters?.get(closest);
      }
    }
    if (!mob || mob.dead) {
      return;
    }
    if (!inCastRange(Math.hypot(mob.x - myX, mob.z - myZ), sk.range)) {
      pendingSkill = { skill: sk, until: performance.now() + APPROACH_MS };
      return;
    }
  }

  sendCast(sk);
}

/**
 * เดินเข้าหาเป้าแล้วปล่อยสกิลที่กดค้างไว้ เรียกทุกเฟรม
 *
 * ยกเลิกเองเมื่อเป้าตาย ยกเลิกเป้า หรือเดินไม่ถึงภายในเวลาที่กำหนด
 * (เช่นมันวิ่งหนีเร็วกว่าเรา) จะได้ไม่ค้างเป็นคำสั่งผีที่ปล่อยออกมาทีหลังโดยไม่ตั้งใจ
 */
function updatePendingSkill(): void {
  if (!pendingSkill || !room) return;

  const self: any = room.state?.players?.get(room.sessionId);
  const mob: any = selectedId ? room.state?.monsters?.get(selectedId) : undefined;
  if (!self || self.dead || !mob || mob.dead || performance.now() > pendingSkill.until) {
    cancelPending();
    return;
  }

  const sk = pendingSkill.skill;
  if (inCastRange(Math.hypot(mob.x - myX, mob.z - myZ), sk.range)) {
    sendCast(sk);
    // เดินมาเพื่อร่ายสกิลนี้ ร่ายได้แล้วก็หยุด ไม่ต้องเดินต่อไปจ่อหน้ามัน
    // (ถ้าจะตีต่อให้กด F เอง ตามกฎที่ว่าร่ายสกิลไม่สั่งตีธรรมดาต่อ)
    wantX = myX;
    wantZ = myZ;
    return;
  }

  // ถ้าใช้จอยสติกอยู่ ให้ผู้เล่นบังคับทิศทางเอง ไม่แย่งคุม wantX, wantZ
  if (!joyActive) {
    const go = approachPoint({ x: myX, z: myZ }, { x: mob.x, z: mob.z }, sk.range);
    wantX = go.x;
    wantZ = go.z;
  }
}

function updateSkillBar(): void {
  const self: any = room?.state?.players?.get(room.sessionId);
  const now = performance.now();

  if (self) {
    for (const btn of bar.querySelectorAll<HTMLButtonElement>(".slot.item[data-item]")) {
      const itemId = btn.dataset["item"] ?? "";
      if (!itemId) continue;

      const def = ITEMS[itemId] ?? getItem(itemId);
      const isEquip = def.kind === "equip";
      const equipped = isEquipped(self, itemId);
      const bagCount = self.items?.get(itemId) ?? 0;
      const have = bagCount + (equipped ? 1 : 0);

      const count = btn.querySelector(".count") as HTMLElement | null;
      if (count) count.textContent = isEquip && equipped ? "E" : String(bagCount);

      btn.classList.toggle("equipped", isEquip && equipped);

      const left = itemReadyAt - now;
      const cd = btn.querySelector(".cd") as HTMLElement | null;
      if (cd && def.cd) {
        cd.style.transform = `scaleY(${left > 0 ? Math.min(1, left / (def.cd * 1000)) : 0})`;
      }
      btn.classList.toggle("cooling", left > 0);
      const num = btn.querySelector(".cdnum") as HTMLElement | null;
      if (num) num.textContent = left > 0 ? (left / 1000).toFixed(1) : "";
      // ไม่ใช้ disabled: ปุ่ม disabled ไม่รับ pointer บนมือถือ ลากออกจากช่องไม่ได้ (useItem เช็คจำนวนเองอยู่แล้ว)
      btn.classList.toggle("out", have <= 0);
    }
  }

  // ต้อง query ลงไปถึงปุ่ม เพราะ bar.children เป็น div ของแต่ละแถว
  for (const node of bar.querySelectorAll("[data-skill]")) {
    const btn = node as HTMLButtonElement;
    const sk = findSkillAnywhere(btn.dataset["skill"] ?? "");
    if (!sk) continue;

    // ล็อกตามการลงแต้ม ไม่ใช่ Job Level และ MP ใช้ค่าตามเลเวลสกิลจริง
    const lv = self ? skillLv(self, sk.id) : 0;
    const locked = lv < 1;
    const noMp = !!self && !locked && self.mp < skillAtLevel(sk, lv).mp;
    btn.classList.toggle("queued", pendingSkill?.skill.id === sk.id);
    btn.classList.toggle("locked", locked);
    btn.classList.toggle("nomp", noMp);
    // ไม่ใช้ disabled: ปุ่ม disabled ลากออกจากช่องไม่ได้ (castSkill เช็คการลงแต้มเองอยู่แล้ว)
    // MP บนช่องต้องเป็นค่าของเลเวลที่ลงไว้ ไม่ใช่ Lv 1 ตอนสร้างแถบ
    // เขียนเฉพาะตอนเปลี่ยน ฟังก์ชันนี้ถูกเรียกทุกเฟรม
    const mpEl = btn.querySelector(".mp") as HTMLElement | null;
    // ช่องกว้างแค่ --slot (40px) ใส่ได้แค่ MP เลเวลสกิลดูในหน้าต่างสกิล
    // ช่องที่ยังไม่เรียนมีคลาส locked ทำให้จางอยู่แล้ว โชว์ MP ของ Lv 1 ไว้ดูล่วงหน้า
    const mpText = `MP ${skillAtLevel(sk, Math.max(1, lv)).mp}`;
    if (mpEl && mpEl.textContent !== mpText) mpEl.textContent = mpText;

    const left = (cooldownUntil.get(sk.id) ?? 0) - now;
    const frac = left > 0 ? Math.min(1, left / (sk.cd * 1000)) : 0;
    const cd = btn.querySelector(".cd") as HTMLElement | null;
    if (cd) cd.style.transform = `scaleY(${frac})`;

    // เหลือไม่ถึง 10 วิแสดงทศนิยมหนึ่งตำแหน่ง เกินกว่านั้นปัดขึ้นเป็นจำนวนเต็ม
    btn.classList.toggle("cooling", left > 0);
    const num = btn.querySelector(".cdnum") as HTMLElement | null;
    if (num) {
      const secs = left / 1000;
      num.textContent = left > 0 ? (secs < 10 ? secs.toFixed(1) : String(Math.ceil(secs))) : "";
    }
  }
}

/* ---------- HUD ---------- */

/** เปอร์เซ็นต์ที่หนีบไว้ในช่วง 0-100 แล้ว */
function pct(cur: number, max: number): number {
  return max > 0 ? Math.max(0, Math.min(100, (cur / max) * 100)) : 0;
}

/** เขียนค่าลงแถบหนึ่งแถบ */
function setBar(fill: HTMLElement, text: HTMLElement, cur: number, max: number, label: string): void {
  fill.style.width = `${pct(cur, max)}%`;
  text.textContent = `${label} ${Math.ceil(cur)} / ${Math.round(max)}`;
}

function updateHud(): void {
  // state แต่ละ field ยังเป็น undefined ได้จนกว่า patch ก้อนแรกจะมาถึง
  // จึงต้อง optional chaining ทุกจุดที่แตะ room.state ตรง ๆ
  const self: any = room?.state?.players?.get(room.sessionId);
  if (!self) return;

  const cls = CLASSES[self.cls];
  uiName.textContent = cls?.name ?? self.cls;
  uiLv.textContent = `Base ${self.baseLevel} · Job ${self.jobLevel}`;

  setBar(uiHpFill, uiHpTxt, self.hp, self.maxHp, "HP");
  setBar(uiMpFill, uiMpTxt, self.mp, self.maxMp, "MP");

  // EXP แสดงเป็นเปอร์เซ็นต์ของเลเวลปัจจุบัน ไม่ใช่ยอดสะสม
  const needBase = expToNextLevel(self.baseLevel);
  const needJob = jobExpToNextLevel(self.jobLevel);
  const basePct = needBase > 0 ? (self.exp / needBase) * 100 : 0;
  const jobPct = needJob > 0 ? (self.jobExp / needJob) * 100 : 0;
  uiXpFill.style.width = `${Math.max(0, Math.min(100, basePct))}%`;
  uiXpTxt.textContent = `Base Lv ${self.baseLevel} · ${basePct.toFixed(1)}%`;
  uiJobFill.style.width = `${Math.max(0, Math.min(100, jobPct))}%`;
  uiJobTxt.textContent = `Job Lv ${self.jobLevel} · ${jobPct.toFixed(1)}%`;

  const pts = self.statPoints > 0 ? ` · แต้มค้าง ${self.statPoints}` : "";
  // ป้าย "!" บนไอคอนเมนู เตือนว่ามีแต้มรอลง (ตัวละคร = แต้มสเตตัส, สกิล = แต้มสกิล)
  const hasStatPts = self.statPoints > 0;
  const hasSkillPts = Number(self.skillPoints ?? 0) > 0;
  const hasAnyPts = hasStatPts || hasSkillPts;

  setMenuAlert("winChar", hasStatPts);
  setMenuAlert("winSkill", hasSkillPts);

  // แจ้งเตือน ! บนปุ่ม drawer ขีด 3 ขีด และบนรูปโปรไฟล์ตัวละคร (portraitbox)
  document.querySelector(".mbtn-drawer")?.classList.toggle("alert", hasAnyPts);
  document.getElementById("portraitbox")?.classList.toggle("alert", hasAnyPts);
  syncDrawerAlerts();

  // หน้าต่างตัวละครวาดใหม่เฉพาะตอนค่าที่โชว์เปลี่ยน ไม่ใช่ทุกเฟรม
  if (winChar.isOpen()) {
    const sig = [
      self.baseLevel, self.jobLevel, self.statPoints, self.gold,
      self.str, self.agi, self.vit, self.int, self.dex, self.luk,
      Math.ceil(self.hp), Math.ceil(self.mp), self.maxHp, self.maxMp,
      // ของในกระเป๋าด้วย ไม่งั้นเก็บของมาแล้วรายการไม่ขยับ
      [...((self.items ?? []) as Iterable<[string, number]>)].join(","),
      // ของที่ใส่อยู่ด้วย ไม่งั้นเปลี่ยนอาวุธแล้วค่ารองในหน้าต่างไม่ขยับตาม
      [...((self.equipped ?? []) as Iterable<[string, string]>)].join(","),
      // ระดับตีบวกด้วย ไม่งั้นตีบวกแล้วเลข + และสเตตัสไม่ขยับตาม
      [...((self.refines ?? []) as Iterable<[string, number]>)].join(","),
      // เลเวลสกิล (สกิลติดตัวเปลี่ยนค่ารอง)
      [...((self.skills ?? []) as Iterable<[string, number]>)].join(","),
    ].join("|");
    if (sig !== charSig) {
      charSig = sig;
      drawCharWindow();
    }
  }
  if (winBag.isOpen()) {
    const sig = [
      self.gold, self.baseLevel, self.str,
      [...((self.items ?? []) as Iterable<[string, number]>)].join(","),
      [...((self.equipped ?? []) as Iterable<[string, string]>)].join(","),
      [...((self.gear ?? []) as Iterable<[string, string]>)].join(","),
    ].join("|");
    if (sig !== bagSig) {
      bagSig = sig;
      drawBagWindow();
    }
  }
  // การ์ดรายละเอียดตามช่องที่เลือก ลากหน้าต่างหรือเลื่อนกระเป๋า/ร้านค้าแล้วต้องไม่ค้างที่เดิม
  if (uiItemPop.innerHTML && (winChar.isOpen() || winBag.isOpen() || winShop.isOpen())) placeItemPop();

  // ร้านค้าวาดใหม่เมื่อเดินเข้าออกระยะ หรือเมื่อกระเป๋า/โกลด์เปลี่ยนจริงใน state
  //
  // **ห้ามพึ่ง event shopDone อย่างเดียว** event ส่งถึงทันที แต่ state ตามมา
  // ในรอบ patch ถัดไป (~50ms) วาดตอนได้ event จึงอ่านจำนวนของเก่า
  // ผลคือขายแล้วของยังโชว์อยู่ กดอีกครั้งถึงจะหาย (เคยหลุดไปแล้ว)
  if (winShop.isOpen()) {
    const me: any = room?.state?.players?.get(room.sessionId);
    const sig = [
      nearShop(),
      me?.gold ?? 0,
      [...((me?.items ?? []) as Iterable<[string, number]>)].join(","),
      [...((me?.gear ?? []) as Iterable<[string, string]>)].map(([u]) => u).join(","),
    ].join("|");
    if (sig !== shopSig) {
      shopSig = sig;
      drawShopWindow();
    }
  }

  // โรงตีเหล็กต้องรู้ว่าเดินออกนอกระยะ หรือของ/เงิน/ระดับตีบวกเปลี่ยน
  if (winRefine.isOpen()) {
    const near = nearForge();
    const sig = [
      near,
      self.gold,
      ALL_REFINE_STONES.map((id) => self.items?.get?.(id) ?? 0).join(","),
      self.items?.get?.(PROTECT_STONE_ID) ?? 0,
      [...((self.equipped ?? []) as Iterable<[string, string]>)].join(","),
      [...((self.refines ?? []) as Iterable<[string, number]>)].join(","),
      [...((self.cards ?? []) as Iterable<[string, string]>)].join(";"),
      [...((self.items ?? []) as Iterable<[string, number]>)]
        .filter(([id]) => ITEMS[id]?.kind === "card").join(","),
    ].join("|");
    if (sig !== refineSig) {
      refineSig = sig;
      forgeWasNear = near;
      drawRefineWindow();
    }
  }

  // ---- ประตูย้ายโซน ----
  updatePortals();

  // ---- หน้าต่างสกิล ----
  if (winSkill.isOpen()) {
    const sig = [
      self.jobLevel, self.baseLevel, self.skillPoints, self.skillResetUsed,
      [...((self.skills ?? []) as Iterable<[string, number]>)].join(","),
      skillNote, skillResetArmed,
    ].join("|");
    if (sig !== skillWinSig) {
      skillWinSig = sig;
      drawSkillWindow();
    }
  }

  updateCarryBadge(self);

  // ---- ปาร์ตี้ ----
  updatePartyFrame();
  if (winParty.isOpen()) {
    // ไม่รวม HP ในลายเซ็น หน้าต่างนี้มีปุ่ม ถ้าวาดใหม่ทุกครั้งที่ใครโดนตี กดแล้วไม่ติด
    const sig = partyPlayers()
      .map((p) => `${p.id}:${p.name}:${p.baseLevel}:${p.partyId}:${p.partyLeader}`)
      .join("|") + `|${partyNote}`;
    if (sig !== partyWinSig) {
      partyWinSig = sig;
      drawPartyWindow();
    }
  }
  // ---- กรอบเป้าหมาย ----
  const target: any = selectedId ? room?.state?.monsters?.get(selectedId) : undefined;
  if (target && !target.dead) {
    const def = MONSTERS[target.type];
    uiTarget.classList.add("on");
    uiTName.textContent = def?.name ?? target.type;
    uiTLv.textContent = def
      ? `Lv ${def.lv}${def.boss ? " · บอส" : ""} · ${RACE_NAMES[def.race] ?? ""} · ธาตุ${ELEMENT_NAMES[def.element] ?? ""} · ตัว${SIZE_NAMES[sizeOf(def)]}${sizeHint(def)}`
      : "";
    setBar(uiTHpFill, uiTHpTxt, target.hp, target.maxHp, "HP");
    const st0 = statusLine(target, selectedId!);
    const st = def?.dummy ? `${st0 ? st0 + " · " : ""}${dummyDps()}` : st0;
    uiTMode.textContent = (autoAttack ? "กำลังโจมตี" : `เลือกไว้ (กด ${keyLabel(bindings.attack)} เพื่อตี)`)
      + (st ? ` · ${st}` : "");
  } else {
    uiTarget.classList.remove("on");
  }

  // แถวค่าสถานะระบบ มุมขวาบนเหนือมินิแมพ
  const total = Math.max(serverTotalOnline, players.size);
  const dashUrl = `${httpBase(SERVER_URL)}/dashboard`;
  uiStats.innerHTML =
    `FPS <b>${engine.getFps().toFixed(0)}</b>`
    + ` · Ping <b>${pingMs >= 0 ? pingMs : "—"}</b>`
    + ` · ผู้เล่น <b>${players.size}</b>${total > 0 ? ` (ทั้งหมด <b>${total}</b>)` : ""}`
    + ` · ห้อง <b>${room?.roomId ?? "—"}</b>`
    + ` · <a href="${dashUrl}" target="_blank" rel="noopener" style="color:#f5c542;text-decoration:none;font-weight:600;" title="เปิดเรดาร์ดูความเคลื่อนไหวผู้เล่นสด">📊 แดชบอร์ด</a>`;
}

/* ---------- แชท ---------- */

/** กำลังพิมพ์อยู่หรือเปล่า ถ้าใช่ต้องไม่ให้ปุ่มเดินและสกิลทำงาน */
let chatting = false;

function addChat(html: string, cls = ""): void {
  const line = document.createElement("div");
  if (cls) line.className = cls;
  line.innerHTML = html;
  uiChatLog.appendChild(line);
  while (uiChatLog.childElementCount > 60) uiChatLog.firstElementChild?.remove();
  uiChatLog.scrollTop = uiChatLog.scrollHeight;
}

/** หนีอักขระพิเศษก่อนใส่ลง innerHTML กันข้อความของผู้เล่นแทรก HTML */
/**
 * ชื่อไอเทมที่อ่านรู้เรื่องจาก id
 *
 * server ส่งมาเป็น id เสมอตามกฎ data-driven ชื่อที่แสดงอยู่ใน items.json ที่เดียว
 * ถ้า id ไม่รู้จัก (เช่น server ใหม่กว่า client) คืน id ไปเลย ดีกว่าพังทั้งบรรทัด
 */
function itemName(id: string): string {
  return ITEMS[id]?.name ?? id;
}

function esc(text: string): string {
  const d = document.createElement("div");
  d.textContent = text;
  return d.innerHTML;
}

function openChat(): void {
  chatting = true;
  // ปล่อยปุ่มที่ค้างอยู่ ไม่งั้นเดินต่อเองระหว่างพิมพ์
  heldKeys.clear();
  uiChatInput.classList.add("on");
  uiChatInput.focus();
}

function closeChat(send: boolean): void {
  const text = uiChatInput.value.trim();
  uiChatInput.value = "";
  uiChatInput.classList.remove("on");
  uiChatInput.blur();
  chatting = false;
  if (!send || !text || !room) return;
  // คำสั่งปาร์ตี้ (/invite /kick /leave /p) แปลงเป็นคำขอแยก ไม่ส่งเป็นแชทรวม
  // ไม่งั้นพิมพ์ "/p ไปบอสกัน" แล้วทั้งแผนที่เห็น
  const partyCmd = parsePartyCommand(text);
  if (partyCmd) {
    runPartyCommand(partyCmd);
    return;
  }
  if (/^\/(?:trade|t)\b/i.test(text)) {
    const targetName = text.replace(/^\/(?:trade|t)\s*/i, "").trim();
    if (!targetName) {
      addChat(`<span class="sys">วิธีใช้: /trade &lt;ชื่อผู้เล่น&gt; หรือ /t &lt;ชื่อผู้เล่น&gt;</span>`);
      return;
    }
    const target = [...(room.state?.players?.values() ?? [])].find(
      (p: any) => p.name.toLowerCase() === targetName.toLowerCase() && p.sessionId !== room?.sessionId,
    );
    if (target) {
      room.send("tradeInvite", { targetId: target.sessionId });
      addLog(`ส่งคำชวนแลกเปลี่ยนไปยัง ${target.name} แล้ว`);
    } else {
      addChat(`<span class="sys">ไม่พบผู้เล่นชื่อ "${esc(targetName)}" ในบริเวณใกล้เคียง</span>`);
    }
    return;
  }
  if (/^\/(?:vending|stall|ตั้งร้าน)\b/i.test(text)) {
    openVendingSetup();
    return;
  }
  room.send("chat", { text });
}

uiChatInput.addEventListener("keydown", (e) => {
  // กันไม่ให้ปุ่มระหว่างพิมพ์ไปสั่งเกม
  e.stopPropagation();
  if (e.key === "Enter") closeChat(true);
  else if (e.key === "Escape") closeChat(false);
});

/* ---------- ไอคอนบัฟที่ติดอยู่ ---------- */

function updateBuffIcons(): void {
  const self: any = room?.state?.players?.get(room.sessionId);
  const ids: string[] = self?.buffs ? String(self.buffs).split(",").filter(Boolean) : [];
  // ดีบัฟบนตัวเรา (พิษ ช้า อ่อนแรง) ต่อท้ายแถวเดียวกัน ขึ้นต้นด้วย "!" แยกจากบัฟ
  for (const k of String(self?.debuffs ?? "").split(",").filter(Boolean)) ids.push(`!${k}`);

  if (ids.length === 0) {
    if (uiBuffs.childElementCount > 0) {
      uiBuffs.textContent = "";
      uiBuffs.dataset["sig"] = "";
    }
    return;
  }

  const now = performance.now();

  // สร้างใหม่เฉพาะตอนชุดบัฟเปลี่ยน
  const signature = ids.join(",");
  if (uiBuffs.dataset["sig"] !== signature) {
    uiBuffs.dataset["sig"] = signature;
    uiBuffs.textContent = "";
    for (const id of ids) {
      if (id.startsWith("!")) {
        const kind = id.slice(1);
        const node = document.createElement("div");
        node.className = "buff debuff";
        node.dataset["id"] = id;
        node.title = `${STATUS_TH[kind] ?? kind} — แก้ได้ด้วยยาแก้พิษ/ยาสมุนไพรชำระภัย หรือสกิลชำระล้าง`;
        node.innerHTML = `<span class="b-icon" style="background:${debuffIcon(kind)} center / cover no-repeat"></span>`
          + `<span class="b-name">${esc(STATUS_TH[kind] ?? kind)}</span><span class="b-time"></span>`;
        uiBuffs.appendChild(node);
        continue;
      }
      const sk = findSkillAnywhere(id);
      const glyph = buffGlyph(id);
      const name = sk ? sk.th : id === BLESSING_ID ? BLESSING_TH : id;
      const desc = sk ? buffBonusText(sk) : id === BLESSING_ID ? describeBlessing() : "";
      const bg = `${sk ? skillIcon(sk) : buffIcon(id)} center / cover no-repeat`;

      if (!buffEndsAt.has(id)) {
        const dur = sk?.buff?.duration ?? (id === BLESSING_ID ? BLESSING.seconds : 10);
        buffEndsAt.set(id, now + dur * 1000);
      }

      const left = Math.max(0, (buffEndsAt.get(id) ?? 0) - now);
      const sec = Math.ceil(left / 1000);

      const node = document.createElement("div");
      node.className = "buff";
      node.dataset["id"] = id;
      node.title = `${name}${sec > 0 ? ` (${sec} วิ)` : ""} — ${desc || name}`;
      node.innerHTML =
        `<span class="b-icon" style="background:${bg}"></span>`
        + `<span class="b-name">${esc(name)}</span>`
        + `<span class="b-time">${sec > 0 ? `${sec}s` : ""}</span>`;
      uiBuffs.appendChild(node);
    }
    if (winChar?.isOpen()) drawCharWindow();
  }

  for (const node of uiBuffs.children) {
    const el = node as HTMLElement;
    const id = el.dataset["id"] ?? "";
    const left = (buffEndsAt.get(id) ?? 0) - now;
    const timeEl = el.querySelector(".b-time");
    if (timeEl) {
      timeEl.textContent = left > 0 ? `${Math.ceil(left / 1000)}s` : "";
    }
  }

  if (winChar?.isOpen()) {
    const rows = winChar.body.querySelectorAll<HTMLElement>(".char-buff-row");
    for (const row of rows) {
      const id = row.dataset["buffId"];
      if (!id) continue;
      const left = Math.max(0, (buffEndsAt.get(id) ?? 0) - now);
      const sec = Math.ceil(left / 1000);
      const timeEl = row.querySelector(".char-buff-time");
      if (timeEl) timeEl.textContent = sec > 0 ? `${sec} วิ` : "ติดอยู่";
    }
  }
}

/* ---------- แถบเลือดเหนือหัวในฉาก ---------- */

/** ไกลเกินนี้ไม่ต้องวาดแถบ อ่านไม่ออกอยู่ดีและเปลืองแรง */
const BAR_RANGE = 34;
/**
 * ระยะที่แสดงแถบของมอนสเตอร์ที่ยังไม่ได้ยุ่งด้วย
 *
 * สั้นกว่า BAR_RANGE มากเพราะทุ่งมีมอนหลายสิบตัว ถ้าโชว์ชื่อทุกตัวในระยะ 34
 * ป้ายจะทับกันจนอ่านไม่ออกและบังฉากทั้งหน้าจอ
 * ตัวที่กำลังสู้อยู่ ตัวที่เลือกไว้ และบอส ยังโชว์ได้ไกลเท่าเดิม
 */
const IDLE_BAR_RANGE = 13;
/** ป้ายสองอันใกล้กันเกินกี่พิกเซลถือว่าทับกัน */
const BAR_GAP = 26;

interface WorldBar {
  root: HTMLDivElement;
  fill: HTMLElement;
  name: HTMLElement;
}
const worldBarPool: WorldBar[] = [];

function takeWorldBar(i: number): WorldBar {
  const hit = worldBarPool[i];
  if (hit) return hit;
  const root = document.createElement("div");
  root.className = "wbar";
  root.innerHTML = `<div class="nm"></div><div class="track"><i></i></div>`;
  worldBars.appendChild(root);
  const made: WorldBar = {
    root,
    fill: root.querySelector(".track i") as HTMLElement,
    name: root.querySelector(".nm") as HTMLElement,
  };
  worldBarPool[i] = made;
  return made;
}

/**
 * วาดแถบเลือดเหนือหัวมอนสเตอร์และผู้เล่นคนอื่น
 *
 * ใช้ div ซ้อนบน canvas แทนการวาดในฉาก 3D เพราะตัวหนังสือคมกว่า
 * และตรงกับที่ GDD เลือกไว้ว่า UI เป็น HTML/CSS ซ้อนบนเกม
 */
function updateWorldBars(): void {
  if (!room) return;
  let used = 0;

  /** ป้ายที่วาดไปแล้วในเฟรมนี้ ใช้กันป้ายทับกัน */
  const placed: Array<{ x: number; y: number }> = [];

  const draw = (
    pos: Vector3,
    headroom: number,
    label: string,
    hp: number,
    maxHp: number,
    cssClass: string,
    important: boolean,
  ): void => {
    const range = important ? BAR_RANGE : IDLE_BAR_RANGE;
    if (Math.hypot(pos.x - myX, pos.z - myZ) > range) return;
    const sp = Vector3.Project(
      new Vector3(pos.x, pos.y + headroom, pos.z),
      Matrix.Identity(),
      scene.getTransformMatrix(),
      cam.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
    );
    // อยู่หลังกล้องหรือนอกจอก็ข้าม
    if (sp.z < 0 || sp.z > 1) return;

    // ป้ายที่ไม่สำคัญและไปทับป้ายที่วาดไว้แล้ว ให้ข้ามไปเลย
    // ดีกว่าวาดซ้อนกันจนอ่านไม่ออกทั้งคู่
    if (!important && placed.some((q) => Math.abs(q.x - sp.x) < BAR_GAP && Math.abs(q.y - sp.y) < 14)) {
      return;
    }
    placed.push({ x: sp.x, y: sp.y });

    const b = takeWorldBar(used++);
    b.root.className = `wbar ${cssClass}`;
    b.root.style.left = `${sp.x}px`;
    b.root.style.top = `${sp.y}px`;
    b.name.innerHTML = label;
    b.fill.style.width = `${maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0}%`;
  };

  for (const [id, av] of monsters) {
    const m: any = room.state?.monsters?.get(id);
    if (!m || m.dead) continue;
    const def = MONSTERS[m.type];
    const cls = (def?.boss ? "boss " : "") + (id === selectedId ? "sel" : "");
    // ตัวที่เลือกไว้ ตัวที่เลือดพร่อง (กำลังมีคนตีอยู่) และบอส ถือว่าสำคัญ
    const important = id === selectedId || m.hp < m.maxHp || !!def?.boss;
    draw(av.mesh.position, def?.boss ? 2.1 : 1.15, def?.name ?? m.type, m.hp, m.maxHp, cls, important);
  }

  for (const [id, av] of players) {
    if (id === room.sessionId) continue;
    const p: any = room.state?.players?.get(id);
    if (!p || p.dead) continue;
    // เพื่อนร่วมห้องโชว์เสมอ คนมีไม่เยอะเท่ามอนและต้องรู้ว่าใครอยู่ตรงไหน
    const vTitle = p.vendingTitle ? `🏪 [${p.vendingTitle}] ` : "";
    draw(av.mesh.position, 1.5, `${vTitle}${p.name}`, p.hp, p.maxHp, p.vendingTitle ? "ally vending" : "ally", true);
  }

  for (const [id, av] of npcs) {
    let questPrefix = "";
    const p = selfPlayer();
    if (p) {
      const qs = getNpcQuestStatus(id, p.baseLevel);
      if (qs === "ready") questPrefix = `<span class="q-mark ready">❓</span> `;
      else if (qs === "available") questPrefix = `<span class="q-mark avail">❗</span> `;
    }
    const actions = av.def.actions ?? [];
    let roleIcon = "";
    if (actions.includes("shop")) roleIcon = "🛒 ";
    else if (actions.includes("refine")) roleIcon = "🔨 ";
    else if (actions.includes("storage")) roleIcon = "📦 ";
    else if (actions.includes("save_point") || actions.includes("blessing")) roleIcon = "🏮 ";

    const titleLine = av.def.title ? `<span class="title-tag">&lt;${av.def.title}&gt;</span>` : "";
    const npcLabel = `${questPrefix}${roleIcon}${av.def.name}${titleLine}`;
    draw(av.mesh.position, 1.45, npcLabel, 0, 0, "npc", true);
  }

  for (const [, drop] of groundDrops) {
    const isGold = drop.itemId === "gold";
    const def = isGold ? undefined : ITEMS[drop.itemId];
    const name = isGold
      ? `💰 ${drop.count.toLocaleString()} Gold`
      : `${itemGlyph(def)} ${def?.name ?? drop.itemId}${drop.count > 1 ? ` x${drop.count}` : ""}`;
    const colorClass = isGold ? "drop-gold" : `drop-${def?.rarity ?? "common"}`;
    draw(drop.mesh.position, 0.45, name, 0, 0, `drop-label ${colorClass}`, true);
  }

  // แถบของตัวเอง วางใต้เท้า แยกจาก pool เพราะมีสองแถบซ้อนและไม่มีชื่อ
  const me = players.get(room.sessionId);
  const meState: any = room.state?.players?.get(room.sessionId);
  if (settings.selfBar && me && meState && !meState.dead) {
    const sp = Vector3.Project(
      // ลบความสูงครึ่งตัวออก ให้แถบไปอยู่ระดับพื้นใต้เท้าพอดี
      new Vector3(me.mesh.position.x, me.mesh.position.y - 0.85, me.mesh.position.z),
      Matrix.Identity(),
      scene.getTransformMatrix(),
      cam.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
    );
    if (sp.z >= 0 && sp.z <= 1) {
      uiSelfBar.classList.add("on");
      uiSelfBar.style.left = `${sp.x}px`;
      uiSelfBar.style.top = `${sp.y}px`;
      uiSelfHp.style.width = `${pct(meState.hp, meState.maxHp)}%`;
      uiSelfMp.style.width = `${pct(meState.mp, meState.maxMp)}%`;
    } else {
      uiSelfBar.classList.remove("on");
    }
  } else {
    uiSelfBar.classList.remove("on");
  }

  for (let i = used; i < worldBarPool.length; i++) {
    const b = worldBarPool[i];
    if (b) b.root.style.display = "none";
  }
  for (let i = 0; i < used; i++) {
    const b = worldBarPool[i];
    if (b) b.root.style.display = "";
  }
}

/**
 * รัศมีที่ยอมให้คลิกใกล้ ๆ มอนแล้วนับว่าเลือกตัวนั้น แทนที่จะสั่งเดิน
 * มากกว่ารัศมีชนจริงของ hitbox มอนพอสมควร เพราะคลิกแม่นยากเป๊ะยากบนจอเล็ก/นิ้ว
 * ตัวเลขเป็นหน่วยโลก ไม่ใช่พิกเซล จึงใกล้ไกลเท่ากันไม่ว่ากล้องจะซูมแค่ไหน
 */
const MONSTER_PICK_RADIUS = 1.4;

/** มอนที่ยังไม่ตายซึ่งอยู่ใกล้จุด (x, z) ที่สุดในรัศมีที่ยอมรับ ไม่มี = undefined */
function nearestMonsterNear(x: number, z: number): string | undefined {
  let best: string | undefined;
  let bestD = MONSTER_PICK_RADIUS;
  for (const [id, av] of monsters) {
    const mob = room?.state?.monsters?.get(id);
    if (!mob || mob.dead) continue;
    const d = Math.hypot(av.mesh.position.x - x, av.mesh.position.z - z);
    if (d < bestD) {
      bestD = d;
      best = id;
    }
  }
  return best;
}

/* ---------- คลิกเพื่อเดินหรือตี ---------- */
scene.onPointerObservable.add((info) => {
  try {
  // ต้องใช้ POINTERTAP ไม่ใช่ POINTERDOWN
  // POINTERDOWN ยิงตอนเริ่มกดด้วย การลากเมาส์เพื่อหมุนกล้องจึงกลายเป็น
  // คำสั่งเดินไปจุดที่เริ่มลาก พอกล้องหมุนตามเมาส์ ภาพเลยดูเหมือนตัวละคร
  // วิ่งตามเมาส์ไปเรื่อย ๆ  POINTERTAP ยิงเฉพาะกดแล้วปล่อยที่เดิมโดยไม่ลาก
  if (info.type !== PointerEventTypes.POINTERTAP || !room) return;
  // ขณะจอยค้างอยู่ การแตะของนิ้วที่สองถูกจัดการแยกด้านล่าง (handleTapWhileJoy)
  if (joyActive) return;

  // เฉพาะปุ่มซ้ายเท่านั้นที่สั่งเดินและสั่งตี
  // ปุ่มขวาสงวนไว้ให้หมุนกล้องอย่างเดียว (บนมือถือการแตะนับเป็นปุ่ม 0 จึงยังใช้ได้)
  if ((info.event as PointerEvent).button !== 0) return;

  const pick = info.pickInfo ?? scene.pick(scene.pointerX, scene.pointerY);
  handleWorldTap(pick);
  } catch (err) {
    showError("คลิก", err);
  }
});

/**
 * แตะด้วยนิ้วที่สองขณะนิ้วแรกค้างอยู่บนจอยสติก
 * Babylon ไม่ยิง POINTERTAP ให้นิ้วนี้ (และ observer ข้ามตอน joyActive) จึงดักจาก canvas เอง
 * แตะสั้น ๆ ไม่ลาก = เลือกมอน/เก็บของ/คุย NPC เหมือนแตะปกติ
 */
const joyTaps = new Map<number, { x: number; y: number; t: number }>();
canvas.addEventListener("pointerdown", (e) => {
  if (!joyActive || e.pointerType === "mouse") return;
  joyTaps.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
});
canvas.addEventListener("pointerup", (e) => {
  const start = joyTaps.get(e.pointerId);
  joyTaps.delete(e.pointerId);
  if (!start || !joyActive || !room) return;
  if (performance.now() - start.t > 400) return;
  if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 14) return;
  try {
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
    handleWorldTap(scene.pick(sx, sy), sx, sy);
  } catch (err) {
    showError("แตะ", err);
  }
});
canvas.addEventListener("pointercancel", (e) => joyTaps.delete(e.pointerId));

function handleWorldTap(
  pick: ReturnType<typeof scene.pick> | null | undefined,
  px: number = scene.pointerX,
  py: number = scene.pointerY,
): void {
  if (!room) return;
  if (!pick?.hit || !pick.pickedMesh) return;

  hidePlayerActionModal();

  const meta = pick.pickedMesh.metadata as { kind?: string; id?: string } | undefined;
  if (meta?.kind === "drop" && meta.id) {
    const drop = groundDrops.get(meta.id);
    if (drop) {
      const d = Math.hypot(drop.x - myX, drop.z - myZ);
      if (d <= 5.0) {
        room.send("pickDrop", { id: meta.id });
        sound.playLoot();
      } else {
        wantX = drop.x;
        wantZ = drop.z;
        pendingNpcId = "";
        cancelPending();
        if (autoAttack) stopAttacking();
      }
    }
    return;
  }

  if (meta?.kind === "monster" && meta.id) {
    // คลิกที่ตัวมอนสเตอร์: เปิด clickAttack = เลือกแล้วสั่งตีเลย ปิด = เลือกไว้เฉย ๆ
    // ตีแล้วจะตีต่อเองจนมอนตาย (clearTarget ใน onChange) กด Esc หรือเดินหนี (หยุดตี)
    selectTarget(meta.id, settings.clickAttack);
    return;
  }

  if (meta?.kind === "lantern") {
    // คลิกตะเกียงจุดวาร์ป: ใกล้พอก็จุดเลย ไกลก็เดินไปแล้วจุดเอง (server ตรวจระยะ)
    const zn = ALL_ZONES[currentZoneId()];
    if (zn && hasLantern(zn)) {
      const l = lanternPos(zn);
      if (Math.hypot(l.x - myX, l.z - myZ) <= LANTERN_RANGE) {
        room.send("lightLantern");
        pendingLantern = false;
      } else {
        wantX = l.x;
        wantZ = l.z;
        pendingLantern = true;
        pendingNpcId = "";
        cancelPending();
        if (autoAttack) stopAttacking();
      }
    }
    return;
  }

  if (meta?.kind === "npc" && meta.id) {
    const npc = npcs.get(meta.id);
    if (npc) {
      const d = Math.hypot(npc.def.x - myX, npc.def.z - myZ);
      if (d <= NPC_INTERACT_RANGE + 0.6) {
        room.send("npcTalk", { npcId: meta.id });
        pendingNpcId = "";
        wantX = myX;
        wantZ = myZ;
      } else {
        wantX = npc.def.x;
        wantZ = npc.def.z;
        pendingNpcId = meta.id;
        cancelPending();
        if (autoAttack) stopAttacking();
      }
    }
    return;
  }

  if (meta?.kind === "player" && meta.id && meta.id !== room.sessionId) {
    const p: any = room.state?.players?.get(meta.id);
    if (p) {
      if (p.vendingTitle) {
        room.send("vendingBrowse", { ownerId: meta.id });
      } else {
        showPlayerActionModal(meta.id, p.name, px, py);
      }
    }
    return;
  }

  if (pick.pickedMesh === ground && pick.pickedPoint) {
    // คลิกใกล้ไอเทมดรอปบนพื้น ให้ถือว่าต้องการเก็บไอเทมนั้น
    const nearDrop = nearestDropNear(pick.pickedPoint.x, pick.pickedPoint.z);
    if (nearDrop) {
      const drop = groundDrops.get(nearDrop);
      if (drop) {
        const d = Math.hypot(drop.x - myX, drop.z - myZ);
        if (d <= 5.0) {
          room.send("pickDrop", { id: nearDrop });
          sound.playLoot();
        } else {
          wantX = drop.x;
          wantZ = drop.z;
          pendingNpcId = "";
          cancelPending();
          if (autoAttack) stopAttacking();
        }
        return;
      }
    }

    // คลิกพลาดตัวมอนไปโดนพื้นข้าง ๆ (hitbox มอนเล็ก คลิกแม่นยากโดยเฉพาะบนมือถือ)
    // หามอนที่ใกล้จุดคลิกที่สุดในรัศมีก่อน ถ้าเจอถือว่าตั้งใจเลือกตัวนั้น ไม่ใช่สั่งเดิน
    const near = nearestMonsterNear(pick.pickedPoint.x, pick.pickedPoint.z);
    if (near) {
      selectTarget(near, settings.clickAttack);
      return;
    }
    wantX = pick.pickedPoint.x;
    wantZ = pick.pickedPoint.z;
    pendingNpcId = "";
    cancelPending();
    // เดินออกไปคือ "ถอย" ไม่ใช่ "เลิกสนใจ" จึงหยุดตีแต่ยังล็อกเป้าไว้
    // จะได้ถอยไปตั้งหลักแล้วกด F ตีต่อตัวเดิมได้ ยกเลิกเป้าจริงต้องกด Esc
    if (autoAttack) stopAttacking();
  }
}

/* ---------- ลูปหลัก ---------- */
const WALK_SPEED = 5;

/* ---------- คีย์บอร์ด WASD ---------- */

/**
 * ปุ่มที่กดค้างอยู่ตอนนี้
 *
 * ใช้ event.code ไม่ใช่ event.key เพราะ code อ้างตำแหน่งปุ่มบนแป้น
 * สลับเป็นภาษาไทยอยู่ก็ยังเดินได้ (event.key จะได้ "ไ" "ห" แทน w a)
 */
const heldKeys = new Set<string>();

/** แปลงคำสั่งเป็นทิศเดิน คืน null ถ้าไม่ใช่คำสั่งเดิน */
function dirOf(action: ActionId | undefined): "f" | "b" | "l" | "r" | null {
  switch (action) {
    case "moveF": return "f";
    case "moveB": return "b";
    case "moveL": return "l";
    case "moveR": return "r";
    default: return null;
  }
}

/** ปิดหน้าต่างย่อยทุกบาน คืน true ถ้ามีบานไหนเปิดอยู่จริง */
function closeAllWindows(): boolean {
  const any = winChar.isOpen() || winBag.isOpen() || winSkill.isOpen() || winShop.isOpen() || winRefine.isOpen()
    || winParty.isOpen() || winQuest.isOpen() || winKeys.isOpen() || winTrade.isOpen() || winVending.isOpen() || winVendingBrowse.isOpen()
    || winStorage.isOpen() || uiNpcDialog.classList.contains("on") || uiPlayerAct.classList.contains("on");
  winChar.close();
  winBag.close();
  winSkill.close();
  winShop.close();
  winRefine.close();
  winParty.close();
  winQuest.close();
  winKeys.close();
  winTrade.close();
  winVending.close();
  winVendingBrowse.close();
  winStorage.close();
  uiNpcDialog.classList.remove("on");
  hidePlayerActionModal();
  syncMenuState();
  return any;
}

/**
 * ทำตามคำสั่งหนึ่งตัว
 *
 * **ไม่มีที่ไหนในนี้รู้จักชื่อปุ่ม** ปุ่มถูกแปลเป็นคำสั่งไปแล้วตั้งแต่ตัวจับ keydown
 * เพิ่มคำสั่งใหม่ต้องเติมใน `ACTIONS` ของ keys.ts แล้วมาเพิ่ม case ที่นี่
 * คืน true ถ้ากินปุ่มนั้นไปแล้ว (ผู้เรียกจะ preventDefault ให้)
 */
function runAction(action: ActionId): boolean {
  // แถบลัด: แต่ละช่องผูกสกิลหรือไอเทมตามที่ผู้เล่นจัด
  const barSlot = BAR_SLOTS.find((x) => x.action === action);
  if (barSlot) {
    if (barSlot.skill) {
      const sk = findSkillAnywhere(barSlot.skill);
      if (sk) castSkill(sk);
    } else if (barSlot.item) {
      triggerHotbarItem(barSlot.item);
    }
    return true;
  }

  switch (action) {
    case "chat":
      openChat();
      return true;

    case "targetNext":
      cycleTarget();
      return true;

    case "cancel":
      // ปิดหน้าต่างที่เปิดอยู่ก่อน ถ้าไม่มีก็ยกเลิกเป้าหมาย
      if (!closeAllWindows()) clearTarget();
      return true;

    // สั่งตีธรรมดาใส่เป้าที่เลือกไว้ กดซ้ำเพื่อหยุด
    // แยกจากการร่ายสกิลโดยตั้งใจ ร่ายสกิลแล้วจะไม่เดินเข้าไปตีเอง
    // อาชีพยิงไกลจึงยืนระยะได้ ยิงแล้วถอยได้ ไม่ถูกลากเข้าไปใกล้
    case "attack":
      toggleAttack();
      return true;

    case "pickAll":
      if (room) {
        room.send("pickAll");
        sound.playLoot();
      }
      return true;

    case "camReset":
      cam.alpha = CAM_HOME.alpha;
      cam.beta = CAM_HOME.beta;
      cam.radius = CAM_HOME.radius;
      return true;

    case "soundToggle": {
      settings.sound = !settings.sound;
      sound.setEnabled(settings.sound);
      saveSettings();
      addLog(settings.sound ? "🔊 เปิดเสียงเอฟเฟกต์แล้ว" : "🔇 ปิดเสียงเอฟเฟกต์แล้ว");
      if (settings.sound) sound.playClick();
      buildSetPanel();
      return true;
    }

    case "selfBar":
    case "selfArrow": {
      settings[action] = !settings[action];
      saveSettings();
      const name = action === "selfBar" ? "แถบเลือดใต้ตัวละคร" : "ลูกศรชี้ตัวเอง";
      addLog(`${name}: ${settings[action] ? "เปิด" : "ปิด"}`);
      buildSetPanel();
      return true;
    }

    case "winChar": winChar.toggle(); syncMenuState(); return true;
    case "winBag": winBag.toggle(); syncMenuState(); return true;
    case "winSkill": winSkill.toggle(); syncMenuState(); return true;
    case "winShop": winShop.toggle(); syncMenuState(); return true;
    case "winRefine": winRefine.toggle(); syncMenuState(); return true;
    case "winParty": winParty.toggle(); syncMenuState(); return true;
    case "winQuest": winQuest.toggle(); syncMenuState(); return true;
    case "winKeys": winKeys.toggle(); syncMenuState(); return true;
    case "winMap": winWorldMap.toggle(); return true;
    case "winSettings": togglePanel(uiSetPanel); return true;
    case "winHelp": togglePanel(uiHelp); return true;

    default:
      return false;
  }
}

window.addEventListener("keydown", (e) => {
  // ยังไม่เข้าห้อง (อยู่หน้าเลือกตัวละคร) ปุ่มทุกอย่างเป็นของหน้านั้น
  if (!room) return;
  // กำลังพิมพ์แชทอยู่ ปุ่มทุกอย่างเป็นของช่องพิมพ์
  if (chatting) return;
  // กำลังรอรับปุ่มใหม่ในหน้าตั้งค่าปุ่ม ปุ่มที่กดเป็นของหน้านั้น ไม่ใช่คำสั่งในเกม
  if (grabKey) {
    e.preventDefault();
    grabKey(e.code);
    return;
  }

  const action = lookup.get(e.code);
  if (!action) return;

  const d = dirOf(action);
  if (d) {
    heldKeys.add(d);
    // กันลูกศรเลื่อนหน้าเว็บ
    e.preventDefault();
    return;
  }

  // Tab ต้องกันไว้ ไม่งั้นเบราว์เซอร์ย้าย focus ไปปุ่มอื่นแทน
  if (runAction(action)) e.preventDefault();
});

window.addEventListener("keyup", (e) => {
  const d = dirOf(lookup.get(e.code));
  if (d) heldKeys.delete(d);
});
// สลับหน้าต่างแล้วปุ่มค้าง ตัวละครจะเดินไม่หยุด จึงต้องล้างทิ้ง
window.addEventListener("blur", () => heldKeys.clear());
let hudTimer = 0;

/** แจ้ง error ของ render loop ครั้งเดียวพอ ไม่งั้นท่วมทุกเฟรม */
let loopErrShown = false;

scene.onBeforeRenderObservable.add(() => {
  try {
  const dt = engine.getDeltaTime() / 1000;
  const self = room ? players.get(room.sessionId) : undefined;
  const selfState: any = room?.state?.players?.get(room.sessionId);

  if (self && room && hasSelf && selfState && !selfState.dead) {
    // ---- WASD หรือ จอยสติกเสมือน มาก่อน ถ้ากดอยู่ให้ยกเลิกการเดินตามคลิก ----
    let kx = 0;
    let kz = 0;
    if (heldKeys.has("f")) kz += 1;
    if (heldKeys.has("b")) kz -= 1;
    if (heldKeys.has("l")) kx -= 1;
    if (heldKeys.has("r")) kx += 1;

    // รวมค่าจากจอยสติกเสมือนบนมือถือ/จอสัมผัส
    if (joyActive && (joyKx !== 0 || joyKz !== 0)) {
      kx += joyKx;
      kz += joyKz;
    }

    const inputMag = Math.min(1, Math.hypot(kx, kz));

    if (inputMag > 0.05) {
      wasMovingManually = true;
      // เดินอิงทิศกล้อง กด W หรือดันจอยขึ้นคือไปทางที่กล้องหันอยู่ ไม่ใช่ทิศคงที่ของโลก
      const fx = cam.target.x - cam.position.x;
      const fz = cam.target.z - cam.position.z;
      const fl = Math.hypot(fx, fz) || 1;
      const fwdX = fx / fl;
      const fwdZ = fz / fl;
      // ขวา = up × forward ในระบบพิกัดซ้ายมือของ Babylon
      const rightX = fwdZ;
      const rightZ = -fwdX;

      let dirX = fwdX * kz + rightX * kx;
      let dirZ = fwdZ * kz + rightZ * kx;
      const dl = Math.hypot(dirX, dirZ) || 1;
      dirX /= dl;
      dirZ /= dl;

      const currentSpeed = WALK_SPEED * inputMag;
      // ชนป่าแล้วไถลไปตามขอบ กฎเดียวกับ server (shared/terrain.ts)
      const to = slideMove(ALL_ZONES[currentZoneId()], myX, myZ, myX + dirX * currentSpeed * dt, myZ + dirZ * currentSpeed * dt);
      myX = to.x;
      myZ = to.z;
      wantRy = Math.atan2(dirX, dirZ);

      // เลิกเดินตามคลิกและเลิกวิ่งเข้าหาเป้า ไม่งั้นสองระบบจะแย่งกันคุม
      wantX = myX;
      wantZ = myZ;
      // เดินด้วยคีย์บอร์ดแล้วเลิกวิ่งเข้าหาเป้า แต่ยังเลือกเป้าไว้เหมือนเดิม
      // ส่วนจอยสติกบนมือถือต้องกดตีระหว่างเดินได้ จึงไม่ยกเลิกคำสั่งตี
      if (autoAttack && !joyActive) stopAttacking();
      // ตั้งใจเดินเองด้วยคีย์บอร์ด ไม่ควรโดนลากกลับไปปล่อยสกิลที่กดค้างไว้
      // แต่ถ้าใช้จอยสติกบนมือถือ ให้รักษาสกิลที่กดไว้จนกว่าจะเข้าระยะหรือหมดเวลา
      if (!joyActive) cancelPending();
      pendingNpcId = "";
      pendingLantern = false;

      room.send("move", { x: myX, z: myZ, ry: wantRy });

      if (self) {
        self.mesh.position.x = myX;
        self.mesh.position.z = myZ;
        self.mesh.rotation.y = wantRy;
      }
    } else {
      if (wasMovingManually) {
        wasMovingManually = false;
        room.send("move", { x: myX, z: myZ, ry: wantRy });
      }

    // ถ้าล็อกมอนสเตอร์ไว้ ให้เดินเข้าไปจนอยู่ในระยะโจมตีของอาชีพ
    const locked: any = autoAttack && selectedId ? room.state?.monsters?.get(selectedId) : undefined;
    if (locked && !locked.dead) {
      const reach = (CLASSES[selfState.cls]?.range ?? 2) - 0.3;
      const d = Math.hypot(locked.x - myX, locked.z - myZ);
      if (d > reach) {
        const k = (d - reach) / d;
        wantX = myX + (locked.x - myX) * k;
        wantZ = myZ + (locked.z - myZ) * k;
      } else {
        wantX = myX;
        wantZ = myZ;
        // ถึงระยะแล้วยืนตีอยู่กับที่ ต้องหันหน้าหาเป้า (ทิศไม่ได้เปลี่ยนเพราะไม่ได้เดิน)
        const face = Math.atan2(locked.x - myX, locked.z - myZ);
        if (Math.abs(face - wantRy) > 0.05) {
          wantRy = face;
          room.send("move", { x: myX, z: myZ, ry: wantRy });
        }
      }
    }

    // สกิลที่รอเข้าระยะ สั่งทีหลังการเดินเข้าหาเป้าของการตีปกติ
    // เพราะถ้ากดสกิลไว้ ความตั้งใจนั้นควรเป็นตัวกำหนดว่าจะเดินไปไหน
    updatePendingSkill();
    checkLantern();

    if (pendingNpcId) {
      const npc = npcs.get(pendingNpcId);
      if (npc) {
        const dNpc = Math.hypot(npc.def.x - myX, npc.def.z - myZ);
        if (dNpc <= NPC_INTERACT_RANGE + 0.6) {
          room.send("npcTalk", { npcId: pendingNpcId });
          pendingNpcId = "";
          wantX = myX;
          wantZ = myZ;
        }
      } else {
        pendingNpcId = "";
      }
    }

    // เดินจากตำแหน่งที่ทำนายเอง แล้วบอก server ว่าไปถึงไหนแล้ว
    // ปลายทางอยู่อีกฝั่งป่า: เดินอ้อมตามทาง (findPath) โดยปลายทางเดิมไม่เปลี่ยน
    const step0 = navStep(wantX, wantZ);
    const dx = step0.x - myX;
    const dz = step0.z - myZ;
    const d = Math.hypot(dx, dz);
    if (d > 0.05) {
      wantRy = Math.atan2(dx, dz);
      const step = Math.min(d, WALK_SPEED * dt);
      const to = slideMove(ALL_ZONES[currentZoneId()], myX, myZ, myX + (dx / d) * step, myZ + (dz / d) * step);
      if (to.x === myX && to.z === myZ) {
        // ชนป่าตรง ๆ ไปต่อไม่ได้ เลิกเดิน ไม่งั้นยืนดันต้นไม้ไปตลอด
        wantX = myX;
        wantZ = myZ;
      }
      myX = to.x;
      myZ = to.z;
      room.send("move", { x: myX, z: myZ, ry: wantRy });
    }
    }
  }

  // ตัวเองวาดที่ตำแหน่งที่ทำนายไว้ จะได้ตอบสนองทันทีไม่ต้องรอ server
  if (self && hasSelf) {
    self.mesh.position.x = myX;
    self.mesh.position.z = myZ;
    self.mesh.rotation.y = wantRy;
  }

  // คนอื่นและมอนสเตอร์ ค่อย ๆ ขยับเข้าหาตำแหน่งที่ server ยืนยัน
  for (const [id, av] of players) {
    if (room && id === room.sessionId) continue;
    const k = Math.min(1, dt * 12);
    av.mesh.position.x += (av.tx - av.mesh.position.x) * k;
    av.mesh.position.z += (av.tz - av.mesh.position.z) * k;
    av.mesh.rotation.y = av.try_;
  }
  for (const av of monsters.values()) {
    const k = Math.min(1, dt * 12);
    av.mesh.position.x += (av.tx - av.mesh.position.x) * k;
    av.mesh.position.z += (av.tz - av.mesh.position.z) * k;
    av.mesh.rotation.y = av.try_;
  }

  // กัน NaN หลุดเข้ากล้อง ถ้าหลุดไปแล้วกล้องจะเล็งไปที่ไม่มีอะไรและจอว่างถาวร
  if (self && Number.isFinite(self.mesh.position.x) && Number.isFinite(self.mesh.position.z)) {
    cam.target.copyFromFloats(self.mesh.position.x, 1, self.mesh.position.z);
    // ริมขอบแผนที่: หดกล้องไม่ให้ไปอยู่หลังวงภูเขา (cam-fit.ts) · ผู้เล่นซูมเอง (radius ไม่ตรงค่าที่เราตั้งรอบก่อน) = ระยะที่อยากได้ใหม่
    if (Math.abs(cam.radius - camSetRadius) > 0.01) camWantRadius = cam.radius;
    const zn = ALL_ZONES[currentZoneId()];
    camSetRadius = zn
      ? fitCameraRadius(cam.target.x, cam.target.z, cam.alpha, cam.beta, camWantRadius, zoneHalf(zn) + 2, cam.lowerRadiusLimit ?? 8)
      : camWantRadius;
    cam.radius = camSetRadius;
  }

  // ท่าทางของตัวละครและมอนสเตอร์ ขับจากตำแหน่งที่ interpolate แล้ว
  // จึงต้องเรียกหลังจากขยับ mesh ของเฟรมนี้เสร็จ
  animTime += dt;
  animateAll(performance.now(), animTime);
  animatePets(dt, animTime);
  // รูปหน้าตัวละครวาดแค่ช่วงแรกแล้วหยุดเอง ไม่กินเฟรมตลอดเวลา
  portrait?.tick(dt);

  fx3d.update(dt);

  // อัปเดตอนิเมชันไอเทมบนพื้น และเช็คว่ามีของตกใกล้ตัวให้แสดงปุ่มเก็บของไหม
  const dropTime = performance.now();
  for (const drop of groundDrops.values()) {
    drop.mesh.position.y = 0.22 + Math.sin(dropTime / 220 + Number(drop.x)) * 0.08;
    drop.mesh.rotation.y += 0.025;
  }
  let hasNearbyDrops = false;
  for (const drop of groundDrops.values()) {
    const distToMe = Math.hypot(drop.x - myX, drop.z - myZ);
    if (distToMe <= 5.0) {
      // เก็บเงินออโต้เมื่อเข้าใกล้ <= 3.5 เมตร ส่วนไอเทม/การ์ด ให้ผู้เล่นเลือกเก็บเอง
      if (drop.itemId === "gold" && distToMe <= 3.5 && !(drop as any).picking) {
        (drop as any).picking = true;
        room?.send("pickDrop", { id: drop.id });
      } else if (drop.itemId !== "gold") {
        hasNearbyDrops = true;
      }
    }
  }
  if (uiLootBtn) {
    if (hasNearbyDrops) uiLootBtn.classList.add("on");
    else uiLootBtn.classList.remove("on");
  }

  updateMarker();
  updateStatusPins();
  updateWorldBars();
  updateSelfArrow();
  updateMinimap();
  updateSkillBar();
  updateBuffIcons();

  hudTimer += dt;
  if (hudTimer > 0.2) {
    hudTimer = 0;
    updateHud();
  }
  } catch (err) {
    if (!loopErrShown) {
      loopErrShown = true;
      showError("render loop", err);
    }
  }
});

engine.runRenderLoop(() => scene.render());
window.addEventListener("resize", () => engine.resize());

/** รหัสที่ server ใช้ปฏิเสธการเข้าห้อง ต้องตรงกับค่าใน server/src/rooms/FieldRoom.ts */
const DENY_TOKEN = 4401;
const DENY_BUSY = 4409;
const DENY_CHARACTER = 4400;

/**
 * ลำดับการเข้าเกม: บัญชี → ตัวละคร → เข้าห้อง
 *
 * เข้าไม่สำเร็จแล้ว **ต้องกลับมาหน้าเดิมได้** ไม่ใช่ค้างอยู่กับข้อความ error
 * เคสที่เจอจริง: เปิดสองแท็บ แท็บที่สองใช้โทเคนเดิมจาก localStorage (ซึ่งแชร์กันทั้งเบราว์เซอร์)
 * แล้วโดนปฏิเสธเพราะบัญชีนั้นกำลังเล่นอยู่ ถ้าไม่ให้กลับมาเลือกใหม่ก็ตันอยู่ตรงนั้น
 */
async function boot(): Promise<void> {
  let accountNotice = "";
  let charNotice = "";
  let forcePrompt = false;

  // ย้ายโซนค้างอยู่ (หน้าเพิ่งรีโหลดหลังเดินเข้าประตู) เข้าห้องปลายทางเลย ไม่ต้องเลือกตัวละครใหม่
  const pending = takePendingWarp();
  if (pending) {
    try {
      await connect(pending.chosen, pending.token);
      return;
    } catch (err) {
      // ตั๋วหมดอายุหรือใช้ไม่ได้ กลับไปเข้าเกมตามปกติ ตัวละครบันทึกที่ปลายทางไว้แล้ว
      charNotice = (err as Error)?.message ?? "";
    }
  }

  for (;;) {
    const token = await chooseAccount(accountNotice, forcePrompt);
    const chosen = await chooseCharacter(charNotice, token);
    accountNotice = "";
    charNotice = "";

    try {
      await connect(chosen, token);
      return;
    } catch (err) {
      const code = (err as { code?: number })?.code;
      const text = (err as Error)?.message ?? "เข้าเกมไม่ได้";

      if (code === DENY_CHARACTER) {
        // โทเคนยังใช้ได้ แค่ตัวละครมีปัญหา ให้กลับไปเลือกใหม่
        charNotice = text;
        forcePrompt = false;
        continue;
      }
      if (code === DENY_TOKEN || code === DENY_BUSY) {
        if (code === DENY_TOKEN) clearToken();
        accountNotice = text;
        forcePrompt = true;
        continue;
      }
      throw err;
    }
  }
}

boot().catch((err: unknown) => {
  showError("เชื่อมต่อ", err);
  hud.innerHTML = `<b>ต่อเซิร์ฟเวอร์ไม่ได้</b><br /><small>สั่ง pnpm dev ค้างไว้ก่อน แล้วรีเฟรช</small>`;
});
